// ZAP v3.0
import React, { useState, useEffect, useCallback, useRef } from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  componentDidCatch(err, info) { console.error('ZAP ErrorBoundary:', err, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, background: '#1a0000', border: '2px solid #EF4444', borderRadius: 12, margin: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#EF4444', marginBottom: 8 }}>⚠️ ZAP Render Error</div>
          <div style={{ fontSize: 12, color: '#fff', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{this.state.error.toString()}</div>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 12, padding: '8px 16px', background: '#EF4444', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Dismiss</button>
        </div>
      );
    }
    return this.props.children;
  }
}


// ============================================================
// CONSTANTS & THEME
// ============================================================
// S226: Direct VC call — Netlify proxy removed. Cold start on proxy was causing VC
// to fail on first load after idle, dropping cascade to P2 unnecessarily.
// VC_API_KEY read from build-time env var (set in Netlify dashboard).
const VC_API_KEY = process.env.REACT_APP_VC_API_KEY || '';
const VC_BASE_URL = 'https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline';
const NWS_USER_AGENT = 'DrZ-Golf-Tech/1.0 (zap-golf.netlify.app)';
const CACHE_TTL = 900000; // 15 min

// Wraps any async fetch in a timeout — returns failure object instead of hanging
const withTimeout = (promise, ms, label) =>
  Promise.race([
    promise,
    new Promise(resolve =>
      setTimeout(() => resolve({ success: false, error: `${label} timed out after ${ms/1000}s` }), ms)
    ),
  ]);
const MB_API_KEY = 'H2nyzakt47srTN5U'; // S273: Swapped to working key — old key tMGERa6DujvH3Tqs exhausted (429 credits exceeded). Verified live 2026-07-11.
const GS_API_KEY = '67b3e95ac8f36707152750727cb3a72b70be8bb0';
const WB_API_KEY = '8fecb123d7ca42989b18388f4c736417';
const WAPI_KEY   = '5a005b16db4e4058b0f34523261603';

// Password gate removed — no longer needed (S96)

// Spond Events
const SPOND_EVENTS_URL = 'https://api.github.com/repos/johnzur-droid/Zap/contents/snapshots/spond_events.json?ref=staging';
const SPOND_GH_TOKEN = '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';
const SPOND_INVITE_LINK = 'https://spond.com/invite/ZRAJZ';

// WU comparison logging (admin only — silent, localStorage)
const WU_LOG_KEY = 'zap_wu_compare_log';
const WU_LOG_MAX = 500; // max entries before truncation

function logWUComparison(courseName, vcData, wuData) {
  try {
    const entry = {
      ts: new Date().toISOString(),
      course: courseName,
      vc: {
        temp: vcData?.temp,
        windSpeed: vcData?.windSpeed,
        windGust: vcData?.windGust,
        humidity: vcData?.humidity,
        precipChance: vcData?.precipChance,
        conditions: vcData?.conditions,
        source: vcData?.source,
      },
      wu: wuData || null, // null until WU API is wired — slot reserved
    };
    const raw = localStorage.getItem(WU_LOG_KEY);
    const log = raw ? JSON.parse(raw) : [];
    log.push(entry);
    if (log.length > WU_LOG_MAX) log.splice(0, log.length - WU_LOG_MAX);
    localStorage.setItem(WU_LOG_KEY, JSON.stringify(log));
  } catch (e) {
    console.warn('WU log write failed:', e);
  }
}

function getWULog() {
  try {
    const raw = localStorage.getItem(WU_LOG_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) { return []; }
}

function clearWULog() {
  try { localStorage.removeItem(WU_LOG_KEY); } catch (e) {}
}

// Colors
const COLOR_BG = '#1e3620';

// iOS PWA scroll fix — window.scrollTo alone is unreliable in standalone mode
const scrollToTop = () => {
  try { document.getElementById('root').scrollTop = 0; } catch {}
};
const COLOR_HEADER = '#243524';
const COLOR_CARD = '#2a4030';
// S69 LOCKED — Tailwind status colors
// S85 UPDATE — bright bg + dark text (Set 4 approved by JZ)
const COLOR_GREEN = '#4ADE80';      // GO accent (kept for non-status uses)
const COLOR_GREEN_DARK = '#22C55E'; // GO bg (bright green)
const COLOR_GOLD = '#FACC15';       // CAUTION accent
const COLOR_ORANGE = '#FB923C';     // STRONG CAUTION accent
const COLOR_WHITE = '#ffffff';
const COLOR_OFF_WHITE = '#f0f7f0';
const COLOR_MUTED = '#a8c4a8';
const COLOR_RED = '#F87171';        // NO-GO accent
const COLOR_RED_BG = '#EF4444';     // NO-GO bg (bright red — S85)
const COLOR_GOLD_BG = '#EAB308';    // CAUTION bg (bright yellow — S85)
const COLOR_GREEN_BG = '#22C55E';   // GO bg (bright green — S85)
const COLOR_ORANGE_BG = '#F97316';  // STRONG CAUTION bg (bright orange — S85)
const COLOR_DARK_GREEN = '#3a5a3a';
const COLOR_DARKEST = '#1e3020';

// Diff color helper — consistent golf scoring colors everywhere
// negative (under par) = red #F1373D, even = green #22C55E, positive (over par) = blue #3B82F6
const getDiffColor = (diff) => {
  if (diff == null) return '#9CA3AF';
  if (diff < 0) return '#F1373D';
  if (diff > 0) return '#3B82F6';
  return '#22C55E';
};
const formatDiff = (diff) => {
  if (diff == null) return '—';
  if (diff < 0) return String(diff);
  if (diff > 0) return '+' + diff;
  return 'E';
};

// ============================================================
// S45 LOCKED GO/NO-GO THRESHOLDS
// ============================================================
// ── GO/NO-GO THRESHOLDS — S98 LOCKED ──────────────────────
const PRECIP_CAUTION  = 30;   // 0-29=GO, 30-49=CAU
const PRECIP_STR_CAU  = 50;   // 50-69=STR
const PRECIP_NOGO     = 70;   // 70+=NOGO
const WIND_CAUTION    = 15;   // 0-14=GO, 15-19=CAU
const WIND_STR_CAU    = 20;   // 20-29=STR
const WIND_NOGO       = 30;   // 30+=NOGO
// S85 LOCKED — Bright bg + dark text (Set 4 — approved by JZ)
const STATUS_COLORS = {
  GO:               { color: '#22C55E', bg: '#000000', border: '#22C55E', text: '#ffffff' },
  CAUTION:          { color: '#FDE047', bg: '#000000', border: '#FDE047', text: '#ffffff' },
  'STRONG CAUTION': { color: '#F97316', bg: '#000000', border: '#F97316', text: '#ffffff' },
  'NO-GO':          { color: '#EF4444', bg: '#000000', border: '#EF4444', text: '#ffffff' },
  UNKNOWN:          { color: '#9CA3AF', bg: '#000000', border: '#9CA3AF', text: '#ffffff' },
};

// Base button style
const BTN_BASE = {
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: "'Georgia', serif",
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
};

const BTN_LARGE = {
  ...BTN_BASE,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '16px 20px',
  borderRadius: 14,
  border: 'none',
  minHeight: 60,
  gap: 10,
};

const CARD_STYLE = {
  background: COLOR_CARD,
  borderRadius: 14,
  border: `1px solid ${COLOR_DARK_GREEN}`,
};

const BTN_SMALL = {
  ...BTN_BASE,
  padding: '10px 16px',
  borderRadius: 10,
  fontSize: '14px',
  fontWeight: 600,
  minHeight: 44,
  minWidth: 44,
};

// ============================================================
// COURSE LORE
// ============================================================
const COURSE_LORE = {
  'spooky-brook': {
    emoji: '👻',
    headline: 'Named after a real brook. Mostly.',
    body: `The course takes its name from Spooky Brook — a small tributary that winds across several fairways. The brook is mostly harmless (the hazard "really doesn't pose much of a threat," per every golf reviewer ever), but hole 17 makes you carry a pond to reach the green, which is the course's one genuine moment of drama. Designed by Ed Ault and opened in 1970, Spooky Brook was built with ordinary golfers in mind — wide fairways, generous rough, trees that are spaced far enough apart that you can usually find your ball. It's the most senior- and beginner-friendly course in the Somerset County system, and it knows it. Part of Colonial Park, owned by Somerset County. No restaurant — just vending machines. That's not a bug, it's a feature.`,
  },
  'quail-brook': {
    emoji: '🌲',
    headline: 'The woods get serious on the back nine.',
    body: `Opened in 1981 as Somerset County's 25th anniversary gift to itself, Quail Brook was designed by the Ed Ault family — Ed, his son Brian, and Tom Clark, the same trio that later designed TPC Avenel in Maryland. The front nine is rolling and fun. The back nine gets tight and hilly in a way that surprises first-timers. At 6,617 yards it's the longest of the Somerset County flat-ish courses. Shares grill management with Spooky Brook — same kitchen, different address.`,
  },
  'warrenbrook': {
    emoji: '🌳',
    headline: 'Heavy woods. Uphill lies. No mercy.',
    body: `Warrenbrook opened in 1982 as Somerset County's third golf course — coming after Green Knoll (1959) and Quail Brook (1981). Designed by Hal C. Purdy, the heavily wooded layout has bunkers on every hole and constant elevation changes that produce uphill, downhill, and sidehill lies. At 6,372 yards from the tips it's not long, but the tight tree-lined fairways punish anything off line. The signature 17th — a 376-yard dogleg right par-4 over a pond — and the distinctive arched stone bridge across the water hazard give the course a character all its own. The Tudor-style clubhouse adds to the period feel. If you haven't played it before, give yourself a stroke.`,
  },
  'green-knoll': {
    emoji: '🏌️',
    headline: 'The original. Somerset County\'s first public course.',
    body: `Green Knoll opened in April 1959 as Somerset County's first public golf course and the first major project completed by the Park Commission. Designed by William F. Gordon — who also designed Oak Hill in Hunterdon — the layout was carved from land purchased from George V. N. Davis at a construction cost of $178,760, and was officially dedicated on July 1, 1960. The first golf pro was John L. Grace. The course plays 6,443 yards with rolling terrain and water in play on holes 5, 6, 8, and 15. The signature 8th is a dogleg-right par-4 where two creeks dictate strategy — the first winds across both the right rough and the landing zone, the second crosses 20 yards short of the green. There is no driving range; the adjacent 9-hole pitch-and-putt (par 27) doubles as a warmup range on weekends and holidays and remains one of the park system's most popular attractions. A classic old-school layout that predates the design philosophy that came after it.`,
  },
  'neshanic-valley': {
    emoji: '🏆',
    headline: 'The flagship. Built on old farmland with mountain views.',
    body: `Neshanic Valley is the crown jewel of the Somerset County system and the newest of the five, opening Labor Day weekend 2004 with the Meadow and Lake nines; the Ridge nine followed in 2005. Designed by Michael Hurdzan and Dana Fry — the same team behind Erin Hills, site of the 2017 US Open — it sprawls across 420 acres of former rolling farmland overlooking the historic Neshanic Valley and the Sourland Mountains. The 27-hole championship layout plays in three combinations of two nines, with five tee stations stretching the round from 5,050 up to 7,108 yards. Bentgrass throughout; deep bunkers, fescue rough, and wetland hazards punish anything off line. Add a 9-hole par-32 Academy Course, the Callaway Golf Performance Center, and a 12,000-square-foot clubhouse, and it's the most complete public golf facility in New Jersey. The Grille Room serves real food — sandwiches and cocktails to enjoy on the second-floor deck with its 270° view. Ranked by Golfweek as a Top 50 Municipal Course in the US, it has hosted the 2007 Met Public Links, 2009 NJSGA Women's Amateur, 2012 USGA Women's Public Links Championship, and 2014 NJSGA Men's Public Links. A Rutgers weather sensor is on-site.`,
  },
  'fox-hollow': {
    emoji: '🦊',
    headline: 'Six par 3s. Originally designed by a Trent Jones protégé.',
    body: `Fox Hollow opened in 1962, designed by Hal C. Purdy — who got his start as a construction supervisor under Robert Trent Jones Sr. The course was completely redesigned in 2000, which is why it plays younger than its age. The layout has an unusual feature: six par 3s, including water carries on the 9th and 18th — an uncommon way to close each nine. At par 70 it plays shorter than the distance suggests, but the tree-covered elevation changes on the back nine make up for it. Voted one of the top Northeast courses by Garden State Golf readers. Still one of Somerset County's best-kept secrets.`,
  },
  'architects': {
    emoji: '🏛️',
    headline: 'Every hole is a tribute to a different legend.',
    body: `The Architects Golf Club opened in 2001, the most original course concept in New Jersey golf. Owners Dennis and Larry Turco — co-owners of Turco Golf Construction — partnered with golf architect Stephen Kay and Golf Digest architectural editor Ron Whitten to build "New Jersey's only legacy golf course." Each of the 18 holes is designed in the style of a different golden-age architect spanning 1885–1955. Hole 1 channels Old Tom Morris with pot bunkers either side of a straightaway par 5. Hole 7 channels A.W. Tillinghast — the dolomite mounds are pure Bethpage. Hole 13 is the showstopper: Alister MacKenzie's diagonal green fronted by a stream and stone bridge, with flashy bunkers behind — Augusta's 12th in spirit. Holes 9 and 15 honor Donald Ross. Hole 18 is pure Robert Trent Jones Sr. — an 80-yard runway tee, fairway pinched to 25 yards by Trent Jones bunkers left and right, recently restored. Plaques at each tee tell the architect's story. The 16,000 sq ft clubhouse opened 2007; Architects Bar & Grill (public year-round) opened 2019. Sits in the rolling hills of Warren County. Managed by Troon since 2020. GolfPass ranked it #1 NJ public course in 2022.`,
  },
  'high-bridge-hills': {
    emoji: '⚡',
    headline: 'Links-style. Ridgeline. 100-foot drop on hole 8.',
    body: `High Bridge Hills opened in 1999, designed by Mark A. Mungeam (ASGCA) — the most authentically links-style course on the Jersey Circuit. Owned by the Borough of High Bridge and managed by Troon Indigo Sports, the course unfolds along a ridgeline overlooking Spruce Run Reservoir in Hunterdon County. Plays 6,500 yards from the tips to a par 70 (course rating 72.3, slope 128). The defining hole is the par-3 8th: a tee-to-green drop of nearly 100 feet over a natural gorge. The exposed ridgeline means wind is always a factor, and the open elevation makes lightning risk higher here than anywhere else on the circuit. When ZAP says caution, this is the course where you listen. Bent grass greens and fairways throughout. The red-barn clubhouse is the on-site landmark — visible from holes 1, 9, 10, and 18.`,
  },
  'pebble-creek': {
    emoji: '🪨',
    headline: 'Two courses in one. Dense forest out, water-heavy in.',
    body: `Pebble Creek opened September 1, 1996 — one of the final designs by Hal C. Purdy, the prolific New Jersey architect who also designed Fox Hollow. The course has a distinct split personality: the front nine is carved from mature hardwood forest with narrow, tree-lined fairways. The back nine opens up — but water comes into play on seven of the nine holes, including the signature par-5 14th, which wraps around a 2.5-acre lake. Plays 6,276 yards from the blue tees to a par 71. Finishes on a par 3 on hole 18, which is unusual and divides opinion. A World Trade Center Memorial sits between the pro shop and the 1st tee — a distinctive sobering touch. No driving range. The clubhouse has a covered patio overlooking 18, plus a snack bar. For a proper meal, Huddy's Inn is a few doors up Route 537 at 206 — listed on the scorecard as "Our 19th Hole."`,
  },
  'hominy-hill': {
    emoji: '👑',
    headline: 'Robert Trent Jones built it for one man. Now it\'s yours.',
    body: `Hominy Hill has the best origin story on the Jersey Circuit. Henry Dickson Mercer Sr. — president of States Marine Corporation, Augusta National member — bought 411 acres of Colts Neck farmland in 1941 to raise prize-winning Guernsey cattle. He named the property "Hominy Hill Farm" after the Manhomoney Hills, the historic name for Colts Neck's small hills. Mercer's home club, Rumson Country Club, balked when he wanted to entertain his Japanese and Greek shipping contacts. So in 1963 he commissioned Robert Trent Jones Sr. to convert 180 acres into a championship course of his own — opened in 1965, with a blank check, 138 bunkers, tributaries of Mine Brook as water hazards, and a converted dairy barn as the clubhouse (designed by architect Derrick Kipp with informal and formal restaurants on two floors). Almost nobody played it — invitation-only, plus occasional charity events. In 1975 the family sold to Monmouth County, resisting higher developer offers. Plays 6,797 yards from the blue tees to a par 72 (rating 73.4, slope 134). Hosted the 1983 U.S. Amateur Public Links and the 1995 U.S. Women's Amateur Public Links. Voted #1 NJ public course by New Jersey Monthly readers. Critical lightning note (per the official scorecard): no on-site lightning detection, no lightning shelters — golfers play at their own risk. ZAP exists for courses like this.`,
  },
  'oak-hill': {
    emoji: '🌳',
    headline: 'Private. Ridge-line. 50-mile views.',
    body: `Oak Hill Golf Club is the only private course on the Jersey Circuit — a 240-acre member-owned gem two miles north of Milford in the hills of Holland Township. Founded as the "Ridge Golf Club" in 1963, the course opened in 1964 with 9 holes designed by William F. Gordon (whose work also designed Green Knoll). A second 9 by Gordon followed in 1965, chosen over a competing bid from George Fazio for design continuity. Members hand-picked stones from the rough during construction — they're still called the "Rock Pickers." Plays 6,669 yards from the tips to a par 72 (rating 72.0, slope 136). Sits high enough to take in 50-mile views, with prevailing winds and exposed lightning risk on the upper holes — the nearest NJWxNet station is NJ53, the same one serving High Bridge Hills, and conditions here can differ sharply from the valley courses on the same day. Hole 5 is one of the most picturesque on the circuit: a short downhill par 3 over a stream and pond. The Grill Room and expansive deck overlook the course. Member-only — Patrick has that covered.`,
  },
  'shark-river': {
    emoji: '🦈',
    headline: 'Old-school Scottish design. Tight, fast, and unforgiving.',
    body: `Shark River Golf Course is one of the oldest and most distinctive layouts on the Jersey Circuit — designed by Scottish golf professional Joseph "Scotty" I'Anson in the early 1900s and operated by Monmouth County Parks. Sits on the inner coastal plain in Neptune, sandwiched between Old Corlies Ave and Route 18, with a layout that meanders through mature trees and sandy soil. The course plays shorter than its 6,348-yard blue-tee distance suggests — tight, tree-lined fairways, deep-faced bunkers, and small, well-protected greens demand precision over power. Holes range from a short drivable par 4 to a long risky par 5, with several strong par 3s mixed in. Par is 71 (rating 71.0, slope 126 from the blues). Architect I'Anson's Scottish roots show: there is no premium on length here — course management and ball-striking win. Critical lightning note (per official scorecard): no lightning detection devices, no lightning shelters — golfers play at their own risk. Mobile concession cart available March through December; vending machines otherwise. Open through winter weather permitting with temporary tees and greens.`,
  },
};

// COURSE REGISTRY
// ============================================================
const COURSES = [
  {
    id: 'dr-z-test',
    name: "Dr. Z's Test Course",
    town: 'Bedminster',
    state: 'NJ', county: 'Somerset',
    lat: 40.6488,
    lng: -74.6286,
    elevation: 362,
    terrain: 'moderate',
    par: 72,
    proShop: '(908) 555-0000',
    snackBar: '(908) 555-0001',
    website: null,
    microClimate: 'TEST COURSE — Bedminster home coordinates.',
    zip: '07921',
    waterDensity: 'moderate',
    soilType: 'standard',
  },
  {
    id: 'spooky-brook',
    name: 'Spooky Brook',
    town: 'Somerset',
    state: 'NJ', county: 'Somerset',
    address: '582 Elizabeth Ave, Somerset, NJ 08873',
    lat: 40.5055,
    lng: -74.556,
    parkingLat: 40.50562719390075,
    parkingLng: -74.55598218813789,
    elevation: 96,
    terrain: 'flat',
    par: 72,
    proShop: '(732) 873-2242',
    snackBar: 'Vending machines in pro shop',
    website: 'https://www.spookybrookgolf.com/',
    teeTime: 'https://somerset-group-v2.book.teeitup.com/?course=7092%2C10158%2C7083%2C7084%2C7094%2C7093',
    microClimate: 'Marshy, borders Six Mile Run. Swamp Risk.',
    zip: '08873',
    waterDensity: 'high',
    soilType: 'standard',
  },
  {
    id: 'pebble-creek',
    name: 'Pebble Creek Golf Club',
    town: 'Colts Neck',
    address: '224 Route 537, Colts Neck, NJ 07722',
    state: 'NJ', county: 'Monmouth',
    lat: 40.2864,
    lng: -74.1701,
    parkingLat: 40.28723443277222,
    parkingLng: -74.16998507502612,
    elevation: 102,
    terrain: 'moderate',
    par: 71,
    proShop: '(732) 303-9090',
    snackBar: '(732) 431-0194',
    website: 'https://www.pebblecreekgolfclub.com/',
    teeTime: 'https://pebble-creek-golf-club-v2.book.teeitup.com/teetimes',
    microClimate: 'Inland Monmouth County; mixed sandy/loam soil; heavy water on back 9.',
    zip: '07722',
    waterDensity: 'high',
    soilType: 'fast',
  },
  {
    id: 'neshanic-valley',
    name: 'Neshanic Valley',
    town: 'Neshanic Station',
    state: 'NJ', county: 'Somerset',
    address: '2301 South Branch Rd, Neshanic Station, NJ 08853',
    lat: 40.5265,
    lng: -74.72,
    parkingLat: 40.526288235926536,
    parkingLng: -74.7201769859349,
    elevation: 157,
    terrain: 'elevated',
    par: 72,
    proShop: '(908) 369-8200',
    snackBar: '(908) 369-8200 x3',
    website: 'https://www.neshanicvalleygolf.com/',
    teeTime: 'https://somerset-group-v2.book.teeitup.com/?course=7092%2C10158%2C7083%2C7084%2C7094%2C7093',
    microClimate: 'On-site Rutgers sensor. High water holes.',
    zip: '08853',
    waterDensity: 'moderate',
    soilType: 'slow',
  },
  {
    id: 'quail-brook',
    name: 'Quail Brook',
    town: 'Somerset',
    state: 'NJ', county: 'Somerset',
    address: '625 New Brunswick Rd, Somerset, NJ 08873',
    lat: 40.5128,
    lng: -74.5161,
    parkingLat: 40.51322548669363,
    parkingLng: -74.51488077922517,
    elevation: 97,
    terrain: 'flat',
    par: 72,
    proShop: '(732) 560-9199',
    snackBar: 'Vending machines in pro shop',
    website: 'https://www.quailbrookgolf.com/',
    teeTime: 'https://somerset-group-v2.book.teeitup.com/?course=7092%2C10158%2C7083%2C7084%2C7094%2C7093',
    microClimate: 'Shares grill management with Spooky.',
    zip: '08873',
    waterDensity: 'moderate',
    soilType: 'slow',
  },
  {
    id: 'fox-hollow',
    name: 'Fox Hollow',
    town: 'Branchburg',
    state: 'NJ', county: 'Somerset',
    address: '59 Fox Chase Run, Branchburg, NJ 08876',
    lat: 40.618,
    lng: -74.705,
    parkingLat: 40.61791480126288,
    parkingLng: -74.70466811339399,
    elevation: 138,
    terrain: 'elevated',
    par: 70,
    proShop: '(908) 526-0010',
    snackBar: '(908) 526-0010',
    website: 'https://www.foxhollowgc.com/',
    teeTime: 'https://fox-hollow-golf-club-2.book.teeitup.golf/teetimes',
    microClimate: 'Near KNJBRANC10 sensor (0.9mi).',
    zip: '08876',
    waterDensity: 'low',
    soilType: 'slow',
  },
  {
    id: 'architects',
    name: 'Architects',
    town: 'Lopatcong',
    address: '700 Strykers Rd, Lopatcong, NJ 08865',
    state: 'NJ', county: 'Warren',
    lat: 40.7086,
    lng: -75.1503,
    parkingLat: 40.708907323280314,
    parkingLng: -75.15111468146712,
    elevation: 404,
    terrain: 'elevated',
    par: 71,
    proShop: '(908) 213-3080',
    snackBar: '(908) 213-3080',
    website: 'https://www.thearchitectsclub.com/',
    teeTime: 'https://architectsgolf.totaleintegrated.com',
    microClimate: 'Elev changes. High runoff. Open field wind.',
    zip: '08865',
    waterDensity: 'low',
    soilType: 'slow',
  },
  {
    id: 'high-bridge-hills',
    name: 'High Bridge Hills',
    town: 'High Bridge',
    address: '203 Cregar Road, High Bridge, NJ 08829',
    state: 'NJ', county: 'Hunterdon',
    lat: 40.6694,
    lng: -74.9104,
    parkingLat: 40.67001004815758,
    parkingLng: -74.91054216427337,
    elevation: 381,
    terrain: 'elevated',
    par: 70,
    proShop: '(908) 638-5055',
    snackBar: '(908) 638-5055',
    website: 'https://www.highbridgehills.com/',
    teeTime: 'https://www.highbridgehills.com/book-a-tee-time/',
    microClimate: 'Ridge-line. High lightning risk.',
    zip: '08829',
    waterDensity: 'low',
    soilType: 'fast',
  },
  {
    id: 'oak-hill',
    name: 'Oak Hill',
    town: 'Milford',
    address: '15 Fernwood Rd, Milford, NJ 08848',
    state: 'NJ', county: 'Hunterdon',
    lat: 40.596,
    lng: -75.085,
    parkingLat: 40.606161250652676,
    parkingLng: -75.11201377087977,
    elevation: 316,
    terrain: 'elevated',
    par: 72,
    proShop: '(908) 995-2285',
    snackBar: '(908) 995-2284',
    website: 'https://oakhillgolf.com/',
    teeTime: 'https://oakhillgolf.com/',
    microClimate: 'Private. Ridge-line with 50-mile views. Exposed to prevailing winds. The Grill Room + deck/patio dining.',
    zip: '08848',
    waterDensity: 'low',
    soilType: 'slow',  // USDA SSURGO verified S235: Penn channery silt loam, well drained (Alfisols)
  },
  {
    id: 'warrenbrook',
    name: 'Warrenbrook',
    town: 'Warren',
    state: 'NJ', county: 'Somerset',
    address: '500 Warrenville Rd, Warren, NJ 07059',
    lat: 40.6132,
    lng: -74.4921,
    parkingLat: 40.61284762505215,
    parkingLng: -74.49223289262243,
    elevation: 292,
    terrain: 'moderate',
    par: 71,
    proShop: '(908) 754-8402',
    snackBar: 'Vending machines in pro shop',
    website: 'https://www.warrenbrookgolf.com/',
    teeTime: 'https://somerset-group-v2.book.teeitup.com/?course=7092%2C10158%2C7083%2C7084%2C7094%2C7093',
    microClimate: '',
    zip: '07059',
    waterDensity: 'moderate',
    soilType: 'slow',
  },
  {
    id: 'green-knoll',
    name: 'Green Knoll',
    town: 'Bridgewater',
    state: 'NJ', county: 'Somerset',
    address: '587 Garretson Rd, Bridgewater, NJ 08807',
    lat: 40.6009,
    lng: -74.6337,
    parkingLat: 40.60049049435238,
    parkingLng: -74.63326033664664,
    elevation: 133,
    terrain: 'moderate',
    par: 72,
    proShop: '(908) 722-1301',
    snackBar: 'Vending machines in pro shop',
    website: 'https://www.greenknollgolf.com/',
    teeTime: 'https://somerset-group-v2.book.teeitup.com/?course=7092%2C10158%2C7083%2C7084%2C7094%2C7093',
    microClimate: 'Call for frost status.',
    zip: '08807',
    waterDensity: 'moderate',
    soilType: 'standard',
  },
  {
    id: 'hominy-hill',
    name: 'Hominy Hill',
    town: 'Colts Neck',
    address: '92 Mercer Road, Colts Neck, NJ 07722',
    state: 'NJ', county: 'Monmouth',
    lat: 40.2744,
    lng: -74.2,
    parkingLat: 40.27258591141753,
    parkingLng: -74.19987180057376,
    elevation: 114,
    terrain: 'moderate',
    par: 72,
    proShop: '(732) 462-9222',
    snackBar: '(732) 462-9222',
    website: 'https://www.monmouthcountyparks.com/page.aspx?ID=2582',
    teeTime: 'https://foreupsoftware.com/index.php/booking/20290#/teetimes',
    microClimate: 'Shore Sentinel logic.',
    zip: '07722',
    waterDensity: 'moderate',
    soilType: 'standard',
  },
  {
    id: 'shark-river',
    name: 'Shark River Golf Course',
    town: 'Neptune',
    address: '320 Old Corlies Ave, Neptune, NJ 07753',
    state: 'NJ', county: 'Monmouth',
    lat: 40.2008,
    lng: -74.0569,
    parkingLat: 40.20587323045803,
    parkingLng: -74.05886686724469,
    elevation: 51,
    terrain: 'flat',
    par: 71,
    proShop: '(732) 922-4141',
    teeTimePhone: '(732) 758-8383',
    website: 'http://sharkrivergolfcourse.com/index.html',
    teeTime: 'https://foreupsoftware.com/index.php/booking/20158/3785#/teetimes',
    microClimate: 'Inner coastal plain, Neptune NJ; sandy soil; tree-lined layout; coastal Monmouth microclimate.',
    zip: '07753',
    waterDensity: 'low',
    soilType: 'fast',
  },
];

const TOWNS = [...new Set(COURSES.map((c) => c.town))].sort();

// ============================================================
// PLAYER ROSTER & SCORECARD STORAGE
// ============================================================
// ---- Cloudinary config ----
const CLOUDINARY_CLOUD_NAME = 'dovzgljte';
const CLOUDINARY_UPLOAD_PRESET = 'zap_photos';
const CDN = 'https://res.cloudinary.com/dovzgljte/image/upload/';
const COURSE_ASSETS = {
  'oak-hill':         { scorecard: CDN+'Oak-Hill-Scorecard_d4feax.webp',           map: null, photos: [CDN+'oak_hill_fog_aerial.jpg', CDN+'oak_hill_5th_par3.jpg'] },
  'spooky-brook':     { scorecard: CDN+'SpookyBrook_fozfxf.png',                   map: CDN+'sbgc_map_zzlhda.jpg', photos: [CDN+'spooky_brook_entrance.jpg', CDN+'spooky_brook_17th_green.jpg', CDN+'spooky_brook_clubhouse.jpg'] },
  'green-knoll':      { scorecard: CDN+'GreenKnoll_1_cu6wok.png',                  map: CDN+'scorecard_1_qxkiyl.jpg', photos: [CDN+'green_knoll_5th_fairway.jpg', CDN+'green_knoll_6th_green.jpg', CDN+'green_knoll_17th_green.jpg'] },
  'warrenbrook':      { scorecard: CDN+'Warrenbrook_tq8dkf.png',                   map: CDN+'wbgc_map_xpwobm.jpg', photos: [CDN+'warrenbrook_clubhouse.jpg', CDN+'warrenbrook_17th_pond.jpg', CDN+'warrenbrook_stone_bridge.jpg'] },
  'quail-brook':      { scorecard: CDN+'correct-score-card-2_bslytg.jpg',          map: CDN+'course-layout_eh5nst.webp', photos: [] },
  'pebble-creek':     { scorecard: CDN+'pebble_creek_scorecard.png',              map: CDN+'pebble_creek_course_layout.png', photos: [CDN+'pebble_creek_cover.png', CDN+'pebble_creek_front9_maps.png', CDN+'pebble_creek_back9_maps.png'] },
  'hominy-hill':      { scorecard: CDN+'hominy_hill_scorecard_2022.jpg',           map: CDN+'hominy_hill_course_layout.jpg', photos: [CDN+'hominy_hill_course.jpg', CDN+'hominy_hill_clubhouse.jpg', CDN+'hominy_hill_hole_diagrams.jpg'] },
  'fox-hollow':       { scorecard: CDN+'fox_hollow_scorecard.jpg',                 map: null, photos: [CDN+'fox-hollow-gc-nj-usa-7_b5wf4n.jpg'] },
  'high-bridge-hills':{ scorecard: CDN+'high_bridge_scorecard.jpg',                map: CDN+'Screenshot_2026-03-25_042936_uizvjg.jpg', photos: [CDN+'high_bridge_aerial.jpg', CDN+'high_bridge_hole8_gorge.jpg', CDN+'high-bridge-hills-gc-nj-usa-1_qtk7tu.jpg'] },
  'architects':       { scorecard: CDN+'architects_scorecard.jpg',                 map: null, photos: [CDN+'architects_misty_morning.jpg', CDN+'architects_13th_mackenzie.jpg', CDN+'the-architects-gc-nj-usa-8_zwvnsw.jpg'] },
  'shark-river':      { scorecard: CDN+'shark_river_scorecard.png', map: CDN+'shark_river_course_layout.png', photos: [CDN+'shark_river_hole2.png', CDN+'shark_river_greens_blue.png', CDN+'shark_river_green_depths.png'] },
  'neshanic-valley':  { scorecard: CDN+'neshanic_meadow_scorecard.png', map: CDN+'neshanic_meadow_layout.png', photos: [CDN+'neshanic-meadow-lake-3_pdcycf.jpg', CDN+'neshanic-meadow-lake-5_bagvqw.jpg', CDN+'neshanic-meadow-lake-10_2_jxisdo.jpg', CDN+'hole-5_mkrptc.webp'], scorecards: [{label:'Meadow',src:CDN+'neshanic_meadow_scorecard.png'},{label:'Lake',src:CDN+'neshanic_lake_scorecard.png'},{label:'Ridge',src:CDN+'neshanic_ridge_scorecard.png'}], maps: [{label:'Meadow',src:CDN+'neshanic_meadow_layout.png'},{label:'Lake',src:CDN+'neshanic_lake_layout.png'},{label:'Ridge',src:CDN+'neshanic_ridge_layout.png'}] },
};

const PLAYERS = [
  { id: 'john',    name: 'John',    emoji: '🔭' },
  { id: 'tony',    name: 'Tony',    emoji: '😏' },
  { id: 'patrick', name: 'Patrick', emoji: '☘️' },
  { id: 'matt',    name: 'Matt',    emoji: '⚖️' },
  { id: 'gary',    name: 'Gary',    emoji: '🎙️' },
  { id: 'tommy',   name: 'Tommy',   emoji: '🦉' },
  { id: 'jeff',    name: 'Jeff',    emoji: '🏆' },
  { id: 'toms',    name: 'Tom S.',  emoji: '🌡️' },
];
// John manages this list — add/remove players here

const SCORECARD_URL = 'https://script.google.com/macros/s/AKfycbwW4oHrqN9Vx9AkZ0zt7pZZrnHmXFT1Xdmd2lWFw05u1Hk9m5nBGfrYX0sdGzjL6H-BGQ/exec';
// Unified system health logging - fire and forget
// ============================================================
// API-3: SYSTEM HEALTH LOGGING
// Logs failures + special events only — never successes.
// Events: failure, p2_activated, p1_recovered, rate_limit,
//         cascade_fail, storm_detected, sensor_anomaly,
//         nj09_excluded, webhook_failed, app_open
// Call counts: per source per device per day in localStorage.
// ============================================================

// Call counter — per source per day, resets at midnight
const ZAP_CALLS_PREFIX = 'zap_calls_';
const _today = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD

const incrementCallCount = (source, crs, stationId = null) => {
  try {
    const key = `${ZAP_CALLS_PREFIX}${source.toLowerCase().replace(/\s+/g, '_')}_${_today()}`;
    const n = parseInt(localStorage.getItem(key) || '0', 10);
    localStorage.setItem(key, String(n + 1));
  } catch (e) {}
  // DATA-HEALTH-1: fire-and-forget POST to Apps Script for aggregate tracking
  try {
    fetch(SCORECARD_URL, {
      method: 'POST', mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'api_count',
        source,
        courseId: crs?.id || '',
        courseName: crs?.name || '',
        deviceDate: _today(),
        stationId: stationId || '',
      }),
    }).catch(() => {});
  } catch (e) {}
};

const getCallCount = (source) => {
  try {
    const key = `${ZAP_CALLS_PREFIX}${source.toLowerCase().replace(/\s+/g, '_')}_${_today()}`;
    return parseInt(localStorage.getItem(key) || '0', 10);
  } catch (e) { return 0; }
};

// Rolling avg response time — last 5 calls per source in localStorage
const ZAP_RT_PREFIX = 'zap_rt_';
const recordResponseTime = (source, ms) => {
  try {
    const key = `${ZAP_RT_PREFIX}${source.toLowerCase().replace(/\s+/g, '_')}`;
    const arr = JSON.parse(localStorage.getItem(key) || '[]');
    arr.push(ms);
    if (arr.length > 5) arr.shift();
    localStorage.setItem(key, JSON.stringify(arr));
  } catch (e) {}
};

const getAvgResponseTime = (source) => {
  try {
    const key = `${ZAP_RT_PREFIX}${source.toLowerCase().replace(/\s+/g, '_')}`;
    const arr = JSON.parse(localStorage.getItem(key) || '[]');
    if (!arr.length) return null;
    return Math.round(arr.reduce((s, v) => s + v, 0) / arr.length);
  } catch (e) { return null; }
};

// P2 active state — track which sources are currently on P2
const _p2Active = {};

// S226: Silent P1 retry tracker — set when any forecast variable falls to P2.
// fetchAll checks this after setWeatherData and schedules a background reload
// to attempt P1 recovery without disrupting the displayed data.
const _forecastP2 = {};

// DATA-HEALTH-1: Aggregate API counts cache — fetched from Apps Script
let _apiCountsCache = {};
const API_LIMITS = { weatherapi: 1000000, pirateweather: 20000 };

const fetchApiCounts = async () => {
  try {
    const r = await fetch(`${SCORECARD_URL}?action=getApiCounts&date=${_today()}`);
    if (!r.ok) return;
    const d = await r.json();
    if (d && typeof d === 'object') _apiCountsCache = d;
  } catch (e) {}
};

const logSystemHealth = (eventType, course, details = {}) => {
  try {
    fetch(SCORECARD_URL, {
      method: 'POST', mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'system_health',
        eventType,
        timestamp: new Date().toISOString(),
        course: course?.name || course || 'Unknown',
        courseId: course?.id || '',
        ...details,
      }),
    }).catch(() => {});
  } catch (e) {}
};

// Response time logger — sends to dedicated Response Times sheet tab
const logResponseTime = (source, callType, course, responseMs, trigger = 'unknown', cached = false, extras = {}) => {
  try {
    fetch(SCORECARD_URL, {
      method: 'POST', mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'response_time',
        source,
        callType,
        course: course?.name || course || '',
        courseId: course?.id || course || '',
        responseMs,
        trigger,
        cached,
        ...extras,
      }),
    }).catch(() => {});
  } catch (e) {}
};

// Timed wrapper — returns { result, ms } for response time tracking
const withTiming = async (promise) => {
  const t0 = Date.now();
  const result = await promise;
  return { result, ms: Date.now() - t0 };
};


function getLocalRounds() {
  try { return JSON.parse(localStorage.getItem('zap_rounds') || '[]'); } catch { return []; }
}
function getRounds(sharedRounds) {
  // If shared rounds loaded from Sheet, use those. Fall back to localStorage.
  if (sharedRounds && sharedRounds.length > 0) return sharedRounds;
  return getLocalRounds();
}

// Sort key for "most recent" — handles ISO date + 12h/24h teeTime.
// sharedRounds from Sheet arrives in arbitrary row order, so naive [length-1]
// returns whichever row Sheet put last — NOT the most recent round.
function _roundSortKey(r) {
  const d = String(r?.date || '').slice(0, 10);
  const t = String(r?.teeTime || r?.startTime || '').trim();
  let tt = t;
  const m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m) {
    let h = parseInt(m[1]); const mm = m[2]; const pm = m[3].toUpperCase() === 'PM';
    if (pm && h !== 12) h += 12;
    if (!pm && h === 12) h = 0;
    tt = String(h).padStart(2, '0') + ':' + mm;
  }
  return `${d}T${tt || '00:00'}`;
}
// Returns rounds sorted newest-first. Does NOT mutate input.
function sortRoundsNewestFirst(rounds) {
  return [...(rounds || [])].sort((a, b) => _roundSortKey(b).localeCompare(_roundSortKey(a)));
}
// Returns the most recent round, optionally excluding a given id.
function mostRecentRound(rounds, excludeId) {
  const list = (rounds || []).filter(r => !excludeId || r.id !== excludeId);
  if (list.length === 0) return null;
  return sortRoundsNewestFirst(list)[0];
}

function saveRound(round) {
  try {
    const rounds = getLocalRounds();
    if (rounds.some(r => r.id === round.id)) return; // dedup — never save same round twice
    rounds.push(round);
    localStorage.setItem('zap_rounds', JSON.stringify(rounds));
  } catch {}
}

function updateRoundSyncStatus(roundId, syncStatus) {
  try {
    const rounds = getLocalRounds();
    const idx = rounds.findIndex(r => r.id === roundId);
    if (idx >= 0) {
      rounds[idx] = { ...rounds[idx], syncStatus };
      localStorage.setItem('zap_rounds', JSON.stringify(rounds));
    }
  } catch {}
}

// Post round to sheet — returns 'synced' or 'pending'
// POST uses no-cors (Apps Script redirects, CORS blocks response — no feedback).
// After POST, wait 1.5s then confirm via checkId GET — only reliable source of truth.
// If checkId says exists=false OR any network error → mark pending → retryPendingRounds picks it up on next app open.
async function syncRoundToSheet(round, onStatusChange) {
  const url = 'https://script.google.com/macros/s/AKfycbwW4oHrqN9Vx9AkZ0zt7pZZrnHmXFT1Xdmd2lWFw05u1Hk9m5nBGfrYX0sdGzjL6H-BGQ/exec';
  try {
    await fetch(url, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'scorecard', ...round }),
    });
    // B2 fix S189: poll checkId 3x with increasing delays (1s/2s/3s)
    // Replaces hardcoded 1500ms wait which fails on slow mobile Apps Script
    const _b2delays = [1000, 2000, 3000];
    for (const _b2delay of _b2delays) {
      await new Promise(r => setTimeout(r, _b2delay));
      try {
        const check = await fetch(`${url}?action=checkId&id=${round.id}`);
        const result = await check.json();
        if (result.exists) {
          if (onStatusChange) onStatusChange('synced');
          return 'synced';
        }
      } catch { /* checkId attempt failed — try next delay */ }
    }
    if (onStatusChange) onStatusChange('pending');
    return 'pending';
  } catch {
    if (onStatusChange) onStatusChange('pending');
    return 'pending';
  }
}

// On app open: retry any pending rounds silently in background
async function retryPendingRounds(onSync) {
  try {
    const rounds = getLocalRounds();
    const pending = rounds.filter(r => r.syncStatus === 'pending');
    for (const round of pending) {
      const status = await syncRoundToSheet(round);
      if (status === 'synced') {
        updateRoundSyncStatus(round.id, 'synced');
        if (onSync) onSync(round.id);
      }
    }
  } catch {}
}
function getRoundDiff(r) {
  // Returns total - par differential. Lower = better.
  const parsArray = Array.isArray(r.pars) ? r.pars
    : (typeof r.pars === 'string' ? r.pars.split(',').map(Number) : []);
  const par = parsArray.length > 0 ? parsArray.reduce((s, p) => s + (Number(p) || 0), 0) : null;
  return par != null ? (Number(r.total) || 0) - par : null;
}
function getCourseRecord(courseId, sharedRounds) {
  const rounds = getRounds(sharedRounds).filter(r => r.courseId === courseId && r.total != null);
  if (!rounds.length) return null;
  // Per-course: same par throughout so raw total sort is fine (spec line 6959/6966)
  return rounds.reduce((best, r) => r.total < best.total ? r : best);
}
function getAllTimeRecord(sharedRounds) {
  const rounds = getRounds(sharedRounds).filter(r => r.total != null);
  if (!rounds.length) return null;
  return rounds.reduce((best, r) => getRoundDiff(r) < getRoundDiff(best) ? r : best);
}

function getWorstRecord(sharedRounds) {
  const rounds = getRounds(sharedRounds).filter(r => r.total != null);
  if (!rounds.length) return null;
  return rounds.reduce((worst, r) => getRoundDiff(r) > getRoundDiff(worst) ? r : worst);
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================
function degreesToCardinal(deg) {
  if (deg == null) return '';
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

function formatTime12h(timeStr) {
  if (!timeStr) return 'N/A';
  try {
    // Handle Unix epoch integer (VC currentConditions returns epoch)
    if (typeof timeStr === 'number' || (typeof timeStr === 'string' && /^\d{9,10}$/.test(timeStr))) {
      const d = new Date(parseInt(timeStr) * 1000);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    // Handle ISO datetime string e.g. "2026-04-12T20:14" or "2026-04-12T20:14:33"
    if (typeof timeStr === 'string' && timeStr.includes('T')) {
      const d = new Date(timeStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      }
    }
    // Handle HH:MM:SS or HH:MM string
    const parts = timeStr.split(':');
    const hour = parseInt(parts[0]);
    const m = parts[1] || '00';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour === 0 ? 12 : hour > 12 ? hour - 12 : hour}:${m} ${ampm}`;
  } catch {
    return String(timeStr);
  }
}

// Short time format for compact display — "7:48" no AM/PM
function formatTimeShort(timeStr) {
  if (!timeStr) return '';
  try {
    if (typeof timeStr === 'number' || (typeof timeStr === 'string' && /^\d{9,10}$/.test(timeStr))) {
      const d = new Date(parseInt(timeStr) * 1000);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: false }).replace(/^0/, '');
    }
    if (typeof timeStr === 'string' && timeStr.includes('T')) {
      const d = new Date(timeStr);
      if (!isNaN(d.getTime())) {
        const h = d.getHours();
        const m = String(d.getMinutes()).padStart(2, '0');
        const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        return `${h12}:${m}`;
      }
    }
    const parts = timeStr.split(':');
    const hour = parseInt(parts[0]);
    const m = parts[1] || '00';
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${h12}:${m}`;
  } catch {
    return '';
  }
}

function calcDryingTrend(rain, temp, humidity) {
  if (rain > 1) return 'POOR';
  if (rain > 0.5) return 'FAIR';
  if (temp > 85 && humidity < 40) return 'FAST (Solar baking active)';
  if (rain > 0.1) return 'FAIR';
  return 'GOOD';
}

function celsiusToF(c) { return (9 * c) / 5 + 32; }

// ============================================================
// VISUAL CROSSING WEATHER API (Priority 1)
// ============================================================
const vcCache = {};

async function fetchVisualCrossing(course) {
  const cacheKey = `vc_${course.lat}_${course.lng}`;
  const cached = vcCache[cacheKey];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.data;

  try {
    // S226: Direct VC call — proxy removed. Same params as proxy was passing.
    // Key kept out of git via REACT_APP_VC_API_KEY build-time env var (Netlify dashboard).
    if (!VC_API_KEY) throw new Error('VC_API_KEY not configured');
    const vcUrl = `https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${course.lat},${course.lng}?unitGroup=us&include=current,hours,days,stations,alerts&key=${VC_API_KEY}&contentType=json`;
    const resp = await fetch(vcUrl);
    if (!resp.ok) throw new Error(`Visual Crossing API error: ${resp.status} ${resp.statusText}`);

    const data = await resp.json();
    if (data.error) throw new Error(`Visual Crossing error: ${data.error}`);

    // Parse station metadata
    const stationMap = {};
    if (data.stations) {
      Object.entries(data.stations).forEach(([id, s]) => {
        stationMap[id] = {
          id,
          name: s.name || id,
          lat: s.latitude,
          lng: s.longitude,
          distanceMi: s.distance ? Math.round((s.distance / 1000) * 0.621371 * 10) / 10 : null,
          quality: s.quality,
          contribution: s.contribution,
          useCount: s.useCount,
        };
      });
    }

    const current = data.currentConditions || {};
    const activeStationIds = current.stations || [];
    const allMapped = activeStationIds.map((id) => stationMap[id]).filter(Boolean);
    const qualityStations = allMapped.filter(s => s.quality == null || s.quality > 0);
    const activeStations = (qualityStations.length > 0 ? qualityStations : allMapped)
      .sort((a, b) => (a.distanceMi || 99) - (b.distanceMi || 99));
    const closestStation = activeStations[0];

    const days = data.days || [];
    const today = days[0] || {};
    const tomorrow = days[1] || {};
    // Store all daily summaries for calendar fallback (days 4-15 have no hourly)
    const vcDailyAll = days.map(d => ({
      date: d.datetime,
      tempMax: d.tempmax != null ? Math.round(d.tempmax) : null, tempMin: d.tempmin != null ? Math.round(d.tempmin) : null,
      windSpeed: Math.round(d.windspeed || 0),
      precipChance: d.precipprob || 0,
      precip: d.precip || 0,
      conditions: d.conditions || '',
      sunset: d.sunset ? formatTime12h(d.sunset) : null,
    }));

    // Build hourly array
    const hourly = [];
    days.forEach((day) => {
      (day.hours || []).forEach((h) => {
        hourly.push({
          time: new Date(`${day.datetime}T${h.datetime}`),
          temp: h.temp,
          feelsLike: h.feelslike != null ? Math.round(h.feelslike) : null,
          humidity: h.humidity != null ? Math.round(h.humidity) : null,
          windSpeed: h.windspeed,
          windSpeedNum: Math.round(h.windspeed || 0),
          windDir: h.winddir,
          windGust: h.windgust != null ? Math.round(h.windgust) : null,
          precipChance: h.precipprob || 0,
          precip: h.precip || 0,
          snow: h.snow || 0,
          uvIndex: h.uvindex || 0,
          cloudCover: h.cloudcover,
          visibility: h.visibility,
          conditions: h.conditions,
          isDaytime: h.datetime >= (today.sunrise || '06:00:00') && h.datetime < (today.sunset || '19:00:00'),
          source: h.source,
          stations: h.stations || [],
        });
      });
    });

    // Rolling 24h rain — sum hourly precip for last 24 hours (not calendar-day total)
    const now24 = new Date();
    const ago24 = new Date(now24.getTime() - 24 * 60 * 60 * 1000);
    const past24Rain = Math.round(100 * hourly.filter(h => h.time >= ago24 && h.time <= now24).reduce((sum, h) => sum + (h.precip || 0), 0)) / 100;

    const result = {
      success: true,
      stale: !current.temp && !current.humidity,
      vcDailyAll,
      source: 'Visual Crossing',
      sourceDetail: (() => {
        if (!closestStation) return 'Visual Crossing composite';
        // Clean up station name: strip FW/CW prefix IDs from personal weather stations
        let sName = closestStation.name || closestStation.id;
        sName = sName.replace(/^[A-Z]{1,2}\d{3,5}\s+/, '').replace(/\s+(US|USA)$/i, '').trim();
        if (!sName || sName === closestStation.id) sName = closestStation.id;
        return `Visual Crossing composite · nearest ${sName} ${closestStation.distanceMi} mi`;
      })(),
      dataType: current.source === 'obs' ? 'Physical Sensor' : current.source === 'fcst' ? 'Forecast Model' : current.source || 'Unknown',
      stations: stationMap,
      activeStations,
      closestStation,
      current: {
        temp: current.temp,
        feelsLike: current.feelslike,
        humidity: current.humidity,
        dewPoint: current.dew,
        windSpeed: `${Math.round(current.windspeed || 0)} mph`,
        windSpeedNum: Math.round(current.windspeed || 0),
        windDir: degreesToCardinal(current.winddir),
        windDirDeg: current.winddir,
        windGust: current.windgust,
        precip: current.precip || 0,
        precipChance: current.precipprob || 0,
        snow: current.snow || 0,
        snowDepth: current.snowdepth || 0,
        uvIndex: current.uvindex || 0,
        visibility: current.visibility,
        cloudCover: current.cloudcover,
        pressure: current.pressure,
        conditions: current.conditions,
        shortForecast: current.conditions,
        sunrise: current.sunrise,
        sunset: current.sunset,

        source: current.source,
      },
      today: {
        high: today.tempmax != null ? Math.round(today.tempmax) : null,
        low: today.tempmin != null ? Math.round(today.tempmin) : null,
        maxWind: Math.round(today.windspeed || 0),
        avgHumidity: Math.round(today.humidity || 0),
        precip: today.precip || 0,
        precipChance: today.precipprob || 0,
        snow: today.snow || 0,
        uvIndex: today.uvindex || 0,
        sunrise: today.sunrise,
        sunset: today.sunset,
        conditions: today.conditions,
        description: today.description,
        dayForecast: { shortForecast: today.conditions, detailedForecast: today.description },
      },
      tomorrow: {
        high: tomorrow.tempmax != null ? Math.round(tomorrow.tempmax) : null,
        low: tomorrow.tempmin != null ? Math.round(tomorrow.tempmin) : null,
        maxWind: Math.round(tomorrow.windspeed || 0),
        avgHumidity: Math.round(tomorrow.humidity || 0),
        precip: tomorrow.precip || 0,
        precipChance: tomorrow.precipprob || 0,
        snow: tomorrow.snow || 0,
        uvIndex: tomorrow.uvindex || 0,
        sunrise: tomorrow.sunrise,
        sunset: tomorrow.sunset,
        conditions: tomorrow.conditions,
        description: tomorrow.description,
        dayForecast: { shortForecast: tomorrow.conditions, detailedForecast: tomorrow.description },
      },
      ground: {
        past24Rain,
        snowpack: current.snowdepth || 0,
        dryingTrend: calcDryingTrend(past24Rain, current.temp, current.humidity),
      },
      sunsetToday: formatTime12h(today.sunset || current.sunset),
      sunsetTomorrow: formatTime12h(tomorrow.sunset),
      sunriseToday: formatTime12h(today.sunrise || current.sunrise),
      hourly,
      alerts: data.alerts || [],
      grid: { id: 'VC', x: course.lat, y: course.lng },
      updateTime: current.datetimeEpoch ? new Date(current.datetimeEpoch * 1000).toISOString() : new Date().toISOString(),
    };

    vcCache[cacheKey] = { data: result, timestamp: Date.now() };
    incrementCallCount('visual_crossing', course);
    return result;
  } catch (err) {
    console.error('Visual Crossing fetch failed:', err);
    return { success: false, error: err.message, source: 'Visual Crossing', stale: true };
  }
}

// ============================================================
// AWC METAR API (Priority 2 — Aviation Weather Center)
// Endpoint: aviationweather.gov/api/data/metar?ids={ICAO}&format=json
// Free, no API key. Covers NJ region with KSMQ, KFWN, KTTN, KCDW, KBLM.
// Used when Visual Crossing fails or returns stale data.
// Station selection: find nearest ICAO to course GPS from a curated NJ list.
// ============================================================
const awcCache = {};
const AWC_METAR_TTL = 900000; // 15 min

// NJ-area ICAO stations with GPS for distance lookup
const AWC_NJ_STATIONS = [
  { id: 'KSMQ', name: 'Somerset Airport', lat: 40.6258, lng: -74.6699 },
  { id: 'KTTN', name: 'Trenton-Mercer', lat: 40.2767, lng: -74.8135 },
  { id: 'KBLM', name: 'Belmar/Monmouth', lat: 40.1869, lng: -74.1249 },
  { id: 'KCDW', name: 'Caldwell/Essex', lat: 40.8752, lng: -74.2814 },
  { id: 'KFWN', name: 'Sussex Airport', lat: 41.2003, lng: -74.6234 },
  { id: 'KMMU', name: 'Morristown Muni', lat: 40.7994, lng: -74.4149 },
  { id: 'KNEL', name: 'Lakehurst/McGuire', lat: 40.0328, lng: -74.3534 },
  { id: 'K12N', name: 'Aeroflex/Andover', lat: 41.0996, lng: -74.7372 },
];

function haversineMilesLocal(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function findNearestAWCStation(lat, lng) {
  let best = null, bestDist = Infinity;
  AWC_NJ_STATIONS.forEach(s => {
    const d = haversineMilesLocal(lat, lng, s.lat, s.lng);
    if (d < bestDist) { bestDist = d; best = { ...s, distanceMi: Math.round(d * 10) / 10 }; }
  });
  return best;
}

// Parse METAR wind string like "24012KT" or "VRB05KT"
function parseMetarWind(windStr) {
  if (!windStr) return { dir: null, speed: 0 };
  const m = windStr.match(/^(\d{3}|VRB)(\d{2,3})(?:G\d{2,3})?KT$/);
  if (!m) return { dir: null, speed: 0 };
  return { dir: m[1] === 'VRB' ? null : parseInt(m[1]), speed: parseInt(m[2]) };
}

async function fetchAWCMETAR(course) {
  const station = findNearestAWCStation(course.lat, course.lng);
  if (!station) return { success: false, error: 'No AWC station found', source: 'AWC METAR' };

  const cacheKey = `awc_${station.id}`;
  const cached = awcCache[cacheKey];
  if (cached && Date.now() - cached.ts < AWC_METAR_TTL) return cached.data;

  try {
    // AWC API — CORS is open, no key required
    const url = `https://aviationweather.gov/api/data/metar?ids=${station.id}&format=json&hours=2`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`AWC API ${resp.status}`);
    const json = await resp.json();

    // json is an array; take the most recent observation
    const obs = Array.isArray(json) ? json[0] : null;
    if (!obs) throw new Error('No AWC METAR observation returned');

    // Detect frozen/malfunctioning sensor (maintenance symbol "$" in raw)
    const raw = obs.rawOb || '';
    if (raw.includes('$')) {
      console.warn(`AWC METAR ${station.id}: maintenance flag — cascading to NWS HRRR`);
      logSystemHealth('sensor_maintenance', course, { station: station.id, flag: '$' });
      return { success: false, error: 'AWC sensor maintenance flag', source: 'AWC METAR' };
    }

    const wind = parseMetarWind(obs.wdir !== undefined ? `${String(obs.wdir).padStart(3,'0')}${String(obs.wspd).padStart(2,'0')}KT` : null);
    const tempC = obs.temp !== undefined ? obs.temp : null;
    const tempF = tempC !== null ? Math.round(tempC * 9/5 + 32) : null;
    const dewC = obs.dewp !== undefined ? obs.dewp : null;
    const dewF = dewC !== null ? Math.round(dewC * 9/5 + 32) : null;

    // Estimate humidity from temp/dew (Magnus formula)
    let humidity = null;
    if (tempF !== null && dewF !== null) {
      const es = 6.112 * Math.exp(17.67 * dewC / (dewC + 243.5));
      const e = 6.112 * Math.exp(17.67 * tempC / (tempC + 243.5));
      humidity = Math.round(Math.min(100, (e > 0 ? (es / e) * 100 : 50)));
    }

    const visibMi = obs.visib !== undefined ? parseFloat(obs.visib) : null;
    const altimHpa = obs.altim !== undefined ? Math.round(obs.altim * 33.8639) / 1000 : null;

    // Sky/cloud — worstCoverage
    const skyCover = obs.clouds ? obs.clouds.map(c => c.cover).join(',') : '';
    const hasBrokenOrOvercast = /BKN|OVC/.test(skyCover);
    const conditions = obs.wxString || (hasBrokenOrOvercast ? 'Overcast' : 'Clear');
    const precipChance = /RA|SN|DZ|SH|TS/.test(conditions) ? 90 : hasBrokenOrOvercast ? 30 : 5;

    // Build minimal hourly array (METAR gives single obs — replicate current for next 2h)
    const now = new Date();
    const hourly = [0, 1, 2].map(offset => ({
      time: new Date(now.getTime() + offset * 3600000),
      temp: tempF,
      humidity,
      windSpeed: `${Math.round((obs.wspd || 0) * 1.15078)} mph`,
      windSpeedNum: Math.round((obs.wspd || 0) * 1.15078),
      windDir: wind.dir !== null ? degreesToCardinal(wind.dir) : 'Variable',
      windGust: obs.wgst ? Math.round(obs.wgst * 1.15078) : null,
      precipChance,
      conditions,
      isDaytime: now.getHours() >= 6 && now.getHours() < 20,
    }));

    const result = {
      success: true,
      stale: false,
      source: 'AWC METAR',
      sourceDetail: `${station.name} · ${station.distanceMi} mi`,
      dataType: 'Physical Sensor',
      current: {
        temp: tempF,
        feelsLike: tempF !== null ? calcFeelsLike(tempF, humidity || 50, obs.wspd || 0) : null,
        humidity,
        dewPoint: dewF,
        windSpeed: `${Math.round((obs.wspd || 0) * 1.15078)} mph`,
        windSpeedNum: Math.round((obs.wspd || 0) * 1.15078),
        windDir: wind.dir !== null ? degreesToCardinal(wind.dir) : 'Variable',
        windDirDeg: wind.dir,
        windGust: obs.wgst ? Math.round(obs.wgst * 1.15078) : null,
        precipChance,
        snow: 0,
        snowDepth: obs.snowDepth || 0,
        uvIndex: 0, // METAR doesn't provide UV
        visibility: visibMi,
        pressure: altimHpa,
        conditions,
        shortForecast: conditions,
      },
      today: { high: tempF, low: tempF, maxWind: obs.wspd || 0, avgHumidity: humidity || 0, precipChance, dayForecast: { shortForecast: conditions } },
      tomorrow: { high: null, low: null, maxWind: null, avgHumidity: null, precipChance: null, dayForecast: { shortForecast: 'Check back later — airport sensor shows current conditions only' } },
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'N/A — airport sensor, no recent rain history' },
      sunsetToday: 'N/A',
      sunsetTomorrow: 'N/A',
      hourly,
      grid: { id: station.id, x: station.lat, y: station.lng },
      updateTime: obs.reportTime ? new Date(obs.reportTime).toISOString() : new Date().toISOString(),
    };

    awcCache[cacheKey] = { data: result, ts: Date.now() };
    incrementCallCount('awc_metar', course);
    return result;
  } catch (err) {
    console.error('AWC METAR fetch failed:', err);
    return { success: false, error: err.message, source: 'AWC METAR' };
  }
}

// ============================================================
// OPEN-METEO ET (Evapotranspiration — Solar Baking Calibration)
// Endpoint: open-meteo.com/v1/forecast?daily=et0_fao_evapotranspiration
// Free, no API key. Used to enhance solar baking offset accuracy.
// ET > 5 mm/day = significant evaporative drying. Replaces temp+humidity heuristic.
// ============================================================
const etCache = {};
const ET_CACHE_TTL = 3600000; // 1 hour — ET is daily, doesn't need frequent refresh

async function fetchET(lat, lng) {
  const key = `et_${lat}_${lng}`;
  const cached = etCache[key];
  if (cached && Date.now() - cached.ts < ET_CACHE_TTL) return cached.data;

  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
      `&daily=et0_fao_evapotranspiration&forecast_days=3&timezone=America%2FNew_York`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Open-Meteo ET ${resp.status}`);
    const json = await resp.json();

    const times = json.daily?.time || [];
    const etVals = json.daily?.et0_fao_evapotranspiration || [];

    const today = new Date().toLocaleDateString('en-CA');
    const todayIdx = times.indexOf(today);
    const todayET = todayIdx >= 0 ? etVals[todayIdx] : null;
    const tomorrowET = todayIdx >= 0 && todayIdx + 1 < etVals.length ? etVals[todayIdx + 1] : null;

    // ET thresholds:
    // < 2 mm/day = minimal drying (winter/cloudy)
    // 2–5 mm/day = moderate drying
    // > 5 mm/day = strong drying (solar baking active)
    // > 8 mm/day = extreme (hot summer day)
    const result = {
      success: true,
      todayET,       // mm/day
      tomorrowET,
      solarBakingActive: todayET !== null && todayET > 5,
      solarBakingStrong: todayET !== null && todayET > 8,
      label: todayET === null ? 'N/A' : todayET > 8 ? 'Extreme drying' : todayET > 5 ? 'Strong drying (solar baking)' : todayET > 2 ? 'Moderate drying' : 'Minimal drying',
    };

    etCache[key] = { data: result, ts: Date.now() };
    return result;
  } catch (err) {
    console.warn('Open-Meteo ET fetch failed:', err);
    return { success: false, todayET: null, solarBakingActive: false };
  }
}

// ============================================================
// OPEN-METEO FORECAST API (Priority 4 fallback — replaces NWS Grid)
// Free, no API key. WMO-standard. Validated 3/16/26: humidity accurate.
// NWS Grid was disqualified: returned 41% humidity when actual was 90%.
// Endpoint: api.open-meteo.com/v1/forecast
// ============================================================
const omForecastCache = {};

// WMO weather code → plain-English description (Open-Meteo subset)
// Used by fetchOpenMeteo and fetchNJWxNetTimeseries (historical archive conditions)
function wmoDescription(code) {
  switch(code) {
    case 0:  return 'Clear sky';
    case 1:  return 'Mainly clear';
    case 2:  return 'Partly cloudy';
    case 3:  return 'Overcast';
    case 45: return 'Fog';
    case 48: return 'Freezing fog';
    case 51: return 'Light drizzle';
    case 53: return 'Drizzle';
    case 55: return 'Heavy drizzle';
    case 56: return 'Light freezing drizzle';
    case 57: return 'Freezing drizzle';
    case 61: return 'Light rain';
    case 63: return 'Rain';
    case 65: return 'Heavy rain';
    case 66: return 'Light freezing rain';
    case 67: return 'Freezing rain';
    case 71: return 'Light snow';
    case 73: return 'Snow';
    case 75: return 'Heavy snow';
    case 77: return 'Snow grains';
    case 80: return 'Light rain shower';
    case 81: return 'Rain shower';
    case 82: return 'Heavy rain shower';
    case 85: return 'Snow shower';
    case 86: return 'Heavy snow shower';
    case 95: return 'Thunderstorm';
    case 96: return 'Thunderstorm with hail';
    case 99: return 'Thunderstorm with heavy hail';
    default:
      logSystemHealth('unknown_wmo_code', null, { code });
      return code <= 3 ? 'Clear sky' : code <= 48 ? 'Fog' : code <= 67 ? 'Rain' : code <= 77 ? 'Snow' : code <= 82 ? 'Rain shower' : code <= 86 ? 'Snow shower' : 'Thunderstorm';
  }
}

async function fetchOpenMeteo(course) {
  const cacheKey = `om_${course.lat}_${course.lng}`;
  const cached = omForecastCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  try {
    const { lat, lng } = course;
    const url =
      `https://api.open-meteo.com/v1/forecast` +
      `?latitude=${lat}&longitude=${lng}` +
      `&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,rain,showers,snowfall,weather_code,cloud_cover,wind_speed_10m,wind_direction_10m,wind_gusts_10m,is_day` +
      `&hourly=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation_probability,precipitation,rain,snowfall,snow_depth,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,uv_index,is_day,soil_moisture_0_to_1cm` +
      `&daily=temperature_2m_max,temperature_2m_min,apparent_temperature_max,apparent_temperature_min,sunrise,sunset,precipitation_sum,rain_sum,snowfall_sum,precipitation_probability_max,wind_speed_10m_max,wind_gusts_10m_max,uv_index_max` +
      `&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch` +
      `&forecast_days=7&timezone=America%2FNew_York`;

    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Open-Meteo forecast ${resp.status}`);
    const json = await resp.json();

    const cur = json.current || {};
    const daily = json.daily || {};
    const hourly = json.hourly || {};

    // wmoDescription is at module scope — shared with fetchNJWxNetTimeseries

    // Build hourly array — full 7 days (S50: removed 48h cap, needed for calendar days 3-7)
    const nowMs = Date.now();
    const omHourly = [];
    const times = hourly.time || [];
    for (let i = 0; i < times.length; i++) {
      const t = new Date(times[i]);
      const diffMs = t - nowMs;
      if (diffMs < -3600000) continue;           // skip past hours (allow 1hr back)
      // no 48h cap — full 7 days needed for calendar
      omHourly.push({
        time: t,
        temp: Math.round(hourly.temperature_2m?.[i] ?? 0),
        feelsLike: calcFeelsLike(Math.round(hourly.temperature_2m?.[i] ?? 0), Math.round(hourly.relative_humidity_2m?.[i] ?? 50), Math.round(hourly.wind_speed_10m?.[i] ?? 0)),
        humidity: Math.round(hourly.relative_humidity_2m?.[i] ?? 0),
        windSpeedNum: Math.round(hourly.wind_speed_10m?.[i] ?? 0),
        windSpeed: `${Math.round(hourly.wind_speed_10m?.[i] ?? 0)} mph`,
        windDir: degreesToCardinal(hourly.wind_direction_10m?.[i] ?? 0),
        windGust: Math.round(hourly.wind_gusts_10m?.[i] ?? 0),
        precipChance: hourly.precipitation_probability?.[i] ?? 0,
        precip: hourly.precipitation?.[i] ?? 0,
        snowfall: hourly.snowfall?.[i] ?? 0,
        snowDepth: hourly.snow_depth?.[i] ?? 0,
        uvIndex: hourly.uv_index?.[i] ?? 0,
        isDaytime: (hourly.is_day?.[i] ?? 1) === 1,
        wmoCode: hourly.weather_code?.[i] ?? 0,
        shortForecast: wmoDescription(hourly.weather_code?.[i] ?? 0),
        soilMoisture: hourly.soil_moisture_0_to_1cm?.[i] ?? null,
      });
    }

    // Current conditions
    const currentHour = omHourly[0] || {};
    const current = {
      temp: Math.round(cur.temperature_2m ?? currentHour.temp ?? 0),
      feelsLike: Math.round(cur.apparent_temperature ?? currentHour.feelsLike ?? 0),
      humidity: Math.round(cur.relative_humidity_2m ?? currentHour.humidity ?? 0),
      windSpeedNum: Math.round(cur.wind_speed_10m ?? currentHour.windSpeedNum ?? 0),
      windSpeed: `${Math.round(cur.wind_speed_10m ?? 0)} mph`,
      windDir: degreesToCardinal(cur.wind_direction_10m ?? 0),
      windGust: Math.round(cur.wind_gusts_10m ?? 0),
      precip: cur.precipitation ?? 0,
      precipChance: currentHour.precipChance ?? 0,
      snowfall: cur.snowfall ?? 0,
      isDaytime: (cur.is_day ?? 1) === 1,
      wmoCode: cur.weather_code ?? 0,
      shortForecast: wmoDescription(cur.weather_code ?? 0),
      conditions: wmoDescription(cur.weather_code ?? 0),
    };

    // Daily helpers
    const getDay = (idx) => ({
      high: Math.round(daily.temperature_2m_max?.[idx] ?? 0),
      low: Math.round(daily.temperature_2m_min?.[idx] ?? 0),
      feelsHigh: Math.round(daily.apparent_temperature_max?.[idx] ?? 0),
      feelsLow: Math.round(daily.apparent_temperature_min?.[idx] ?? 0),
      maxWind: Math.round(daily.wind_speed_10m_max?.[idx] ?? 0),
      maxGust: Math.round(daily.wind_gusts_10m_max?.[idx] ?? 0),
      avgHumidity: null,   // OM daily doesn't give avg humidity — hourly is better
      precip: daily.precipitation_sum?.[idx] ?? 0,
      precipChance: daily.precipitation_probability_max?.[idx] ?? 0,
      snow: daily.snowfall_sum?.[idx] ?? 0,
      uvIndex: daily.uv_index_max?.[idx] ?? 0,
      sunrise: daily.sunrise?.[idx] ?? null,
      sunset: daily.sunset?.[idx] ?? null,
      conditions: wmoDescription(0),   // OM daily has no single weather_code
      dayForecast: { shortForecast: `High ${Math.round(daily.temperature_2m_max?.[idx] ?? 0)}°`, detailedForecast: '' },
    });

    const todayDay = getDay(0);
    const tomorrowDay = getDay(1);

    // avg humidity from today's hourly
    const todayStr = new Date().toLocaleDateString('en-CA');
    const todayHours = omHourly.filter(h => new Date(h.time).toLocaleDateString('en-CA') === todayStr);
    if (todayHours.length) {
      todayDay.avgHumidity = Math.round(todayHours.reduce((s, h) => s + h.humidity, 0) / todayHours.length);
    }
    const tomorrowStr = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA'); })();
    const tomorrowHours = omHourly.filter(h => new Date(h.time).toLocaleDateString('en-CA') === tomorrowStr);
    if (tomorrowHours.length) {
      tomorrowDay.avgHumidity = Math.round(tomorrowHours.reduce((s, h) => s + h.humidity, 0) / tomorrowHours.length);
    }

    // Past 24hr precip — use hourly precipitation sum
    const past24Rain = Math.round(
      omHourly.filter(h => nowMs - h.time > 0 && nowMs - h.time <= 86400000)
              .reduce((s, h) => s + h.precip, 0) * 100
    ) / 100;

    const snowpack = current.snowfall > 0 ? current.snowfall : 0;
    const dryingTrend = calcDryingTrend(past24Rain, current.temp, current.humidity);

    // Current soil moisture — most recent non-null value from hourly (0-1cm depth, m³/m³ → %)
    const omSoilMoistureRaw = omHourly[0]?.soilMoisture ?? null; // S267 fix: use current hour, not last forecast hour
    const omSoilMoisturePct = omSoilMoistureRaw != null ? Math.round(omSoilMoistureRaw * 100 * 10) / 10 : null;

    const result = {
      success: true,
      stale: false,
      source: 'Open-Meteo',
      sourceDetail: 'Open-Meteo WMO Forecast',
      dataType: 'Forecast Model',
      updateTime: new Date().toISOString(),
      current,
      today: todayDay,
      tomorrow: tomorrowDay,
      ground: { past24Rain, snowpack, dryingTrend },
      soilMoisturePct: omSoilMoisturePct,  // VWC % at 0-1cm depth — feeds cart status
      sunsetToday: formatTime12h(todayDay.sunset),
      sunsetTomorrow: formatTime12h(tomorrowDay.sunset),
      sunriseToday: formatTime12h(todayDay.sunrise),
      hourly: omHourly,
      vcDailyAll: (daily.time || []).map((dateStr, idx) => ({
        date: dateStr,
        tempMax: Math.round(daily.temperature_2m_max?.[idx] ?? 0),
        tempMin: Math.round(daily.temperature_2m_min?.[idx] ?? 0),
        windSpeed: Math.round(daily.wind_speed_10m_max?.[idx] ?? 0),
        precipChance: daily.precipitation_probability_max?.[idx] ?? 0,
        precip: daily.precipitation_sum?.[idx] ?? 0,
        conditions: wmoDescription(daily.weather_code?.[idx] ?? 0),
        sunset: daily.sunset?.[idx] ? formatTime12h(daily.sunset[idx]) : null,
      })),
      alerts: [],
      grid: { id: 'OM', x: lat, y: lng },
    };

    omForecastCache[cacheKey] = { data: result, ts: Date.now() };
    incrementCallCount('open_meteo', course);
    return result;
  } catch (err) {
    console.error('Open-Meteo forecast fetch failed:', err);
    return { success: false, error: err.message, source: 'Open-Meteo', stale: true };
  }
}

// ============================================================
// NWS GRID MAP — hardcoded per-course gridId/X/Y
// One-time lookup performed S174. NWS /points/{lat},{lng} returns these.
// Grid coords change only if NWS redraws their forecast grid (rare).
// If you add a new course: run `curl https://api.weather.gov/points/{lat},{lng}`
// and add the resulting gridId, gridX, gridY here.
// ============================================================
const NWS_GRID_MAP = {
  'dr-z-test':         { gridId: 'PHI', gridX: 63, gridY: 113 },
  'spooky-brook':      { gridId: 'PHI', gridX: 67, gridY: 107 },
  'pebble-creek':      { gridId: 'PHI', gridX: 82, gridY:  99 },
  'neshanic-valley':   { gridId: 'PHI', gridX: 61, gridY: 107 },
  'quail-brook':       { gridId: 'PHI', gridX: 68, gridY: 107 },
  'fox-hollow':        { gridId: 'PHI', gridX: 61, gridY: 111 },
  'architects':        { gridId: 'PHI', gridX: 45, gridY: 113 },
  'high-bridge-hills': { gridId: 'PHI', gridX: 54, gridY: 112 },
  'oak-hill':          { gridId: 'PHI', gridX: 48, gridY: 108 },
  'warrenbrook':       { gridId: 'PHI', gridX: 68, gridY: 112 },
  'green-knoll':       { gridId: 'PHI', gridX: 64, gridY: 111 },
  'hominy-hill':       { gridId: 'PHI', gridX: 81, gridY:  98 },
  'shark-river':       { gridId: 'PHI', gridX: 86, gridY:  96 },
};

// ============================================================
// NWS HOURLY FORECAST (S174 — new fetcher built for cascade rewrite)
// Endpoint: api.weather.gov/gridpoints/{office}/{x},{y}/forecast/hourly
// Returns: ~156 hours (6.5 days) of clean hourly forecast data.
// Free, unlimited, no API key. User-Agent header required.
// PROVIDES: temp, wind, conditions, precipChance — humidity comes from VC.
// DOES NOT PROVIDE: UV (no UV anywhere in NWS API), gust.
// NOTE: Reference §1.4 — NWS humidity is buggy (~50% off). Reads humidity field
//       but downstream cascade should NOT use NWS humidity. We pass it through
//       only to populate the row; cascade-merge picks humidity from VC.
// ============================================================
const nwsHourlyCache = {};

async function fetchNWSHourlyForecast(course) {
  const cacheKey = `nwsh_${course.id || course.lat + '_' + course.lng}`;
  const cached = nwsHourlyCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  try {
    const grid = NWS_GRID_MAP[course.id];
    if (!grid) throw new Error(`No NWS grid mapping for course '${course.id}'`);

    const url = `https://api.weather.gov/gridpoints/${grid.gridId}/${grid.gridX},${grid.gridY}/forecast/hourly`;
    const resp = await fetch(url, { headers: { 'User-Agent': NWS_USER_AGENT } });
    if (!resp.ok) throw new Error(`NWS hourly HTTP ${resp.status}`);
    const data = await resp.json();
    const periods = data.properties?.periods || [];
    if (!periods.length) throw new Error('NWS returned no periods');

    const hourly = periods.map(p => {
      const dt = new Date(p.startTime);
      // Wind speed comes as string like "5 mph" or "5 to 10 mph" — take average of range
      const windStr = p.windSpeed || '';
      const windNums = [...windStr.matchAll(/(\d+)/g)].map(m => parseInt(m[1]));
      const windNum = windNums.length > 1 ? Math.round((windNums[0] + windNums[windNums.length - 1]) / 2) : (windNums[0] || 0);
      const tempF = p.temperatureUnit === 'F' ? p.temperature : Math.round(celsiusToF(p.temperature));
      const humidityVal = p.relativeHumidity?.value ?? null;  // NWS humidity is buggy — flagged in cascade merge
      return {
        time: dt,
        temp: tempF,
        feelsLike: null,           // computed in cascade merge from temp + humidity + wind
        humidity: humidityVal,     // do NOT trust — Reference §1.4
        windSpeedNum: windNum,
        windSpeed: `${windNum} mph`,
        windDir: p.windDirection || '',
        windGust: null,            // NWS does not provide
        precipChance: p.probabilityOfPrecipitation?.value ?? 0,
        precip: 0,                 // NWS hourly does not provide precip amounts
        uvIndex: null,             // NWS does not provide UV
        isDaytime: !!p.isDaytime,
        conditions: p.shortForecast || '',
        shortForecast: p.shortForecast || '',
        source: 'NWS',
      };
    });

    const nowMs = Date.now();
    const current = hourly.reduce((best, h) => Math.abs(h.time - nowMs) < Math.abs(best.time - nowMs) ? h : best, hourly[0]) || {};
    const todayHours = hourly.filter(h => h.time.toDateString() === new Date().toDateString());
    const tmrDate = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
    const tmrHours = hourly.filter(h => h.time.toDateString() === tmrDate.toDateString());

    const dayStats = (hrs) => ({
      high: hrs.length ? Math.max(...hrs.map(h => h.temp)) : null,
      low:  hrs.length ? Math.min(...hrs.map(h => h.temp)) : null,
      maxWind: hrs.length ? Math.max(...hrs.map(h => h.windSpeedNum)) : null,
      maxGust: null,
      precipChance: hrs.length ? Math.max(...hrs.map(h => h.precipChance || 0)) : null,
      precip: 0,
      conditions: hrs[Math.floor(hrs.length/2)]?.conditions || '',
      dayForecast: { shortForecast: hrs[Math.floor(hrs.length/2)]?.shortForecast || '' },
    });

    const result = {
      success: true, stale: false,
      source: 'NWS', sourceDetail: `NWS Hourly · ${grid.gridId} ${grid.gridX},${grid.gridY}`,
      dataType: 'Forecast Model',
      current: { ...current },
      today: dayStats(todayHours),
      tomorrow: dayStats(tmrHours),
      hourly,
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'Unknown' },
      vcDailyAll: [],
      sunsetToday: '', sunsetTomorrow: '', sunriseToday: '',
      updateTime: new Date().toISOString(),
    };

    nwsHourlyCache[cacheKey] = { data: result, ts: Date.now() };
    incrementCallCount('nws', course);
    return result;
  } catch (err) {
    console.error('NWS hourly fetch failed:', err);
    return { success: false, error: err.message, source: 'NWS' };
  }
}

// ============================================================
// WEATHERAPI FORECAST (S174 — new fetcher built for cascade rewrite)
// Endpoint: api.weatherapi.com/v1/forecast.json?days=3
// Free tier returns 3 days hourly. Used for WIND P2, GUST P3, UV P2,
// PRECIP 48h+ P2, CONDITIONS P3 fallback paths. NOT precip0_12 (binary spikes — replaced S195).
// 1M calls/month — effectively unlimited at alpha scale.
// ============================================================
const wapiForecastCache = {};
const mbCache = {}; // Meteoblue forecast cache — restored S206

async function fetchWeatherAPIForecast(course) {
  const cacheKey = `wapif_${course.lat}_${course.lng}`;
  const cached = wapiForecastCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  try {
    const url = `https://api.weatherapi.com/v1/forecast.json` +
      `?key=${WAPI_KEY}&q=${course.lat},${course.lng}&days=3&aqi=no&alerts=no`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`WeatherAPI forecast HTTP ${resp.status}`);
    const data = await resp.json();
    const days = data.forecast?.forecastday || [];
    if (!days.length) throw new Error('WeatherAPI returned no forecast days');

    const hourly = [];
    days.forEach(day => {
      (day.hour || []).forEach(h => {
        const dt = new Date(h.time_epoch * 1000);
        hourly.push({
          time: dt,
          temp: h.temp_f != null ? Math.round(h.temp_f) : null,
          feelsLike: h.feelslike_f != null ? Math.round(h.feelslike_f) : null,
          humidity: h.humidity != null ? Math.round(h.humidity) : null,
          windSpeedNum: h.wind_mph != null ? Math.round(h.wind_mph) : 0,
          windSpeed: `${h.wind_mph != null ? Math.round(h.wind_mph) : 0} mph`,
          windDir: h.wind_dir || '',
          windGust: h.gust_mph != null ? Math.round(h.gust_mph) : 0,
          precipChance: h.chance_of_rain || 0,
          precip: h.precip_in || 0,
          uvIndex: h.uv || 0,
          isDaytime: (h.is_day ?? 1) === 1,
          conditions: h.condition?.text || '',
          shortForecast: h.condition?.text || '',
          source: 'WeatherAPI',
        });
      });
    });

    if (!hourly.length) throw new Error('WeatherAPI returned no usable hours');

    const nowMs = Date.now();
    const current = hourly.reduce((best, h) => Math.abs(h.time - nowMs) < Math.abs(best.time - nowMs) ? h : best, hourly[0]) || {};
    const todayHours = hourly.filter(h => h.time.toDateString() === new Date().toDateString());
    const tmrDate = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
    const tmrHours = hourly.filter(h => h.time.toDateString() === tmrDate.toDateString());

    const dayStats = (hrs) => ({
      high: hrs.length ? Math.max(...hrs.map(h => h.temp)) : null,
      low:  hrs.length ? Math.min(...hrs.map(h => h.temp)) : null,
      maxWind: hrs.length ? Math.max(...hrs.map(h => h.windSpeedNum)) : null,
      maxGust: hrs.length ? Math.max(...hrs.map(h => h.windGust)) : null,
      precipChance: hrs.length ? Math.max(...hrs.map(h => h.precipChance || 0)) : null,
      precip: hrs.reduce((s,h) => s + (h.precip||0), 0),
      conditions: hrs[Math.floor(hrs.length/2)]?.conditions || '',
      dayForecast: { shortForecast: hrs[Math.floor(hrs.length/2)]?.shortForecast || '' },
    });

    const result = {
      success: true, stale: false,
      source: 'WeatherAPI', sourceDetail: 'WeatherAPI 3-day forecast',
      dataType: 'Forecast Model',
      current: { ...current },
      today: dayStats(todayHours),
      tomorrow: dayStats(tmrHours),
      hourly,
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'Unknown' },
      vcDailyAll: [],
      sunsetToday: '', sunsetTomorrow: '', sunriseToday: '',
      updateTime: new Date().toISOString(),
    };

    wapiForecastCache[cacheKey] = { data: result, ts: Date.now() };
    incrementCallCount('weatherapi', course);
    return result;
  } catch (err) {
    console.error('WeatherAPI forecast fetch failed:', err);
    return { success: false, error: err.message, source: 'WeatherAPI' };
  }
}
// ============================================================
// FORECAST CASCADE (S174 — sequential per-variable rewrite)
//
// Replaces S167's all-or-nothing fetchWeatherCascade.
//
// Per S172 cascade table (status doc § Forecast Cascade):
//   TEMP:           NWS  → VC      → PirateWeather
//   WIND:           VC   → WAPIfcst → NWS
//   GUST:           VC   → PirateWeather → WAPIfcst
//   FEELS:          calcFeelsLike(local) → VC → PirateWeather
//   HUMIDITY:       VC   → PirateWeather  (NWS humidity is broken — Reference §1.4)
//   PRECIP 0-12h:   Meteoblue → VC → NWS  (MB restored S206 — ECMWF true hourly; PirateWeather was P1 S195–S205)
//   PRECIP 12-48h:  Meteoblue → VC → Open-Meteo  (MB restored S206 — ECMWF true hourly; PirateWeather was P1 S195–S205)
//   PRECIP 48h+:    Open-Meteo → WAPIfcst → (no P3)
//   CONDITIONS:     NWS → Open-Meteo → WAPIfcst
//   UV:             Open-Meteo → WAPIfcst → (S174 override: NWS has no UV)
//
// SEQUENTIAL: each source fetched ONLY when needed. Sources reused across
// variables (e.g. VC is P1 for wind/gust/precip12-48h — single VC call serves all).
// On the happy path (everything works), 4 forecast calls fire:
//   NWS, VC, WeatherAPI forecast, Open-Meteo.
// PirateWeather only fires when something else failed.
// ============================================================

// S274: Sanitize rain labels when precip < 30% — prevents VC returning "Rain"
// at low probability. Only downgrades pure rain-intensity labels.
// Never touches: thunder, lightning, freezing, snow, sleet, ice, fog, mist, haze.
const sanitizeCondition = (cond, precipChance) => {
  if ((precipChance ?? 0) >= 30) return cond;
  const c = (cond || '').toLowerCase();
  if (/thunder|lightning|tstorm|freezing|snow|blizzard|sleet|ice|fog|mist|haze/.test(c)) return cond;
  if (/rain|drizzle|shower/.test(c)) return 'Chance of Showers';
  return cond;
};

async function fetchForecastCascade(crs) {
  // Lazy fetch cache — each source called at most once per cascade
  const sources = {};
  const fetchSource = async (name, fn, timeoutMs, label) => {
    if (sources[name] !== undefined) return sources[name];
    const { result, ms } = await withTiming(withTimeout(fn(crs), timeoutMs, label));
    sources[name] = result;
    recordResponseTime(name, ms);
    logResponseTime(name, 'forecast', crs, ms, 'page_load', false, { success: !!result?.success }); // S216 F7
    return result;
  };

  // P1 fetch helpers — only called when a P1 variable needs them
  const getNWS = () => fetchSource('nws', fetchNWSHourlyForecast, 8000, 'NWS Hourly');
  const getVC  = () => fetchSource('visual_crossing', fetchVisualCrossing, 10000, 'Visual Crossing');
  const getWAPI = () => fetchSource('weatherapi', fetchWeatherAPIForecast, 8000, 'WeatherAPI Forecast');
  const getOM  = () => fetchSource('open_meteo', fetchOpenMeteo, 10000, 'Open-Meteo');
  const getPW  = () => fetchSource('pirateweather', fetchPirateWeather, 8000, 'PirateWeather');
  const getMB  = () => fetchSource('meteoblue', fetchMeteoblue, 10000, 'Meteoblue');

  // Per-variable cascade resolution. Each entry returns the source object
  // that won (and the source name) so the merger can pull the right field.
  // If P1 succeeds → P2/P3 never fetched.
  const resolveVar = async (varName, cascade) => {
    let p1Error = null;
    for (const step of cascade) {
      const src = await step.fetch();
      if (src?.success) {
        if (step.name !== cascade[0].name) {
          logSystemHealth('p2_activated', crs, { variable: varName, p1: cascade[0].name, p2: step.name, p1Error });
          _forecastP2[crs.id] = true; // S226: flag for silent retry
        }
        return { source: src, name: step.name };
      }
      // S216 F7: log each individual source failure
      logSystemHealth('forecast_source_fail', crs, {
        variable: varName,
        source: step.name,
        error: src?.error || 'unknown',
        position: step.name === cascade[0].name ? 'P1' : 'P2/P3',
      });
      if (step.name === cascade[0].name) {
        p1Error = src?.error || 'unknown';
      }
    }
    // S216 F7: log when entire cascade fails for a variable
    logSystemHealth('forecast_cascade_fail', crs, { variable: varName });
    return null;
  };

  // ── Cascade definitions per S172 lock + S174 UV correction + S206 MB precip restore ──
  const cascades = {
    temp:           [{ name: 'VC', fetch: getVC }, { name: 'PirateWeather', fetch: getPW }, { name: 'NWS', fetch: getNWS }],  // S237: NWS demoted to P3 — temp consistent across all sources, cold-start cost not justified
    wind:           [{ name: 'VC', fetch: getVC }, { name: 'WeatherAPI', fetch: getWAPI }, { name: 'NWS', fetch: getNWS }],
    gust:           [{ name: 'VC', fetch: getVC }, { name: 'PirateWeather', fetch: getPW }, { name: 'WeatherAPI', fetch: getWAPI }],
    humidity:       [{ name: 'VC', fetch: getVC }, { name: 'PirateWeather', fetch: getPW }],
    precip0_12:     [{ name: 'Meteoblue', fetch: getMB }, { name: 'VC', fetch: getVC }, { name: 'NWS', fetch: getNWS }],  // S206: MB P1 (ECMWF true hourly), VC P2, NWS P3
    precip12_48:    [{ name: 'Meteoblue', fetch: getMB }, { name: 'VC', fetch: getVC }, { name: 'OpenMeteo', fetch: getOM }],  // S206: MB P1 (ECMWF true hourly), VC P2, OM P3
    precip48plus:   [{ name: 'OpenMeteo', fetch: getOM }, { name: 'WeatherAPI', fetch: getWAPI }],
    conditions:     [{ name: 'VC', fetch: getVC }, { name: 'OpenMeteo', fetch: getOM }, { name: 'WeatherAPI', fetch: getWAPI }],
    uv:             [{ name: 'OpenMeteo', fetch: getOM }, { name: 'WeatherAPI', fetch: getWAPI }],
  };

  // Pre-fire all P1 sources in parallel before resolving variables.
  // Without this, the for-await loop below fires each source serially — up to 30s+
  // on a cold load. WeatherAPI not pre-fired — P2 wind/gust only, not a P1 source.
  await Promise.allSettled([getVC(), getOM(), getMB()]); // S237: NWS removed from pre-fire — demoted to P3 temp fallback only, fires lazily if VC+PW both fail

  // Resolve all variables. Order matters only for logging clarity — actual fetches
  // are deduplicated by fetchSource (cache hit after parallel pre-fire above).
  const resolved = {};
  for (const [varName, cascade] of Object.entries(cascades)) {
    resolved[varName] = await resolveVar(varName, cascade);
  }

  // ── Merge the resolved sources into a single forecast payload ──
  // Backbone (skeleton with current + today + tomorrow + hourly + ground + sunrise/sunset)
  // comes from VC if available, else NWS, else OM, else WAPI.
  // NOTE: PirateWeather is NEVER used as backbone — it is fallback-only per design.
  // S200 FIX: VC restored as P1 backbone. VC provides full 48h hourly array including
  // past hours of today. NWS as P1 only returns hours from current period forward,
  // causing drilldown to show only remaining hours of the day.
  const backbone =
    (sources.visual_crossing?.success && sources.visual_crossing) ||
    (sources.nws?.success && sources.nws) ||
    (sources.open_meteo?.success && sources.open_meteo) ||
    (sources.weatherapi?.success && sources.weatherapi) ||
    null;

  if (!backbone) {
    logSystemHealth('cascade_fail', crs, { source: 'ALL', error: 'All forecast sources failed', severity: 'CRITICAL' });
    return { success: false, error: 'All forecast sources failed', source: 'cascade' };
  }

  // Per-hour merge: walk backbone.hourly. For each hour, replace fields
  // with values from the cascade-winning source for that variable.
  // Match by timestamp (ms epoch) — sources may have different array lengths.
  const indexHourly = (src) => {
    // S274 FIX: bucket to nearest hour so MB/VC/NWS timestamp offsets don't cause
    // per-hour misses. Previously MB won the cascade but its data never landed in
    // hourly cards because exact ms keys didn't match VC backbone timestamps.
    // Must remain iterable (for...of in closestFromIdx) and support .get().
    if (!src?.hourly) return new Map();
    const m = new Map();
    src.hourly.forEach(h => {
      const bucket = Math.round(new Date(h.time).getTime() / 3600000) * 3600000;
      m.set(bucket, h);
    });
    // Wrap Map to intercept .get() with bucketed key, while preserving full Map interface
    return new Proxy(m, {
      get(target, prop) {
        if (prop === 'get') return (t) => target.get(Math.round(Number(t) / 3600000) * 3600000);
        const val = target[prop];
        return typeof val === 'function' ? val.bind(target) : val;
      }
    });
  };

  const winnerFor = (varName) => resolved[varName]?.source || null;
  const winnerName = (varName) => resolved[varName]?.name || null;

  const tempIdx     = indexHourly(winnerFor('temp'));
  const windIdx     = indexHourly(winnerFor('wind'));
  const gustIdx     = indexHourly(winnerFor('gust'));
  const humidityIdx = indexHourly(winnerFor('humidity'));
  const condIdx     = indexHourly(winnerFor('conditions'));
  const uvIdx       = indexHourly(winnerFor('uv'));

  // Precip — three time windows. Cutoffs are relative to "now" of this call.
  const nowMs       = Date.now();
  const cutoff12h   = nowMs + 12 * 3600 * 1000;
  const cutoff48h   = nowMs + 48 * 3600 * 1000;
  const precip0_12  = indexHourly(winnerFor('precip0_12'));
  const precip12_48 = indexHourly(winnerFor('precip12_48'));
  const precip48p   = indexHourly(winnerFor('precip48plus'));

  const mergedHourly = backbone.hourly.map(bh => {
    const t = new Date(bh.time).getTime();
    const tempH     = tempIdx.get(t);
    const windH     = windIdx.get(t);
    const gustH     = gustIdx.get(t);
    const humH      = humidityIdx.get(t);
    const condH     = condIdx.get(t);
    const uvH       = uvIdx.get(t);
    // Pick precip source by horizon
    let precipH = null;
    if (t <= cutoff12h)        precipH = precip0_12.get(t);
    else if (t <= cutoff48h)   precipH = precip12_48.get(t);
    else                       precipH = precip48p.get(t);

    const tempVal     = tempH?.temp     ?? bh.temp;
    const humVal      = humH?.humidity  ?? bh.humidity ?? 50;
    const windVal     = windH?.windSpeedNum ?? bh.windSpeedNum ?? 0;
    const gustVal     = gustH?.windGust ?? bh.windGust ?? 0;
    const feelsVal    = (tempVal != null) ? calcFeelsLike(tempVal, humVal, windVal) : bh.feelsLike;
    // S206 FIX: conditions follow precip winner ONLY when precip >= 30% — prevents Meteoblue sky label
    // (often wrong on clear days) from overriding VC conditions when MB wins precip at 0% prob.
    // Below 30%: conditions cascade winner (VC) owns sky label.
    // At/above 30%: precip winner owns it — matters what kind of precip is coming.
    // S274: sanitizeCondition prevents rain labels (VC) showing below 30% precip threshold.
    const rawCondVal  = (precipH && (precipH.precipChance ?? 0) >= 30)
                          ? (precipH.conditions ?? condH?.conditions ?? bh.conditions ?? '')
                          : (condH?.conditions  ?? bh.conditions   ?? '');
    const condVal     = sanitizeCondition(rawCondVal, precipH?.precipChance ?? bh.precipChance ?? 0);
    const shortVal    = precipH?.shortForecast ?? condH?.shortForecast ?? bh.shortForecast ?? condVal;
    const uvVal       = uvH?.uvIndex ?? bh.uvIndex ?? 0;
    const pChanceVal  = precipH?.precipChance ?? bh.precipChance ?? 0;
    const pAmtVal     = precipH?.precip ?? bh.precip ?? 0;

    return {
      ...bh,
      temp: tempVal,
      feelsLike: feelsVal,
      humidity: humVal,
      windSpeedNum: windVal,
      windSpeed: `${windVal} mph`,
      windDir: windH?.windDir ?? bh.windDir,
      windGust: gustVal,
      precipChance: pChanceVal,
      precip: pAmtVal,
      uvIndex: uvVal,
      conditions: condVal,
      shortForecast: shortVal,
    };
  });

  // Current conditions — apply same per-variable cascade to the "now" point.
  // Use the closest hour from each winning source's hourly array.
  const closestFromIdx = (idx) => {
    let best = null, bestDist = Infinity;
    for (const [t, h] of idx) {
      const d = Math.abs(t - nowMs);
      if (d < bestDist) { bestDist = d; best = h; }
    }
    return best;
  };
  const curTempH = closestFromIdx(tempIdx);
  const curWindH = closestFromIdx(windIdx);
  const curGustH = closestFromIdx(gustIdx);
  const curHumH  = closestFromIdx(humidityIdx);
  const curCondH = closestFromIdx(condIdx);
  const curUvH   = closestFromIdx(uvIdx);
  const curPrecH = closestFromIdx(precip0_12);  // current = 0-12h cascade

  const baseCurrent = backbone.current || {};
  const curTemp  = curTempH?.temp   ?? baseCurrent.temp;
  const curHum   = curHumH?.humidity ?? baseCurrent.humidity ?? 50;
  const curWind  = curWindH?.windSpeedNum ?? baseCurrent.windSpeedNum ?? 0;
  const curGust  = curGustH?.windGust ?? baseCurrent.windGust ?? 0;
  const curFeels = (curTemp != null) ? calcFeelsLike(curTemp, curHum, curWind) : baseCurrent.feelsLike;

  const mergedCurrent = {
    ...baseCurrent,
    temp: curTemp,
    feelsLike: curFeels,
    humidity: curHum,
    windSpeedNum: curWind,
    windSpeed: `${curWind} mph`,
    windDir: curWindH?.windDir ?? baseCurrent.windDir,
    windGust: curGust,
    precipChance: curPrecH?.precipChance ?? baseCurrent.precipChance ?? 0,
    precip: curPrecH?.precip ?? baseCurrent.precip ?? 0,
    uvIndex: curUvH?.uvIndex ?? baseCurrent.uvIndex ?? 0,
    conditions: curCondH?.conditions ?? baseCurrent.conditions ?? '',
    shortForecast: curCondH?.shortForecast ?? baseCurrent.shortForecast ?? '',
  };

  // Build the merged result. Keep VC's daily/sunrise/sunset/ground/vcDailyAll
  // when VC was the backbone, else fall through to whatever the backbone provides.
  const merged = {
    ...backbone,
    success: true,
    source: 'S174 Cascade',
    sourceDetail: `Cascade: T=${winnerName('temp')}, W=${winnerName('wind')}, P=${winnerName('precip0_12')}/${winnerName('precip12_48')}/${winnerName('precip48plus')}, C=${winnerName('conditions')}, UV=${winnerName('uv')}`,
    current: mergedCurrent,
    hourly: mergedHourly,
    _sources: {
      temp:         winnerName('temp'),
      wind:         winnerName('wind'),
      gust:         winnerName('gust'),
      humidity:     winnerName('humidity'),
      precip0_12:   winnerName('precip0_12'),
      precip12_48:  winnerName('precip12_48'),
      precip48plus: winnerName('precip48plus'),
      conditions:   winnerName('conditions'),
      uv:           winnerName('uv'),
      backbone:     backbone === sources.visual_crossing ? 'VC' :
                    backbone === sources.nws ? 'NWS' :
                    backbone === sources.open_meteo ? 'OpenMeteo' : 'WeatherAPI',
    },
    // Per-source success flags for modal dot rendering
    _sourceStatus: {
      nws:           sources.nws?.success === true,
      visualCrossing: sources.visual_crossing?.success === true,
      weatherapi:    sources.weatherapi?.success === true,
      pirateweather: sources.pirateweather?.success === true,
      openMeteo:     sources.open_meteo?.success === true,
      meteoblue:     sources.meteoblue?.success === true,
    },
    // Pass through full Open-Meteo result so fetchAll can extend forecast to 7 days
    // (OM is unique in returning 168h — others cap at 48-72h). Avoids double-fetching OM.
    _omFull: sources.open_meteo?.success ? sources.open_meteo : null,
  };

  return merged;
}

// ============================================================
// METEOBLUE — P1 for precip 0-12h and 12-48h (restored S206)
// Benchmark winner: #1 combined precip 0-12h and 12-48h (Apr 2026).
// Uses basic-1h package only (8,000 credits/call — NOT wind package).
// Wind stays on VC cascade. New key H2nyzakt47srTN5U expires 2027-04-30.
// decodeMb: precipProb drives conditions ≥30%; pictocode drives sky <30%.
// ============================================================
async function fetchMeteoblue(course) {
  const cacheKey = `mb_${course.lat}_${course.lng}`;
  const cached = mbCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;
  try {
    const url = `https://my.meteoblue.com/packages/basic-1h_basic-day` +
      `?lat=${course.lat}&lon=${course.lng}&apikey=${MB_API_KEY}` +
      `&temperature=F&windspeed=mph&precipitationamount=inch&format=json`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Meteoblue HTTP ${resp.status}`);
    const data = await resp.json();
    if (data.error) throw new Error(`Meteoblue: ${data.error_message || 'API error'}`);

    const h1 = data.data_1h || {};
    const times = h1.time || [];

    const decodeMb = (picto, precipProb, temp) => {
      const pp = precipProb ?? 0;
      // Rain/Snow derived from precipProb — never from pictocode alone
      if (pp >= 70) return 'Rain';
      if (pp >= 50) return 'Rain Likely';
      if (pp >= 30) return 'Chance of Rain';
      // Below 30% — sky state from hourly pictocode (1-35). Source: meteoblue.com/en/weather/docs/pictogramoverview
      if (!picto) return 'Clear';
      // Thunderstorm codes
      if (picto >= 10 && picto <= 12) return 'Thunderstorm';
      if (picto >= 27 && picto <= 30) return 'Thunderstorm';
      // Snow codes — temp-gated: above 35F treat as Rain (melts before ground)
      const isAboveFreezing = temp != null && temp > 35;
      if (picto === 24) return isAboveFreezing ? 'Rain' : 'Snow';
      if (picto === 26) return isAboveFreezing ? 'Rain' : 'Snow';
      if (picto === 32) return isAboveFreezing ? 'Rain' : 'Snow';
      if (picto === 34) return isAboveFreezing ? 'Rain' : 'Snow';
      if (picto === 35) return isAboveFreezing ? 'Rain' : 'Snow';
      // Rain codes
      if (picto === 23 || picto === 25) return 'Rain';
      if (picto === 31) return 'Rain';
      if (picto === 33) return 'Rain';
      // Fog
      if (picto >= 16 && picto <= 18) return 'Fog';
      // Clear (1-6) / Hazy (13-15) / Partly cloudy (7-9) / Overcast (19-22)
      if (picto >= 1  && picto <= 6)  return 'Clear';
      if (picto >= 13 && picto <= 15) return 'Cloudy';
      if (picto >= 7  && picto <= 9)  return 'Partly Cloudy';
      if (picto >= 19 && picto <= 22) return 'Overcast';
      return 'Cloudy';
    };

    const hourly = times.map((t, i) => {
      const dt = new Date(t);
      const picto = h1.pictocode?.[i];
      const temp = h1.temperature?.[i];
      const wind = h1.windspeed?.[i];
      const precip = h1.precipitation?.[i] ?? 0;
      const precipProb = h1.precipitation_probability?.[i] ?? null;
      const cond = decodeMb(picto, precipProb, temp);
      return {
        time: dt,
        temp: temp != null ? Math.round(temp) : null,
        feelsLike: temp != null ? calcFeelsLike(Math.round(temp), h1.relativehumidity?.[i] != null ? Math.round(h1.relativehumidity[i]) : 50, wind != null ? Math.round(wind) : 0) : null,
        humidity: h1.relativehumidity?.[i] != null ? Math.round(h1.relativehumidity[i]) : null,
        windSpeedNum: wind != null ? Math.round(wind) : 0,
        windSpeed: `${wind != null ? Math.round(wind) : 0} mph`,
        windDir: degreesToCardinal(h1.winddirection?.[i] || 0),
        windGust: 0, // basic-1h has no gust — gust stays on VC cascade
        precipChance: precipProb,
        precip: Math.round((precip || 0) * 10000) / 10000,
        uvIndex: h1.uvindex?.[i] || 0,
        isDaytime: dt.getHours() >= 6 && dt.getHours() < 20,
        conditions: cond,
        shortForecast: cond,
        source: 'Meteoblue',
      };
    }).filter(h => h.temp != null);

    if (!hourly.length) throw new Error('Meteoblue returned no usable hours');

    const nowMs = Date.now();
    const current = hourly.reduce((best, h) => Math.abs(h.time - nowMs) < Math.abs(best.time - nowMs) ? h : best, hourly[0]) || {};
    const todayHours = hourly.filter(h => h.time.toDateString() === new Date().toDateString());
    const tmrDate = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
    const tmrHours = hourly.filter(h => h.time.toDateString() === tmrDate.toDateString());

    const dayStats = (hrs) => ({
      high: hrs.length ? Math.max(...hrs.map(h => h.temp)) : null,
      low:  hrs.length ? Math.min(...hrs.map(h => h.temp)) : null,
      maxWind: hrs.length ? Math.max(...hrs.map(h => h.windSpeedNum)) : null,
      precipChance: hrs.length ? Math.max(...hrs.map(h => h.precipChance || 0)) : null,
      precip: hrs.reduce((s, h) => s + (h.precip || 0), 0),
      conditions: hrs[Math.floor(hrs.length / 2)]?.conditions || '',
      dayForecast: { shortForecast: hrs[Math.floor(hrs.length / 2)]?.shortForecast || '' },
    });

    const result = {
      success: true, stale: false,
      source: 'Meteoblue', sourceDetail: 'Meteoblue ECMWF 1h forecast',
      dataType: 'Forecast Model',
      current: { ...current, windSpeed: current.windSpeed || '0 mph', conditions: current.conditions || '' },
      today: dayStats(todayHours),
      tomorrow: dayStats(tmrHours),
      hourly,
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'Unknown' },
      vcDailyAll: [],
      sunsetToday: '', sunsetTomorrow: '', sunriseToday: '',
      updateTime: new Date().toISOString(),
    };
    mbCache[cacheKey] = { data: result, ts: Date.now() };
    incrementCallCount('meteoblue', course);
    return result;
  } catch (err) {
    console.error('Meteoblue fetch failed:', err);
    return { success: false, error: err.message, source: 'Meteoblue' };
  }
}

// ============================================================
// PIRATEWEATHER — backup source (not in main cascade after S74)
// Was P2 for wind/gust, now GribStream is P2 per S60 benchmark
// Kept for potential future use or fallback scenarios
// Dark Sky compatible API. Returns 168 hours (7 days).
// ============================================================
const PIRATE_KEY = 'QjanbYhlQ7UY4uG8ckaDSKyqvgjDCOEB';
const pirateCache = {};

async function fetchPirateWeather(course) {
  const cacheKey = `pirate_${course.lat}_${course.lng}`;
  const cached = pirateCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;
  try {
    const url = `https://api.pirateweather.net/forecast/${PIRATE_KEY}/${course.lat},${course.lng}?units=us&extend=hourly`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`PirateWeather HTTP ${resp.status}`);
    const data = await resp.json();

    if (!data.hourly?.data?.length) throw new Error('PirateWeather returned no hourly data');

    const hourly = data.hourly.data.map(h => {
      const dt = new Date(h.time * 1000);
      return {
        time: dt,
        temp: h.temperature != null ? Math.round(h.temperature) : null,
        feelsLike: h.apparentTemperature != null ? Math.round(h.apparentTemperature) : null,
        humidity: h.humidity != null ? Math.round(h.humidity * 100) : null,
        windSpeedNum: h.windSpeed != null ? Math.round(h.windSpeed) : 0,
        windSpeed: `${h.windSpeed != null ? Math.round(h.windSpeed) : 0} mph`,
        windGust: h.windGust != null ? Math.round(h.windGust) : 0,
        windDir: degreesToCardinal(h.windBearing || 0),
        precipChance: h.precipProbability != null ? Math.round(h.precipProbability * 100) : 0,
        precip: h.precipIntensity || 0,
        uvIndex: h.uvIndex || 0,
        isDaytime: dt.getHours() >= 6 && dt.getHours() < 20,
        conditions: h.summary || 'Clear',
        shortForecast: h.summary || 'Clear',
        source: 'PirateWeather',
      };
    }).filter(h => h.temp != null);

    const now_pw = Date.now();
    const current = hourly.reduce((best, h) => Math.abs(h.time - now_pw) < Math.abs(best.time - now_pw) ? h : best, hourly[0]) || {};
    const todayHours = hourly.filter(h => h.time.toDateString() === new Date().toDateString());
    const tmrDate = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
    const tmrHours = hourly.filter(h => h.time.toDateString() === tmrDate.toDateString());

    const dayStats = (hrs) => ({
      high: hrs.length ? Math.max(...hrs.map(h => h.temp)) : null,
      low:  hrs.length ? Math.min(...hrs.map(h => h.temp)) : null,
      maxWind: hrs.length ? Math.max(...hrs.map(h => h.windSpeedNum)) : null,
      maxGust: hrs.length ? Math.max(...hrs.map(h => h.windGust)) : null,
      precipChance: hrs.length ? Math.max(...hrs.map(h => h.precipChance || 0)) : null,
      precip: hrs.reduce((s,h) => s + (h.precip||0), 0),
      conditions: hrs[Math.floor(hrs.length/2)]?.conditions || '',
      dayForecast: { shortForecast: hrs[Math.floor(hrs.length/2)]?.shortForecast || '' },
    });

    const result = {
      success: true, stale: false,
      source: 'PirateWeather', sourceDetail: 'PirateWeather hourly',
      dataType: 'Forecast Model',
      current: { ...current },
      today: dayStats(todayHours),
      tomorrow: dayStats(tmrHours),
      hourly,
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'Unknown' },
      updateTime: new Date().toISOString(),
    };
    pirateCache[cacheKey] = { data: result, ts: Date.now() };
    return result;
  } catch (err) {
    console.error('PirateWeather fetch failed:', err);
    return { success: false, error: err.message, source: 'PirateWeather' };
  }
}

// ============================================================
// WEATHERBIT — P1 forecast precip probability, P2 realtime temp/feels
// GET /v2.0/forecast/hourly — 48h hourly, imperial units
// ============================================================
const wbCache = {};
async function fetchWeatherbit(course) {
  const cacheKey = `wb_${course.lat}_${course.lng}`;
  const cached = wbCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;
  try {
    const url = `https://api.weatherbit.io/v2.0/forecast/hourly` +
      `?lat=${course.lat}&lon=${course.lng}&key=${WB_API_KEY}&units=I&hours=48`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Weatherbit HTTP ${resp.status}`);
    const data = await resp.json();
    if (!data.data) throw new Error('Weatherbit no data');

    const hourly = data.data.map(h => {
      const dt = new Date(h.timestamp_local);
      const weather = h.weather || {};
      const wid = weather.code || 0;
      const thunder = wid >= 200 && wid <= 233;
      const cond = weather.description || '';
      return {
        time: dt,
        temp: h.temp != null ? Math.round(h.temp) : null,
        feelsLike: h.app_temp != null ? Math.round(h.app_temp) : null,
        humidity: h.rh != null ? Math.round(h.rh) : null,
        windSpeedNum: h.wind_spd != null ? Math.round(h.wind_spd) : 0,
        windSpeed: `${h.wind_spd != null ? Math.round(h.wind_spd) : 0} mph`,
        windDir: degreesToCardinal(h.wind_dir || 0),
        windGust: h.wind_gust_spd != null ? Math.round(h.wind_gust_spd) : 0,
        precipChance: h.pop || 0,
        precip: h.precip || 0,
        uvIndex: h.uv || 0,
        isDaytime: dt.getHours() >= 6 && dt.getHours() < 20,
        conditions: cond, shortForecast: cond,
        thunder,
        source: 'Weatherbit',
      };
    }).filter(h => h.temp != null);

    if (!hourly.length) throw new Error('Weatherbit returned no usable hours');

    const now_wb = Date.now();
    const current = hourly.reduce((best, h) => Math.abs(h.time - now_wb) < Math.abs(best.time - now_wb) ? h : best, hourly[0]) || {};
    const todayHours = hourly.filter(h => h.time.toDateString() === new Date().toDateString());
    const tmrDate = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
    const tmrHours = hourly.filter(h => h.time.toDateString() === tmrDate.toDateString());

    const dayStats = (hrs) => ({
      high: hrs.length ? Math.max(...hrs.map(h => h.temp)) : null,
      low:  hrs.length ? Math.min(...hrs.map(h => h.temp)) : null,
      maxWind: hrs.length ? Math.max(...hrs.map(h => h.windSpeedNum)) : null,
      maxGust: hrs.length ? Math.max(...hrs.map(h => h.windGust)) : null,
      precipChance: hrs.length ? Math.max(...hrs.map(h => h.precipChance || 0)) : null,
      precip: hrs.reduce((s,h) => s + (h.precip||0), 0),
      conditions: hrs[Math.floor(hrs.length/2)]?.conditions || '',
      dayForecast: { shortForecast: hrs[Math.floor(hrs.length/2)]?.shortForecast || '' },
    });

    const result = {
      success: true, stale: false,
      source: 'Weatherbit', sourceDetail: 'Weatherbit 48h hourly forecast',
      dataType: 'Forecast Model',
      current: { ...current, windSpeed: current.windSpeed || '0 mph', conditions: current.conditions || '' },
      today: dayStats(todayHours),
      tomorrow: dayStats(tmrHours),
      hourly,
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'Unknown' },
      vcDailyAll: [],
      sunsetToday: '', sunsetTomorrow: '', sunriseToday: '',
      updateTime: new Date().toISOString(),
    };
    wbCache[cacheKey] = { data: result, ts: Date.now() };
    return result;
  } catch (err) {
    console.error('Weatherbit fetch failed:', err);
    return { success: false, error: err.message, source: 'Weatherbit' };
  }
}

// ============================================================
// WEATHERAPI CURRENT — P2 realtime temp/feels when NJWxNet fails
// Also P1 direct for Architects (no NJWxNet station)
// GET /v1/current.json — actual current observations, imperial units
// ============================================================
const wapiCurrentCache = {};
async function fetchWeatherAPICurrentObs(course) {
  const cacheKey = `wapic_${course.lat}_${course.lng}`;
  const cached = wapiCurrentCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;
  try {
    const url = `https://api.weatherapi.com/v1/current.json` +
      `?key=${WAPI_KEY}&q=${course.lat},${course.lng}&aqi=no`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`WeatherAPI Current HTTP ${resp.status}`);
    const data = await resp.json();
    if (!data.current) throw new Error('WeatherAPI Current no data');
    const obs = data.current;
    const result = {
      success: true,
      source: 'WeatherAPI',
      sourceDetail: `WeatherAPI obs · ${data.location?.name || 'nearby'}`,
      current: {
        temp: obs.temp_f != null ? Math.round(obs.temp_f) : null,
        feelsLike: obs.feelslike_f != null ? Math.round(obs.feelslike_f) : null,
        humidity: obs.humidity != null ? Math.round(obs.humidity) : null,
        windSpeedNum: obs.wind_mph != null ? Math.round(obs.wind_mph) : null,
        windSpeed: obs.wind_mph != null ? `${Math.round(obs.wind_mph)} mph` : null,
        windGust: obs.gust_mph != null ? Math.round(obs.gust_mph) : null,
        windDir: obs.wind_dir || '',
        conditions: obs.condition?.text || '',
        precip1hr: obs.precip_in != null ? parseFloat(obs.precip_in.toFixed(2)) : null,
      },
    };
    wapiCurrentCache[cacheKey] = { data: result, ts: Date.now() };
    return result;
  } catch (err) {
    console.error('WeatherAPI Current fetch failed:', err);
    return { success: false, error: err.message, source: 'WeatherAPI Current' };
  }
}

// ============================================================
// NWS HRRR API (kept for reference — replaced by Open-Meteo as P4)
// ============================================================
const nwsCache = {};

async function nwsFetchHourly(gridId, gridX, gridY) {
  const key = `hourly_${gridId}_${gridX}_${gridY}`;
  const cached = nwsCache[key];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.data;

  const resp = await fetch(
    `https://api.weather.gov/gridpoints/${gridId}/${gridX},${gridY}/forecast/hourly`,
    { headers: { 'User-Agent': NWS_USER_AGENT } }
  );
  if (!resp.ok) throw new Error(`NWS hourly forecast error: ${resp.status}`);

  const data = await resp.json();
  const periods = data.properties.periods.map((p) => ({
    time: new Date(p.startTime),
    temp: p.temperature,
    tempUnit: p.temperatureUnit,
    humidity: p.relativeHumidity?.value || null,
    dewpoint: p.dewpoint?.value ? Math.round(celsiusToF(p.dewpoint.value)) : null,
    windSpeed: p.windSpeed,
    windSpeedNum: parseInt(p.windSpeed) || 0,
    windDir: p.windDirection,
    precipChance: p.probabilityOfPrecipitation?.value || 0,
    shortForecast: p.shortForecast,
    isDaytime: p.isDaytime,
    icon: p.icon,
  }));

  nwsCache[key] = { data: periods, timestamp: Date.now() };
  return periods;
}

async function nwsFetchGridData(gridId, gridX, gridY) {
  const key = `grid_data_${gridId}_${gridX}_${gridY}`;
  const cached = nwsCache[key];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.data;

  const resp = await fetch(
    `https://api.weather.gov/gridpoints/${gridId}/${gridX},${gridY}`,
    { headers: { 'User-Agent': NWS_USER_AGENT } }
  );
  if (!resp.ok) throw new Error(`NWS grid data error: ${resp.status}`);

  const props = (await resp.json()).properties;
  const parseVals = (field, convert) =>
    field?.values
      ? field.values.map((v) => ({
          time: v.validTime.split('/')[0],
          duration: v.validTime.split('/')[1],
          value: convert ? convert(v.value) : v.value,
        }))
      : [];

  const result = {
    updateTime: props.updateTime,
    quantPrecip: parseVals(props.quantitativePrecipitation, (v) => Math.round(v * 0.0393701 * 100) / 100),
    snowfall: parseVals(props.snowfallAmount, (v) => Math.round(v * 0.0393701 * 100) / 100),
    temperature: parseVals(props.temperature, (v) => Math.round(celsiusToF(v))),
    apparentTemp: parseVals(props.apparentTemperature, (v) => Math.round(celsiusToF(v))),
    relativeHumidity: parseVals(props.relativeHumidity),
    windSpeed: parseVals(props.windSpeed, (v) => Math.round(v * 0.621371)),
    windDirection: parseVals(props.windDirection),
    windGust: parseVals(props.windGust, (v) => Math.round(v * 0.621371)),
    skyCover: parseVals(props.skyCover),
    probabilityOfPrecipitation: parseVals(props.probabilityOfPrecipitation),
  };

  nwsCache[key] = { data: result, timestamp: Date.now() };
  return result;
}

async function nwsFetchDayForecast(gridId, gridX, gridY) {
  const key = `day_forecast_${gridId}_${gridX}_${gridY}`;
  const cached = nwsCache[key];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.data;

  const resp = await fetch(
    `https://api.weather.gov/gridpoints/${gridId}/${gridX},${gridY}/forecast`,
    { headers: { 'User-Agent': NWS_USER_AGENT } }
  );
  if (!resp.ok) throw new Error(`NWS day forecast error: ${resp.status}`);

  const periods = (await resp.json()).properties.periods.slice(0, 4).map((p) => ({
    name: p.name,
    temp: p.temperature,
    tempUnit: p.temperatureUnit,
    windSpeed: p.windSpeed,
    windDir: p.windDirection,
    shortForecast: p.shortForecast,
    detailedForecast: p.detailedForecast,
    isDaytime: p.isDaytime,
    precipChance: p.probabilityOfPrecipitation?.value || 0,
  }));

  nwsCache[key] = { data: periods, timestamp: Date.now() };
  return periods;
}


// ============================================================
// NJWxNet REAL-TIME LAYER (Synoptic Data API — NJWxNet network 109)
// Role: "Right now at the course" — wind truth + precip confirmation
// NOT a forecast source. Observations only.
// One call fetches all 4 stations simultaneously.
// Station → course mapping:
//   NJ84 (Franklin Twp-Somerset) → spooky-brook, quail-brook, warrenbrook, dr-z-test
//   NJ76 (Hillsborough-Duke)     → green-knoll, neshanic-valley, fox-hollow
//   NJ53 (Pittstown)             → high-bridge-hills, oak-hill
//   NJ09 (Holmdel)               → pebble-creek, hominy-hill
//   architects                   → null (no station in range — OM/TIO fallback)
// Precip: sum all non-null precip_accum_five_minute values over 2-hour rolling window (inches)
// ============================================================
const NJWXNET_TOKEN = '8ef5230a1abc4b3181b786988690ceae';
const NJWXNET_STIDS = 'NJ84,NJ76,NJ53,NJ09';
const NJWXNET_COURSE_MAP = {
  'dr-z-test':       'NJ76', // S270: corrected from NJ84 — NJ76 is 7.4mi vs NJ84 8.3mi (verified via Synoptic metadata API)
  'spooky-brook':    'NJ84',
  'quail-brook':     'NJ84',
  'warrenbrook':     'NJ84',
  'fox-hollow':      'NJ76', // S243: corrected from NJ84 — NJ76 is 6.7mi vs NJ84 9.1mi
  'green-knoll':     'NJ76',
  'neshanic-valley': 'NJ76',
  'high-bridge-hills': 'NJ53',
  'oak-hill':          'NJ53',
  'pebble-creek':    'NJ09',
  'hominy-hill':     'NJ09',
  'shark-river':     'NJ09',
  'architects':      null,
};

// Module-level cache: one shared cache for all 4 stations (single API call)
const njwxCache = { data: null, timestamp: 0 };
const NJWX_CACHE_TTL = 300000; // 5 min — stations update every 5 min

// S222: Canonical cache clear — used by all four refresh triggers
const clearAllCaches = () => {
  [vcCache, omForecastCache, nwsHourlyCache, wapiForecastCache,
   pirateCache, mbCache, wapiCurrentCache, awcCache, wbCache,
   nwsCache, lightningCache, osmCache]
    .forEach(cache => { try { Object.keys(cache).forEach(k => delete cache[k]); } catch {} });
  njwxCache.data = null; njwxCache.timestamp = 0;
};

// Return last non-null value from an array
function lastNonNull(arr) {
  if (!arr || !arr.length) return null;
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i] !== null && arr[i] !== undefined) return arr[i];
  }
  return null;
}

async function fetchNJWxNetAll(stidOverride) {
  // B5 fix S189: fetch only the station needed for the active course
  // stidOverride = single station ID (e.g. "NJ84") — avoids fetching all 4 every call
  const stidToFetch = stidOverride || NJWXNET_STIDS;
  // Return cached if fresh — cache key includes stid so per-station cache works
  const cacheKey = stidToFetch;
  if (njwxCache.data && njwxCache.stid === cacheKey && Date.now() - njwxCache.timestamp < NJWX_CACHE_TTL) {
    logResponseTime('njwxnet', 'realtime', stidOverride || 'unknown', 0, 'cache_hit', true); // S222
    return njwxCache.data;
  }
  try {
    const url = `https://api.synopticdata.com/v2/stations/timeseries` +
      `?stid=${stidToFetch}&recent=120` +
      `&vars=air_temp,relative_humidity,wind_speed,wind_gust,precip_accum_five_minute,soil_moisture` +
      `&units=english,speed|mph&token=${NJWXNET_TOKEN}`;
    const _njwxT0 = Date.now();
    const resp = await fetch(url);
    const _njwxMs = Date.now() - _njwxT0;
    recordResponseTime('njwxnet_realtime', _njwxMs);
    if (!resp.ok) throw new Error(`Synoptic API error: ${resp.status}`);
    const data = await resp.json();
    if (data.SUMMARY?.RESPONSE_CODE !== 1) {
      throw new Error(`Synoptic error: ${data.SUMMARY?.RESPONSE_MESSAGE}`);
    }
    // Build stid → parsed obs map
    const stationMap = {};
    for (const s of (data.STATION || [])) {
      const obs = s.OBSERVATIONS || {};
      // Sum all non-null 5-min precip values — last 12 readings = 1hr, all 24 = 2hr
      const precipVals = (obs.precip_accum_five_minute_set_1 || []).filter(v => v !== null && v !== undefined);
      // NJWxNet precip_accum_five_minute — Synoptic fetch uses units=english, values already in inches
      const precip2hr = precipVals.length > 0
        ? Math.round(precipVals.reduce((sum, v) => sum + v, 0) * 10000) / 10000
        : 0;
      const last12 = precipVals.slice(-12);
      const precip1hr = last12.length > 0
        ? Math.round(last12.reduce((sum, v) => sum + v, 0) * 10000) / 10000
        : 0;
      stationMap[s.STID] = {
        stid: s.STID,
        name: s.NAME,
        lat: parseFloat(s.LATITUDE),
        lng: parseFloat(s.LONGITUDE),
        temp: lastNonNull(obs.air_temp_set_1),
        humidity: lastNonNull(obs.relative_humidity_set_1),
        windSpeedRaw: lastNonNull(obs.wind_speed_set_1),   // mph (english units)
        windGustRaw:  lastNonNull(obs.wind_gust_set_1),    // mph
        precip1hr,
        precip2hr,
        // soil_moisture_set_1: NJ76 (Duke Farms) only — other stations return null
        soilMoisture: lastNonNull(obs.soil_moisture_value_1) ?? null,
        obsTime: lastNonNull(obs.date_time),
      };
    }
    njwxCache.data = stationMap;
    njwxCache.stid = cacheKey; // B5 fix: track which station(s) are cached
    njwxCache.timestamp = Date.now();
    return stationMap;
  } catch (err) {
    console.warn('ZAP NJWxNet fetch failed:', err.message);
    return null;
  }
}

// NJWxNet historical timeseries — used for weather snapshot at scorecard submit
// Queries actual obs for a specific station over a tee-to-finish window.
// start/end are JS Date objects in local ET — converted to UTC for Synoptic.
// Returns { success, avgTemp, avgWind, avgGust, avgHumidity, precipAccum, obsCount } or { success:false }
async function fetchNJWxNetTimeseries(stid, start, end, course) {
  try {
    const fmtSynoptic = d => d.toISOString().slice(0, 16).replace(/[-:T]/g, ''); // YYYYmmddHHMM UTC
    const url = `https://api.synopticdata.com/v2/stations/timeseries` +
      `?stid=${stid}` +
      `&start=${fmtSynoptic(start)}&end=${fmtSynoptic(end)}` +
      `&vars=air_temp,relative_humidity,wind_speed,wind_gust,precip_accum_five_minute` +
      `&units=english,speed|mph&token=${NJWXNET_TOKEN}`;
    const _t0 = Date.now();
    const resp = await fetch(url);
    const _ms = Date.now() - _t0;
    recordResponseTime('njwxnet_timeseries', _ms);
    logResponseTime('njwxnet', 'timeseries', course, _ms, 'scorecard_submit', false, { stid, start: fmtSynoptic(start), end: fmtSynoptic(end) });
    incrementCallCount('njwxnet_snapshot', course);
    if (!resp.ok) throw new Error(`Synoptic timeseries error: ${resp.status}`);
    const data = await resp.json();
    if (data.SUMMARY?.RESPONSE_CODE !== 1) throw new Error(data.SUMMARY?.RESPONSE_MESSAGE);
    const s = (data.STATION || [])[0];
    if (!s) throw new Error('No station data returned');
    const obs = s.OBSERVATIONS || {};
    const temps    = (obs.air_temp_set_1                   || []).filter(v => v != null);
    const winds    = (obs.wind_speed_set_1                 || []).filter(v => v != null);
    const gusts    = (obs.wind_gust_set_1                  || []).filter(v => v != null);
    const hums     = (obs.relative_humidity_set_1          || []).filter(v => v != null);
    const precipVals = (obs.precip_accum_five_minute_set_1 || []).filter(v => v != null);
    const avg = arr => arr.length ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length * 10) / 10 : null;

    // Open-Meteo forecast API — get WMO conditions for the round window
    // Uses past_days to cover same-day and next-day submits. No ERA5, no archive endpoint.
    let conditions = null;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const roundDay = new Date(start);
      roundDay.setHours(0, 0, 0, 0);
      const pastDays = Math.round((today - roundDay) / 86400000);
      const omUrl = `https://api.open-meteo.com/v1/forecast` +
        `?latitude=${course.lat}&longitude=${course.lng}` +
        `&hourly=weather_code` +
        `&past_days=${pastDays}&forecast_days=1` +
        `&timezone=America%2FNew_York`;
      const omResp = await fetch(omUrl);
      if (omResp.ok) {
        const omData = await omResp.json();
        const omTimes = omData.hourly?.time || [];
        const omCodes = omData.hourly?.weather_code || [];
        // Find hours within the tee-to-finish window
        const startH = start.getHours();
        const endH   = end.getHours();
        const windowCodes = omTimes
          .map((t, i) => ({ h: new Date(t).getHours(), code: omCodes[i] }))
          .filter(e => e.h >= startH && e.h <= endH)
          .map(e => e.code)
          .filter(c => c != null);
        if (windowCodes.length > 0) {
          // Most common WMO code in the window
          const freq = {};
          windowCodes.forEach(c => { freq[c] = (freq[c] || 0) + 1; });
          const dominant = parseInt(Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0]);
          conditions = wmoDescription(dominant);
        }
      }
    } catch { /* conditions stays null — non-fatal */ }

    return {
      success:     true,
      obsCount:    temps.length,
      avgTemp:     avg(temps),
      avgWind:     avg(winds),
      avgGust:     avg(gusts),
      avgHumidity: avg(hums),
      precipAccum: precipVals.length ? Math.round(precipVals.reduce((s, v) => s + v, 0) * 10000) / 10000 : 0, // units=english — already inches
      conditions,
      responseMs:  _ms,
    };
  } catch (err) {
    console.warn('ZAP NJWxNet timeseries failed:', err.message);
    return { success: false, error: err.message };
  }
}

async function fetchNJWxNet(course) {
  const stid = NJWXNET_COURSE_MAP[course.id] || null;
  if (!stid) {
    return { success: false, source: 'NJWxNet', error: 'No NJWxNet station for this course', stid: null };
  }
  const t0 = Date.now();
  const stationMap = await fetchNJWxNetAll(stid);
  logResponseTime('njwxnet', 'realtime', course, Date.now() - t0, 'page_load', false, { stid }); // S223: add stid
  incrementCallCount('njwxnet', course, stid);
  if (!stationMap) {
    return { success: false, source: 'NJWxNet', error: 'NJWxNet unavailable', stid };
  }
  const s = stationMap[stid];
  if (!s) {
    return { success: false, source: 'NJWxNet', error: `Station ${stid} not in response`, stid };
  }
  const windSpeedNum = s.windSpeedRaw !== null ? Math.round(s.windSpeedRaw * 10) / 10 : null;
  const windGust     = s.windGustRaw  !== null ? Math.round(s.windGustRaw  * 10) / 10 : null;
  const windSpeedRaw = s.windSpeedRaw;
  const windGustRaw  = s.windGustRaw;
  const distMi = (s.lat && s.lng) ? Math.round(haversineMiles(course.lat, course.lng, s.lat, s.lng) * 10) / 10 : null;
  return {
    success: true,
    source: 'NJWxNet',
    sourceDetail: distMi ? `NJWxNet ${s.name} (${stid}) · ${distMi} mi` : `NJWxNet ${s.name} (${stid})`,
    stid,
    obsTime: s.obsTime,
    current: {
      temp:         s.temp !== null ? Math.round(s.temp) : null,
      humidity:     s.humidity !== null ? Math.round(s.humidity) : null,
      windSpeedNum,
      windSpeed:    windSpeedNum !== null ? `${windSpeedNum} mph` : null,
      windGust,
      windSpeedRaw,
      windGustRaw,
      precip1hr:    s.precip1hr,
      precip2hr:    s.precip2hr,
    },
  };
}

// ============================================================
// LIGHTNING ENGINE (NWS Alerts API — FREE FOREVER)
// Source: api.weather.gov/alerts/active?point=lat,lng
// Parses TIME...MOT...LOC field from Severe Thunderstorm / Tornado warnings:
//   Format: TIME...MOT...LOC HHMMZ DDDDEGG SSKT LLLL LLLL
//   DDDDEG = storm motion bearing (degrees, direction storm is COMING FROM)
//   SSKT   = storm speed in knots
//   LLLL LLLL = centroid lat/lon (truncated: 4212 9133 = 42.12N, 91.33W)
// No API key. No cost. No expiry. NWS is the data source Xweather/FAA/NWS all use.
// ============================================================
const LIGHTNING_CACHE_TTL = 60000; // 1-min cache — lightning is time-critical
const lightningCache = {};

// Haversine distance in miles between two lat/lng points
function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

// Bearing in degrees (0=N, 90=E, 180=S, 270=W) from point 1 to point 2
function bearingDeg(lat1, lng1, lat2, lng2) {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const lat1R = lat1 * Math.PI / 180;
  const lat2R = lat2 * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(lat2R);
  const x = Math.cos(lat1R) * Math.sin(lat2R) - Math.sin(lat1R) * Math.cos(lat2R) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function bearingLabel(deg) {
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

// Cluster strikes into cells: group by proximity (within 5 miles of each other)
function clusterStrikes(strikes) {
  const clusters = [];
  const used = new Set();
  for (let i = 0; i < strikes.length; i++) {
    if (used.has(i)) continue;
    const cluster = [strikes[i]];
    used.add(i);
    for (let j = i + 1; j < strikes.length; j++) {
      if (used.has(j)) continue;
      const d = haversineMiles(strikes[i].lat, strikes[i].lng, strikes[j].lat, strikes[j].lng);
      if (d <= 5) { cluster.push(strikes[j]); used.add(j); }
    }
    clusters.push(cluster);
  }
  return clusters;
}

// From a cluster compute centroid, approach vector, and ETA to course
function analyzeCell(cluster, courseLat, courseLng) {
  const centLat = cluster.reduce((s,s2) => s + s2.lat, 0) / cluster.length;
  const centLng = cluster.reduce((s,s2) => s + s2.lng, 0) / cluster.length;
  const distMiles = haversineMiles(courseLat, courseLng, centLat, centLng);
  const bearing = bearingDeg(courseLat, courseLng, centLat, centLng);
  const dirLabel = bearingLabel(bearing);

  // Sort strikes by time to get velocity estimate — need at least 2 strikes spread in time
  const sorted = [...cluster].sort((a, b) => a.timestamp - b.timestamp);
  let approachSpeedMph = null;
  let etaMinutes = null;
  let movingToward = false;

  if (sorted.length >= 3) {
    // Use oldest and newest in cluster to estimate cell movement
    const oldest = sorted[0];
    const newest = sorted[sorted.length - 1];
    const timeDeltaHours = (newest.timestamp - oldest.timestamp) / 3600000;
    if (timeDeltaHours > 0.02) { // at least ~1 min spread
      const cellBearing = bearingDeg(oldest.lat, oldest.lng, newest.lat, newest.lng);
      const cellSpeed = haversineMiles(oldest.lat, oldest.lng, newest.lat, newest.lng) / timeDeltaHours;
      // Is the cell moving toward the course? Check angle delta between cell bearing and bearing-to-course
      const toBearing = bearingDeg(centLat, centLng, courseLat, courseLng);
      const angleDelta = Math.abs(((cellBearing - toBearing) + 360) % 360);
      movingToward = angleDelta < 90 || angleDelta > 270;
      if (movingToward && cellSpeed > 5) {
        approachSpeedMph = Math.round(cellSpeed);
        etaMinutes = Math.round((distMiles / cellSpeed) * 60);
      }
    }
  }

  const inHazardRing = distMiles <= 5;
  const inWarningZone = distMiles <= 10;

  return {
    strikeCount: cluster.length,
    centLat, centLng,
    distMiles: Math.round(distMiles * 10) / 10,
    bearing,
    dirLabel,
    approachSpeedMph,
    etaMinutes,
    movingToward,
    inHazardRing,
    inWarningZone,
    latestStrike: sorted[sorted.length - 1]?.timestamp || null,
  };
}

// Parse TIME...MOT...LOC line from NWS warning description text
// Example: "TIME...MOT...LOC 0025Z 228DEG 42KT 4212 9133"
//   bearingDeg: direction storm is MOVING FROM (228 = moving NE)
//   speedKt: knots
//   centroid: lat/lon truncated integers (4212 = 42.12N, 9133 = 91.33W)
function parseTimMotLoc(text) {
  if (!text) return null;
  const match = text.match(/TIME\.\.\.MOT\.\.\.LOC\s+(\d{4})Z\s+(\d{3,4})DEG\s+(\d+)KT\s+(\d{4})\s+(\d{4,5})/);
  if (!match) return null;
  const bearingFrom = parseInt(match[2], 10); // direction storm is coming FROM
  const speedKt = parseInt(match[3], 10);
  const rawLat = parseInt(match[4], 10);  // e.g. 4212 → 42.12
  const rawLon = parseInt(match[5], 10);  // e.g. 9133 → 91.33 (West, so negative)
  const centLat = rawLat / 100;
  const centLng = -(rawLon / 100); // NWS omits the minus sign for western longitudes
  // Storm is MOVING TO the opposite direction
  const motionBearing = (bearingFrom + 180) % 360;
  const speedMph = speedKt * 1.15078;
  return { centLat, centLng, bearingFrom, motionBearing, speedKt, speedMph };
}

async function fetchLightningData(lat, lng) {
  const cacheKey = `lightning_${Math.round(lat * 100)}_${Math.round(lng * 100)}`;
  const cached = lightningCache[cacheKey];
  if (cached && Date.now() - cached.fetchTime < LIGHTNING_CACHE_TTL) return cached;

  try {
    // NWS active alerts for this exact GPS point
    const url = `https://api.weather.gov/alerts/active?point=${lat},${lng}&status=actual&message_type=alert`;
    const resp = await fetch(url, {
      headers: { 'User-Agent': 'ZAP-Golf-Weather/1.0 (contact: zap-golf@zur.dev)' }
    });
    if (!resp.ok) throw new Error(`NWS alerts API: ${resp.status}`);
    const data = await resp.json();

    const features = data.features || [];

    // Filter to lightning-relevant alert types
    const lightningEvents = ['Tornado Warning', 'Severe Thunderstorm Warning',
      'Tornado Watch', 'Severe Thunderstorm Watch', 'Special Weather Statement'];
    const relevantAlerts = features.filter(f => {
      const evt = f.properties?.event || '';
      return lightningEvents.some(e => evt.includes(e));
    });

    if (relevantAlerts.length === 0) {
      // No active warnings — check if we have a recent all-clear still counting down
      const prev = lightningCache[cacheKey];
      if (prev?.lastStrikeTime) {
        const minutesSince = (Date.now() - prev.lastStrikeTime) / 60000;
        if (minutesSince < 30) {
          const remaining = Math.round(30 - minutesSince);
          const result = {
            ...prev,
            fetchTime: Date.now(),
            strikeCount: 0,
            hazardActive: false,
            warningActive: false,
            allClearMinutesRemaining: remaining,
            statusMsg: `All-clear in ${remaining} min (warning expired)`,
          };
          lightningCache[cacheKey] = result;
          return result;
        }
      }
      // Truly clear
      const result = {
        success: true,
        fetchTime: Date.now(),
        strikeCount: 0,
        cells: [],
        closestCell: null,
        hazardActive: false,
        warningActive: false,
        latestTimestamp: null,
        allClearBy: null,
        allClearMinutesRemaining: 0,
        alerts: [],
        source: 'NWS Alerts API',
      };
      lightningCache[cacheKey] = result;
      return result;
    }

    // Parse each alert for storm cell vectors
    const cells = [];
    let latestTimestamp = null;
    const parsedAlerts = [];

    for (const alert of relevantAlerts) {
      const props = alert.properties;
      const desc = props.description || '';
      const evt = props.event || '';
      const expires = props.expires ? new Date(props.expires).getTime() : null;
      const sent = props.sent ? new Date(props.sent).getTime() : null;

      // Track most recent alert time for 30-min all-clear
      if (sent && (!latestTimestamp || sent > latestTimestamp)) {
        latestTimestamp = sent;
      }

      parsedAlerts.push({ event: evt, expires, sent, headline: props.headline });

      // Try to parse TIME...MOT...LOC vector
      const motloc = parseTimMotLoc(desc);
      if (motloc) {
        const { centLat, centLng, motionBearing, speedMph } = motloc;
        const distMiles = haversineMiles(lat, lng, centLat, centLng);
        const bearingToCell = bearingDeg(lat, lng, centLat, centLng);
        const dirLabel = bearingLabel(bearingToCell);
        const inHazardRing = distMiles <= 5;
        const inWarningZone = distMiles <= 10;

        // Is the cell moving toward the course?
        // angleDelta between storm motion bearing and bearing FROM storm TO course
        const bearingFromCellToCourse = bearingDeg(centLat, centLng, lat, lng);
        const angleDelta = Math.abs(((motionBearing - bearingFromCellToCourse) + 360) % 360);
        const movingToward = angleDelta < 90 || angleDelta > 270;

        let etaMinutes = null;
        if (movingToward && speedMph > 3 && distMiles > 0) {
          etaMinutes = Math.round((distMiles / speedMph) * 60);
        }

        cells.push({
          strikeCount: 1, // NWS doesn't give individual strike count
          centLat, centLng,
          distMiles: Math.round(distMiles * 10) / 10,
          bearing: bearingToCell,
          dirLabel,
          approachSpeedMph: Math.round(speedMph),
          etaMinutes,
          movingToward,
          inHazardRing,
          inWarningZone,
          latestStrike: latestTimestamp,
          event: evt,
        });
      } else {
        // Warning exists but no MOT...LOC parseable — still flag as active threat
        // Use warning polygon centroid if available
        const geo = alert.geometry;
        if (geo?.coordinates) {
          try {
            const coords = geo.type === 'Polygon' ? geo.coordinates[0] : geo.coordinates;
            const centLat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
            const centLng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
            const distMiles = haversineMiles(lat, lng, centLat, centLng);
            cells.push({
              strikeCount: 1,
              centLat, centLng,
              distMiles: Math.round(distMiles * 10) / 10,
              bearing: bearingDeg(lat, lng, centLat, centLng),
              dirLabel: bearingLabel(bearingDeg(lat, lng, centLat, centLng)),
              approachSpeedMph: null,
              etaMinutes: null,
              movingToward: null,
              inHazardRing: distMiles <= 5,
              inWarningZone: distMiles <= 10,
              latestStrike: latestTimestamp,
              event: evt,
            });
          } catch (e) { /* skip bad geometry */ }
        }
      }
    }

    cells.sort((a, b) => a.distMiles - b.distMiles);
    const closestCell = cells[0] || null;
    const hazardActive = cells.some(c => c.inHazardRing) || relevantAlerts.some(f =>
      ['Tornado Warning', 'Severe Thunderstorm Warning'].includes(f.properties?.event));
    const warningActive = cells.some(c => c.inWarningZone) || relevantAlerts.length > 0;

    const allClearMinutesRemaining = latestTimestamp
      ? Math.max(0, Math.round((latestTimestamp + 30 * 60000 - Date.now()) / 60000))
      : 0;

    const result = {
      success: true,
      fetchTime: Date.now(),
      strikeCount: relevantAlerts.length, // active warning count
      cells,
      closestCell,
      hazardActive,
      warningActive,
      latestTimestamp,
      lastStrikeTime: latestTimestamp,
      allClearBy: latestTimestamp ? new Date(latestTimestamp + 30 * 60000) : null,
      allClearMinutesRemaining,
      alerts: parsedAlerts,
      source: 'NWS Alerts API',
    };

    lightningCache[cacheKey] = result;
    return result;

  } catch (err) {
    console.error('Lightning fetch failed:', err);
    return { success: false, error: err.message, source: 'NWS Alerts API' };
  }
}


const osmCache = {};

async function fetchWaterDensity(lat, lng) {
  const key = `water_${lat}_${lng}`;
  // B6 fix S189: check in-memory cache first, then localStorage
  if (osmCache[key]) return osmCache[key];
  try {
    const stored = localStorage.getItem(`zap_osm_${key}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      osmCache[key] = parsed;
      return parsed;
    }
  } catch { /* localStorage unavailable — continue to fetch */ }

  try {
    const query = `
      [out:json][timeout:10];
      (
        way["natural"="water"](around:1000,${lat},${lng});
        relation["natural"="water"](around:1000,${lat},${lng});
        way["landuse"="reservoir"](around:1000,${lat},${lng});
        relation["landuse"="reservoir"](around:1000,${lat},${lng});
        way["waterway"~"stream|river|ditch"](around:1000,${lat},${lng});
        relation["waterway"~"stream|river|ditch"](around:1000,${lat},${lng});
      );
      out body;
      >;
      out skel qt;
    `;

    const resp = await fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'data=' + encodeURIComponent(query),
    });
    if (!resp.ok) throw new Error(`Overpass API error: ${resp.status}`);

    const data = await resp.json();
    const ways = data.elements.filter(
      (e) => e.type === 'way' && e.tags && (e.tags.natural === 'water' || e.tags.landuse === 'reservoir' || /^(stream|river|ditch)$/.test(e.tags.waterway))
    );
    const relations = data.elements.filter(
      (e) => e.type === 'relation' && e.tags && (e.tags.natural === 'water' || e.tags.landuse === 'reservoir' || /^(stream|river|ditch)$/.test(e.tags.waterway))
    );
    const featureCount = ways.length + relations.length;

    let areaPercentage, multiplier, label;
    if (featureCount === 0) {
      areaPercentage = 0; multiplier = 1; label = 'Baseline (No water nearby)';
    } else if (featureCount <= 2) {
      areaPercentage = 2; multiplier = 1; label = 'Baseline';
    } else if (featureCount <= 5) {
      areaPercentage = 4; multiplier = 1.3; label = 'Moderate water density';
    } else {
      areaPercentage = 8; multiplier = 1.8; label = 'High water density — Swamp is hungry';
    }

    const result = { featureCount, areaPercentage, multiplier, label };
    osmCache[key] = result;
    // B6 fix: persist to localStorage so High Bridge doesn't re-query every load
    try { localStorage.setItem(`zap_osm_${key}`, JSON.stringify(result)); } catch { /* storage full — no-op */ }
    return result;
  } catch (err) {
    console.error('OSM water density query failed:', err);
    logSystemHealth('overpass_fail', null, { error: err.message, lat, lng });
    return { featureCount: 0, areaPercentage: 0, multiplier: 1, label: 'Unknown (OSM query failed)', error: err.message };
  }
}

// ============================================================
// POLLEN DATA — Google Pollen API (lat/lng, date-keyed localStorage cache)
// UPI scale 0–5. Categories: None / Very Low / Low / Medium / High / Very High
// Key restricted by HTTP referrer — safe to call from browser directly (no proxy needed)
// Cache: date-keyed localStorage. New date = new key. No TTL math needed.
// ============================================================
const GOOGLE_POLLEN_KEY = 'AIzaSyD7THXUzFUzKN1qBc2h0ObUf93zCrHfcYM';

function pollenCategoryToResult(category) {
  const map = {
    'NONE':      { label: 'None',     value: 0, color: '#4ADE80' },
    'VERY_LOW':  { label: 'Very Low', value: 1, color: '#4ADE80' },
    'LOW':       { label: 'Low',      value: 2, color: '#4ADE80' },
    'MODERATE':  { label: 'Moderate', value: 3, color: '#FACC15' },
    'HIGH':      { label: 'High',     value: 4, color: '#FB923C' },
    'VERY_HIGH': { label: 'Very High',value: 5, color: '#F87171' },
  };
  return map[category] || { label: 'Unknown', value: 0, color: '#9CA3AF' };
}

async function fetchPollenData(lat, lng, courseId) {
  const today = new Date().toISOString().slice(0, 10);
  const cacheKey = `pollen_v2_${courseId}_${today}`;

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.success) return parsed;
    }
  } catch {}

  try {
    const url = `https://pollen.googleapis.com/v1/forecast:lookup?key=${GOOGLE_POLLEN_KEY}&location.latitude=${lat}&location.longitude=${lng}&days=1`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Google Pollen API HTTP ${resp.status}`);
    incrementCallCount('google_pollen', { id: courseId, name: courseId });
    const json = await resp.json();

    const day = json?.dailyInfo?.[0];
    if (!day) throw new Error('No daily pollen data');

    const getType = (code) => day.pollenTypeInfo?.find(t => t.code === code);
    const normCat = (cat) => cat ? cat.toUpperCase().replace(/ /g, '_') : 'NONE';
    const tree  = pollenCategoryToResult(normCat(getType('TREE')?.indexInfo?.category));
    const grass = pollenCategoryToResult(normCat(getType('GRASS')?.indexInfo?.category));
    const weed  = pollenCategoryToResult(normCat(getType('WEED')?.indexInfo?.category));

    const worstValue = Math.max(tree.value, grass.value, weed.value);
    const worstLabels = ['None', 'Very Low', 'Low', 'Medium', 'High', 'Very High'];
    const worstColors = ['#4ADE80', '#4ADE80', '#4ADE80', '#FACC15', '#FB923C', '#F87171'];

    const result = {
      success: true,
      tree,
      grass,
      weed,
      worst: worstLabels[worstValue],
      worstColor: worstColors[worstValue],
      // Legacy compat fields for any display code still using .label / .color
      label: worstLabels[worstValue],
      color: worstColors[worstValue],
      offSeason: worstValue === 0,
    };

    try { localStorage.setItem(cacheKey, JSON.stringify(result)); } catch {}
    return result;
  } catch (err) {
    console.error('Google Pollen fetch failed:', err);
    return { success: false, error: err.message, label: 'Unavailable', color: '#a8c4a8', offSeason: false };
  }
}

// Aliases for legacy references
const fetchVC = fetchVisualCrossing;
const fetchOSMWaterDensity = fetchWaterDensity;
const fetchPollen = fetchPollenData;

// ============================================================
// FEELS-LIKE (NWS standard: Wind Chill + Heat Index)
// ============================================================
function calcFeelsLike(tempF, humidity, windMph) {
  // NWS Wind Chill: applies when temp <= 50°F and wind > 3 mph
  if (tempF <= 50 && windMph > 3) {
    return Math.round(
      35.74 + 0.6215 * tempF - 35.75 * Math.pow(windMph, 0.16) + 0.4275 * tempF * Math.pow(windMph, 0.16)
    );
  }
  // NWS Heat Index: applies when temp >= 80°F
  if (tempF >= 80) {
    const hi = -42.379 + 2.04901523 * tempF + 10.14333127 * humidity
      - 0.22475541 * tempF * humidity - 0.00683783 * tempF * tempF
      - 0.05481717 * humidity * humidity + 0.00122874 * tempF * tempF * humidity
      + 0.00085282 * tempF * humidity * humidity - 0.00000199 * tempF * tempF * humidity * humidity;
    return Math.round(hi);
  }
  // Between 50-80°F with low wind — feels like actual temp
  return Math.round(tempF);
}

// DEAD CODE — never called. Candidate for removal.
// Steadman Australian Apparent Temperature — covers ALL temp ranges
// AT = Ta + 0.33×e − 0.70×ws − 4.00
// Ta=temp °C, e=vapor pressure hPa, ws=wind m/s
function calcApparentTemp(tempF, humidityPct, windMph) {
  const Ta = (tempF - 32) * 5 / 9;
  const ws = windMph * 0.44704;
  const e = (humidityPct / 100) * 6.105 * Math.exp((17.27 * Ta) / (237.7 + Ta));
  const AT_c = Ta + 0.33 * e - 0.70 * ws - 4.00;
  return Math.round(AT_c * 9 / 5 + 32);
}

// ============================================================
// NJWxNet P1 OVERRIDE — patch VC weatherData with NJWxNet current obs
// NJWxNet provides: temp, humidity, windSpeed, windGust, precip1hr
// VC keeps: precipChance, conditions, shortForecast, UV, visibility,
//           cloudCover, sunrise/sunset, hourly forecast, tomorrow, etc.
// The current-hour slot in hourly[] is also patched so buildWindowWeather
// picks up NJWxNet values for the BAD engine window analysis.
// ============================================================
function applyNJWxNetOverride(weatherData, njwx) {
  if (!weatherData?.success || !njwx?.success) return weatherData;
  const nj = njwx.current;
  if (!nj) return weatherData;

  // Build patched current — only override fields where NJWxNet has data
  const patched = { ...weatherData.current };
  const overrideFields = [];

  if (nj.temp != null) {
    patched.temp = Math.round(nj.temp);
    overrideFields.push('temp');
  }
  if (nj.humidity != null) {
    patched.humidity = nj.humidity;
    overrideFields.push('humidity');
  }
  if (nj.windSpeedNum != null) {
    patched.windSpeedNum = nj.windSpeedNum;
    patched.windSpeed = `${nj.windSpeedNum} mph`;
    overrideFields.push('wind');
  }
  if (nj.windGust != null) {
    patched.windGust = nj.windGust;
  }

  // Compute feelsLike from NJWxNet inputs (or best available)
  const flTemp = patched.temp ?? weatherData.current.temp ?? 65;
  const flHumidity = patched.humidity ?? weatherData.current.humidity ?? 50;
  const flWind = patched.windSpeedNum ?? weatherData.current.windSpeedNum ?? 0;
  patched.feelsLike = calcFeelsLike(flTemp, flHumidity, flWind);

  // Tag the source so dashboard knows this is NJWxNet-patched
  patched._njwxOverride = true;
  patched._njwxFields = overrideFields;

  // Also patch ground.past24Rain? No — NJWxNet precip1hr is 1-hour rolling,
  // not 24-hour total. VC past24Rain from hourly accumulation stays.

  // Update source attribution
  const njSource = njwx.sourceDetail || 'NJWxNet';
  const vcSource = weatherData.sourceDetail || weatherData.source || 'Visual Crossing';

  // Build _rtSources reflecting what NJWxNet actually provided
  const rtSources = {};
  if (nj.temp != null)         rtSources.temp     = njSource;
  if (nj.humidity != null)     rtSources.humidity = njSource;
  if (nj.windSpeedNum != null) rtSources.wind     = njSource;
  if (nj.windGust != null)     rtSources.gust     = njSource;
  if (nj.precip1hr != null)    rtSources.precip   = njSource;

  return {
    ...weatherData,
    current: patched,
    hourly: weatherData.hourly,
    source: `NJWxNet + ${weatherData.source}`,
    sourceDetail: `${njSource} (obs) + ${vcSource} (forecast)`,
    _njwxApplied: true,
    _rtSources: rtSources,
  };
}

// ============================================================
// P2 OVERRIDE — patch forecast with P2 realtime when NJWxNet fails
// WeatherAPI current → temp, feelsLike
// VC current → wind, gust
// ============================================================
function applyP2Override(weatherData, wapiCurrent, vcCurrent) {
  if (!weatherData?.success) return weatherData;
  
  const patched = { ...weatherData.current };
  const sources = [];
  
  // WeatherAPI P2: temp + feelsLike
  if (wapiCurrent?.success && wapiCurrent.current) {
    const wa = wapiCurrent.current;
    if (wa.temp != null) patched.temp = wa.temp;
    if (wa.feelsLike != null) patched.feelsLike = wa.feelsLike;
    if (wa.humidity != null) patched.humidity = wa.humidity;
    sources.push('WeatherAPI');
  }
  
  // VC P2: wind + gust
  if (vcCurrent?.success && vcCurrent.current) {
    const vc = vcCurrent.current;
    if (vc.windSpeedNum != null) {
      patched.windSpeedNum = vc.windSpeedNum;
      patched.windSpeed = `${vc.windSpeedNum} mph`;
    }
    if (vc.windGust != null) patched.windGust = vc.windGust;
    if (vc.windDir) patched.windDir = vc.windDir;
    sources.push('VC');
  }
  
  // Recompute feelsLike if we have the inputs but no direct feelsLike
  if (patched.feelsLike == null && patched.temp != null) {
    const t = patched.temp;
    const h = patched.humidity ?? weatherData.current?.humidity ?? 50;
    const w = patched.windSpeedNum ?? weatherData.current?.windSpeedNum ?? 0;
    patched.feelsLike = calcFeelsLike(t, h, w);
  }
  
  const sourceStr = sources.length > 0 ? sources.join('+') : 'P2 fallback';
  return {
    ...weatherData,
    current: patched,
    hourly: weatherData.hourly,
    source: `${sourceStr} + ${weatherData.source}`,
    sourceDetail: `${sourceStr} (obs) + ${weatherData.sourceDetail || weatherData.source} (forecast)`,
    _p2Applied: true,
  };
}

// ============================================================
// REALTIME-ONLY REFRESH — called every 5 min on Day of Play
// Fetches NJWxNet P1 + per-variable P2s only. Never touches forecast.
// ============================================================
async function fetchRealtimeOnly(course) {
  const isNJ09 = course.id === 'pebble-creek' || course.id === 'hominy-hill' || course.id === 'shark-river';

  // P1: NJWxNet
  const njwx = await fetchNJWxNet(course).catch(() => ({ success: false }));

  const result = {
    temp: null, humidity: null,
    windSpeedNum: null, windSpeed: null, windGust: null,
    feelsLike: null, precip1hr: null,
    _sources: {},
  };

  if (njwx?.success && njwx.current) {
    const nj = njwx.current;
    result.temp        = nj.temp;
    result.humidity    = nj.humidity;
    result.windSpeedNum = isNJ09 ? null : nj.windSpeedNum;
    result.windSpeed   = isNJ09 ? null : (nj.windSpeedNum != null ? `${nj.windSpeedNum} mph` : null);
    result.windGust    = isNJ09 ? null : nj.windGust;
    result.windSpeedRaw = isNJ09 ? null : nj.windSpeedRaw;
    result.windGustRaw  = isNJ09 ? null : nj.windGustRaw;
    result.precip1hr   = nj.precip1hr;
    result._sources.temp     = 'NJWxNet';
    result._sources.humidity = 'NJWxNet';
    if (!isNJ09) {
      result._sources.wind = 'NJWxNet';
      result._sources.gust = 'NJWxNet';
    }
    result._sources.precip = 'NJWxNet';
  } else {
    // NJWxNet failed entirely — P2 temp from WeatherAPI
    const wapi = await fetchWeatherAPICurrentObs(course).catch(() => ({ success: false }));
    incrementCallCount('weatherapi', course);
    if (wapi?.success && wapi.current) {
      result.temp     = wapi.current.temp;
      result.humidity = wapi.current.humidity ?? null;
      result.precip1hr = wapi.current.precip1hr ?? null;
      result._sources.temp     = 'WeatherAPI';
      result._sources.humidity = 'WeatherAPI';
      result._sources.precip   = 'WeatherAPI';
    }
  }

  // Wind/gust P2: PirateWeather — always for NJ09, or when NJWxNet wind is truly zero (use raw to avoid Math.round false positive)
  const rawWind = result.windSpeedRaw ?? result.windSpeedNum ?? null;
  const rawGust = result.windGustRaw  ?? result.windGust    ?? null;
  // B1 fix S189: trigger P2 when NJ09, null wind, OR sensor stuck at zero
  const needsWindP2 = isNJ09 || result.windSpeedNum == null;
  if (needsWindP2) {
    const pw = await fetchPirateWeather(course).catch(() => ({ success: false }));
    if (pw?.success && pw.current) {
      result.windSpeedNum = pw.current.windSpeedNum ?? 0;
      result.windSpeed    = `${result.windSpeedNum} mph`;
      result.windGust     = pw.current.windGust ?? 0;
      result._sources.wind = isNJ09 ? 'PirateWeather (NJ09 excluded)' : 'PirateWeather (P2)';
      result._sources.gust = result._sources.wind;
      const windP2Reason = isNJ09 ? 'nj09_excluded' : 'njwxnet_wind_null';
      logSystemHealth('p2_activated', course, { variable: 'wind_gust', p1: 'NJWxNet', p2: 'PirateWeather', reason: windP2Reason, pwWind: result.windSpeedNum });
    }
  }

  // Feels-like always calculated internally
  const flTemp = result.temp ?? 65;
  const flHum  = result.humidity ?? 50;
  const flWind = result.windSpeedNum ?? 0;
  result.feelsLike = calcFeelsLike(flTemp, flHum, flWind);
  result._sources.feelsLike = 'calculated';

  return result;
}

// ============================================================
// B.A.D. ENGINE
// ============================================================
function calcBugIndex(temp, humidity, windSpeed, waterMultiplier, past24Rain, sunriseStr, sunsetStr) {
  // Biological gate — peer-reviewed thresholds (Yamana-Eltahir 2013, Rowley & Graham 1968)
  // < 50°F: mosquitoes inactive. > 90°F: above upper flight limit (32°C). > 4.5 mph: sustained flight suppressed (2 m/s).
  // < 42% RH: desiccation stress suppresses activity (Yamana-Eltahir humidity threshold).
  if (temp < 50 || temp > 90 || windSpeed > 4.5 || humidity < 42) {
    return {
      index: 0,
      label: 'None',
      advice: temp < 50 ? 'Too cold for bugs' : temp > 90 ? 'Too hot even for bugs' : windSpeed > 4.5 ? 'Too windy for bugs' : 'Too dry for bugs',
      gateKill: true,
      hatchAlert: false,
    };
  }

  // Bell curve peak at 72°F (Rowley & Graham 1968 — Aedes aegypti peak flight activity)
  // Gaussian: e^(-(temp-72)^2 / (2*sigma^2)), sigma=12 gives ~0.5 weight at 60°F and 90°F edges
  const sigma = 12;
  const tempScore = Math.exp(-Math.pow(temp - 72, 2) / (2 * sigma * sigma)); // 0–1

  // Humidity score: scales linearly from 42% (gate) to 100% RH
  const humScore = Math.max(0, (humidity - 42) / 58); // 0–1

  // Combined activity score × water multiplier
  let rawIndex = tempScore * humScore * 10 * waterMultiplier;

  // S267: Temperature lethargic multiplier — 50-65°F range: mosquitoes present but sluggish
  // At 50°F: 0.3×, at 57°F: ~0.63×, at 65°F: 1.0× (Rowley & Graham — reduced flight below 65°F)
  const tempMult = temp < 65 ? (0.3 + ((temp - 50) / 15) * 0.7) : 1.0;
  rawIndex *= tempMult;

  // S267: Time-of-day multiplier — mosquitoes peak at dusk/dawn, lowest activity midnight-to-pre-dawn
  // Parse sunrise/sunset strings e.g. "5:32 AM" / "8:26 PM"
  const parseTimeStr = (str) => {
    if (!str) return null;
    const m = str.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!m) return null;
    let h = parseInt(m[1]); const min = parseInt(m[2]); const ap = m[3].toUpperCase();
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return h + min / 60;
  };
  const nowHour = new Date().getHours() + new Date().getMinutes() / 60;
  const srHour = parseTimeStr(sunriseStr);
  const ssHour = parseTimeStr(sunsetStr);
  let timeMult = 0.4; // daytime default
  if (srHour !== null && ssHour !== null) {
    const duskStart = ssHour - 2, duskEnd = ssHour + 2;
    const dawnStart = srHour - 1, dawnEnd = srHour + 2;
    const preDown = srHour - 1;
    if (nowHour >= duskStart && nowHour <= duskEnd) timeMult = 1.0;        // dusk peak
    else if (nowHour >= dawnStart && nowHour <= dawnEnd) timeMult = 0.8;   // dawn peak
    else if (nowHour > duskEnd && nowHour <= 24) timeMult = 0.5;           // post-dusk evening
    else if (nowHour >= 0 && nowHour < preDown) timeMult = 0.2;            // dead of night
    // else: daytime 0.4 (default)
  }
  rawIndex *= timeMult;

  let index = Math.max(1, Math.min(10, Math.round(rawIndex)));
  // Allow true zero when both multipliers kill activity
  if (rawIndex < 0.5) index = 0;
  let hatchAlert = false;

  if (past24Rain > 0.5 && temp > 75) {
    hatchAlert = true;
    index = Math.min(10, index + 2);
  }

  // Return None label when index drops to 0 after multipliers
  if (index === 0) return { index: 0, label: 'None', advice: 'Minimal conditions for bug activity', gateKill: false, hatchAlert: false };

  let label, advice;
  if (index <= 2) { label = 'Low'; advice = 'Minimal bug activity'; }
  else if (index <= 4) { label = 'Moderate'; advice = 'Some bugs possible, spray optional'; }
  else if (index <= 6) { label = 'Moderate-High'; advice = 'Pack the spray'; }
  else if (index <= 8) { label = 'High'; advice = 'Heavy spray recommended'; }
  else { label = 'Extreme'; advice = 'Swarm conditions — consider postponing'; }

  if (hatchAlert) advice = 'HATCH ALERT: Recent rain + warm temps = high bug activity. ' + advice;

  return { index, label, advice, gateKill: false, hatchAlert };
}

function calcGroundSaturation(past24Rain, snowpack, temp, humidity, terrain, elevation, etData, soilMoisturePct, soilType) {
  // Snowpack override
  if (snowpack > 0) {
    return {
      cartStatus: 'COURSE UNPLAYABLE',
      cartDetail: `Snow on ground (${snowpack}")`,
      topoNote: 'Snowpack > 1.0" — course closed',
      effectiveRain: past24Rain,
      dryingTrend: 'N/A — Snow',
      sogginess: 'unplayable',
    };
  }

  // ── VWC-BASED CART STATUS (primary path when soil moisture available) ──────
  // Thresholds by soil tier — USDA SSURGO verified (S233).
  // VWC % from NJ76 (Green Knoll/Neshanic) or Open-Meteo soil_moisture_0_to_1cm × 100.
  const VWC_THRESHOLDS = {
    fast:     { allClear: 35, soft: 40, ninetyDeg: 46 }, // sandy/gravelly: Pebble Creek, High Bridge
    standard: { allClear: 28, soft: 34, ninetyDeg: 40 }, // standard loam: Spooky Brook, Green Knoll, Hominy Hill, Dr. Z
    slow:     { allClear: 25, soft: 30, ninetyDeg: 36 }, // slow silt loam: Quail Brook, Warrenbrook, Neshanic, Fox Hollow, Architects
  };
  const tier = soilType && VWC_THRESHOLDS[soilType] ? soilType : 'standard';
  const thresh = VWC_THRESHOLDS[tier];

  if (soilMoisturePct != null) {
    let cartStatus, cartDetail, sogginess;
    if (soilMoisturePct > thresh.ninetyDeg) {
      cartStatus = 'Cart Path Only';
      cartDetail = `Soil VWC ${soilMoisturePct}% — waterlogged (>${thresh.ninetyDeg}% ${tier} threshold)`;
      sogginess = 'soggy';
    } else if (soilMoisturePct > thresh.soft) {
      cartStatus = '90-Degree Rule Likely';
      cartDetail = `Soil VWC ${soilMoisturePct}% — soft conditions`;
      sogginess = 'soft';
    } else if (soilMoisturePct > thresh.allClear) {
      cartStatus = 'Soft — Carts Likely Allowed';
      cartDetail = `Soil VWC ${soilMoisturePct}% — slightly moist`;
      sogginess = 'soft';
    } else {
      cartStatus = 'All Clear';
      cartDetail = `Soil VWC ${soilMoisturePct}% — firm and dry`;
      sogginess = 'dry';
    }
    return { cartStatus, cartDetail, topoNote: null, effectiveRain: past24Rain, dryingTrend: 'Live soil data', sogginess, solarBaking: false };
  }

  // ── RAINFALL FALLBACK (when soil moisture unavailable) ───────────────────
  let effectiveRain = past24Rain;
  let topoNote = null;

  if (terrain === 'flat' && elevation < 100) {
    effectiveRain = past24Rain * 2;
    topoNote = `Flat course (${elevation}ft) — water pools. ${past24Rain}" treated as ${effectiveRain.toFixed(2)}" impact.`;
  } else if (terrain === 'elevated') {
    effectiveRain = Math.max(0, past24Rain - 0.15);
    topoNote = 'Elevated course — 12-hour drying credit applied.';
  }

  let solarBaking = false;
  let solarBakingSource = null;

  // ET math: etInches = todayET(mm) / 25.4. netMoisture = past24Rain - etInches.
  if (etData && etData.success && etData.todayET != null) {
    const etInches = etData.todayET / 25.4;
    const netMoisture = Math.max(0, past24Rain - etInches);
    if (etData.solarBakingActive) {
      effectiveRain = netMoisture * 0.5;
      solarBaking = true;
      solarBakingSource = etData.solarBakingStrong ? 'ET ' + etData.todayET.toFixed(1) + 'mm/day (extreme)' : 'ET ' + etData.todayET.toFixed(1) + 'mm/day';
      topoNote = (topoNote || '') + ` Solar baking active (ET=${etData.todayET.toFixed(1)}mm, net=${netMoisture.toFixed(2)}") — soggy duration reduced 50%.`;
    } else {
      effectiveRain = netMoisture;
      topoNote = (topoNote || '') + ` ET ${etData.todayET.toFixed(1)}mm removed — net rain ${netMoisture.toFixed(2)}".`;
    }
  } else if (!etData || !etData.success) {
    // Fallback heuristic if ET fetch failed
    if (temp > 85 && humidity < 40) {
      effectiveRain *= 0.5;
      solarBaking = true;
      solarBakingSource = 'heuristic (hot+dry)';
      topoNote = (topoNote || '') + ' Solar baking active — soggy duration reduced 50%.';
    }
  }

  let dryingTrend = 'GOOD';
  if (effectiveRain > 1) dryingTrend = 'POOR';
  else if (effectiveRain > 0.5) dryingTrend = 'FAIR';
  else if (solarBaking) dryingTrend = 'FAST (Solar baking active)';

  let cartStatus = 'All Clear';
  let cartDetail = 'Carts should be allowed on fairways';
  let sogginess = 'dry';

  if (effectiveRain > 0.8) {
    cartStatus = 'Cart Path Only';
    cartDetail = `Ground is soggy from ${past24Rain}" rain`;
    sogginess = 'soggy';
  } else if (effectiveRain > 0.4) {
    cartStatus = '90-Degree Rule Likely';
    cartDetail = `${past24Rain}" rain — soft conditions`;
    sogginess = 'soft';
  } else if (effectiveRain > 0.1) {
    cartStatus = 'Soft — Carts Likely Allowed';
    cartDetail = `Minimal rain impact (${past24Rain}")`;
    sogginess = 'soft';
  }

  return { cartStatus, cartDetail, topoNote, effectiveRain, dryingTrend, sogginess, solarBaking };
}

function calcFrostWarning(hourly, snowpack) {
  if (snowpack > 0) return { frost: false, reason: 'Snowpack override' };
  for (let i = 0; i < hourly.length - 3; i++) {
    const h = hourly[i];
    if (h.time.getHours() === 3 && h.humidity > 70) {
      for (let j = i + 1; j < Math.min(i + 5, hourly.length); j++) {
        if (hourly[j].time.getHours() === 6 && hourly[j].temp <= 36) {
          return { frost: true, reason: `Frost likely — 3AM humidity ${h.humidity}% + 6AM temp ${hourly[j].temp}°F` };
        }
      }
    }
  }
  return { frost: false, reason: null };
}

// ============================================================
// CURRENT WEATHER BUILDER
// Play window has been removed. The BAD engine now evaluates
// current real-time conditions from weatherData.current directly.
// Morning/Afternoon Go/No-Go evaluation happens in the UI
// components from the hourly array — not here.
// This function is a pass-through kept for call-site compatibility.
// ============================================================
function buildWindowWeather(weatherData, profile) {
  return weatherData;
}

function runBADEngine(weatherData, course, profile, waterDensity, etData, soilMoisturePct) {
  if (!weatherData || !weatherData.success) {
    return { status: 'UNKNOWN', reasons: ['No weather data available'], statusReason: 'No weather data available' };
  }

  const cur = weatherData.current;
  const ground = weatherData.ground;
  const temp = cur.temp || 0;
  const feelsLike = cur.feelsLike != null ? cur.feelsLike : temp; // feelsLike fallback to temp
  const effectiveTemp = feelsLike ?? temp; // S48 LOCKED: use feels-like   // actual temp only — feels-like deferred until source validated by benchmarks
  const effectiveCold = feelsLike ?? temp; // S48 LOCKED: use feels-like   // actual temp only — feels-like deferred until source validated by benchmarks
  const humidity = cur.humidity || 50;
  const windSpeed = cur.windSpeedNum || 0;
  const windGust = cur.windGust || windSpeed;
  const past24Rain = ground.past24Rain || 0;
  const snowpack = ground.snowpack || 0;
  const precipChance = cur.precipChance || 0;
  const conditions = cur.conditions || cur.shortForecast || '';
  const feelsDiff = Math.abs(feelsLike - temp) >= 2;

  const bug = calcBugIndex(temp, humidity, windSpeed, (waterDensity?.multiplier != null ? waterDensity.multiplier : 1), past24Rain, weatherData.sunriseToday, weatherData.sunsetToday);
  const groundResult = calcGroundSaturation(past24Rain, snowpack, temp, humidity, course.terrain, course.elevation, etData, soilMoisturePct ?? null, course.soilType ?? 'standard');
  const frost = calcFrostWarning(weatherData.hourly || [], snowpack);

  // ── STATUS CALCULATION — S45 LOCKED THRESHOLDS ─────────────
  // Base status from canonical function
  const baseStatus = getHourStatus(cur, profile);
  
  // Build reasons arrays
  const noGoReasons = [];
  const strCauReasons = [];
  const cautionReasons = [];
  const condLower = conditions.toLowerCase();
  
  // NO-GO triggers
  if (snowpack > 0) noGoReasons.push(`Snow on ground (${snowpack}") — course unplayable`);
  if (/thunder|lightning/.test(condLower)) noGoReasons.push('Active thunderstorms — lightning danger');
  if (/heavy rain|freezing rain|sleet|ice pellet|\bsnow\b|blizzard/.test(condLower) && !/blowing snow|drifting snow|dust storm/.test(condLower)) noGoReasons.push(`${conditions} — unplayable`);
  if (precipChance > PRECIP_NOGO) noGoReasons.push(`${precipChance}% rain chance — no-go threshold`);
  if (windSpeed >= WIND_NOGO) noGoReasons.push(`Wind ${windSpeed} mph — no-go threshold`);
  if (effectiveCold < profile.minTemp - 5) noGoReasons.push(`Too cold — feels like ${Math.round(effectiveCold)}°F`);
  if (effectiveTemp > profile.maxTemp + 5) noGoReasons.push(`Too hot — feels like ${Math.round(effectiveTemp)}°F`);
  
  // STRONG CAUTION triggers
  if (precipChance > PRECIP_STR_CAU && precipChance <= PRECIP_NOGO) strCauReasons.push(`${precipChance}% rain chance`);
  if (windSpeed > WIND_STR_CAU && windSpeed <= WIND_NOGO) strCauReasons.push(`Wind ${windSpeed} mph — strong`);
  
  // CAUTION triggers — weather
  if (precipChance > PRECIP_CAUTION && precipChance <= PRECIP_STR_CAU) cautionReasons.push(`${precipChance}% rain chance`);
  if (/\bmist\b|\bfog\b|\bhaze\b/.test(condLower)) cautionReasons.push(`${conditions} — reduced visibility`);
  if (windSpeed > WIND_CAUTION && windSpeed <= WIND_STR_CAU) cautionReasons.push(`Wind ${windSpeed} mph — breezy`);
  // Gusts: informational only — color coded but do NOT trigger Go/No-Go (S92 decision per NWS standard)
  if (effectiveTemp > profile.maxTemp && effectiveTemp <= profile.maxTemp + 5) cautionReasons.push(`Feels like ${Math.round(effectiveTemp)}°F — over hot limit`);
  if (effectiveCold < profile.minTemp && effectiveCold >= profile.minTemp - 5) cautionReasons.push(`Feels like ${Math.round(effectiveCold)}°F — under cold limit`);
  
  // CAUTION triggers — extra checks (bugs, frost, ground, visibility)
  if (groundResult.sogginess === 'soggy') cautionReasons.push(`${groundResult.cartStatus} — ${past24Rain}" rain in last 24h`);
  else if (groundResult.sogginess === 'soft') cautionReasons.push(`Soft conditions — ${past24Rain}" recent rain, ${groundResult.cartStatus}`);
  if (bug.index > 6) cautionReasons.push(`High bug activity (${bug.index}/10) — ${bug.advice}`);
  if (frost.frost) cautionReasons.push(frost.reason);
  if (weatherData.current.visibility && weatherData.current.visibility < 3) cautionReasons.push(`Low visibility (${weatherData.current.visibility} mi)`);

  let status, reasons, statusReason;
  if (noGoReasons.length > 0) {
    status = 'NO-GO'; reasons = noGoReasons; statusReason = noGoReasons.join(' · ');
  } else if (strCauReasons.length > 0) {
    status = 'STRONG CAUTION'; reasons = strCauReasons.concat(cautionReasons); statusReason = strCauReasons.concat(cautionReasons).join(' · ');
  } else if (cautionReasons.length > 0) {
    status = 'CAUTION'; reasons = cautionReasons; statusReason = cautionReasons.join(' · ');
  } else {
    status = 'GO';
    const details = [];
    details.push(`${temp}°F`);
    if (windSpeed <= 10) details.push('light wind');
    else details.push(`${windSpeed} mph wind`);
    if (bug.index <= 3) details.push('low bugs');
    if (past24Rain < 0.1) details.push('dry course');
    statusReason = `Conditions are favorable — ${details.join(', ')}`;
    reasons = [statusReason];
  }

  return {
    status, reasons, statusReason,
    bug, ground: groundResult, frost,
    temp,
    waterDensity: waterDensity || { multiplier: 1, label: 'Not queried' },
  };
}

// ============================================================
// HOUR LABEL HELPER
// ============================================================
function hourLabel(h) {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

// ============================================================
// 15-MINUTE INTERVAL INTERPOLATION
// Visual Crossing gives hourly. Interpolate linearly between
// hourly points to produce 15-min rows for the drilldown table.
// ============================================================
function interpolateToFifteenMin(hourlyArray) {
  const result = [];
  if (!hourlyArray || hourlyArray.length === 0) return result;

  for (let i = 0; i < hourlyArray.length - 1; i++) {
    const a = hourlyArray[i];
    const b = hourlyArray[i + 1];
    for (let q = 0; q < 4; q++) {
      const frac = q / 4;
      const t = new Date(a.time.getTime() + q * 15 * 60000);
      const lerp = (av, bv) => av != null && bv != null ? Math.round((av + (bv - av) * frac) * 10) / 10 : av;
      result.push({
        time: t,
        temp: lerp(a.temp, b.temp),
        humidity: lerp(a.humidity, b.humidity),
        windSpeedNum: lerp(a.windSpeedNum, b.windSpeedNum),
        windGust: lerp(a.windGust, b.windGust),
        windDir: a.windDir,
        precipChance: lerp(a.precipChance, b.precipChance),
        precip: lerp(a.precip, b.precip),
        conditions: a.conditions,
        uvIndex: lerp(a.uvIndex, b.uvIndex),
        isDaytime: a.isDaytime,
        isInterpolated: q > 0,
      });
    }
  }
  // Add the final hour point
  const last = hourlyArray[hourlyArray.length - 1];
  result.push({ ...last, isInterpolated: false });
  return result;
}

// ============================================================
// CANONICAL GO/NO-GO — S45 LOCKED SPEC
// Single source of truth for all status calculations
// ============================================================
function getHourStatus(hour, profile) {
  const temp = hour.temp || 0;
  const feelsLike = hour.feelsLike != null ? hour.feelsLike : temp;
  const wind = hour.windSpeedNum || 0;
  const gust = hour.windGust || 0;
  const precip = hour.precipChance || 0;
  const cond = (hour.conditions || hour.shortForecast || '').toLowerCase();

  // Collect ALL triggers at ALL severity levels
  const noGoTriggers = [];
  const strCauTriggers = [];
  const cauTriggers = [];
  let isLightning = false;

  // ── NO-GO triggers ──
  if (/thunder|lightning/.test(cond)) { noGoTriggers.push('Thunderstorms'); isLightning = true; }
  if (/freezing rain|freezing drizzle|sleet|ice pellet|\bsnow\b|blizzard/.test(cond) && !/blowing snow|drifting snow|dust storm/.test(cond)) noGoTriggers.push(hour.conditions || 'Severe weather');
  if (gust >= 40) noGoTriggers.push(`Gust ${gust} mph`);
  if (precip >= PRECIP_NOGO) noGoTriggers.push(`Precip ${precip}%`);
  if (wind >= WIND_NOGO) noGoTriggers.push(`Wind ${wind} mph`);
  if (feelsLike < profile.minTemp - 5) noGoTriggers.push(`Feels ${Math.round(feelsLike)}° (limit ${profile.minTemp}°)`);
  if (feelsLike > profile.maxTemp + 5) noGoTriggers.push(`Feels ${Math.round(feelsLike)}° (limit ${profile.maxTemp}°)`);

  // ── STRONG CAUTION triggers ──
  if (precip >= PRECIP_STR_CAU && precip < PRECIP_NOGO) strCauTriggers.push(`Precip ${precip}%`);
  if (wind >= WIND_STR_CAU && wind < WIND_NOGO) strCauTriggers.push(`Wind ${wind} mph`);

  // ── CAUTION triggers ──
  if (precip >= PRECIP_CAUTION && precip < PRECIP_STR_CAU) cauTriggers.push(`Precip ${precip}%`);
  if (wind >= WIND_CAUTION && wind < WIND_STR_CAU) cauTriggers.push(`Wind ${wind} mph`);
  if (feelsLike < profile.minTemp && feelsLike >= profile.minTemp - 5) cauTriggers.push(`Feels ${Math.round(feelsLike)}° (limit ${profile.minTemp}°)`);
  if (feelsLike > profile.maxTemp && feelsLike <= profile.maxTemp + 5) cauTriggers.push(`Feels ${Math.round(feelsLike)}° (limit ${profile.maxTemp}°)`);
  // Rain/shower/drizzle: defers to precip % thresholds (S98 locked). Do NOT trigger CAUTION from condition text.
  if (/\bmist\b|\bfog\b|\bhaze\b/.test(cond)) cauTriggers.push('Fog');

  // ── Determine worst status ──
  let status, color, icon;
  if (noGoTriggers.length > 0) {
    status = 'NO-GO'; color = STATUS_COLORS['NO-GO'].color; icon = isLightning ? 'lightning' : 'nogo';
  } else if (strCauTriggers.length > 0) {
    status = 'STRONG CAUTION'; color = STATUS_COLORS['STRONG CAUTION'].color; icon = 'strong-caution';
  } else if (cauTriggers.length > 0) {
    status = 'CAUTION'; color = STATUS_COLORS['CAUTION'].color; icon = 'caution';
  } else {
    status = 'GO'; color = STATUS_COLORS['GO'].color; icon = 'go';
  }

  // Build triggers object (all levels) for display
  const triggers = {};
  if (noGoTriggers.length) triggers['NO-GO'] = noGoTriggers;
  if (strCauTriggers.length) triggers['STRONG CAUTION'] = strCauTriggers;
  if (cauTriggers.length) triggers['CAUTION'] = cauTriggers;

  // Backward-compatible reason string (worst-level triggers joined)
  const worstTriggers = noGoTriggers.length ? noGoTriggers : strCauTriggers.length ? strCauTriggers : cauTriggers;
  const reason = worstTriggers.length ? worstTriggers[0] : null;

  return { status, color, reason, icon, triggers };
}

// Wrapper for rowPlayability — returns { label, color, reason } for row display
function rowPlayability(row, past24Rain, course, profile) {
  const result = getHourStatus(row, profile);
  const label = result.icon === 'lightning' ? 'NO-GO' : result.status;
  const isLightning = result.icon === 'lightning';
  return { label, color: result.color, reason: result.reason, isLightning };
}

// Get worst status from array of hours — for window evaluation
// S270: on a status tie between hours, compare actual severity instead of
// keeping whichever hour was encountered first. Severity is judged ONLY by
// the category that actually produced that hour's worst-level trigger (per
// getHourStatus's own triggers object) — not by re-scanning all raw fields,
// which would let an irrelevant background value (e.g. a stray 5% precip on
// a wind-driven NO-GO hour) outrank the real cause. Category rank matches
// the documented NO-GO/CAUTION check order (Ch09 §9.4): conditions text
// (severe weather/lightning) > gust > precip > wind > feelsLike deviation.
function _hourSeverityRank(hour, profile, result) {
  const worstLevel = result.status;
  const triggerTexts = (result.triggers && result.triggers[worstLevel]) || [];
  const gust = hour.windGust || 0;
  const precip = hour.precipChance || 0;
  const wind = hour.windSpeedNum || 0;
  const temp = hour.temp || 0;
  const feelsLike = hour.feelsLike != null ? hour.feelsLike : temp;
  const tempDeviation = feelsLike > profile.maxTemp ? feelsLike - profile.maxTemp
    : feelsLike < profile.minTemp ? profile.minTemp - feelsLike
    : 0;
  // Only consider a category if THIS hour's own triggers at its worst level
  // actually include that category's text — prevents unrelated fields from
  // contaminating the score.
  const hasTrigger = (prefix) => triggerTexts.some(t => t.startsWith(prefix));
  if (triggerTexts.some(t => t === 'Thunderstorms' || (hour.conditions && t === hour.conditions))) return 500;
  if (hasTrigger('Gust'))   return 400 + gust;
  if (hasTrigger('Precip')) return 300 + precip;
  if (hasTrigger('Wind'))   return 200 + wind;
  if (hasTrigger('Feels'))  return 100 + tempDeviation;
  if (hasTrigger('Fog'))    return 50;
  return 0;
}

function getWindowStatus(hours, profile) {
  if (!hours || !hours.length) return { status: 'UNKNOWN', color: STATUS_COLORS['UNKNOWN'].color, reason: 'No data' };
  
  const statusPriority = { 'NO-GO': 4, 'STRONG CAUTION': 3, 'CAUTION': 2, 'GO': 1 };
  let worst = { status: 'GO', color: STATUS_COLORS['GO'].color, reason: null, priority: 1, severity: -1 };
  
  for (const hour of hours) {
    const result = getHourStatus(hour, profile);
    const priority = statusPriority[result.status] || 0;
    const severity = _hourSeverityRank(hour, profile, result);
    if (priority > worst.priority || (priority === worst.priority && severity > worst.severity)) {
      worst = { ...result, priority, severity };
    }
  }
  
  return { status: worst.status, color: worst.color, reason: worst.reason };
}

// Get BEST status from array of hours — for day-card summary semantic.
// A golfer schedules a 4-5hr round inside the day; if ANY hour is playable,
// the day is playable (you tee off then). Worst-hour semantic punishes whole
// days because of dawn/dusk hours nobody plays in. Used for 7-day forecast cards
// where the user is asking "should I plan to play that day?" — not "is every
// minute of 8AM-8PM playable?". RIGHT NOW + calendar tiles still use worst.

// ============================================================
// MAIN APP COMPONENT

// ============================================================
// MAIN APP COMPONENT — v3.0 Three-Mode Architecture
// ============================================================
// ── S85 STATUS ICON — custom SVG circles (replaces all status emoji) ──
const StatusIcon = ({ status, size = 20 }) => {
  const r = size / 2;
  // S98 LOCKED icon designs
  const cfgs = {
    GO: {
      shape: <circle cx={r} cy={r} r={r - 1} fill="#16A34A" />,
      sym: <path d={`M${r*0.52},${r*1.0} L${r*0.82},${r*1.32} L${r*1.48},${r*0.62}`} stroke="#fff" strokeWidth={size * 0.13} fill="none" strokeLinecap="round" strokeLinejoin="round" />,
    },
    CAUTION: {
      shape: <circle cx={r} cy={r} r={r - 1} fill="#FDE047" />,
      sym: <text x={r} y={r + 1} textAnchor="middle" dominantBaseline="central" fill="#000" fontSize={size * 0.62} fontWeight="900" fontFamily="Arial,sans-serif">!</text>,
    },
    'STRONG CAUTION': {
      shape: <circle cx={r} cy={r} r={r - 1} fill="#EA580C" />,
      sym: <text x={r} y={r + 1} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={size * 0.48} fontWeight="900" fontFamily="Arial,sans-serif">!!</text>,
    },
    'NO-GO': {
      // Octagon (stop sign shape)
      shape: (() => {
        const s = size; const o = s * 0.29;
        const pts = `${o},0 ${s-o},0 ${s},${o} ${s},${s-o} ${s-o},${s} ${o},${s} 0,${s-o} 0,${o}`;
        return <polygon points={pts} fill="#DC2626" />;
      })(),
      sym: <text x={r} y={r + 1} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={size * 0.28} fontWeight="900" fontFamily="Arial,sans-serif" letterSpacing="0">STOP</text>,
    },
  };
  const cfg = cfgs[status] || cfgs.GO;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0, verticalAlign: 'middle' }}>
      {cfg.shape}{cfg.sym}
    </svg>
  );
};

  const fmtDateStats = (d) => {
    if (!d) return '';
    try {
      // Apps Script returns e.g. "2026-04-14T03:18:00.000Z" or "2026-04-14"
      return new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return String(d).slice(0, 10); }
  };

  const fmtTimeStats = (t) => {
    if (!t) return '';
    try {
      const s = String(t).trim();
      if (/[AP]M$/i.test(s)) return s;
      const match = s.match(/T(\d{2}):(\d{2})/);
      if (match) {
        let h = parseInt(match[1]), m = parseInt(match[2]);
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
      }
      const parts = s.split(':').map(Number);
      if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        let h = parts[0], m = parts[1];
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
      }
    } catch {}
    return '';
  };

  // Must be defined before ExpandedRoundCard — forward reference fix (WHITE-SCREEN-REVIEW)
  const getGolfNotationForBadge = (score, par) => {
    if (!score) return { color: COLOR_MUTED, shape: null };
    const diff = score - par;
    if (diff <= -2) return { color: '#F1373D', shape: 'double-circle' };
    if (diff === -1) return { color: '#F1373D', shape: 'circle' };
    if (diff === 0)  return { color: '#FFFFFF', shape: 'none' };
    if (diff === 1)  return { color: '#3B82F6', shape: 'square' };
    if (diff === 2)  return { color: '#3B82F6', shape: 'double-square' };
    if (diff === 3)  return { color: '#FF6B35', shape: 'high' };
    if (diff >= 4 && score < par * 2) return { color: '#DC2626', shape: 'high' };
    if (score >= par * 2) return { color: COLOR_RED, shape: 'snowman' };
    return { color: COLOR_RED, shape: 'high' };
  };

  const StatsNotationBadge = ({ score, par, highlight }) => {
    const n = getGolfNotationForBadge(score, par);
    if (!score) return <span style={{ fontSize: '13px', color: COLOR_MUTED, display: 'block', textAlign: 'center' }}>—</span>;
    const base = {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '13px', fontWeight: 800, color: n.color, width: 28, height: 28,
      ...(highlight ? { background: '#7f0000', borderRadius: 4, border: `2px solid ${COLOR_RED}` } : {})
    };
    if (n.shape === 'circle') return <span style={{ ...base, border: highlight ? `2px solid ${COLOR_RED}` : `2px solid ${n.color}`, borderRadius: '50%' }}>{score}</span>;
    if (n.shape === 'double-circle') return <span style={{ ...base, border: highlight ? `2px solid ${COLOR_RED}` : `3px double ${n.color}`, borderRadius: '50%' }}>{score}</span>;
    if (n.shape === 'square') return <span style={{ ...base, border: highlight ? `2px solid ${COLOR_RED}` : `2px solid ${n.color}`, borderRadius: 3 }}>{score}</span>;
    if (n.shape === 'double-square') return <span style={{ ...base, border: highlight ? `2px solid ${COLOR_RED}` : `3px double ${n.color}`, borderRadius: 3 }}>{score}</span>;
    if (n.shape === 'snowman') return (
      <span style={{ ...base, fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <svg width="13" height="18" viewBox="0 0 13 18" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <circle cx="6.5" cy="4.8" r="3.8" fill="#ffffff"/>
          <circle cx="6.5" cy="13.5" r="4" fill="#ffffff"/>
          <circle cx="5.2" cy="4.1" r="0.7" fill="#111"/>
          <circle cx="7.8" cy="4.1" r="0.7" fill="#111"/>
          <path d="M5.4 5.8 Q6.5 6.8 7.6 5.8" stroke="#111" strokeWidth="0.9" fill="none" strokeLinecap="round"/>
        </svg>{score}
      </span>
    );
    return <span style={{ ...base }}>{score}</span>;
  };

  // Shared weather snapshot renderer — used by all 4 round-card render sites.
  // Handles both snapshot schemas (conditions||condition, precipAccum||precipChance)
  // so old rounds still display correctly. No Go/No-Go — snapshots are actuals, not forecasts.
  const WeatherSnapshotLine = ({ ws, variant }) => {
    if (!ws) return null;
    const conditions = ws.conditions || ws.condition;
    const hasAny = conditions || ws.feelsLike != null || ws.wind != null || ws.gust != null
                   || ws.humidity != null || ws.precipAccum != null || ws.precipChance != null;
    if (!hasAny) return null;
    const wrap = variant === 'compact'
      ? { fontSize: 11, color: COLOR_MUTED, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6, paddingTop: 6, borderTop: `1px solid ${COLOR_DARK_GREEN}` }
      : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '3px 10px', marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${COLOR_DARK_GREEN}` };
    const item = variant === 'compact' ? {} : { fontSize: 12, color: COLOR_MUTED };
    const itemBright = variant === 'compact' ? {} : { fontSize: 12, color: COLOR_OFF_WHITE };
    const sourceLabel = ws.dataSource === 'njwxnet' ? `NJWxNet${ws.stid ? ' ' + ws.stid : ''}`
                      : ws.dataSource === 'njwxnet+forecast' ? `NJWxNet${ws.stid ? ' ' + ws.stid : ''} + forecast wind`
                      : ws.dataSource === 'forecast' ? 'Forecast only'
                      : ws.dataSource === 'forecast_fallback' ? 'NJWxNet failed — forecast'
                      : null;
    return (
      <div style={wrap}>
        {conditions && <span style={itemBright}>{conditions}</span>}
        {ws.feelsLike != null && <span style={item}>Feels {Math.round(ws.feelsLike)}°</span>}
        {ws.wind != null && <span style={item}>Wind {ws.wind}mph</span>}
        {ws.gust != null && <span style={item}>Gust {ws.gust}mph</span>}
        {ws.humidity != null && <span style={item}>Hum {Math.round(ws.humidity)}%</span>}
        {ws.precipAccum != null ? <span style={item}>Rain {ws.precipAccum}"</span>
          : ws.precipChance != null ? <span style={item}>Precip {ws.precipChance}%</span> : null}
        {sourceLabel && <span style={{ fontSize: variant === 'compact' ? 10 : 11, color: COLOR_MUTED, opacity: 0.7, marginLeft: 'auto' }}>· {sourceLabel}</span>}
      </div>
    );
  };

  const ExpandedRoundCard = ({ round, worstHoleIndex }) => {
    const [expandedPhoto, setExpandedPhoto] = React.useState(null);
    const [expandedRoundId, setExpandedRoundId] = React.useState(null);
    const pars = round.pars || Array(18).fill(4);
    const front9 = (round.scores || []).slice(0, 9).reduce((s, v) => s + (v || 0), 0);
    const back9  = (round.scores || []).slice(9, 18).reduce((s, v) => s + (v || 0), 0);
    // S166: removed dead ws / hasWeather / goNoGoColor — orphaned by S161 when
    // WeatherSnapshotLine was lifted out of ExpandedRoundCard. Per S164 rule,
    // dead code from feature removal must be deleted in same commit, not left
    // behind for a Phase 1.1 cleanup.

    return (
      <div style={{ ...CARD_STYLE, padding: '14px', marginTop: -1, borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 'none' }}>
        {/* Front 9 */}
        <div style={{ fontSize: '11px', color: COLOR_GOLD, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Front 9 — {front9 || '—'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4, marginBottom: 14 }}>
          {[0,1,2,3,4,5,6,7,8].map(i => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: COLOR_MUTED, marginBottom: 2 }}>{i + 1}</div>
              <div style={{ fontSize: '9px', color: COLOR_MUTED, marginBottom: 3 }}>p{pars[i]}</div>
              <StatsNotationBadge score={round.scores?.[i]} par={pars[i]} highlight={worstHoleIndex === i} />
            </div>
          ))}
        </div>
        {/* Back 9 */}
        <div style={{ fontSize: '11px', color: COLOR_GOLD, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Back 9 — {back9 || '—'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4, marginBottom: 14 }}>
          {[9,10,11,12,13,14,15,16,17].map(i => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: COLOR_MUTED, marginBottom: 2 }}>{i + 1}</div>
              <div style={{ fontSize: '9px', color: COLOR_MUTED, marginBottom: 3 }}>p{pars[i]}</div>
              <StatsNotationBadge score={round.scores?.[i]} par={pars[i]} highlight={worstHoleIndex === i} />
            </div>
          ))}
        </div>
        {/* Subtotals */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: COLOR_MUTED, padding: '8px 0', borderTop: `1px solid ${COLOR_DARK_GREEN}`, marginBottom: 8 }}>
          <span>Out: <span style={{ color: COLOR_GOLD, fontWeight: 700 }}>{front9}</span></span>
          <span>In: <span style={{ color: COLOR_GOLD, fontWeight: 700 }}>{back9}</span></span>
          <span>Total: <span style={{ color: COLOR_GOLD, fontWeight: 700 }}>{round.total}</span></span>
        </div>
        {/* Tee + Finish times */}
        {(round.teeTime || round.finishTime) && (
          <div style={{ fontSize: '13px', color: COLOR_MUTED, marginBottom: 8, display: 'flex', gap: 16 }}>
            {round.teeTime && <span>Tee: {fmtTimeStats(round.teeTime)}</span>}
            {round.finishTime && <span>Finish: {fmtTimeStats(round.finishTime)}</span>}
          </div>
        )}
        {/* Photo */}
        {round.photoUrl && (
          <div onClick={() => setExpandedPhoto(round.photoUrl)} style={{ cursor: 'pointer', position: 'relative', marginBottom: 10 }}>
            <img src={round.photoUrl} alt="Round group" style={{ width: '100%', height: 100, borderRadius: 8, objectFit: 'cover', display: 'block' }} />
            <div style={{ position: 'absolute', bottom: 6, right: 8, background: 'rgba(0,0,0,0.6)', padding: '3px 7px', borderRadius: 4, fontSize: 11, color: '#fff' }}>Tap to expand</div>
          </div>
        )}
        {/* S161 Report 2 fix: WeatherSnapshotLine REMOVED from ExpandedRoundCard.
            Weather now lives only on the parent card header (RoundSummaryRow / inline card).
            Showing it twice was confusing per JZ device test. */}
        {/* Tier 2 — notes */}
        {round.comment && (
          <div style={{ fontSize: '13px', color: COLOR_OFF_WHITE, padding: '8px 10px', background: COLOR_DARKEST, borderRadius: 6, fontStyle: 'italic', lineHeight: 1.4, marginBottom: 6 }}>
            {round.comment}
          </div>
        )}
        {/* Tier 2 — Truth Chain flags */}
        {round.truthChain && round.truthChain.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {round.truthChain.map((flag, i) => (
              <span key={i} style={{ background: '#2a2000', border: `1px solid ${COLOR_GOLD}`, borderRadius: 12, padding: '3px 10px', fontSize: 11, color: COLOR_GOLD, fontWeight: 600 }}>{flag}</span>
            ))}
          </div>
        )}
        {/* Full-screen photo overlay */}
        {expandedPhoto && (
          <div onClick={() => setExpandedPhoto(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.95)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <button onClick={() => setExpandedPhoto(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 40, height: 40, fontSize: 20, color: '#fff', cursor: 'pointer' }}>✕</button>
            <img src={expandedPhoto} alt="Full size" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
          </div>
        )}
      </div>
    );
  };

const RoundSummaryRow = ({ round, isExpanded, onToggle, borderColor }) => {
  // Compute par from pars array if available, fallback to course par
  const parsArray = Array.isArray(round.pars) ? round.pars
                  : (typeof round.pars === 'string' ? round.pars.split(',').map(Number) : []);
  const roundPar = parsArray.length > 0 ? parsArray.reduce((s, p) => s + (Number(p) || 0), 0) : null;
  const total = Number(round.total) || 0;
  const diff = roundPar != null ? total - roundPar : null;

  // Differential display + color (golf standard)
  let diffText, diffColor;
  if (diff < 0)      { diffText = String(diff); diffColor = '#F1373D'; }
  else if (diff > 0) { diffText = '+' + diff;   diffColor = '#3B82F6'; }
  else               { diffText = 'E';           diffColor = '#22C55E'; }

  // Duration "Xh Ym"
  let durationText = '';
  if (round.teeTime && round.finishTime) {
    // Both times stored as 12-hour with AM/PM (per S143 to12Hour conversion)
    // Need to parse back to compute elapsed minutes
    const parseTime = (t) => {
      const s = String(t).trim();
      const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const ampm = (m[3] || '').toUpperCase();
      if (ampm === 'PM' && h !== 12) h += 12;
      else if (ampm === 'AM' && h === 12) h = 0;
      return h * 60 + min;
    };
    const teeMin = parseTime(round.teeTime);
    const finMin = parseTime(round.finishTime);
    if (teeMin != null && finMin != null && finMin >= teeMin) {
      const total = finMin - teeMin;
      const h = Math.floor(total / 60);
      const m = total % 60;
      durationText = `${h}h ${m}m`;
    }
  }

  // Player list (max 4, no wrap, ellipsis handled by CSS)
  const playerList = (round.players || []).filter(Boolean);
  const playersText = Array.isArray(playerList) ? playerList.join(' · ') : String(playerList);

  // Tee → Finish display
  const fmtT = (t) => {
    if (!t) return '';
    const s = String(t).trim();
    if (/[AP]M$/i.test(s)) return s; // already 12-hour
    const parts = s.split(':').map(Number);
    if (parts.length < 2 || isNaN(parts[0])) return s;
    const h = parts[0], m = parts[1];
    return (h > 12 ? h - 12 : h || 12) + ':' + String(m).padStart(2, '0') + ' ' + (h >= 12 ? 'PM' : 'AM');
  };
  const dateLine = `${fmtDateStats(round.date)}${round.teeTime ? ' · ' + fmtT(round.teeTime) : ''}${round.finishTime ? ' — ' + fmtT(round.finishTime) : ''}`;

  // Weather line gating
  const ws = round.weatherSnapshot;
  const hasWeather = ws && (
    ws.feelsLike != null || ws.wind != null || ws.gust != null ||
    ws.humidity != null || ws.precipAccum != null || ws.precipChance != null ||
    ws.conditions || ws.condition
  );

  return (
    <div style={{ width: '100%' }}>
      <button
        onClick={onToggle}
        style={{
          ...CARD_STYLE, width: '100%', textAlign: 'left', cursor: 'pointer',
          padding: '14px 16px',
          border: isExpanded ? `2px solid ${borderColor || COLOR_GOLD}` : `1px solid ${COLOR_DARK_GREEN}`,
          borderBottomLeftRadius: isExpanded ? 0 : 12,
          borderBottomRightRadius: isExpanded ? 0 : 12,
          display: 'block'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '15px', color: COLOR_WHITE, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{round.courseName}</div>
            <div style={{ fontSize: '13px', color: COLOR_GOLD, fontWeight: 600, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dateLine}</div>
            {durationText && (
              <div style={{ fontSize: '13px', color: COLOR_WHITE, fontWeight: 600, marginTop: 2 }}>{durationText}</div>
            )}
            {playersText && (
              <div style={{ fontSize: '12px', color: COLOR_MUTED, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{playersText}</div>
            )}
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ fontSize: '28px', fontWeight: 800, color: diffColor, lineHeight: 1 }}>{diffText}</div>
            <div style={{ fontSize: '12px', color: COLOR_WHITE, fontWeight: 600, marginTop: 4 }}>{round.total} · Par {roundPar}</div>
            <div style={{ fontSize: '11px', color: COLOR_MUTED, marginTop: 4 }}>{isExpanded ? '▲' : '▼'}</div>
          </div>
        </div>
        {hasWeather && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${COLOR_DARK_GREEN}` }}>
            <WeatherSnapshotLine ws={ws} />
          </div>
        )}
      </button>
    </div>
  );
};


function ScorecardScreen({ selectedCourse, sharedRounds, setSharedRounds, navigateTo, goBack, cfgPlayers, scorecardBackGuard, cfgHolePars, weatherData, badResult, spondEvents, onEnterReview, onExitReview }) {
  const course = selectedCourse;
  const today = new Date();
  const [roundDate, setRoundDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const dateStr = (() => {
    const d = new Date(roundDate + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  })();
  const timeStr = today.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  const [players, setPlayers] = useState(['', '', '', '']);
  const [guestNames, setGuestNames] = useState(['', '', '', '']);
  const [scores, setScores] = useState(Array(18).fill(''));
  const [sending, setSending] = useState(false);
  const [phase, setPhase] = useState('players');

  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [submitWarning, setSubmitWarning] = useState(null); // inline warning, requires second tap to proceed
  const [teeTime, setTeeTime] = useState('');
  const [finishTime, setFinishTime] = useState('');
  const [roundComment, setRoundComment] = useState('');
  const [truthFlags, setTruthFlags] = useState([]);
  const [truthOther, setTruthOther] = useState('');
  const [submittedRoundId, setSubmittedRoundId] = useState(null);
  const [expandedPhoto, setExpandedPhoto] = useState(null);
  const [expandedRoundId, setExpandedRoundId] = useState(null);
  const toggleRound = (id, suffix) => setExpandedRoundId(prev => prev === id + suffix ? null : id + suffix);
  const [neshanicNineA, setNeshanicNineA] = useState('meadow');
  const [neshanicNineB, setNeshanicNineB] = useState('lake');

  // S161: Back guard rewrite per S160 locked spec.
  //   - Players phase → go back to hub (existing behavior, preserved)
  //   - Scoring phase → ALWAYS warn, regardless of entered count.
  //     Spec: don't preserve scorecard state on back; user starts over if they continue.
  //     Wording locked.
  //   - Review phase → goBack() with no warning (existing behavior, preserved)
  const phaseRef = React.useRef(phase);
  phaseRef.current = phase;
  const playersRef = React.useRef(players);
  playersRef.current = players;
  const scoresRef = React.useRef(scores);
  scoresRef.current = scores;
  useEffect(() => {
    scorecardBackGuard.current = () => {
      const currentPhase = phaseRef.current;
      // S161 Report 1: Players phase back goes HOME (goBack), not to orphan hub.
      // Previous behavior routed to setPhase('hub') which renders the dead S98-removed
      // PLAY/SCORE/HIST/STATS tile page at line 3662. JZ device-tested S160 — confirmed
      // both phases were dropping users on that orphan page. Hub render itself stays
      // (Phase 1.1 dedicated dead-code session will remove it).
      if (currentPhase === 'players') {
        // Read from ref — avoids stale closure on empty dependency array
        const anySelected = playersRef.current.some(p => p && p !== '');
        if (anySelected) {
          if (window.confirm('Going back will lose your player selections. Continue?')) goBack();
        } else {
          goBack();
        }
        return;
      }
      // From scorecard phase — warn + go back to players (not home)
      if (currentPhase === 'scorecard') {
        // Read from ref — avoids stale closure on empty dependency array
        const anyScores = scoresRef.current.some(s => s !== '');
        if (anyScores) {
          if (window.confirm("Going back will lose all your scores. Continue?")) {
            setPhase('players');
            setTimeout(() => scrollToTop(), 50);
          }
        } else {
          setPhase('players');
          setTimeout(() => scrollToTop(), 50);
        }
        return;
      }
      // Any other phase (review etc.) — exit without warning
      goBack();
    };
    return () => { scorecardBackGuard.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-course hole pars — verified against official scorecards S126
  const COURSE_HOLE_PARS = {
    'spooky-brook':     [5,4,3,4,5,4,4,3,4, 4,3,4,5,4,4,3,4,5], // par 72
    'quail-brook':      [5,4,4,3,4,5,4,3,4, 4,3,4,4,5,3,4,5,4], // par 72 — S175 audit: H10/H11 unswapped to match scorecard image
    'warrenbrook':      [4,4,4,3,5,3,4,5,3, 4,4,3,4,4,5,4,4,4], // par 71
    'green-knoll':      [5,4,3,4,5,4,3,4,4, 4,4,3,4,3,4,5,4,5], // par 72
    'neshanic-valley':  [4,4,3,5,4,3,4,4,5, 4,4,3,4,5,4,4,3,5], // par 72 (Meadow+Lake default)
    'fox-hollow':       [4,3,5,3,4,5,4,4,3, 5,4,3,4,3,5,4,4,3], // par 70
    'architects':       [5,3,5,4,4,3,4,3,4, 4,5,3,5,4,4,4,3,4], // par 71
    'high-bridge-hills':[5,4,5,3,4,3,4,3,4, 4,4,3,4,3,5,4,4,4], // par 70
    'oak-hill':         [4,4,3,4,3,5,4,5,4, 5,3,4,4,4,3,4,4,5], // par 72
    'hominy-hill':      [4,4,3,5,4,4,3,4,5, 4,3,4,4,5,4,3,5,4], // par 72
    'pebble-creek':     [4,4,3,4,3,4,4,5,4, 4,3,4,4,5,4,4,5,3], // par 71 — corrected S165 per authoritative scorecard PDF + pebblecreekgolfclub.com
    'shark-river':      [4,4,3,4,4,5,3,5,4, 4,5,3,4,3,4,4,3,5], // par 71 — S274 per official Monmouth County scorecard (front 36, back 35; H18=par5)
  };
  // Neshanic Valley individual nine pars — verified against official Somerset County scorecard S126
  const NESHANIC_NINES = {
    meadow: [4,4,3,5,4,3,4,4,5], // par 36
    lake:   [4,4,3,4,5,4,4,3,5], // par 36
    ridge:  [4,4,4,3,5,3,4,5,4], // par 36
  };
  const isNeshanic = course?.id === 'neshanic-valley';
  const HOLE_PARS = isNeshanic ? [...(NESHANIC_NINES[neshanicNineA] || NESHANIC_NINES.meadow), ...(NESHANIC_NINES[neshanicNineB] || NESHANIC_NINES.lake)] : ((course && (cfgHolePars?.[course.id] || COURSE_HOLE_PARS[course.id])) || Array(18).fill(4));

  const total = scores.reduce((sum, s) => sum + (parseInt(s) || 0), 0);
  const holesEntered = scores.filter(s => s !== '').length;
  const front9Total = scores.slice(0,9).reduce((s,v) => s+(parseInt(v)||0), 0);
  const back9Total = scores.slice(9,18).reduce((s,v) => s+(parseInt(v)||0), 0);

  const getPlayerLabel = (idx) => {
    const pid = players[idx];
    if (!pid) return '—';
    if (pid === 'guest') return guestNames[idx] || 'Guest';
    const p = cfgPlayers.find(p => p.id === pid);
    return p ? p.emoji + ' ' + p.name : '—';
  };

  const getGolfNotation = (score, par) => {
    if (!score) return { label: '—', color: COLOR_MUTED, shape: null };
    const diff = score - par;
    if (diff <= -2) return { label: 'Eagle', color: '#F1373D', shape: 'double-circle' };
    if (diff === -1) return { label: 'Birdie', color: '#F1373D', shape: 'circle' };
    if (diff === 0)  return { label: 'Par', color: '#FFFFFF', shape: 'none' };
    if (diff === 1)  return { label: 'Bogey', color: '#3B82F6', shape: 'square' };
    if (diff === 2)  return { label: 'Double', color: '#3B82F6', shape: 'double-square' };
    if (diff === 3)  return { label: 'Triple', color: '#FF6B35', shape: 'high' };
    if (diff >= 4 && score < par * 2) return { label: `+${diff}`, color: '#DC2626', shape: 'high' };
    if (score >= par * 2) return { label: 'Snowman', color: COLOR_RED, shape: 'snowman' };
    return { label: `+${diff}`, color: COLOR_RED, shape: 'high' };
  };

  const uploadPhoto = async (file) => {
    setPhotoUploading(true);
    try {
      // B-2: Resize large photos before upload (iPhone photos can be 6MB+)
      const resizedFile = await new Promise((resolve) => {
        const MAX_DIM = 1600;
        const MAX_BYTES = 2 * 1024 * 1024; // 2MB target
        if (file.size <= MAX_BYTES) { resolve(file); return; }
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })), 'image/jpeg', 0.85);
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
        img.src = url;
      });
      const fd = new FormData();
      fd.append('file', resizedFile);
      fd.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      const resp = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, { method: 'POST', body: fd });
      const json = await resp.json();
      setPhotoUploading(false);
      return json.secure_url || null;
    } catch (err) {
      console.error('Cloudinary upload failed:', err);
      setPhotoUploading(false);
      return null;
    }
  };

  const submitRound = async () => {
    // S166: wrap entire submit in try/catch — async/event-handler errors
    // are NOT caught by React ErrorBoundary. This converts a silent crash /
    // white screen into a visible alert + console.error with full stack.
    try {
    setSending(true);
    const playerLabels = players.map((pid, i) => pid === 'guest' ? (guestNames[i] || 'Guest') : (cfgPlayers.find(p => p.id === pid)?.name || null)).filter(Boolean);

    // SCORE-6: Weather snapshot — actual NJWxNet obs for tee-to-finish window
    // Falls back to Meteoblue hourly if: future date, no station, NJWxNet fails/times out.
    // NJ09 (Pebble Creek/Hominy Hill): NJWxNet for temp/humidity/precip, Meteoblue for wind/gust.
    const buildMeteoblueSnapshot = () => {
      try {
        const hourly = weatherData?.hourly || [];
        if (!hourly.length) return null;
        const parseTimeStr = (t) => {
          if (!t) return null;
          const [hStr, mStr] = t.split(':');
          const today = new Date();
          today.setHours(parseInt(hStr), parseInt(mStr), 0, 0);
          return today;
        };
        const teeDate = parseTimeStr(teeTime);
        const finishDate = parseTimeStr(finishTime);
        const windowHours = hourly.filter(h => {
          const ht = new Date(h.time || h.dt * 1000);
          if (teeDate && finishDate) return ht >= teeDate && ht <= finishDate;
          if (teeDate) return ht >= teeDate && ht <= new Date(teeDate.getTime() + 5 * 3600000);
          return false;
        });
        if (!windowHours.length && hourly.length) windowHours.push(hourly[0]);
        const avgFeels = Math.round(windowHours.reduce((s, h) => s + (h.feelsLike ?? h.temp ?? 0), 0) / windowHours.length);
        const avgWind  = Math.round(windowHours.reduce((s, h) => s + (h.windSpeedNum ?? 0), 0) / windowHours.length);
        // B8 fix S191: correct field name — h.windGust is used everywhere in the app, h.gust/h.gustNum never populated
        const gustVals = windowHours.map(h => h.windGust ?? null).filter(g => g != null);
        const avgGust  = gustVals.length ? Math.round(gustVals.reduce((s, v) => s + v, 0) / gustVals.length) : null;
        const midH = windowHours[Math.floor(windowHours.length / 2)];
        const conditions = midH?.conditions || midH?.shortForecast || null;
        return { feelsLike: avgFeels, wind: avgWind, gust: avgGust, conditions, dataSource: 'forecast' };
      } catch { return null; }
    };

    let weatherSnapshot = null;
    const stid = NJWXNET_COURSE_MAP[course.id] || null;
    const isNJ09course = course.id === 'pebble-creek' || course.id === 'hominy-hill' || course.id === 'shark-river';
    const roundDateObj = new Date(`${roundDate}T00:00:00`); // local midnight
    const isFutureDate = roundDateObj > new Date(new Date().toDateString()); // date-only comparison

    if (stid && !isFutureDate && teeTime && finishTime) {
      try {
        // Build start/end as local Date objects — device is ET, JS handles UTC conversion
        const start = new Date(`${roundDate}T${teeTime}:00`);
        const end   = new Date(`${roundDate}T${finishTime}:00`);
        const njwxResult = await Promise.race([
          fetchNJWxNetTimeseries(stid, start, end, course),
          new Promise(resolve => setTimeout(() => resolve({ success: false, error: 'timeout' }), 8000))
        ]);
        if (njwxResult.success && njwxResult.obsCount > 0) {
          const isNJ09 = isNJ09course;
          // NJ09: wind/gust from Meteoblue (sensor unreliable), everything else from NJWxNet
          // S277: also fall back to Meteoblue wind when NJWxNet returns null wind (e.g. NJ76 stations
          // that intermittently drop wind_speed_set_1 — Neshanic, Green Knoll, Fox Hollow).
          // Same P2 logic the live display already uses, now applied at submission time.
          const needsWindFallback = !isNJ09 && njwxResult.avgWind == null;
          const mbSnap = (isNJ09 || needsWindFallback) ? buildMeteoblueSnapshot() : null;
          weatherSnapshot = {
            feelsLike:   njwxResult.avgTemp   != null ? Math.round(njwxResult.avgTemp)  : null,
            wind:        isNJ09 ? mbSnap?.wind : (needsWindFallback ? (mbSnap?.wind ?? null) : (njwxResult.avgWind  != null ? Math.round(njwxResult.avgWind)  : null)),
            gust:        isNJ09 ? (mbSnap?.gust ?? null) : (needsWindFallback ? (mbSnap?.gust ?? null) : (njwxResult.avgGust != null ? Math.round(njwxResult.avgGust) : null)),
            humidity:    njwxResult.avgHumidity != null ? Math.round(njwxResult.avgHumidity) : null,
            precipAccum: njwxResult.precipAccum,
            conditions:  njwxResult.conditions || null,
            dataSource:  (isNJ09 || needsWindFallback) ? 'njwxnet+forecast' : 'njwxnet',
            stid:        stid,
          };
        } else {
          // NJWxNet failed or timed out — fall back to Meteoblue
          console.warn('ZAP snapshot: NJWxNet failed, using Meteoblue fallback. Reason:', njwxResult.error);
          const mb = buildMeteoblueSnapshot();
          if (mb) weatherSnapshot = { ...mb, dataSource: 'forecast_fallback' };
        }
      } catch (err) {
        console.warn('ZAP snapshot error:', err.message);
        const mb = buildMeteoblueSnapshot();
        if (mb) weatherSnapshot = { ...mb, dataSource: 'forecast_fallback' };
      }
    } else {
      // No station, future date, or missing times — Meteoblue fallback
      const mb = buildMeteoblueSnapshot();
      if (mb) weatherSnapshot = { ...mb, dataSource: 'forecast_fallback' };
    }

    const round = {
      id: Date.now(),
      courseId: course.id,
      courseName: course.name,
      date: roundDate,
      time: timeStr,
      teeTime: teeTime || null,
      finishTime: finishTime || null,
      comment: roundComment || null,
      truthFlags: truthFlags.length > 0 ? truthFlags : null,
      truthOther: truthOther || null,
      truthChain: truthFlags.length > 0 ? [...truthFlags] : null,
      neshanicNines: isNeshanic ? [neshanicNineA, neshanicNineB] : null,
      players: playerLabels,
      scores: scores.map(s => parseInt(s) || null),
      pars: HOLE_PARS,
      total,
      holesEntered,
      photoUrl: photoUrl || null,
      weatherSnapshot: weatherSnapshot || null,
      submittedAt: new Date().toISOString(),
    };
    // Save locally immediately — round is safe regardless of network
    const roundWithSync = { ...round, syncStatus: 'pending' };
    saveRound(roundWithSync);
    setSharedRounds(prev => [...prev, roundWithSync]);
    // Invalidate timestamp cache so next getRounds fetch is forced — ensures new round appears
    try { localStorage.removeItem('zap_rounds_ts'); } catch {}
    setSending(false);
    setSubmittedRoundId(roundWithSync.id);
    setPhase('review'); if (onEnterReview) onEnterReview(); setTimeout(() => scrollToTop(), 50);

    // Background sync — non-blocking, golfer already on Review screen
    syncRoundToSheet(roundWithSync, (status) => {
      updateRoundSyncStatus(roundWithSync.id, status);
      setSharedRounds(prev => prev.map(r => r.id === roundWithSync.id ? { ...r, syncStatus: status } : r));
    });
    } catch (err) {
      // S166: surface failure on screen. WHITE-SCREEN-ON-SUBMIT investigation —
      // before this, async errors here would silently freeze UI or white-screen.
      console.error('ZAP submitRound failed:', {
        message: err?.message,
        stack: err?.stack,
        error: err,
      });
      setSending(false);
      alert(
        'Submit failed: ' + (err?.message || String(err)) +
        '\n\nThe round was NOT saved. Check DevTools console for the full stack trace.'
      );
    }
  };

  if (!course) return (
    <div style={{ padding: '40px 16px', maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: COLOR_GREEN, marginBottom: 12 }}>ZAP</div>
      <div style={{ fontSize: '18px', color: COLOR_OFF_WHITE, fontWeight: 700, marginBottom: 8 }}>No Course Selected</div>
      <div style={{ fontSize: '14px', color: COLOR_MUTED, marginBottom: 20 }}>Go back and select a course first.</div>
      <button onClick={() => navigateTo('home')} style={{ ...BTN_LARGE, background: COLOR_GREEN_DARK, justifyContent: 'center', color: COLOR_WHITE }}>
        Back to Home
      </button>
    </div>
  );

  // ---- HUB ----
  if (phase === 'hub') {
    return (
      <div style={{ padding: '20px 16px', maxWidth: 480, margin: '0 auto' }}>
        {/* Course + date context — always prominent */}
        <div style={{ padding: '8px 0 16px 0' }}>
          <div style={{ fontSize: '22px', fontWeight: 800, color: COLOR_WHITE, marginBottom: 2 }}>{course.name}</div>
          <div style={{ fontSize: '14px', color: COLOR_MUTED }}>{dateStr}</div>
        </div>

        {/* Three options */}
        <button onClick={() => navigateTo('coursedetail')}
          style={{ ...BTN_LARGE, background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, marginBottom: 12, padding: '16px', justifyContent: 'flex-start' }}>
          <span style={{ fontSize: '16px', marginRight: 12, fontWeight: 700, color: COLOR_GOLD }}>PLAY</span>
          <span>
            <div style={{ fontSize: '20px', fontWeight: 700, color: COLOR_WHITE }}>Course Detail</div>
            <div style={{ fontSize: '14px', color: COLOR_MUTED, marginTop: 2 }}>Map &amp; scorecard</div>
          </span>
        </button>

        <button onClick={() => { setPhase('players'); if (onExitReview) onExitReview(); setTimeout(() => scrollToTop(), 50); }}
          style={{ ...BTN_LARGE, background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, marginBottom: 12, padding: '16px', justifyContent: 'flex-start' }}>
          <span style={{ fontSize: '16px', marginRight: 12, fontWeight: 700, color: COLOR_GOLD }}>SCORE</span>
          <span>
            <div style={{ fontSize: '20px', fontWeight: 700, color: COLOR_WHITE }}>Record Round</div>
            <div style={{ fontSize: '14px', color: COLOR_MUTED, marginTop: 2 }}>Enter today's best ball score</div>
          </span>
        </button>

        <button onClick={() => navigateTo('roundhistory')}
          style={{ ...BTN_LARGE, background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, marginBottom: 12, padding: '16px', justifyContent: 'flex-start' }}>
          <span style={{ fontSize: '16px', marginRight: 12, fontWeight: 700, color: COLOR_GOLD }}>HIST</span>
          <span>
            <div style={{ fontSize: '20px', fontWeight: 700, color: COLOR_WHITE }}>History</div>
            <div style={{ fontSize: '14px', color: COLOR_MUTED, marginTop: 2 }}>View all past rounds</div>
          </span>
        </button>

        <button onClick={() => navigateTo('scorestats')}
          style={{ ...BTN_LARGE, background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, padding: '16px', justifyContent: 'flex-start' }}>
          <span style={{ fontSize: '16px', marginRight: 12, fontWeight: 700, color: COLOR_GOLD }}>STATS</span>
          <span>
            <div style={{ fontSize: '20px', fontWeight: 700, color: COLOR_WHITE }}>Stats</div>
            <div style={{ fontSize: '14px', color: COLOR_MUTED, marginTop: 2 }}>Season leaderboards and records</div>
          </span>
        </button>

        {/* Best Round of Year + Last Round */}
        {(() => {
          const rounds = getRounds(sharedRounds).filter(r => r.total > 0);
          if (!rounds.length) return null;
          const best = rounds.reduce((b, r) => getRoundDiff(r) < getRoundDiff(b) ? r : b);
          const last = mostRecentRound(rounds);
          return (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>BEST ROUND</div>
              <RoundSummaryRow round={best} isExpanded={expandedRoundId === best.id} onToggle={() => setExpandedRoundId(expandedRoundId === best.id ? null : best.id)} />
              {expandedRoundId === best.id && <ExpandedRoundCard round={best} />}
              {last && last.id !== best.id && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>LAST ROUND</div>
                  <RoundSummaryRow round={last} isExpanded={expandedRoundId === last.id} onToggle={() => setExpandedRoundId(expandedRoundId === last.id ? null : last.id)} />
                  {expandedRoundId === last.id && <ExpandedRoundCard round={last} />}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  }

  // ---- REVIEW (read-only, post-submit) ----
  if (phase === 'review') { // review
    const NotationBadge = ({ score, par }) => {
      const n = getGolfNotation(score, par);
      if (!score) return <span style={{ fontSize: '13px', color: COLOR_MUTED, display: 'block', textAlign: 'center' }}>—</span>;
      const base = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, color: n.color, width: 26, height: 26 };
      if (n.shape === 'circle') return <span style={{ ...base, border: `2px solid ${n.color}`, borderRadius: '50%' }}>{score}</span>;
      if (n.shape === 'double-circle') return <span style={{ ...base, border: `3px double ${n.color}`, borderRadius: '50%' }}>{score}</span>;
      if (n.shape === 'square') return <span style={{ ...base, border: `2px solid ${n.color}`, borderRadius: 3 }}>{score}</span>;
      if (n.shape === 'double-square') return <span style={{ ...base, border: `3px double ${n.color}`, borderRadius: 3 }}>{score}</span>;
      if (n.shape === 'snowman') return <span style={{ ...base, fontSize: '11px', display:'inline-flex', alignItems:'center', gap:1 }}><svg width="13" height="18" viewBox="0 0 13 18" fill="none" style={{display:'inline-block',verticalAlign:'middle'}}><circle cx="6.5" cy="4.8" r="3.8" fill="#ffffff"/><circle cx="6.5" cy="13.5" r="4" fill="#ffffff"/><circle cx="5.2" cy="4.1" r="0.7" fill="#111"/><circle cx="7.8" cy="4.1" r="0.7" fill="#111"/><path d="M5.4 5.8 Q6.5 6.8 7.6 5.8" stroke="#111" strokeWidth="0.9" fill="none" strokeLinecap="round"/></svg>{score}</span>;
      return <span style={{ ...base }}>{score}</span>;
    };
    const ScorecardGrid = ({ title, roundScores, roundPars, roundTotal, label }) => (
      <div style={{ ...CARD_STYLE, padding: '14px', marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: '13px', color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</div>
          {label && <div style={{ fontSize: '11px', color: COLOR_MUTED }}>{label}</div>}
          <div style={{ fontSize: '20px', fontWeight: 800, color: COLOR_GOLD }}>{roundTotal}</div>
        </div>
        <div style={{ fontSize: '10px', color: COLOR_MUTED, fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' }}>{isNeshanic ? `${neshanicNineA.toUpperCase()} 9` : 'Front 9'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4, marginBottom: 10 }}>
          {[0,1,2,3,4,5,6,7,8].map(i => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: COLOR_MUTED, marginBottom: 1 }}>{i+1}</div>
              <div style={{ fontSize: '9px', color: COLOR_GREEN, fontWeight: 700, marginBottom: 2 }}>p{roundPars[i]}</div>
              <NotationBadge score={roundScores[i]} par={roundPars[i]} />
            </div>
          ))}
        </div>
        <div style={{ fontSize: '10px', color: COLOR_MUTED, fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' }}>{isNeshanic ? `${neshanicNineB.toUpperCase()} 9` : 'Back 9'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4 }}>
          {[9,10,11,12,13,14,15,16,17].map(i => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: COLOR_MUTED, marginBottom: 1 }}>{i+1}</div>
              <div style={{ fontSize: '9px', color: COLOR_GREEN, fontWeight: 700, marginBottom: 2 }}>p{roundPars[i]}</div>
              <NotationBadge score={roundScores[i]} par={roundPars[i]} />
            </div>
          ))}
        </div>
      </div>
    );
    // Get comparison rounds — defensive: ensure scores and pars are arrays
    const safeRound = (r) => {
      if (!r) return null;
      const scores = Array.isArray(r.scores) ? r.scores : Array(18).fill(null);
      const pars = Array.isArray(r.pars) ? r.pars : Array(18).fill(4);
      const dateStr2 = r.date ? (() => { try { return new Date(String(r.date).slice(0,10)+'T12:00:00').toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'}); } catch { return ''; } })() : '';
      return { ...r, scores, pars, dateStr2 };
    };
    const allRounds = getRounds(sharedRounds).filter(r => r.total > 0);
    const bestRound = safeRound(allRounds.length > 0 ? allRounds.reduce((b, r) => getRoundDiff(r) < getRoundDiff(b) ? r : b) : null);
    // LAST ROUND = most recent round OTHER than the one just submitted.
    // Sort by date+teeTime desc (sharedRounds arrives in arbitrary Sheet row order).
    const lastRound = safeRound(mostRecentRound(allRounds, submittedRoundId));
    const currentRound = sharedRounds.find(r => r.id === submittedRoundId);
    // fmtT: safe 12-hour format — handles both HH:MM (24h) and already-12h strings like "3:10 PM"
    const fmtTSafe = (t) => {
      if (!t) return '';
      const s = String(t).trim();
      if (/[AP]M$/i.test(s)) return s; // already 12-hour — pass through
      const parts = s.split(':').map(Number);
      if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return s;
      const h = parts[0], m = parts[1];
      return (h > 12 ? h - 12 : h || 12) + ':' + String(m).padStart(2, '0') + ' ' + (h >= 12 ? 'PM' : 'AM');
    };

    const curWs = (() => {
      const r = sharedRounds.find(r => r.id === submittedRoundId);
      return r?.weatherSnapshot || null;
    })();
    const curWsHasData = curWs && (curWs.feelsLike != null || curWs.wind != null || curWs.gust != null || curWs.humidity != null || curWs.precipAccum != null || curWs.precipChance != null || curWs.conditions || curWs.condition);
    const curPlayerNames = players.map((_,i) => getPlayerLabel(i)).filter(l=>l!=='—');
    return (
      <ErrorBoundary><>
      <div style={{ padding: '0 0 40px', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ padding: '16px' }}>
        {/* This Round — tap to expand, same pattern as Best/Last Round */}
        {(() => {
          const reviewRound = {
            ...(currentRound || {}),
            scores: scores.map(s => parseInt(s) || null),
            pars: HOLE_PARS,
            courseName: course.name,
            dateStr2: dateStr,
            teeTime,
            finishTime,
            players: curPlayerNames,
            total,
          };
          return (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: COLOR_GREEN, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>ROUND SAVED ✓</div>
              <RoundSummaryRow round={reviewRound} isExpanded={expandedRoundId === submittedRoundId + '_submitted'} onToggle={() => toggleRound(submittedRoundId, '_submitted')} />
              {expandedRoundId === submittedRoundId + '_submitted' && <ExpandedRoundCard round={reviewRound} />}
            </div>
          );
        })()}
        {/* Best Round */}
        {bestRound && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>BEST ROUND</div>
            <RoundSummaryRow round={bestRound} isExpanded={expandedRoundId === bestRound.id + '_best'} onToggle={() => toggleRound(bestRound.id, '_best')} />
            {expandedRoundId === bestRound.id + '_best' && <ExpandedRoundCard round={bestRound} />}
          </div>
        )}
        {/* Last Round */}
        {lastRound && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>LAST ROUND</div>
            <RoundSummaryRow round={lastRound} isExpanded={expandedRoundId === lastRound.id + '_last'} onToggle={() => toggleRound(lastRound.id, '_last')} />
            {expandedRoundId === lastRound.id + '_last' && <ExpandedRoundCard round={lastRound} />}
          </div>
        )}
                {/* Legend — SVG snowman replacing emoji */}
        <div style={{ ...CARD_STYLE, padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            ['◎', 'Eagle', '#F1373D'],
            ['○', 'Birdie', '#F1373D'],
            ['—', 'Par', '#FFFFFF'],
            ['□', 'Bogey', '#3B82F6'],
            ['□□', 'Double', '#3B82F6'],
          ].map(([sym, label, color]) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color, fontWeight: 700 }}>{sym}</div>
              <div style={{ fontSize: '10px', color: COLOR_MUTED }}>{label}</div>
            </div>
          ))}
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 21 }}>
              <svg width="13" height="18" viewBox="0 0 13 18" fill="none">
                <circle cx="6.5" cy="4.8" r="3.8" fill="#ffffff"/>
                <circle cx="6.5" cy="13.5" r="4" fill="#ffffff"/>
                <circle cx="5.2" cy="4.1" r="0.7" fill="#111"/>
                <circle cx="7.8" cy="4.1" r="0.7" fill="#111"/>
                <path d="M5.4 5.8 Q6.5 6.8 7.6 5.8" stroke="#111" strokeWidth="0.9" fill="none" strokeLinecap="round"/>
              </svg>
            </div>
            <div style={{ fontSize: '10px', color: COLOR_MUTED }}>Snowman</div>
          </div>
        </div>
        {/* Spond Events preview — 3 cards, exact match to Events screen, no Spond link */}
        {(() => {
          const reviewCourseEmojis = {
            'Spooky Brook': '🌲', 'Quail Brook': '🦅', 'Warrenbrook': '⛰️',
            'Green Knoll': '🌿', 'Neshanic Valley': '🏞️', 'Fox Hollow': '🦊',
            'High Bridge Hills': '⛰️', 'Architects': '📐', 'Pebble Creek': '🪨',
            'Hominy Hill': '🌾', 'Oak Hill': '🌳', 'Shark River': '🦈'
          };
          const reviewFormatDate = (isoStr) => {
            if (!isoStr) return '';
            return new Date(isoStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
          };
          const reviewFormatTime = (isoStr) => {
            if (!isoStr) return '';
            return new Date(isoStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
          };
          const reviewGetMeetTime = (isoStr) => {
            if (!isoStr) return '';
            const d = new Date(isoStr);
            d.setMinutes(d.getMinutes() - 30);
            return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
          };
          const futureEvents = (spondEvents || []).filter(e => new Date(e.startTime) > new Date() && !e.cancelled).slice(0, 3);
          if (!futureEvents.length) return null;
          return (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_GOLD, letterSpacing: '0.5px' }}>UPCOMING EVENTS</div>
                <span onClick={() => navigateTo('events')} style={{ fontSize: 12, color: COLOR_GREEN, cursor: 'pointer', fontWeight: 700 }}>See More →</span>
              </div>
              {futureEvents.map(evt => {
                const spotsText = evt.spotsRemaining > 0 ? `${evt.spotsRemaining} spot${evt.spotsRemaining !== 1 ? 's' : ''} open` : 'FULL';
                const spotsColor = evt.spotsRemaining > 0 ? COLOR_GREEN : COLOR_RED;
                const courseEmoji = reviewCourseEmojis[evt.course] || '';
                return (
                  <div key={evt.id} style={{ background: COLOR_CARD, borderRadius: 12, marginBottom: 12, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}` }}>
                    {/* Photo banner */}
                    {evt.coursePhoto && evt.coursePhoto.startsWith('http') && (
                      <div style={{ width: '100%', overflow: 'hidden' }}>
                        <img src={evt.coursePhoto} alt={evt.course || evt.heading}
                          style={{ width: '100%', height: '200px', objectFit: 'cover', objectPosition: 'center', background: '#111', display: 'block' }}
                          onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    {/* Course banner */}
                    <div style={{ background: '#111', padding: '16px 16px 12px', borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: 11, color: COLOR_MUTED, fontWeight: 600, marginBottom: 4 }}>{reviewFormatDate(evt.startTime)}</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_OFF_WHITE }}>{courseEmoji} {evt.course || evt.heading}</div>
                          {evt.locationAddress && <div style={{ fontSize: 11, color: COLOR_OFF_WHITE, marginTop: 3 }}>{evt.locationAddress}</div>}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_WHITE, background: spotsColor === COLOR_GREEN ? COLOR_GREEN_BG : COLOR_RED_BG, padding: '3px 8px', borderRadius: 6 }}>
                            {spotsText}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Time details */}
                    <div style={{ padding: '10px 16px', display: 'flex', gap: 20, borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>
                      <div>
                        <div style={{ fontSize: 10, color: COLOR_MUTED, fontWeight: 600 }}>TEE TIME</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_GREEN }}>{reviewFormatTime(evt.startTime)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: COLOR_MUTED, fontWeight: 600 }}>ARRIVE BY</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OFF_WHITE }}>{reviewGetMeetTime(evt.startTime)}</div>
                      </div>
                      {evt.maxSpots && (
                        <div>
                          <div style={{ fontSize: 10, color: COLOR_MUTED, fontWeight: 600 }}>GROUP SIZE</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OFF_WHITE }}>{evt.attendance.in.length}/{evt.maxSpots}</div>
                        </div>
                      )}
                    </div>
                    {/* Attendance */}
                    <div style={{ padding: '10px 16px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_MUTED, marginBottom: 6 }}>ATTENDANCE</div>
                      {evt.attendance.in.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          {evt.attendance.in.map((m, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLOR_GREEN_BG, padding: '3px 8px', borderRadius: 6, marginRight: 6, marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#000' }}>
                              <StatusIcon status="GO" size={12}/> {m.name}
                              {m.phone && <a href={`tel:${m.phone}`} style={{ color: '#000', textDecoration: 'none', marginLeft: 4, display: 'inline-flex', alignItems: 'center' }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg></a>}
                            </span>
                          ))}
                        </div>
                      )}
                      {evt.attendance.out.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          {evt.attendance.out.map((m, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLOR_RED_BG, padding: '3px 8px', borderRadius: 6, marginRight: 6, marginBottom: 4, fontSize: 12, fontWeight: 600, opacity: 0.7, color: COLOR_WHITE }}>
                              Declined: {m.name}
                            </span>
                          ))}
                        </div>
                      )}
                      {evt.attendance.no_answer.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          {evt.attendance.no_answer.map((m, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLOR_GOLD_BG, padding: '3px 8px', borderRadius: 6, marginRight: 6, marginBottom: 4, fontSize: 12, fontWeight: 600, opacity: 0.7, color: '#000' }}>
                              Maybe: {m.name}
                              {m.phone && <a href={`tel:${m.phone}`} style={{ color: '#000', textDecoration: 'none', marginLeft: 4, display: 'inline-flex', alignItems: 'center' }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg></a>}
                            </span>
                          ))}
                        </div>
                      )}
                      {evt.attendance.waiting.length > 0 && (
                        <div>
                          {evt.attendance.waiting.map((m, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLOR_GOLD_BG, padding: '3px 8px', borderRadius: 6, marginRight: 6, marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#000' }}>
                              Pending: {m.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
        </div>
      </div>
      {expandedPhoto && (
        <div onClick={() => setExpandedPhoto(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.95)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <button onClick={() => setExpandedPhoto(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 40, height: 40, fontSize: 20, color: '#fff', cursor: 'pointer' }}>✕</button>
          <img src={expandedPhoto} alt="Full size" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
        </div>
      )}
        {/* S175 Fix #2: Removed "Enter Another Round" + "Done — Back to Home" buttons.
            Locked scorecard flow (per spec): Player Selection → Score Entry → Review (terminal).
            Hub phase eliminated. Done page eliminated. User exits Review via app nav. */}
    </>
    </ErrorBoundary>
    );
  }


  // ---- PLAYER SELECTION ----
  if (phase === 'players') return (
    <div style={{ padding: '16px', maxWidth: 480, margin: '0 auto' }}>
      {/* Course name only — date/tee/finish moved to scorecard page */}
      <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 16, borderLeft: `4px solid ${COLOR_GREEN}` }}>
        <div style={{ fontSize: '20px', fontWeight: 800, color: COLOR_WHITE }}>{course.name}</div>
      </div>
      {/* Neshanic nine picker */}
      {isNeshanic && (
        <div style={{ ...CARD_STYLE, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: '14px', color: COLOR_OFF_WHITE, fontWeight: 700, marginBottom: 10 }}>Which two nines?</div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 6 }}>Front 9</div>
              {['meadow', 'lake', 'ridge'].map(nine => (
                <button key={nine} onClick={() => { if (nine !== neshanicNineB) setNeshanicNineA(nine); }}
                  style={{ ...BTN_SMALL, display: 'block', width: '100%', marginBottom: 6, padding: '10px', fontSize: '14px', textTransform: 'uppercase', fontWeight: 700, background: neshanicNineA === nine ? COLOR_GREEN_DARK : COLOR_DARKEST, color: nine === neshanicNineB ? COLOR_MUTED : (neshanicNineA === nine ? COLOR_WHITE : COLOR_OFF_WHITE), border: `2px solid ${neshanicNineA === nine ? COLOR_GREEN : COLOR_DARK_GREEN}`, opacity: nine === neshanicNineB ? 0.4 : 1 }}>
                  {nine} 9
                </button>
              ))}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 6 }}>Back 9</div>
              {['meadow', 'lake', 'ridge'].map(nine => (
                <button key={nine} onClick={() => { if (nine !== neshanicNineA) setNeshanicNineB(nine); }}
                  style={{ ...BTN_SMALL, display: 'block', width: '100%', marginBottom: 6, padding: '10px', fontSize: '14px', textTransform: 'uppercase', fontWeight: 700, background: neshanicNineB === nine ? COLOR_GREEN_DARK : COLOR_DARKEST, color: nine === neshanicNineA ? COLOR_MUTED : (neshanicNineB === nine ? COLOR_WHITE : COLOR_OFF_WHITE), border: `2px solid ${neshanicNineB === nine ? COLOR_GREEN : COLOR_DARK_GREEN}`, opacity: nine === neshanicNineA ? 0.4 : 1 }}>
                  {nine} 9
                </button>
              ))}
            </div>
          </div>
          <div style={{ fontSize: '12px', color: COLOR_GOLD, textAlign: 'center' }}>Par {(NESHANIC_NINES[neshanicNineA]||[]).reduce((a,b)=>a+b,0) + (NESHANIC_NINES[neshanicNineB]||[]).reduce((a,b)=>a+b,0)}</div>
        </div>
      )}

      <div style={{ fontSize: '16px', color: COLOR_OFF_WHITE, fontWeight: 700, marginBottom: 14 }}>Who's in the foursome?</div>
      {[0,1,2,3].map(i => (
        <div key={i} style={{ ...CARD_STYLE, padding: '14px', marginBottom: 10 }}>
          <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Player {i+1}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {cfgPlayers.map(p => {
              const usedElsewhere = players.some((pid, j) => j !== i && pid === p.id);
              return (
                <button key={p.id} onClick={() => { if (!usedElsewhere) setPlayers(prev => { const n=[...prev]; n[i]=p.id; return n; }); }}
                  style={{ ...BTN_SMALL, padding: '10px 12px', fontSize: '14px', background: players[i]===p.id ? COLOR_GREEN_DARK : COLOR_DARKEST, color: usedElsewhere ? COLOR_MUTED : COLOR_WHITE, border: `2px solid ${players[i]===p.id ? COLOR_GREEN : usedElsewhere ? COLOR_DARKEST : COLOR_DARK_GREEN}`, opacity: usedElsewhere ? 0.4 : 1, cursor: usedElsewhere ? 'default' : 'pointer' }}>
                  {p.emoji} {p.name}
                </button>
              );
            })}
            <button onClick={() => setPlayers(prev => { const n=[...prev]; n[i]='guest'; return n; })}
              style={{ ...BTN_SMALL, padding: '10px 12px', fontSize: '14px', background: players[i]==='guest' ? '#4a3000' : COLOR_DARKEST, color: COLOR_WHITE, border: `2px solid ${players[i]==='guest' ? COLOR_GOLD : COLOR_DARK_GREEN}` }}>
              + Guest
            </button>
          </div>
          {players[i] === 'guest' && (
            <input value={guestNames[i]} onChange={e => setGuestNames(prev => { const n=[...prev]; n[i]=e.target.value; return n; })}
              onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
              placeholder="Guest name"
              style={{ marginTop: 10, width: '100%', background: COLOR_DARKEST, color: COLOR_OFF_WHITE, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, padding: '10px 12px', fontSize: '15px', boxSizing: 'border-box' }} />
          )}
        </div>
      ))}
      {/* S161: Players-phase inline banner removed — modal validation replaces it */}
      <button onClick={() => {
          // Players < 4 — soft warning
          const selected = players.filter(p => p && p !== '').length;
          if (selected < 4) {
            const noun = selected === 1 ? 'player' : 'players';
            const msg = `Only ${selected} ${noun} selected — fewer than a full group of 4.\n\nTap OK to continue with ${selected} ${noun}, or Cancel to add more.`;
            if (!window.confirm(msg)) return;
          }

          // All checks passed — proceed
          setSubmitWarning(null);
          setPhase('scorecard'); if (onExitReview) onExitReview(); setTimeout(() => scrollToTop(), 50);
        }}
        style={{ ...BTN_LARGE, background: players.every(p => !p) ? COLOR_CARD : COLOR_GREEN, justifyContent: 'center', opacity: players.every(p => !p) ? 0.5 : 1, color: COLOR_WHITE, WebkitTextFillColor: '#ffffff', fontWeight: 800, fontSize: 18, border: `2px solid ${COLOR_GREEN}`, padding: '18px 16px' }}>
        Start Scoring
      </button>
    </div>
  );

  // ---- SCORECARD ENTRY ----
  const HoleRow = ({ holeIdx }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: holeIdx % 9 < 8 ? 12 : 0, marginBottom: holeIdx % 9 < 8 ? 12 : 0, borderBottom: holeIdx % 9 < 8 ? `1px solid ${COLOR_DARK_GREEN}` : 'none' }}>
      <div style={{ fontSize: '15px', color: COLOR_MUTED, minWidth: 52, flexShrink: 0 }}>
        Hole {holeIdx+1}
        <div style={{ fontSize: '10px', color: COLOR_GREEN, fontWeight: 700, marginTop: 1 }}>Par {HOLE_PARS[holeIdx]}</div>
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        {['2','3','4','5','6','7','8','9'].map(n => (
          <button key={n} onClick={() => setScores(prev => { const s=[...prev]; s[holeIdx]=s[holeIdx]===n?'':n; return s; })}
            style={{ width: 28, height: 36, borderRadius: 7, fontSize: '14px', fontWeight: 700, background: scores[holeIdx]===n ? COLOR_GREEN_DARK : COLOR_DARKEST, color: scores[holeIdx]===n ? COLOR_WHITE : COLOR_MUTED, border: `2px solid ${scores[holeIdx]===n ? COLOR_GREEN : COLOR_DARK_GREEN}`, cursor: 'pointer', flexShrink: 0 }}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '16px', maxWidth: 480, margin: '0 auto' }}>
      {/* Date / Tee Time / Finish Time — moved from player card per SCORECARD-TEE-TIME-MOVE */}
      <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 14, borderLeft: `4px solid ${COLOR_GREEN}` }}>
        <div style={{ fontSize: '18px', fontWeight: 800, color: COLOR_WHITE, marginBottom: 10 }}>{course.name}</div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input type="date" value={roundDate} onChange={e => setRoundDate(e.target.value)}
              style={{ background: COLOR_DARKEST, color: COLOR_GOLD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 6, padding: '6px 10px', fontSize: '15px', fontWeight: 700, colorScheme: 'dark' }} />
            <div style={{ fontSize: '12px', color: '#F97316', fontWeight: 700, lineHeight: 1.3 }}>Enter the date you actually played.</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, boxSizing: 'border-box' }}>
          <div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 4 }}>Tee Time</div>
            <input type="time" defaultValue="" onChange={e => setTeeTime(e.target.value)}
              style={{ background: COLOR_DARKEST, color: teeTime ? COLOR_OFF_WHITE : COLOR_MUTED, border: `1px solid ${teeTime ? COLOR_GREEN : COLOR_DARK_GREEN}`, borderRadius: 6, padding: '8px 10px', fontSize: '14px', width: '100%', boxSizing: 'border-box', minHeight: 44, colorScheme: 'dark' }} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 4 }}>Finish Time</div>
            <input type="time" defaultValue="" onChange={e => setFinishTime(e.target.value)}
              style={{ background: COLOR_DARKEST, color: finishTime ? COLOR_OFF_WHITE : COLOR_MUTED, border: `1px solid ${finishTime ? COLOR_GREEN : COLOR_DARK_GREEN}`, borderRadius: 6, padding: '8px 10px', fontSize: '14px', width: '100%', boxSizing: 'border-box', minHeight: 44, colorScheme: 'dark' }} />
          </div>
        </div>
      </div>
      {/* Course + players summary */}
      <div style={{ ...CARD_STYLE, padding: '12px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '13px', color: COLOR_MUTED }}>{course.name} · {players.map((_,i) => getPlayerLabel(i)).filter(l=>l!=='—').join(', ')}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '28px', fontWeight: 800, color: COLOR_GOLD, lineHeight: 1 }}>{total || '—'}</div>
          <div style={{ fontSize: '11px', color: COLOR_MUTED }}>{holesEntered}/18</div>
        </div>
      </div>
      <div style={{ fontSize: '12px', color: COLOR_GOLD, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>{isNeshanic ? `${neshanicNineA.toUpperCase()} 9` : 'Front 9'}</div>
      <div style={{ ...CARD_STYLE, padding: '14px', marginBottom: 8 }}>
        {[0,1,2,3,4,5,6,7,8].map(h => <HoleRow key={h} holeIdx={h} />)}
        <div style={{ textAlign: 'right', fontSize: '14px', color: COLOR_MUTED, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${COLOR_DARK_GREEN}` }}>
          Out: <span style={{ color: COLOR_GOLD, fontWeight: 700 }}>{front9Total || '—'}</span>
        </div>
      </div>
      <div style={{ fontSize: '12px', color: COLOR_GOLD, fontWeight: 700, marginBottom: 10, marginTop: 16, textTransform: 'uppercase', letterSpacing: 1 }}>{isNeshanic ? `${neshanicNineB.toUpperCase()} 9` : 'Back 9'}</div>
      <div style={{ ...CARD_STYLE, padding: '14px', marginBottom: 16 }}>
        {[9,10,11,12,13,14,15,16,17].map(h => <HoleRow key={h} holeIdx={h} />)}
        <div style={{ textAlign: 'right', fontSize: '14px', color: COLOR_MUTED, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${COLOR_DARK_GREEN}` }}>
          In: <span style={{ color: COLOR_GOLD, fontWeight: 700 }}>{back9Total || '—'}</span>
        </div>
      </div>
      {/* Truth Chain */}
      <div style={{ ...CARD_STYLE, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ fontSize: '14px', color: COLOR_OFF_WHITE, fontWeight: 700, marginBottom: 10 }}>Something off today?</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {['Rain wrong', 'Wind wrong', 'Lightning missed', 'Temp wrong', 'Bugs wrong', 'Other'].map(flag => (
            <button key={flag} onClick={() => setTruthFlags(prev => prev.includes(flag) ? prev.filter(f => f !== flag) : [...prev, flag])}
              style={{ ...BTN_SMALL, padding: '8px 12px', fontSize: '13px', background: truthFlags.includes(flag) ? COLOR_GREEN_DARK : COLOR_DARKEST, color: truthFlags.includes(flag) ? COLOR_WHITE : COLOR_MUTED, border: `1px solid ${truthFlags.includes(flag) ? COLOR_GREEN : COLOR_DARK_GREEN}` }}>
              {flag}
            </button>
          ))}
        </div>
        <textarea value={roundComment} onChange={e => setRoundComment(e.target.value.slice(0, 200))}
          placeholder="Selected something above? Add details — e.g. 'Rain hit at 2pm, app showed 10%' · 'Wind was 25mph but app showed 12' · 'Storm rolled in with no warning' · 'Bugs were zero despite High rating' · 'Felt 10° colder than shown' · 'Carts were out but app said restricted' · 'Pollen brutal, app said Low'. Also use for round notes — great shots, funny moments, memorable holes."
          rows={10}
          style={{ width: '100%', background: COLOR_DARKEST, color: COLOR_OFF_WHITE, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, padding: '10px 12px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
        <div style={{ fontSize: '11px', color: COLOR_MUTED, textAlign: 'right', marginTop: 4 }}>{roundComment.length}/200</div>
      </div>
      {/* Group Photo */}
      <div style={{ ...CARD_STYLE, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ fontSize: '14px', color: COLOR_OFF_WHITE, fontWeight: 700, marginBottom: 6 }}>Group Photo</div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {photoUrl
            ? (
              <div style={{ position: 'relative', width: '100%' }}>
                <img src={photoUrl} alt="Group" style={{ width: '100%', borderRadius: 8, maxHeight: 180, objectFit: 'cover' }} />
                <label style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.7)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid #555' }}>
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={async (e) => { const f = e.target.files[0]; if (!f) return; const url = await uploadPhoto(f); if (url) setPhotoUrl(url); }} />
                  <span style={{ color: '#fff', fontSize: 16, lineHeight: 1 }}>↺</span>
                </label>
                <button onClick={() => setPhotoUrl(null)} style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.7)', borderRadius: '50%', width: 32, height: 32, border: '1px solid #555', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
              </div>
            )
            : (
              <label style={{ ...BTN_LARGE, background: COLOR_CARD, border: `2px dashed ${COLOR_DARK_GREEN}`, justifyContent: 'center', cursor: 'pointer', display: 'flex', width: '100%' }}>
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={async (e) => { const f = e.target.files[0]; if (!f) return; const url = await uploadPhoto(f); if (url) setPhotoUrl(url); }} />
                {photoUploading ? 'Uploading...' : 'Take / Choose Photo'}
              </label>
            )
          }
        </div>
      </div>
      {/* Submit — full width */}
      <button onClick={() => {
          // SCORECARD-TEE-TIME-MOVE: all date/time validation now lives here (moved from player card)
          const hardErrors = [];
          const now = new Date();
          const todayOnly = new Date(now.toDateString());

          // All 18 holes required
          if (holesEntered < 18) {
            hardErrors.push('• All 18 holes are required to save.\n\nCouldn\'t finish? Record on paper and email John with course, date, tee time, holes played, scores, conditions, and why you stopped.');
          }

          // Date not in future
          const roundDateOnly = new Date(roundDate + 'T00:00:00');
          if (roundDateOnly > todayOnly) {
            hardErrors.push('• Date is in the future — enter the date you actually played.');
          }

          // Tee time required + not in future
          if (teeTime) {
            const teeDateTime = new Date(`${roundDate}T${teeTime}:00`);
            if (teeDateTime > now) hardErrors.push('• Tee time is in the future — enter the time you actually teed off.');
          } else {
            hardErrors.push('• Tee time is required.');
          }

          // Finish time required + not in future
          if (finishTime) {
            const finishDateTime = new Date(`${roundDate}T${finishTime}:00`);
            if (finishDateTime > now) hardErrors.push('• Finish time is in the future — enter the time you actually finished.');
          } else {
            hardErrors.push('• Finish time is required.');
          }

          // Tee before finish
          if (teeTime && finishTime) {
            const teeDT = new Date(`${roundDate}T${teeTime}:00`);
            const finDT = new Date(`${roundDate}T${finishTime}:00`);
            if (teeDT >= finDT) hardErrors.push('• Tee time must be before finish time.');
          }

          if (hardErrors.length > 0) {
            window.alert('Cannot save round:\n\n' + hardErrors.join('\n\n'));
            return;
          }
          setSubmitWarning(null);
          submitRound();
        }} disabled={holesEntered === 0 || sending}
        style={{ ...BTN_LARGE, background: COLOR_GREEN_DARK, justifyContent: 'center', opacity: (holesEntered === 0 || sending) ? 0.4 : 1, color: COLOR_WHITE, marginBottom: 24, width: '100%', fontSize: 18, fontWeight: 700 }}>
        {sending ? 'Saving...' : `Submit Round${holesEntered < 18 ? ' ('+holesEntered+'/18)' : ''}`}
      </button>
      {submitWarning && phase === 'scorecard' && (
        <div style={{ background: '#1a0000', border: '1px solid #F97316', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#F97316', fontWeight: 600 }}>
          ⚠️ {submitWarning}
        </div>
      )}
    </div>
  );
}

// ============================================================
// EVENTS SCREEN — Spond integration

// ---- Recovery Screen — triggered by ?admin=recover URL param ----
// JZ sends this URL to a golfer when their round is stuck: https://zap-golf.netlify.app?admin=recover
// Must be opened in the same browser used to submit the round (localStorage is origin-locked).
function RecoveryScreen({ webhookUrl }) {
  const [localRounds, setLocalRounds] = React.useState([]);
  const [recoverStatus, setRecoverStatus] = React.useState('');

  React.useEffect(() => {
    setLocalRounds(getLocalRounds());
  }, []);

  const forceSync = async () => {
    setRecoverStatus('Sending...');
    let sent = 0;
    const url = webhookUrl;
    for (const round of localRounds) {
      try {
        await fetch(url, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'scorecard', ...round }),
        });
        // B7 fix S191: poll checkId 3x with increasing delays (1s/2s/3s)
        // Mirrors B2 fix in syncRoundToSheet — replaces hardcoded 1500ms single attempt
        // which always failed on slow mobile connections, reporting "0 confirmed" every time.
        const _b7delays = [1000, 2000, 3000];
        let confirmed = false;
        for (const _b7delay of _b7delays) {
          await new Promise(r => setTimeout(r, _b7delay));
          try {
            const check = await fetch(`${url}?action=checkId&id=${round.id}`);
            const result = await check.json();
            if (result.exists) {
              updateRoundSyncStatus(round.id, 'synced');
              sent++;
              confirmed = true;
              break;
            }
          } catch { /* checkId attempt failed — try next delay */ }
        }
      } catch {}
    }
    setLocalRounds(getLocalRounds());
    setRecoverStatus(`Done. ${sent} round(s) confirmed in sheet.`);
  };

  return (
    <div style={{ padding: '24px 16px', maxWidth: 480, margin: '0 auto', color: '#fff' }}>
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>ZAP Recovery</div>
      <div style={{ fontSize: 13, color: '#aaa', marginBottom: 20 }}>
        {localRounds.length} round(s) found on this device.
      </div>
      {localRounds.map(r => (
        <div key={r.id} style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: '10px 14px', marginBottom: 10, fontSize: 13 }}>
          <div style={{ fontWeight: 700 }}>{r.courseName} — {r.date}</div>
          <div style={{ color: '#aaa', marginTop: 2 }}>Score: {r.total} &nbsp;|&nbsp; Players: {(r.players || []).join(', ')}</div>
          <div style={{ color: r.syncStatus === 'synced' ? '#22c55e' : '#f97316', marginTop: 4, fontWeight: 700 }}>
            {r.syncStatus === 'synced' ? '✓ Synced' : '⚠ Pending — not in sheet'}
          </div>
        </div>
      ))}
      {localRounds.length === 0 && (
        <div style={{ color: '#aaa', fontSize: 14 }}>No rounds found in this browser's local storage.</div>
      )}
      <button onClick={forceSync} style={{ width: '100%', padding: '14px', background: '#22c55e', color: '#000', fontWeight: 800, fontSize: 16, borderRadius: 10, border: 'none', marginTop: 12, cursor: 'pointer' }}>
        Send All to Sheet
      </button>
      {recoverStatus ? <div style={{ marginTop: 12, color: '#22c55e', fontWeight: 700, fontSize: 14 }}>{recoverStatus}</div> : null}
    </div>
  );
}

export default function App() {

  // StatusIcon moved to module level — see above App()

  // ── ICON-1: FULL SVG WEATHER ICON SYSTEM — no emoji ──
  // Day: Clear, MostlyClear, PartlyCloudy, MostlyCloudy, Overcast, Fog, Rain, Thunder, Snow, Sleet
  // Night: Clear→crescent, MostlyClear→crescent, PartlyCloudy→crescent+cloud, MostlyCloudy→cloud, rest same as day

  const skyIcon = (conditions, isDaytime = true) => {
    const c = (conditions || '').toLowerCase();
    if (/thunder|lightning|tstorm/.test(c))          return 'thunder';
    if (/freezing|sleet|ice pellet/.test(c))          return 'sleet';
    if (/snow|blizzard/.test(c))                      return 'snow';
    if (/heavy rain|downpour/.test(c))                return 'rain';
    if (/rain|drizzle|shower/.test(c))                return 'rain';
    if (/fog|mist|haze/.test(c))                      return 'fog';
    if (/overcast/.test(c))                           return 'overcast';
    if (/mostly cloudy/.test(c))                      return isDaytime ? 'mostly-cloudy' : 'overcast';
    if (/partly|cloudy|clouds/.test(c))               return isDaytime ? 'partly-cloudy' : 'partly-cloudy-night';
    if (/mostly clear|few clouds/.test(c))            return isDaytime ? 'mostly-clear' : 'clear-night';
    if (/clear|sunny|fair/.test(c))                   return isDaytime ? 'clear' : 'clear-night';
    return isDaytime ? 'clear' : 'clear-night';
  };

  const renderSkyIcon = (conditions, isDaytime = true, size = 20) => {
    const key = skyIcon(conditions, isDaytime);
    const s = size;
    const Sun = ({ cx = 20, cy = 20, r = 7 }) => <>
      <circle cx={cx} cy={cy} r={r} fill="#FFD54F" />
      {[0,45,90,135,180,225,270,315].map((deg, i) => {
        const rad = Math.PI * deg / 180;
        return <line key={i}
          x1={cx + (r+2)*Math.cos(rad)} y1={cy + (r+2)*Math.sin(rad)}
          x2={cx + (r+5)*Math.cos(rad)} y2={cy + (r+5)*Math.sin(rad)}
          stroke="#FFD54F" strokeWidth="2" strokeLinecap="round" />;
      })}
    </>;
    const Cloud = ({ cx = 22, cy = 24, fill = "#78909c" }) => <>
      <ellipse cx={cx} cy={cy} rx="10" ry="6" fill={fill} />
      <ellipse cx={cx-6} cy={cy-2} rx="7" ry="5" fill={fill} />
      <ellipse cx={cx-12} cy={cy+1} rx="5" ry="4" fill={fill} />
    </>;
    const Crescent = () =>
      <path d="M22 20a9 9 0 1 1-9-9 6.5 6.5 0 0 0 9 9z" fill="white" />;
    const Drops = ({ n = 3, fill = "#4a90d9", heavy = false }) => {
      const xs = n === 2 ? [14, 24] : n === 3 ? [11, 19, 27] : [8, 16, 24, 32];
      return <>{xs.map((x, i) => <line key={i}
        x1={x} y1={23} x2={x-2} y2={heavy ? 33 : 29}
        stroke={fill} strokeWidth={heavy ? 2.5 : 2} strokeLinecap="round" />)}</>;
    };

    const icons = {
      'clear': <Sun cx={20} cy={20} r={8} />,
      'mostly-clear': <><Sun cx={13} cy={15} r={6} /><Cloud cx={26} cy={26} fill="#b0bec5" /></>,
      'partly-cloudy': <><Sun cx={12} cy={13} r={6} /><Cloud cx={24} cy={25} fill="#90a4ae" /></>,
      'mostly-cloudy': <><circle cx={10} cy={12} r={4} fill="#FFD54F" opacity={0.6} /><Cloud cx={22} cy={23} fill="#607d8b" /></>,
      'overcast': <><ellipse cx={22} cy={16} rx="11" ry="7" fill="#607d8b" /><ellipse cx={14} cy={14} rx="9" ry="6" fill="#607d8b" /><ellipse cx={20} cy={23} rx="13" ry="6" fill="#546e7a" /></>,
      'fog': <>{[9,16,23,30].map((y, i) => <rect key={i} x={i%2===0?4:8} y={y} width={i%2===0?32:24} height="3" rx="1.5" fill="#90a4ae" opacity="0.8" />)}</>,
      'rain': <><Cloud cx={22} cy={14} fill="#546e7a" /><Drops n={3} heavy={true} /></>,
      'thunder': <><ellipse cx={22} cy={12} rx="11" ry="6" fill="#37474f" /><ellipse cx={14} cy={10} rx="9" ry="6" fill="#37474f" /><polygon points="21,19 17,19 14,28 19,28 13,38 24,26 19,26 22,19" fill="#FFD54F" /></>,
      'snow': <><Cloud cx={22} cy={13} fill="#78909c" /><circle cx={12} cy={26} r="2.5" fill="#e0e0e0" /><circle cx={20} cy={30} r="2.5" fill="#e0e0e0" /><circle cx={28} cy={26} r="2.5" fill="#e0e0e0" /></>,
      'sleet': <><Cloud cx={22} cy={13} fill="#607d8b" /><line x1={12} y1={23} x2={10} y2={30} stroke="#5b9bd5" strokeWidth="2" strokeLinecap="round" /><circle cx={21} cy={28} r="2.5" fill="#e0e0e0" /><line x1={29} y1={23} x2={27} y2={30} stroke="#5b9bd5" strokeWidth="2" strokeLinecap="round" /></>,
      'clear-night': <Crescent />,
      'partly-cloudy-night': <><Crescent /><Cloud cx={28} cy={28} fill="#78909c" /></>,
    };

    return (
      <svg width={s} height={s} viewBox="0 0 40 40" style={{ flexShrink: 0, display: 'inline-block' }}>
        {icons[key] || icons['clear']}
      </svg>
    );
  };
  // Sky condition as readable text label
  const skyLabel = (conditions) => {
    const c = (conditions || '').toLowerCase();
    if (/thunder|lightning|tstorm/.test(c)) return 'Thunderstorms';
    if (/freezing rain|freezing drizzle/.test(c)) return 'Freezing Rain';
    if (/snow|blizzard/.test(c)) return 'Snow';
    if (/sleet|ice/.test(c)) return 'Sleet/Ice';
    if (/heavy rain|downpour/.test(c)) return 'Heavy Rain';
    if (/rain shower/.test(c)) return 'Rain Showers';
    if (/light rain|drizzle/.test(c)) return 'Light Rain';
    // S275 FIX: preserve MB's probability qualifier instead of collapsing to bare 'Rain' —
    // decodeMb() outputs these exact strings at 30-70% precip (a common range, not an edge case).
    if (/rain likely/.test(c)) return 'Rain Likely';
    if (/chance of rain/.test(c)) return 'Chance of Rain';
    if (/rain/.test(c)) return 'Rain';
    if (/fog/.test(c)) return 'Fog';
    if (/mist/.test(c)) return 'Mist';
    if (/haze/.test(c)) return 'Haze';
    if (/overcast/.test(c)) return 'Overcast';
    if (/mostly cloudy/.test(c)) return 'Mostly Cloudy';
    // S275 FIX: VC uses 'Partially cloudy' (not 'Partly cloudy') — was falling through to bare 'Cloudy'.
    if (/partly cloudy|partially cloudy|partly sunny/.test(c)) return 'Partly Cloudy';
    if (/cloudy|clouds/.test(c)) return 'Cloudy';
    if (/mostly clear|mostly sunny/.test(c)) return 'Mostly Clear';
    if (/clear|sunny|fair/.test(c)) return 'Clear';
    return conditions || 'Clear';
  };

  // ── S95 SHARED HOURLY ROW — matches V6 mockup HRow exactly ──
  // ── S98 TRIGGER POPUP STATE ───────────────────────────────
  const [triggerPopup, setTriggerPopup] = React.useState(null);
  // triggerPopup: { trigger, value, unit, thresholds: [{label,color,min,max}], settingsLink? }

  const TriggerPopupOverlay = () => {
    if (!triggerPopup) return null;
    const { title, value, unit, thresholds, settingsLink } = triggerPopup;
    // Build color bar: find total range
    const minVal = thresholds[0].min;
    const maxVal = thresholds[thresholds.length - 1].max;
    const totalRange = maxVal - minVal || 1;
    // Clamp value position to bar
    const pct = Math.min(100, Math.max(0, ((value - minVal) / totalRange) * 100));
    return (
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        onClick={() => setTriggerPopup(null)}
      >
        <div
          style={{ background: '#111', borderRadius: 14, padding: 20, maxWidth: 320, width: '100%', border: '1px solid #333' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{title}</div>
            <button onClick={() => setTriggerPopup(null)} style={{ background: 'transparent', border: 'none', color: COLOR_MUTED, fontSize: 22, cursor: 'pointer', padding: 4 }}>×</button>
          </div>
          {/* Current value callout */}
          <div style={{ fontSize: 28, fontWeight: 900, color: (() => {
            for (const t of thresholds) if (value >= t.min && value < t.max) return t.color;
            return thresholds[thresholds.length-1].color;
          })(), marginBottom: 14, textAlign: 'center' }}>
            {Math.round(value)}{unit}
          </div>
          {/* Color bar */}
          <div style={{ position: 'relative', marginBottom: 24 }}>
            <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', height: 14 }}>
              {thresholds.map((t, i) => (
                <div key={i} style={{ flex: t.max - t.min, background: t.color, opacity: 0.85 }} />
              ))}
            </div>
            {/* Triangle marker */}
            <div style={{ position: 'absolute', top: -2, left: `calc(${pct}% - 6px)`, fontSize: 14, color: '#fff', lineHeight: 1 }}>▲</div>
            {/* Labels */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              {thresholds.map((t, i) => (
                <div key={i} style={{ flex: t.max - t.min, textAlign: 'center', fontSize: 10, color: COLOR_MUTED }}>{t.label}</div>
              ))}
            </div>
          </div>
          {settingsLink && (
            <button
              onClick={() => { setTriggerPopup(null); navigateTo('profile'); }}
              style={{ width: '100%', padding: '10px', background: 'transparent', border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, color: COLOR_GREEN, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
            >
              Change your temperature limits in Settings →
            </button>
          )}
          <div style={{ fontSize: 11, color: COLOR_MUTED, textAlign: 'center', marginTop: 12 }}>Tap outside to close</div>
        </div>
      </div>
    );
  };

  // Build popup config for a trigger
  const buildTriggerPopup = (triggerText, h, prof) => {
    const wind = h.windSpeedNum || 0;
    const gust = h.windGust || 0;
    const precip = h.precipChance || 0;
    const feels = h.feelsLike != null ? h.feelsLike : (h.temp || 0);

    if (triggerText.startsWith('Precip') || triggerText.includes('%')) {
      return {
        title: 'Precipitation Chance',
        value: precip, unit: '%',
        thresholds: [
          { label: '0–29%', color: '#22C55E', min: 0, max: 30 },
          { label: '30–49%', color: '#FDE047', min: 30, max: 50 },
          { label: '50–69%', color: '#F97316', min: 50, max: 70 },
          { label: '70%+', color: '#EF4444', min: 70, max: 100 },
        ],
      };
    }
    if (triggerText.startsWith('Wind')) {
      return {
        title: 'Wind Speed',
        value: wind, unit: ' mph',
        thresholds: [
          { label: '0–14 mph', color: '#22C55E', min: 0, max: 15 },
          { label: '15–19', color: '#FDE047', min: 15, max: 20 },
          { label: '20–29', color: '#F97316', min: 20, max: 30 },
          { label: '30+ mph', color: '#EF4444', min: 30, max: 50 },
        ],
      };
    }
    if (triggerText.startsWith('Gust')) {
      return {
        title: 'Wind Gust',
        value: gust, unit: ' mph',
        thresholds: [
          { label: '0–19 mph', color: '#22C55E', min: 0, max: 20 },
          { label: '20–29', color: '#FDE047', min: 20, max: 30 },
          { label: '30–39', color: '#F97316', min: 30, max: 40 },
          { label: '40+ mph', color: '#EF4444', min: 40, max: 60 },
        ],
      };
    }
    if (triggerText.startsWith('Feels')) {
      return {
        title: 'Feels Like Temperature',
        value: feels, unit: '°',
        thresholds: [
          { label: `<${prof.minTemp - 5}°`, color: '#EF4444', min: prof.minTemp - 15, max: prof.minTemp - 5 },
          { label: `${prof.minTemp - 5}–${prof.minTemp}°`, color: '#FDE047', min: prof.minTemp - 5, max: prof.minTemp },
          { label: `${prof.minTemp}–${prof.maxTemp}°`, color: '#22C55E', min: prof.minTemp, max: prof.maxTemp },
          { label: `${prof.maxTemp}–${prof.maxTemp + 5}°`, color: '#FDE047', min: prof.maxTemp, max: prof.maxTemp + 5 },
          { label: `>${prof.maxTemp + 5}°`, color: '#EF4444', min: prof.maxTemp + 5, max: prof.maxTemp + 15 },
        ],
        settingsLink: true,
      };
    }
    // Storm alert triggers — show storm explanation, not precip gauge
    if (triggerText.includes('STORM') || triggerText.includes('GET IN NOW') || triggerText.includes('RAIN APPROACHING')) {
      return null; // No threshold popup for storm alerts — the Storm Alert Zone card is the source of truth
    }
    // Right Now card rain labels — plain English, no forecast % popup
    if (triggerText === 'Heavy Rain' || triggerText === 'Raining' || triggerText === 'Light Rain') {
      return null;
    }
    // Condition triggers (Rain, Fog, Thunder, Snow, Ice, etc.) — no threshold bar, show precip gauge as context
    return {
      title: triggerText,
      value: precip, unit: '%',
      thresholds: [
        { label: '0–29%', color: '#22C55E', min: 0, max: 30 },
        { label: '30–49%', color: '#FDE047', min: 30, max: 50 },
        { label: '50–69%', color: '#F97316', min: 50, max: 70 },
        { label: '70%+', color: '#EF4444', min: 70, max: 100 },
      ],
    };
  };

  // ── S98 HOURLY ROW — new card design system ───────────────
  // Used in Forecast drilldown AND Day of Play for visual uniformity
  const HourlyRow = ({ h, profile: prof, postSunset = false }) => {
    const hTime = new Date(h.time || h.dt * 1000);
    const hHour = hTime.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
    const wind = h.windSpeedNum || 0;
    const gust = h.windGust || 0;
    const precip = h.precipChance || 0;
    const temp = h.temp || 0;
    const feels = h.feelsLike != null ? h.feelsLike : temp;
    const hum = h.humidity || 0;
    const uv = Math.round(h.uvIndex || 0);
    const isDaytime = h.isDaytime !== false;
    const hourResult = postSunset ? { status: 'GO', triggers: {} } : getHourStatus(h, prof);
    const statusColor = postSunset ? COLOR_MUTED : (STATUS_COLORS[hourResult.status]?.color || '#9CA3AF');
    const labelShort = hourResult.status === 'STRONG CAUTION' ? 'STR CAU' : hourResult.status;
    const cond = h.conditions || h.shortForecast || 'Clear';
    const showGust = gust > wind + 10;
    // eslint-disable-next-line no-unused-vars
    const gustColor = gust >= 40 ? '#EF4444' : gust >= 30 ? '#F97316' : gust >= 20 ? '#FDE047' : COLOR_MUTED;

    // Trigger logic — matches locked DrilldownHourCard spec (S115):
    // NO-GO: 1 trigger only | STR CAU: max 2 | CAUTION: max 3 (Precip/Wind/Feels)
    // Gust advisory if gust > wind+10 AND gust >= 20
    const triggers = hourResult.triggers || {};
    const allTriggers = [];
    if (!postSunset) {
      if (triggers['NO-GO']?.length) {
        allTriggers.push({ text: triggers['NO-GO'][0], level: 'NO-GO', color: STATUS_COLORS['NO-GO'].color });
      } else if (triggers['STRONG CAUTION']?.length) {
        triggers['STRONG CAUTION'].slice(0, 2).forEach(t => allTriggers.push({ text: t, level: 'STRONG CAUTION', color: STATUS_COLORS['STRONG CAUTION'].color }));
      } else if (triggers['CAUTION']?.length) {
        triggers['CAUTION'].slice(0, 3).forEach(t => allTriggers.push({ text: t, level: 'CAUTION', color: STATUS_COLORS['CAUTION'].color }));
      }
      if (gust > wind + 10 && gust >= 20 && !allTriggers.some(t => t.text.startsWith('Gust'))) {
        const gustLevel = gust >= 40 ? 'NO-GO' : gust >= 30 ? 'STRONG CAUTION' : 'CAUTION';
        allTriggers.push({ text: `Gust ${gust} mph`, level: gustLevel, color: STATUS_COLORS[gustLevel].color });
      }
    }

    return (
      <div style={{
        background: '#000',
        border: postSunset ? `2px solid #333` : `8px solid ${statusColor}`,
        borderRadius: 12,
        padding: '10px 14px',
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* R1: Time (left) · Status icon + label (right, not shown post-sunset) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{hHour}</span>
          {!postSunset && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <StatusIcon status={hourResult.status} size={18} />
              <span style={{ fontSize: 13, fontWeight: 800, color: statusColor }}>{labelShort}</span>
            </div>
          )}
        </div>
        {/* R2: Sky icon + condition text */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          {renderSkyIcon(cond, isDaytime, 28)}
          <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{skyLabel(cond)}</span>
        </div>
        {/* R3: FEELS + actual */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase' }}>Feels</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{Math.round(feels)}°</span>
          <span style={{ fontSize: 13, color: COLOR_MUTED }}>({Math.round(temp)}° actual)</span>
        </div>
        {/* R4: Stats row — Precip · Wind · Gust · Hum · UV */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginBottom: allTriggers.length > 0 ? 6 : 0 }}>
          <span style={{ fontSize: 13, color: '#fff', display:'flex', alignItems:'center' }}><svg width="11" height="13" viewBox="0 0 14 18" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><path d="M7 1 C7 1 1 8 1 12 A6 6 0 0 0 13 12 C13 8 7 1 7 1Z" fill="#4a90d9"/></svg>{precip}%</span>
          <span style={{ fontSize: 13, color: '#fff', display:'flex', alignItems:'center' }}><svg width="14" height="12" viewBox="0 0 16 12" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><line x1="1" y1="3" x2="15" y2="3" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="6.5" x2="12" y2="6.5" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="10" x2="9" y2="10" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/></svg>{wind} mph</span>
          {showGust && <span style={{ fontSize: 13, color: gustColor }}>Gust {gust} mph</span>}
          <span style={{ fontSize: 13, color: '#fff' }}>Hum {Math.round(hum)}%</span>
          {isDaytime && uv >= 3 && <span style={{ fontSize: 13, color: uv >= 11 ? '#EF4444' : uv >= 8 ? '#F97316' : uv >= 6 ? '#FDE047' : '#fff' }}>UV {uvLabel(Math.round(uv))}</span>}
        </div>
        {/* R5: Trigger lines — non-GO only, pins to bottom via margin-top:auto */}
        <div style={{
          borderTop: allTriggers.length > 0 ? '1px solid #333' : '1px solid transparent',
          paddingTop: allTriggers.length > 0 ? 6 : 0,
          marginTop: 'auto',
          display: 'flex', flexDirection: 'column', gap: 4,
          minHeight: 18,
        }}>
          {allTriggers.map((trig, i) => {
            const hasThreshold = /^(Precip|Wind|Gust|Feels)/.test(trig.text);
            const popup = hasThreshold ? buildTriggerPopup(trig.text, h, prof) : null;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <StatusIcon status={trig.level} size={14} />
                <span style={{ fontSize: 12, fontWeight: 700, color: trig.color, flex: 1, lineHeight: 1.3 }}>{trig.text}</span>
                {popup && (
                  <button
                    onClick={() => setTriggerPopup(popup)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '1px 4px', flexShrink: 0, lineHeight: 1 }}
                  ><svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="none" stroke="#E5E7EB" strokeWidth="1"/><text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#E5E7EB" fontFamily="Arial,sans-serif">i</text></svg></button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── S115 DRILLDOWN HOUR CARD — 2-per-row grid cards for forecast drilldown ──
  // Matches the locked HTML mockup in ZAP_Forecast_DrillDown_Card_LookAndFeel.md
  const DrilldownHourCard = ({ h, profile: prof, isPast = false, isInWindow = false }) => {
    const hTime = new Date(h.time || h.dt * 1000);
    const hHour = hTime.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
    const hNextTime = new Date(hTime.getTime() + 60 * 60 * 1000);
    const hNextHour = hNextTime.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
    const hHourRange = `${hHour} – ${hNextHour}`;
    const wind = h.windSpeedNum || 0;
    const gust = h.windGust || 0;
    const precip = h.precipChance || 0;
    const temp = h.temp || 0;
    const feels = h.feelsLike != null ? h.feelsLike : temp;
    const hum = h.humidity || 0;
    const uv = Math.round(h.uvIndex || 0);
    const isDaytime = h.isDaytime !== false;
    const hourResult = getHourStatus(h, prof);
    const sc = STATUS_COLORS[hourResult.status]?.color || '#9CA3AF';
    const labelShort = hourResult.status === 'STRONG CAUTION' ? 'STR CAU' : hourResult.status;
    const cond = h.conditions || h.shortForecast || 'Clear';
    // NWS owns both conditions and precip — use directly, no override needed
    const condDisplay = cond;

    // Build trigger display per spec rules:
    // NO-GO: 1 trigger only (worst)
    // STR CAU: max 2 (STR CAU level only, no CAUTION below)
    // CAUTION: max 3 (Precip/Wind/Feels)
    // Gust advisory if gust > wind+10 AND gust >= 20
    const triggers = hourResult.triggers || {};
    let displayTriggers = [];
    if (triggers['NO-GO']?.length) {
      displayTriggers = [{ text: triggers['NO-GO'][0], level: 'NO-GO', color: STATUS_COLORS['NO-GO'].color }];
    } else if (triggers['STRONG CAUTION']?.length) {
      displayTriggers = triggers['STRONG CAUTION'].slice(0, 2).map(t => ({ text: t, level: 'STRONG CAUTION', color: STATUS_COLORS['STRONG CAUTION'].color }));
    } else if (triggers['CAUTION']?.length) {
      displayTriggers = triggers['CAUTION'].slice(0, 3).map(t => ({ text: t, level: 'CAUTION', color: STATUS_COLORS['CAUTION'].color }));
    }
    // Gust advisory line
    if (gust > wind + 10 && gust >= 20 && !displayTriggers.some(t => t.text.startsWith('Gust'))) {
      const gustLevel = gust >= 40 ? 'NO-GO' : gust >= 30 ? 'STRONG CAUTION' : 'CAUTION';
      displayTriggers.push({ text: `Gust ${gust} mph`, level: gustLevel, color: STATUS_COLORS[gustLevel].color });
    }

    // Trigger text joined for R5 display
    const triggerText = displayTriggers.map(t => t.text).join(' · ');
    const worstLevel = displayTriggers.length > 0 ? displayTriggers[0].level : null;
    const triggerColor = displayTriggers.length > 0 ? displayTriggers[0].color : null;

    // ⓘ only on threshold triggers (Precip/Wind/Feels/Gust) — not condition triggers
    const hasThresholdTrigger = displayTriggers.some(t => /^(Precip|Wind|Gust|Feels)/.test(t.text));
    const popup = hasThresholdTrigger ? buildTriggerPopup(displayTriggers.find(t => /^(Precip|Wind|Gust|Feels)/.test(t.text)).text, h, prof) : null;

    return (
      <div style={{
        background: '#000',
        border: `5px solid ${sc}`,
        borderRadius: 12,
        padding: 7,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Georgia, serif',
        minWidth: 0,
        boxSizing: 'border-box',
        opacity: isPast ? 0.4 : isInWindow ? 1 : 0.4,
      }}>
        {/* R1: hour (left) + status icon/label (right) — flex row per S113/S115 spec */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 4 }}>
          <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>{hHourRange}</span>
          <div style={{ flexShrink: 0, marginLeft: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <StatusIcon status={hourResult.status} size={16} />
            <span style={{ fontSize: 9, fontWeight: 700, color: sc, whiteSpace: 'nowrap' }}>{labelShort}</span>
          </div>
        </div>

        {/* R2: sky SVG 36px + condition 20px bold white — DOMINANT element */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
          {renderSkyIcon(condDisplay, isDaytime, 36)}
          <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{skyLabel(condDisplay)}</span>
        </div>

        {/* R3: feels temp (left) | precip/wind/humidity (right, border-left) */}
        <div style={{ display: 'flex', alignItems: 'stretch', marginBottom: 4 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{Math.round(feels)}°</span>
            <span style={{ fontSize: 10, color: '#fff' }}>({Math.round(temp)}° actual)</span>
          </div>
          <div style={{ borderLeft: '1px solid #333', paddingLeft: 7, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', display:'flex', alignItems:'center' }}><svg width="11" height="13" viewBox="0 0 14 18" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><path d="M7 1 C7 1 1 8 1 12 A6 6 0 0 0 13 12 C13 8 7 1 7 1Z" fill="#4a90d9"/></svg>{precip}%</span>
            <span style={{ fontSize: 11, color: '#fff', whiteSpace: 'nowrap', display:'flex', alignItems:'center' }}><svg width="14" height="12" viewBox="0 0 16 12" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><line x1="1" y1="3" x2="15" y2="3" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="6.5" x2="12" y2="6.5" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="10" x2="9" y2="10" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/></svg>{wind} mph</span>
            <span style={{ fontSize: 11, color: '#fff', whiteSpace: 'nowrap' }}>Hum {Math.round(hum)}%</span>
            {isDaytime && uv >= 3 && <span style={{ fontSize: 11, color: uv >= 11 ? '#EF4444' : uv >= 8 ? '#F97316' : uv >= 6 ? '#FDE047' : '#fff', whiteSpace: 'nowrap' }}>UV {uvLabel(Math.round(uv))}</span>}
          </div>
        </div>

        {/* R5: trigger line — non-GO only. Uniform height via minHeight. */}
        <div style={{
          borderTop: displayTriggers.length > 0 ? '1px solid #333' : '1px solid transparent',
          paddingTop: displayTriggers.length > 0 ? 3 : 0,
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          minHeight: 18,
        }}>
          {displayTriggers.length > 0 ? (<>
            <StatusIcon status={worstLevel} size={12} />
            <span style={{ fontSize: 11, fontWeight: 700, color: triggerColor, flex: 1, lineHeight: 1.3 }}>{triggerText}</span>
            {popup && (
              <button
                onClick={(e) => { e.stopPropagation(); setTriggerPopup(popup); }}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="7" fill="none" stroke="#E5E7EB" strokeWidth="1" />
                  <text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#E5E7EB" fontFamily="Arial,sans-serif">i</text>
                </svg>
              </button>
            )}
          </>) : null}
        </div>
      </div>
    );
  };

  // ── AUTH ──────────────────────────────────────────────────
  const [authed, setAuthed] = useState(true); // S50: password removed for testing

  // ── LAST ROUND OVERLAY — show once per calendar day ───────
  const [showRoundOverlay, setShowRoundOverlay] = useState(() => {
    try {
      const last = localStorage.getItem('zap_overlay_shown');
      const today = new Date().toDateString();
      return last !== today; // show if not shown today
    } catch { return true; }
  });
  const dismissOverlay = () => {
    try { localStorage.setItem('zap_overlay_shown', new Date().toDateString()); } catch {}
    setShowRoundOverlay(false);
  };
  const [isAdmin, setIsAdmin] = useState(() => {
    try { return localStorage.getItem('zap_admin') === 'true'; } catch { return false; }
  });
  const [authInput, setAuthInput] = useState('');
  const [authError, setAuthError] = useState('');

  // ── CONFIG ─────────────────────────────────────────────────
  const [zapConfig, setZapConfig] = useState(null);
  useEffect(() => {
    fetch('/zap-config.json')
      .then(r => r.json())
      .then(cfg => setZapConfig(cfg))
      .catch(() => setZapConfig(null));
  }, []);

  // ── S166 LOGGING: window-level error capture ──────────────
  // Catches errors that React ErrorBoundary can't:
  //   - errors thrown inside event handlers (Submit click, etc.)
  //   - unhandled promise rejections (failed fetches, async/await)
  //   - errors thrown inside setTimeout / setInterval callbacks
  // Logs to console with a 'ZAP' prefix so they're easy to grep in DevTools.
  // Mounted once at App startup; cleaned up on unmount (defensive — App never actually unmounts in practice).
  useEffect(() => {
    const onError = (e) => {
      console.error('ZAP window.error:', {
        message: e.message,
        filename: e.filename,
        line: e.lineno,
        col: e.colno,
        error: e.error,
        stack: e.error?.stack,
      });
    };
    const onRejection = (e) => {
      console.error('ZAP unhandledrejection:', {
        reason: e.reason,
        message: e.reason?.message,
        stack: e.reason?.stack,
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  // ── CONFIG DERIVATIONS ─────────────────────────────────────
  const cfgCourses = zapConfig?.courses || COURSES;
  const cfgPlayers = zapConfig?.players || PLAYERS;
  const cfgLore = zapConfig?.courseLore || COURSE_LORE;
  const cfgAssets = zapConfig?.courseAssets || COURSE_ASSETS;
  const cfgHolePars = zapConfig?.holePars || {};

  // ── MISSING STATE ──────────────────────────────────────────
  const [townFilter, setTownFilter] = useState(null);
  const [homeCourse, setHomeCourse] = useState(null);

  // ── AUTH HANDLER (S96: password gate removed — always authed) ──
  const handleAuth = (val) => {
    setAuthed(true);
    setIsAdmin(true);
    localStorage.setItem('zap_authed', '1');
    localStorage.setItem('zap_admin', '1');
  };

  // ── NAVIGATION ─────────────────────────────────────────────
  // mode: kept for backward compat with Planning/OnCourse sub-screens
  // screen: 'home' is now the default landing; null = legacy mode screens
  const [mode, setMode] = useState('planning');
  const [screen, setScreen] = useState(() => {
    try {
      // Recovery URL takes priority over everything — check before loading last course
      const params = new URLSearchParams(window.location.search);
      if (params.get('admin') === 'recover') return 'recover';
      const saved = localStorage.getItem('zap_last_course');
      return saved ? 'home' : null;
    } catch { return null; }
  });
  const [screenHistory, setScreenHistory] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('admin') === 'recover') return ['recover'];
      const saved = localStorage.getItem('zap_last_course');
      return saved ? ['home'] : [];
    } catch { return []; }
  });
  const scorecardBackGuard = useRef(null);
  const loadInFlight = useRef(false); // FETCHALL-DOUBLE-INVOCATION guard — prevents two useEffects both firing loadCourse simultaneously
  const [scorecardInReview, setScorecardInReview] = React.useState(false);
  // Reset review state when navigating away from scorecard screen
  React.useEffect(() => { if (screen !== 'scorecard') setScorecardInReview(false); }, [screen]);

  const [screenParams, setScreenParams] = React.useState({});

  const navigateTo = useCallback((newScreen, params) => {
    setScreenHistory(prev => [...prev, newScreen]);
    setScreen(newScreen);
    if (params) setScreenParams(prev => ({ ...prev, [newScreen]: params }));
    setTimeout(() => scrollToTop(), 50);
  }, []);

  const goBack = useCallback(() => {
    setScreenHistory(prev => {
      if (prev.length <= 1) { setScreen(null); return []; }
      const newHistory = prev.slice(0, -1);
      setScreen(newHistory[newHistory.length - 1]);
      return newHistory;
    });
    setTimeout(() => scrollToTop(), 50);
  }, []);

  // ── RECOVERY URL — ?admin=recover ──────────────────────────
  // JZ sends https://zap-golf.netlify.app?admin=recover to a golfer with a stuck round.
  // Must be opened in the same browser used to submit the round (localStorage is origin-locked).
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('admin') === 'recover') {
        setScreen('recover');
        setScreenHistory(['recover']);
        return;
      }
    } catch {}
    // S184: app_open logged inside loadCourse — removed duplicate log here that was causing
    // double app_open entries on every cold start for every course.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchMode = useCallback((newMode) => {
    setMode(newMode);
    setScreen(null);
    setScreenHistory([]);
    setTimeout(() => scrollToTop(), 50);
  }, []);

  // ── COURSE SELECTION ───────────────────────────────────────
  const selectedCourseRef = useRef(null); // S183: stable ref for modal closure
  const [selectedCourse, setSelectedCourse] = useState(() => {
    try {
      const saved = localStorage.getItem('zap_last_course');
      if (saved) {
        const id = JSON.parse(saved);
        return COURSES.find(c => c.id === id) || null;
      }
    } catch {}
    return null;
  });
  const [showCourseSwitcher, setShowCourseSwitcher] = useState(false);
  const [showSourceLegend, setShowSourceLegend] = useState(false);
  const [modalRefreshTime, setModalRefreshTime] = useState(null);

  // ── PROFILE ────────────────────────────────────────────────
  const [profile, setProfile] = useState({    minTemp: 58, maxTemp: 95, maxWind: 25,
    bugSensitivity: 'medium',
    playWindow: 'all',
    playStart: '08:00', playEnd: '20:00',
    homeCourse: null,
    webhookUrl: '',
    adminUnlocked: false,
    audioAlertEnabled: true,  // S274: storm audio alert — default ON (safety)
  });
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileLoadedLate, setProfileLoadedLate] = useState(false); // cold-start race: profile arrived after forecast

  // S227: AudioContext for storm audio alerts — unlocked on first user touch
  const audioCtxRef = React.useRef(null);
  const audioEnabledRef = React.useRef(profile.audioAlertEnabled);
  React.useEffect(() => {
    const unlock = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
      document.removeEventListener('touchstart', unlock);
    };
    document.addEventListener('touchstart', unlock, { once: true });
    return () => document.removeEventListener('touchstart', unlock);
  }, []);

  // Keep audioEnabledRef in sync at App level — avoids re-render inside OnCourseScreen
  const _prevAudioEnabled = React.useRef(profile.audioAlertEnabled);
  React.useEffect(() => {
    const was = _prevAudioEnabled.current;
    audioEnabledRef.current = profile.audioAlertEnabled;
    _prevAudioEnabled.current = profile.audioAlertEnabled;
    // If toggled ON — signal OnCourseScreen to treat current storm as new
    if (!was && profile.audioAlertEnabled) {
      audioEnabledRef._justEnabled = true;
    }
  }, [profile.audioAlertEnabled]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('zap_profile');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Guard against old wrapped format { profile: {...}, homeCourse: '...' }
        const profileData = parsed?.profile ?? parsed;
        setProfile(prev => ({ ...prev, ...profileData }));
        // Cold-start race: if weatherData already loaded when profile arrives, flag it
        if (weatherData) setProfileLoadedLate(true);
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfileToStore = useCallback(() => {
    try { localStorage.setItem('zap_profile', JSON.stringify(profile)); } catch {}
  }, [profile]);
  useEffect(() => { saveProfileToStore(); }, [saveProfileToStore]);

  // ── WEATHER STATE ──────────────────────────────────────────
  const [weatherData, setWeatherData] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [realtimeRefreshTick, setRealtimeRefreshTick] = useState(0);
  const [weatherError, setWeatherError] = useState(null);
  const [badResult, setBadResult] = useState(null);
  const [waterDensity, setWaterDensity] = useState(null);
  const [lightningData, setLightningData] = useState(null);
  const [pollenData, setPollenData] = useState(null);
  const [showRadarInfo, setShowRadarInfo] = useState(false);
  const [etData, setEtData] = useState(null);
  const [njwxData, setNjwxData] = useState(null);
  const [wapiCurrentData, setWapiCurrentData] = useState(null);
  const wapiLoadStartRef = useRef(null); // S216 F1: tracks when fire-and-forget fired for 3s timeout
  const [sharedRounds, setSharedRounds] = useState([]);

  const [activeWindow, setActiveWindow] = useState({
    dayIdx: -1,
    period: null
  });
  const [selectedForecastDay, setSelectedForecastDay] = useState(null); // date string 'YYYY-MM-DD'

  // Scorecard webhook from config
  const SCORECARD_WEBHOOK = 'https://script.google.com/macros/s/AKfycbwW4oHrqN9Vx9AkZ0zt7pZZrnHmXFT1Xdmd2lWFw05u1Hk9m5nBGfrYX0sdGzjL6H-BGQ/exec';

  // ── SHARED ROUNDS (Google Sheet via webhook) ───────────────
  // CACHE-1: Show cache instantly, fetch fresh in background.
  useEffect(() => {
    const ROUNDS_CACHE_KEY = 'zap_rounds_cache';
    const ROUNDS_TS_KEY    = 'zap_rounds_ts';

    // Show cache immediately — no waiting for network
    try {
      const cached = localStorage.getItem(ROUNDS_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.length > 0) setSharedRounds(parsed);
      }
    } catch {}

    // Fetch fresh in background
    const load = async () => {
      try {
        // Timestamp check — getTimestamp is wired in Apps Script (Code.gs line 341).
        // Auto-updates on every scorecard submission. Skip full fetch if timestamp unchanged.
        let sheetTs = '';
        let skipFetch = false;
        try {
          const tsResp = await fetch(`${SCORECARD_WEBHOOK}?action=getTimestamp`);
          if (tsResp.ok) {
            const tsData = await tsResp.json();
            sheetTs = tsData.timestamp || '';
            const cachedTs = localStorage.getItem(ROUNDS_TS_KEY) || '';
            if (sheetTs && sheetTs === cachedTs) skipFetch = true;
          }
        } catch {} // network failure — fall through to full fetch

        if (skipFetch) return;

        const r = await fetch(`${SCORECARD_WEBHOOK}?action=getRounds`);
        if (!r.ok) return;
        const d = await r.json();
        const normalized = (d.rounds || []).map(row => {
          if (Array.isArray(row.scores)) return row;
          const scores = Array.from({length: 18}, (_, i) => {
            const v = row[`H${i+1}`];
            return (v !== '' && v != null) ? parseInt(v) : null;
          });
          // Apps Script sometimes serializes numeric cells as ISO date strings (Excel serial bug).
          // e.g. Total=72 comes back as "1900-03-12T05:00:00.000Z" — day 71 from 1900-01-00.
          // Fix: if a field that should be a number is an ISO string, extract days-since-1900 as integer.
          const parseSheetInt = (v) => {
            if (v == null || v === '') return 0;
            if (typeof v === 'number') return Math.round(v);
            const s = String(v);
            // Plain integer string
            if (/^\d+$/.test(s)) return parseInt(s, 10);
            // Apps Script date-serialized number: "1900-MM-DDTHH:mm:ss.000Z"
            // The day-of-year from Jan 0 1900 = the original integer value
            const m = s.match(/^1900-(\d{2})-(\d{2})T/);
            if (m) {
              const month = parseInt(m[1], 10); // 1-based
              const day   = parseInt(m[2], 10);
              // Days since Jan 0 1900: Jan=31, Feb=28(non-leap), Mar=31...
              const daysInMonth = [0,31,28,31,30,31,30,31,31,30,31,30,31];
              let total = day;
              for (let mo = 1; mo < month; mo++) total += daysInMonth[mo];
              return total;
            }
            return parseInt(s, 10) || 0;
          };

          // Parse date — always produce clean YYYY-MM-DD string
          const parseSheetDate = (v) => {
            if (!v) return '';
            const s = String(v).trim();
            // Already ISO format
            if (s.match(/^\d{4}-\d{2}-\d{2}/)) return s.slice(0, 10);
            // Human-readable e.g. "Mon, Mar 23, 2026" — parse via Date, use en-CA for YYYY-MM-DD in local time
            try {
              const d = new Date(s);
              if (!isNaN(d.getTime())) return d.toLocaleDateString('en-CA');
            } catch {}
            return '';
          };

          // Parse time — if Apps Script serialized as "1899-12-30THH:mm:ss.000Z" extract HH:MM AM/PM
          const parseSheetTime = (v) => {
            if (!v) return '';
            const s = String(v).trim();
            if (/[AP]M$/i.test(s)) return s; // already formatted
            const m = s.match(/T(\d{2}):(\d{2})/);
            if (m) {
              let h = parseInt(m[1], 10), min = parseInt(m[2], 10);
              const ampm = h >= 12 ? 'PM' : 'AM';
              h = h % 12 || 12;
              return `${h}:${String(min).padStart(2,'0')} ${ampm}`;
            }
            return s;
          };

          return {
            id: row.ID || row.id,
            courseId: row.CourseID || row.courseId,
            courseName: row.CourseName || row.courseName,
            date: parseSheetDate(row.Date || row.date),
            teeTime: parseSheetTime(row.TeeTime || row.teeTime),
            finishTime: parseSheetTime(row.FinishTime || row.finishTime),
            comment: row.Comment || row.comment || '',
            players: typeof row.Players === 'string'
              ? row.Players.split(',').map(p => p.trim()).filter(Boolean)
              : (row.players || []),
            scores,
            pars: (row.Pars || row.pars)
              ? String(row.Pars || row.pars).split(',').map(p => parseInt(p,10)).filter(n => !isNaN(n))
              : Array(18).fill(4),
            total: (() => {
              // S277: always compute from hole scores — stored Total may be stale if sheet was edited
              const holeSum = scores.reduce((s, v) => s + (v != null ? v : 0), 0);
              return holeSum > 0 ? holeSum : parseSheetInt(row.Total || row.total);
            })(),
            holesEntered: parseSheetInt(row.HolesEntered || row.holesEntered) || 18,
            photoUrl: row.PhotoURL || row.photoUrl || '',
            submittedAt: row.SubmittedAt || row.submittedAt || '',
            truthChain: (() => {
              const flags = row.TruthFlags || row.truthFlags || '';
              const other = row.TruthOther || row.truthOther || '';
              const arr = flags ? String(flags).split(',').map(s => s.trim()).filter(Boolean) : [];
              if (other) arr.push(String(other).trim());
              return arr;
            })(),
            weatherSnapshot: (() => {
              // S166: rehydrate full S154 schema. Reads new keys first, falls back to old keys
              // for rounds written before S166 (where Code.gs only persisted feelsLike, wind,
              // precipChance, condition — gust/humidity/dataSource columns didn't exist).
              const num = (v) => (v != null && v !== '' ? Number(v) : null);
              const str = (v) => (v != null && v !== '' ? String(v) : null);
              const feelsLike   = num(row.WeatherFeels);
              const wind        = num(row.WeatherWind);
              const gust        = num(row.WeatherGust);                       // S166 — null on old rows
              const humidity    = num(row.WeatherHumidity);                   // S166 — null on old rows
              const precipAccum = num(row.WeatherPrecip);                     // S166: column now stores inches (was %)
              const conditions  = str(row.WeatherCond) || str(row.weatherCond);
              const rawSource   = str(row.WeatherSource);                     // S166 — null on old rows
              // S246: parse stid out of WeatherSource ("njwxnet+forecast NJ09" → dataSource="njwxnet+forecast", stid="NJ09")
              const sourceParts = rawSource ? rawSource.split(' ') : [];
              const dataSource  = sourceParts[0] || null;
              const stid        = sourceParts[1] || null;
              if (feelsLike == null && wind == null && gust == null && humidity == null
                  && precipAccum == null && !conditions) return null;
              // Field names match in-memory snapshot from buildWeatherSnapshot — renderer reads same.
              return { feelsLike, wind, gust, humidity, precipAccum, conditions, dataSource, stid };
            })(),
            neshanicNines: row.NeshanicNines || row.neshanicNines || '',
          };
        }).filter(r => r.total > 0);
        try {
          // Only write cache if Sheet returned at least as many rounds as currently cached
          // Protects against race condition where Sheet hasn't finished writing newest round
          const existingCache = localStorage.getItem(ROUNDS_CACHE_KEY);
          const existingCount = existingCache ? JSON.parse(existingCache).length : 0;
          if (normalized.length >= existingCount) {
            localStorage.setItem(ROUNDS_CACHE_KEY, JSON.stringify(normalized));
            if (sheetTs) localStorage.setItem(ROUNDS_TS_KEY, sheetTs);
          }
        } catch {}
        setSharedRounds(normalized);
      } catch {}
    };
    load();
  }, []);

  // Cache invalidation fix: expose refreshRounds so 19th Hole + Statistics can re-fetch on mount
  const refreshRounds = useCallback(async () => {
    const ROUNDS_CACHE_KEY = 'zap_rounds_cache';
    const ROUNDS_TS_KEY    = 'zap_rounds_ts';
    try {
      let sheetTs = '';
      try {
        const tsResp = await fetch(`${SCORECARD_WEBHOOK}?action=getTimestamp`);
        if (tsResp.ok) {
          const tsData = await tsResp.json();
          sheetTs = tsData.timestamp || '';
          const cachedTs = localStorage.getItem(ROUNDS_TS_KEY) || '';
          if (sheetTs && sheetTs === cachedTs) return; // still fresh
        }
      } catch {}
      const resp = await fetch(`${SCORECARD_WEBHOOK}?action=getRounds`);
      if (!resp.ok) return;
      const d = await resp.json();
      const normalized = (d.rounds || []).map(row => {
        if (Array.isArray(row.scores)) return row;
        const scores = Array.from({length: 18}, (_, i) => {
          const v = row[`H${i+1}`];
          return (v !== '' && v != null) ? parseInt(v) : null;
        });
        const parseSheetInt = (v) => {
          if (v == null || v === '') return 0;
          if (typeof v === 'number') return Math.round(v);
          const s = String(v);
          if (/^\d+$/.test(s)) return parseInt(s, 10);
          const m = s.match(/^1900-(\d{2})-(\d{2})T/);
          if (m) {
            const daysInMonth = [0,31,28,31,30,31,30,31,31,30,31,30,31];
            let total = parseInt(m[2], 10);
            for (let mo = 1; mo < parseInt(m[1], 10); mo++) total += daysInMonth[mo];
            return total;
          }
          return parseInt(s, 10) || 0;
        };
        const parseSheetTime = (v) => {
          if (!v) return '';
          const s = String(v).trim();
          if (/[AP]M$/i.test(s)) return s;
          const m = s.match(/T(\d{2}):(\d{2})/);
          if (m) {
            let h = parseInt(m[1], 10), min = parseInt(m[2], 10);
            const ampm = h >= 12 ? 'PM' : 'AM';
            h = h % 12 || 12;
            return `${h}:${String(min).padStart(2,'0')} ${ampm}`;
          }
          return s;
        };
        return {
          id: row.ID || row.id,
          courseId: row.CourseID || row.courseId,
          courseName: row.CourseName || row.courseName,
          date: (() => { const v = String(row.Date || row.date || '').trim(); if (!v) return ''; if (v.match(/^\d{4}-\d{2}-\d{2}/)) return v.slice(0,10); try { const d = new Date(v); if (!isNaN(d.getTime())) return d.toLocaleDateString('en-CA'); } catch {} return ''; })(),
          teeTime: parseSheetTime(row.TeeTime || row.teeTime),
          finishTime: parseSheetTime(row.FinishTime || row.finishTime),
          comment: row.Comment || row.comment || '',
          players: typeof row.Players === 'string'
            ? row.Players.split(',').map(p => p.trim()).filter(Boolean)
            : (row.players || []),
          scores,
          pars: (row.Pars || row.pars)
            ? String(row.Pars || row.pars).split(',').map(p => parseInt(p,10)).filter(n => !isNaN(n))
            : Array(18).fill(4),
          total: (() => {
            // S277: always compute from hole scores — stored Total may be stale if sheet was edited
            const holeSum = scores.reduce((s, v) => s + (v != null ? v : 0), 0);
            return holeSum > 0 ? holeSum : parseSheetInt(row.Total || row.total);
          })(),
          holesEntered: parseSheetInt(row.HolesEntered || row.holesEntered) || 18,
          photoUrl: row.PhotoURL || row.photoUrl || '',
          submittedAt: row.SubmittedAt || row.submittedAt || '',
          truthChain: (() => {
            const flags = row.TruthFlags || row.truthFlags || '';
            const other = row.TruthOther || row.truthOther || '';
            const arr = flags ? String(flags).split(',').map(s => s.trim()).filter(Boolean) : [];
            if (other) arr.push(String(other).trim());
            return arr;
          })(),
          weatherSnapshot: (() => {
            // S166: rehydrate full S154 schema. Reads new keys first, falls back to old keys
            // for rounds written before S166 (where Code.gs only persisted feelsLike, wind,
            // precipChance, condition — gust/humidity/dataSource columns didn't exist).
            const num = (v) => (v != null && v !== '' ? Number(v) : null);
            const str = (v) => (v != null && v !== '' ? String(v) : null);
            const feelsLike   = num(row.WeatherFeels);
            const wind        = num(row.WeatherWind);
            const gust        = num(row.WeatherGust);                       // S166 — null on old rows
            const humidity    = num(row.WeatherHumidity);                   // S166 — null on old rows
            const precipAccum = num(row.WeatherPrecip);                     // S166: column now stores inches (was %)
            const conditions  = str(row.WeatherCond) || str(row.weatherCond);
            const rawSource   = str(row.WeatherSource);                     // S166 — null on old rows
            // S246: parse stid out of WeatherSource ("njwxnet+forecast NJ09" → dataSource="njwxnet+forecast", stid="NJ09")
            const sourceParts = rawSource ? rawSource.split(' ') : [];
            const dataSource  = sourceParts[0] || null;
            const stid        = sourceParts[1] || null;
            if (feelsLike == null && wind == null && gust == null && humidity == null
                && precipAccum == null && !conditions) return null;
            // Field names match in-memory snapshot from buildWeatherSnapshot — renderer reads same.
            return { feelsLike, wind, gust, humidity, precipAccum, conditions, dataSource, stid };
          })(),
          neshanicNines: row.NeshanicNines || row.neshanicNines || '',
        };
      }).filter(r => r.total > 0);
      try {
        // Only write cache if Sheet returned at least as many rounds as currently cached
        const existingCache = localStorage.getItem(ROUNDS_CACHE_KEY);
        const existingCount = existingCache ? JSON.parse(existingCache).length : 0;
        if (normalized.length >= existingCount) {
          localStorage.setItem(ROUNDS_CACHE_KEY, JSON.stringify(normalized));
          if (sheetTs) localStorage.setItem(ROUNDS_TS_KEY, sheetTs);
        }
      } catch {}
      // 19TH-HOLE-STALE fix: merge any local rounds not yet in Sheet so a freshly
      // submitted round isn't clobbered when refreshRounds overwrites sharedRounds
      // before the Sheet has received it. Dedup by id (both sides are JS numbers).
      const localOnly = getLocalRounds().filter(
        lr => !normalized.some(sr => sr.id === lr.id)
      );
      const merged = localOnly.length > 0 ? [...normalized, ...localOnly] : normalized;
      setSharedRounds(merged);
    } catch {}
  }, [SCORECARD_WEBHOOK]);

  // ── LOAD COURSE ───────────────────────────────────────────
  const loadCourse = (course, silent = false, profileOverride = null) => {
    if (loadInFlight.current) { return; } // FETCHALL-DOUBLE-INVOCATION: block concurrent loads
    loadInFlight.current = true;
    setSelectedCourse(course);
    selectedCourseRef.current = course; // S183: keep ref in sync
    setShowCourseSwitcher(false);
    try { localStorage.setItem('zap_last_course', JSON.stringify(course.id)); } catch {}
    if (silent) { loadInFlight.current = false; return; } // S183: guard stuck-open fix
    if (!silent) {
      logSystemHealth('app_open', course, { courseId: course.id });
      // Silently retry any rounds that didn't sync last time
      retryPendingRounds((roundId) => {
        setSharedRounds(prev => prev.map(r => r.id === roundId ? { ...r, syncStatus: 'synced' } : r));
      });
      // S183: navigation removed from loadCourse — loadCourse is data-only.
      // Navigation on course switch handled by caller (course switcher, cold-start useEffect).
      // S222: clearAllCaches() — complete cache clear on course switch
      clearAllCaches();
      setIsLoading(true);
      setWeatherError(null);
      setWeatherData(null);
      setBadResult(null);
      setWaterDensity(null);
      setLightningData(null);
      setPollenData(null);
      setEtData(null);
      setNjwxData(null);
      setWapiCurrentData(null);
      wapiLoadStartRef.current = null; // S216 F1: reset timeout on each load
    }

    // S174: fetchWeatherCascade replaced by per-variable fetchForecastCascade.
    // Old all-or-nothing GS→VC→OM cascade removed. New cascade lazy-fetches each
    // source on demand; PirateWeather only fires when something else broke.


    const fetchAll = async (crs) => {
      const effectiveProfile = profileOverride || profile;
      try {
        const [forecast, pollen, et, njwx, lightning] = await Promise.all([
          withTimeout(fetchForecastCascade(crs), 25000, 'Forecast cascade'),
          withTimeout(fetchPollen(crs.lat, crs.lng, crs.id), 8000, 'Pollen'),
          withTimeout(fetchET(crs.lat, crs.lng), 8000, 'ET'),
          withTimeout(fetchNJWxNet(crs), 8000, 'NJWxNet'),
          withTimeout(fetchLightningData(crs.lat, crs.lng), 8000, 'Lightning'),
        ]);
        // Open-Meteo data is exposed via cascade._omFull (no separate fetch needed)
        const om = forecast?._omFull || null;

        // Soil moisture: NJ76 (Duke Farms) covers Green Knoll + Neshanic only. All others use Open-Meteo.
        // Computed here so it's in scope for both the OSM background callback and the main BAD call below.
        const _njwxSoilStid = (crs.id === 'green-knoll' || crs.id === 'neshanic-valley' || crs.id === 'fox-hollow') ? 'NJ76' : null; // S243: fox-hollow added (NJ76 assigned)
        const _njwxSoilObs = _njwxSoilStid && njwx?.success ? njwx[_njwxSoilStid] : null;
        const _soilMoisturePct = _njwxSoilObs?.soilMoisture != null
          ? Math.round(_njwxSoilObs.soilMoisture * 10) / 10
          : (om?.soilMoisturePct ?? null);

        // OSM water density — non-blocking, loads in background after page renders
        // Does not affect Go/No-Go or forecast display — only Bug Index multiplier
        withTimeout(fetchOSMWaterDensity(crs.lat, crs.lng), 8000, 'OSM')
          .then(osm => {
            if (osm?.multiplier != null) {
              setWaterDensity(osm);
              setBadResult(prev => prev ? runBADEngine(weatherWithRealtime, crs, effectiveProfile, osm, et, _soilMoisturePct) : prev);
            }
            if (osm?.error) {
              logSystemHealth('overpass_fail', crs, { error: osm.error });
            }
          })
          .catch(err => {
            logSystemHealth('overpass_fail', crs, { error: err?.message || 'timeout' });
          });

        // ── S50: Extend forecast to 7 days using Open-Meteo beyond 48h ──
        // GribStream and Weatherbit cap at 48h. Meteoblue P1 for 0-48h (restored S206).
        // Open-Meteo: free, no key, no rate limit, returns 168h (7 days).
        // This fills calendar days 3-7 which would otherwise show UNKNOWN.
        // forecast_snap.py now captures all 7-day sources for future validation.
        if (om?.success && om?.hourly?.length > 0 && forecast?.hourly?.length > 0) {
          const cutoffMs = Date.now() + (48 * 3600 * 1000);
          const existingTimes = new Set(forecast.hourly.map(h => new Date(h.time).getTime()));
          const extension = om.hourly
            .filter(h => new Date(h.time).getTime() > cutoffMs)
            .filter(h => !existingTimes.has(new Date(h.time).getTime()))
            .map(h => ({ ...h, source: 'Open-Meteo (48h+ calendar)' }));
          if (extension.length > 0) {
            forecast.hourly = [...forecast.hourly, ...extension];
          }
        }

        // P1 = NJWxNet, P2 = WeatherAPI(temp/feels) + VC(wind/gust). Architects: WeatherAPI direct P1.
        // P2 wind: PirateWeather when NJWxNet wind = 0 (broken sensor)
        let weatherWithRealtime;
        if (njwx?.success) {
          // p1_recovered: NJWxNet is back after a P2 cycle
          if (_p2Active[crs.id]) {
            logSystemHealth('p1_recovered', crs, {
              source: 'NJWxNet',
              p2WasActive: _p2Active[crs.id].source,
              p2Since: _p2Active[crs.id].since,
            });
            delete _p2Active[crs.id];
          }
          weatherWithRealtime = applyNJWxNetOverride(forecast, njwx);
          // API-9: NJ09 wind sensor permanently unreliable (reads ~0.39mph when reality is 9-13mph)
          // Always override wind+gust with PirateWeather for Pebble Creek and Hominy Hill
          if (crs.id === 'pebble-creek' || crs.id === 'hominy-hill' || crs.id === 'shark-river') {
            try {
              const pw = await withTimeout(fetchPirateWeather(crs), 6000, 'PirateWeather NJ09 override');
              incrementCallCount('pirateweather', crs);
              if (pw?.success && pw.current) {
                const pwWind = pw.current.windSpeedNum ?? 0;
                const pwGust = pw.current.windGust ?? 0;
                weatherWithRealtime = {
                  ...weatherWithRealtime,
                  current: {
                    ...weatherWithRealtime.current,
                    windSpeedNum: pwWind,
                    windSpeed: `${pwWind} mph`,
                    windGust: pwGust,
                    feelsLike: calcFeelsLike(
                      weatherWithRealtime.current.temp ?? 65,
                      weatherWithRealtime.current.humidity ?? 50,
                      pwWind
                    ),
                    _windSource: 'PirateWeather (NJ09 sensor excluded)',
                  },
                  _rtSources: {
                    ...weatherWithRealtime._rtSources,
                    wind: 'PirateWeather (NJ09 excluded)',
                    gust: 'PirateWeather (NJ09 excluded)',
                  },
                };
                console.log(`ZAP API-9: NJ09 wind excluded for ${crs.name}, using PirateWeather ${pwWind}mph`);
                incrementCallCount('nj09_excluded', crs);
              }
            } catch (e) {
              console.warn('ZAP API-9 PirateWeather fallback failed:', e.message);
            }
          }
          // Per-variable P2: cascade if NJWxNet wind is null.
          // B14 fix S191: mirrors fetchRealtimeOnly logic (B1 fix) — initial full load was
          // only checking for null, missing the stuck-at-zero sensor case. User could see
          // 0 mph for up to 5 minutes until the refresh cycle caught it.
          const curWindRaw = weatherWithRealtime?.current?.windSpeedRaw ?? weatherWithRealtime?.current?.windSpeedNum ?? null;
          if (curWindRaw === null) {
            try {
              const pw = await withTimeout(fetchPirateWeather(crs), 6000, 'PirateWeather P2 wind');
              incrementCallCount('pirateweather', crs);
              if (pw?.success && pw.current) {
                const pwWind = pw.current.windSpeedNum ?? 0;
                const pwGust = pw.current.windGust ?? 0;
                if (pwWind > 0 || pwGust > 0) {
                  logSystemHealth('p2_activated', crs, { variable: 'wind_gust', p1: 'NJWxNet', p2: 'PirateWeather', reason: 'njwxnet_wind_null', pwWind, p1Error: `NJWxNet wind=${curWindRaw}` });
                  weatherWithRealtime = {
                    ...weatherWithRealtime,
                    current: {
                      ...weatherWithRealtime.current,
                      windSpeedNum: pwWind,
                      windSpeed: `${pwWind} mph`,
                      windGust: pwGust,
                      feelsLike: calcFeelsLike(
                        weatherWithRealtime.current.temp ?? 65,
                        weatherWithRealtime.current.humidity ?? 50,
                        pwWind
                      ),
                      _windSource: 'PirateWeather (NJWxNet sensor fault)',
                    },
                  };
                  console.log(`ZAP P2 wind: NJWxNet null return for ${crs.name}, using PirateWeather ${pwWind}mph`);
                }
              }
            } catch (e) {
              console.warn('ZAP P2 wind fallback failed:', e.message);
            }
          }
        } else {
          // NJWxNet failed — check whether this is a no-station course (Architects) or a real failure
          const njwxError = njwx?.error || 'Unknown failure';
          const noStation = njwx?.stid === null;

          if (noStation) {
            // Architects: no NJWxNet station — WeatherAPI is P1 direct, skip NJWxNet P2 path entirely
            console.log('ZAP: No NJWxNet station for this course — WeatherAPI as P1 direct');
            logSystemHealth('architects_wapi_p1', crs, { course: crs.name });
            const wapiDirect = await fetchWeatherAPICurrentObs(crs).catch(() => ({ success: false }));
            incrementCallCount('weatherapi', crs);
            if (wapiDirect?.success && wapiDirect.current) {
              const wa = wapiDirect.current;
              weatherWithRealtime = {
                ...forecast,
                current: {
                  ...forecast.current,
                  temp: wa.temp ?? forecast.current?.temp,
                  feelsLike: wa.feelsLike ?? forecast.current?.feelsLike,
                  humidity: wa.humidity ?? forecast.current?.humidity,
                  windSpeedNum: wa.windSpeedNum ?? forecast.current?.windSpeedNum,
                  windSpeed: wa.windSpeed ?? forecast.current?.windSpeed,
                  windGust: wa.windGust ?? forecast.current?.windGust,
                  windDir: wa.windDir || forecast.current?.windDir,
                  precip1hr: wa.precip1hr ?? forecast.current?.precip1hr,
                },
                _rtSources: {
                  temp: 'WeatherAPI', humidity: 'WeatherAPI',
                  wind: 'WeatherAPI', gust: 'WeatherAPI',
                  precip: 'WeatherAPI',
                },
              };
            } else {
              weatherWithRealtime = forecast;
            }
            // S246: set wapiCurrentData so Right Now card sky icon/label renders
            if (wapiDirect?.success) setWapiCurrentData(wapiDirect);
          } else {
            // Real NJWxNet failure — fetch P2 sources and log to spreadsheet
            console.log('ZAP: NJWxNet failed, fetching P2 sources (WeatherAPI + VC)');
            logSystemHealth('p2_activated', crs, {
              variable: 'realtime_all', p1: 'NJWxNet', p2: 'WeatherAPI+VC',
              p1Error: njwxError,
            });
            _p2Active[crs.id] = { source: 'NJWxNet', since: new Date().toISOString() };
            const [wapiCurrent, vcCurrent] = await Promise.all([
              fetchWeatherAPICurrentObs(crs),
              fetchVC(crs),
            ]);
            incrementCallCount('weatherapi', crs);
            incrementCallCount('visual_crossing', crs);
            weatherWithRealtime = applyP2Override(forecast, wapiCurrent, vcCurrent);

            // Log P2 fallback to Google Sheet
            logSystemHealth('realtime_p2', crs, {
              p1Failed: 'NJWxNet',
              p1Error: njwxError,
              p2Temp: wapiCurrent?.success ? 'WeatherAPI' : 'FAILED',
              p2Wind: vcCurrent?.success ? 'VC' : 'FAILED',
              wapiTemp: wapiCurrent?.current?.temp,
              wapiFeels: wapiCurrent?.current?.feelsLike,
              vcWind: vcCurrent?.current?.windSpeedNum,
              vcGust: vcCurrent?.current?.windGust,
            });
          }
        } // end NJWxNet failed else
        // UI-17: Patch sunset from Open-Meteo if Meteoblue (P1) didn't provide it
        if (om?.success) {
          if (!weatherWithRealtime.sunsetToday && om.sunsetToday) weatherWithRealtime = { ...weatherWithRealtime, sunsetToday: om.sunsetToday };
          if (!weatherWithRealtime.sunsetTomorrow && om.sunsetTomorrow) weatherWithRealtime = { ...weatherWithRealtime, sunsetTomorrow: om.sunsetTomorrow };
          if (!weatherWithRealtime.sunriseToday && om.sunriseToday) weatherWithRealtime = { ...weatherWithRealtime, sunriseToday: om.sunriseToday };
          // Patch vcDailyAll from om so days 3-7 have sunset data
          if ((!weatherWithRealtime.vcDailyAll || weatherWithRealtime.vcDailyAll.length === 0) && om.vcDailyAll?.length > 0) {
            weatherWithRealtime = { ...weatherWithRealtime, vcDailyAll: om.vcDailyAll };
          }
        }
        setWeatherData(weatherWithRealtime);
        setPollenData(pollen);
        setEtData(et);
        setNjwxData(njwx);
        setLightningData(lightning);
        // Fetch WeatherAPI current obs for R1 conditions icon — real-time conditions text, slot 28
        wapiLoadStartRef.current = Date.now(); // S216 F1: start 3s timeout clock
        fetchWeatherAPICurrentObs(crs).then(w => {
          setWapiCurrentData(w?.success ? w : null);
          if (!w?.success) logSystemHealth('wapi_current_fail', crs, { error: w?.error || 'unknown' }); // S216 F7
        }).catch(err => {
          logSystemHealth('wapi_current_fail', crs, { error: err?.message || 'unknown' }); // S216 F7
        });
        setLastRefreshTime(new Date());

        // Use existing waterDensity state (or fallback) — OSM loads in background and updates BAD when ready
        const currentOsm = waterDensity || { featureCount: 0, multiplier: 1, label: 'Loading...' };
        const bad = runBADEngine(weatherWithRealtime, crs, effectiveProfile, currentOsm, et, _soilMoisturePct);
        setBadResult(bad);
        setIsLoading(false);
        loadInFlight.current = false; // FETCHALL guard release — success path

        // S226: Silent P1 retry — if any forecast variable fell to P2 this load,
        // schedule a background reload after 20s to attempt P1 recovery.
        // By then, VC cold start will have resolved. One shot, no loop.
        // Uses crs (in scope) so it reloads the same course regardless of nav.
        if (_forecastP2[crs.id] && !_forecastP2[crs.id + '_retried']) {
          _forecastP2[crs.id + '_retried'] = true;
          delete _forecastP2[crs.id];
          logSystemHealth('silent_retry_scheduled', crs, { delayMs: 20000 });
          setTimeout(() => {
            loadCourse(crs, false); // S243: removed delete of _retried flag — must persist for session to prevent loop
          }, 20000);
        }
      } catch (err) {
        console.error('ZAP fetchAll error:', err);
        setWeatherError('Weather data unavailable. Check connection.');
        setIsLoading(false);
        loadInFlight.current = false; // FETCHALL guard release — error path
      }
    };

    if (!silent) fetchAll(course);;
  };

  // B-8: Cold-start fix — if course restored from localStorage but weatherData is null, fire loadCourse.
  // Without this, app opens to Home screen with course shown but no weather data loaded.
  // S193: guard against overwriting ?admin=recover screen on cold-start.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin') === 'recover') return;
    if (selectedCourse && !weatherData) {
        setScreen('home'); // S183: navigate home on cold-start
      setScreenHistory(['home']);
      loadCourse(selectedCourse, false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // S183: removed loadInFlight reset-on-course-change useEffect — was causing double-fire.
  // Guard releases correctly in silent path (5767), fetchAll success (6005), and error (6010).
  // Course switching still works — setSelectedCourse fires immediately inside loadCourse.

  // S101: Global 15-min auto-refresh KILLED — burns Meteoblue 500/day limit.
  // Realtime refresh: every 5 min when on Day of Play screen, handled here in parent
  // so setWeatherData doesn't remount OnCourseScreen (which would reset iemLoading).
  // Manual refresh via course header time tap. Full load on course switch/app open.
  useEffect(() => {
    if (screen !== 'dayofplay' || !selectedCourse) return;
    // DD-LOAD: if no weather data, fire silent full load
    if (!weatherData) { loadCourse(selectedCourse, true); return; }
    const refreshRealtime = async () => {
      try {
        const rt = await fetchRealtimeOnly(selectedCourse);
        setWeatherData(prev => {
          if (!prev) return prev;
          const patched = {
            ...prev,
            current: {
              ...prev.current,
              ...(rt.temp         != null && { temp: rt.temp }),
              ...(rt.humidity     != null && { humidity: rt.humidity }),
              ...(rt.windSpeedNum != null && { windSpeedNum: rt.windSpeedNum, windSpeed: rt.windSpeed }),
              ...(rt.windGust     != null && { windGust: rt.windGust }),
              ...(rt.feelsLike    != null && { feelsLike: rt.feelsLike }),
              ...(rt.precip1hr    != null && { precip1hr: rt.precip1hr }),
              _realtimeSources: rt._sources,
              _realtimeRefreshedAt: new Date(),
            },
          };
          return patched;
        });
      } catch (e) { console.warn('ZAP: Realtime refresh failed:', e.message); }
    };
    refreshRealtime();
    const rtInterval = setInterval(() => {
      // S181: Visibility guard — skip polling when app backgrounded (PWA closed,
      // phone locked, tab not active). Saves ~70% NJWxNet calls during a round
      // when phone is in pocket. User returning to app triggers immediate refresh
      // via the visibilitychange listener below.
      if (typeof document !== 'undefined' && document.hidden) return;
      refreshRealtime();
    }, 5 * 60 * 1000);
    // Immediate refresh on return-to-foreground so user sees fresh data on resume.
    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) refreshRealtime();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(rtInterval);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, selectedCourse, realtimeRefreshTick]);


  // ── SPOND EVENTS ───────────────────────────────────────────
  const [spondEvents, setSpondEvents] = useState([]);
  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(SPOND_EVENTS_URL, {
          headers: { 'Authorization': `token ${SPOND_GH_TOKEN}`, 'Accept': 'application/vnd.github.v3+json' }
        });
        if (r.ok) {
          const meta = await r.json();
          const d = JSON.parse(atob(meta.content));
          setSpondEvents(d.events || []);
        }
      } catch {}
    };
    load();
  }, []);

  // ── WINDOW STATUS — uses canonical getWindowStatus ─────────
  const windowStatus = (hrs) => {
    const result = getWindowStatus(hrs, profile);
    return result.status;
  };

  const statusStyle = (status) => {
    const s = STATUS_COLORS[status] || STATUS_COLORS['UNKNOWN'];
    return { bg: s.bg, color: s.color, border: s.border, text: s.text };
  };
  // S95: Trigger indicator symbols matching S84 max-case card reference
  // eslint-disable-next-line no-unused-vars
  const windowReason = (hrs) => {
    if (!hrs || !hrs.length) return null;
    const maxWind = Math.max(...hrs.map(h => h.windSpeedNum || 0));
    const minFeels = Math.min(...hrs.map(h => h.feelsLike != null ? h.feelsLike : (h.temp || 999)));
    const maxFeels = Math.max(...hrs.map(h => h.feelsLike != null ? h.feelsLike : (h.temp || 0)));
    const maxPrecip = Math.max(...hrs.map(h => h.precipChance || 0));
    const condStrings = hrs.map(h => (h.conditions || h.shortForecast || '').toLowerCase());
    const hasNoGoWx = condStrings.some(c => /thunder|lightning|heavy rain|freezing rain|sleet|snow|blizzard/.test(c) && !/blowing snow|drifting snow|dust storm/.test(c));
    const hasCautionWx = condStrings.some(c => /\brain\b|shower|drizzle|\bmist\b|\bfog\b|\bhaze\b/.test(c));
    const reasons = [];
    if (hasNoGoWx) reasons.push('Severe weather');
    else if (hasCautionWx) reasons.push('Rain expected');
    if (maxWind > WIND_NOGO) reasons.push(`Wind ${maxWind} mph — no-go`);
    else if (maxWind > WIND_STR_CAU) reasons.push(`Wind ${maxWind} mph — strong`);
    else if (maxWind > WIND_CAUTION) reasons.push(`Wind ${maxWind} mph — breezy`);
    if (maxPrecip > PRECIP_NOGO) reasons.push(`Rain ${maxPrecip}% — no-go`);
    else if (maxPrecip > PRECIP_STR_CAU) reasons.push(`Rain ${maxPrecip}% — strong caution`);
    else if (maxPrecip > PRECIP_CAUTION) reasons.push(`Rain ${maxPrecip}%`);
    if (minFeels < profile.minTemp) reasons.push(`Feels like ${Math.round(minFeels)}°F — below your ${profile.minTemp}°F limit`);
    if (maxFeels > profile.maxTemp) reasons.push(`Feels like ${Math.round(maxFeels)}°F — above your ${profile.maxTemp}°F limit`);
    return reasons.length ? reasons.join(' · ') : null;
  };

  // ── WINDOW SUMMARY ─────────────────────────────────────────
  const windowSummary = (hrs) => {
    if (!hrs || !hrs.length) return null;
    const temps = hrs.map(h => h.temp || 0);
    const feelsLikes = hrs.map(h => h.feelsLike != null ? h.feelsLike : (h.temp || 0));
    // Dominant condition: pick the "worst" (most impactful) condition across hours
    // Priority: thunder > snow > rain > drizzle > fog > cloudy > clear
    const condPriority = (c) => {
      const cl = (c || '').toLowerCase();
      if (/thunder|lightning|tstorm/.test(cl)) return 8;
      if (/freezing|sleet|ice/.test(cl)) return 7;
      if (/heavy rain|downpour/.test(cl)) return 6;
      if (/rain shower/.test(cl)) return 5;
      if (/snow/.test(cl)) return 5;
      if (/drizzle|light rain/.test(cl)) return 4;
      if (/rain/.test(cl)) return 5;
      if (/fog|mist|haze/.test(cl)) return 3;
      if (/overcast/.test(cl)) return 2;
      if (/cloudy|clouds/.test(cl)) return 1;
      return 0;
    };
    let dominantCondition = 'Clear';
    let maxPri = -1;
    for (const h of hrs) {
      const cond = h.conditions || h.shortForecast || 'Clear';
      const pri = condPriority(cond);
      if (pri > maxPri) { maxPri = pri; dominantCondition = cond; }
    }
    // isDaytime: true if any hour in window is daytime
    const isDaytime = hrs.some(h => h.isDaytime !== false);
    return {
      high: Math.max(...temps),
      low: Math.min(...temps),
      maxWind: Math.max(...hrs.map(h => h.windSpeedNum || 0)),
      maxGust: Math.max(...hrs.map(h => h.windGust || 0)),
      maxPrecip: Math.max(...hrs.map(h => h.precipChance || 0)),
      totalPrecip: hrs.reduce((s, h) => s + (h.precip || 0), 0),
      minFeelsLike: Math.min(...feelsLikes),
      maxFeelsLike: Math.max(...feelsLikes),
      maxUV: Math.max(...hrs.map(h => h.uvIndex || 0)),
      dominantCondition,
      isDaytime,
    };
  };

  // ── BUILD 7-DAY CALENDAR DATA ──────────────────────────────
  const buildCalendarDays = React.useCallback(() => {
    if (!weatherData?.hourly) return [];
    const now = new Date();
    const days = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(now);
      date.setDate(date.getDate() + d);
      const dayLabel = d === 0 ? 'Today' : d === 1 ? 'Tomorrow' :
        date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const dateStr = date.toLocaleDateString('en-CA');

      // AM: 6–12, PM: 12–20
      const mornHrs = weatherData.hourly.filter(h => {
        const hDate = new Date(h.time || h.dt * 1000);
        return hDate.toLocaleDateString('en-CA') === dateStr &&
          hDate.getHours() >= 6 && hDate.getHours() < 12;
      });
      const aftnHrs = weatherData.hourly.filter(h => {
        const hDate = new Date(h.time || h.dt * 1000);
        return hDate.toLocaleDateString('en-CA') === dateStr &&
          hDate.getHours() >= 12 && hDate.getHours() < 20;
      });

      // Mark past windows
      const mornPast = d === 0 && now.getHours() >= 12;
      const aftnPast = d === 0 && now.getHours() >= 20;

      days.push({
        d, dateStr, dayLabel,
        mornHrs, aftnHrs,
        mornStatus: mornPast ? 'PAST' : windowStatus(mornHrs),
        aftnStatus: aftnPast ? 'PAST' : windowStatus(aftnHrs),
        mornSummary: windowSummary(mornHrs),
        aftnSummary: windowSummary(aftnHrs),
        mornReason: windowReason(mornHrs),
        aftnReason: windowReason(aftnHrs),
      });
    }
    return days;
  }, [weatherData, profile]);

  // ── SORTED COURSES ─────────────────────────────────────────
  const sortedCourses = [...COURSES].sort((a, b) => a.name.localeCompare(b.name));

  // ── AUTH GATE ──────────────────────────────────────────────
  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', background: COLOR_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: COLOR_CARD, borderRadius: 16, padding: 32, maxWidth: 320, width: '100%', textAlign: 'center', border: `1px solid ${COLOR_DARK_GREEN}` }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: COLOR_GREEN, marginBottom: 8 }}>ZAP</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: COLOR_WHITE, marginBottom: 4 }}>ZAP</div>
          <div style={{ fontSize: 13, color: COLOR_MUTED, marginBottom: 24 }}>Zur's Activity Planner</div>
          <input
            type="password"
            id="zap-password"
            name="zap-password"
            autoComplete="current-password"
            placeholder="Password"
            value={authInput}
            onChange={e => { setAuthInput(e.target.value); setAuthError(''); }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                const val = authInput.trim().toLowerCase();
                if (val) {
                  const admin = true;
                  setAuthed(true);
                  setIsAdmin(admin);
                  try { localStorage.setItem('zap_authed', 'true'); localStorage.setItem('zap_admin', String(admin)); } catch {}
                } else {
                  setAuthError('Wrong password');
                  setAuthInput('');
                }
              }
            }}
            style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${authError ? '#F87171' : COLOR_DARK_GREEN}`, background: COLOR_BG, color: COLOR_WHITE, fontSize: 16, marginBottom: 8, boxSizing: 'border-box', outline: 'none' }}
          />
          {authError && <div style={{ color: '#F87171', fontSize: 13, marginBottom: 8 }}>{authError}</div>}
          <button
            onClick={() => {
              const val = authInput.trim().toLowerCase();
              if (val) {
                const admin = true;
                setAuthed(true);
                setIsAdmin(admin);
                try { localStorage.setItem('zap_authed', 'true'); localStorage.setItem('zap_admin', String(admin)); } catch {}
              } else {
                setAuthError('Wrong password');
                setAuthInput('');
              }
            }}
            style={{ ...BTN_LARGE, width: '100%', justifyContent: 'center', background: COLOR_GREEN_DARK, color: COLOR_WHITE }}
          >
            Enter
          </button>
        </div>
      </div>
    );
  }

  // ── COURSE SELECTOR (first launch + switcher) ──────────────
  if (!selectedCourse || showCourseSwitcher) {
    const isSwitch = !!selectedCourse && showCourseSwitcher;
    return (
      <div style={{ minHeight: '100vh', background: COLOR_BG, color: COLOR_WHITE }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div style={{ background: COLOR_HEADER, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>
          {isSwitch && (
            <button onClick={() => setShowCourseSwitcher(false)} style={{ ...BTN_BASE, padding: '8px 12px', background: 'transparent', color: COLOR_MUTED, fontSize: 20 }}>←</button>
          )}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_WHITE }}>
              {isSwitch ? 'Switch Course' : 'Select a Course'}
            </div>
            <div style={{ fontSize: 12, color: COLOR_MUTED }}>Jersey Circuit — 12 courses</div>
          </div>
        </div>
        <div style={{ padding: '8px 0' }}>
          {['Somerset', 'Hunterdon', 'Monmouth', 'Warren'].map(county => {
            const countyCourses = sortedCourses.filter(c => c.county === county && c.id !== 'dr-z-test');
            if (countyCourses.length === 0) return null;
            return (
              <div key={county}>
                <div style={{ padding: '12px 20px 6px', fontSize: 11, fontWeight: 700, color: COLOR_GOLD, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{county}</div>
                {countyCourses.map(course => (
                  <button
                    key={course.id}
                    onClick={() => { setScreen('home'); setScreenHistory(['home']); loadCourse(course); }}
                    style={{
                      width: '100%', textAlign: 'left', background: selectedCourse?.id === course.id ? COLOR_DARK_GREEN : 'transparent',
                      border: 'none', borderBottom: `1px solid ${COLOR_DARK_GREEN}`,
                      padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                  >
                    <span style={{ fontSize: 16, fontWeight: 600, color: COLOR_WHITE }}>{course.name}</span>
                    {selectedCourse?.id === course.id && <span style={{ color: COLOR_GREEN, fontSize: 18 }}>✓</span>}
                  </button>
                ))}
              </div>
            );
          })}
          {/* Dr. Z's Test Course — pinned to bottom with divider */}
          {(() => {
            const testCourse = sortedCourses.find(c => c.id === 'dr-z-test');
            if (!testCourse) return null;
            return (
              <div>
                <div style={{ borderTop: `2px solid ${COLOR_DARK_GREEN}`, margin: '8px 0' }} />
                <button
                  onClick={() => { setScreen('home'); setScreenHistory(['home']); loadCourse(testCourse); }}
                  style={{
                    width: '100%', textAlign: 'left', background: selectedCourse?.id === testCourse.id ? COLOR_DARK_GREEN : 'transparent',
                    border: 'none', borderBottom: `1px solid ${COLOR_DARK_GREEN}`,
                    padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: 16, fontWeight: 600, color: COLOR_MUTED }}>{testCourse.name}</span>
                  {selectedCourse?.id === testCourse.id && <span style={{ color: COLOR_GREEN, fontSize: 18 }}>✓</span>}
                </button>
              </div>
            );
          })()}
        </div>
        </div>
      </div>
    );
  }

  // ── INNER SCREEN COMPONENTS ────────────────────────────────
  const Header = ({ title, showBack, onBack }) => (
    <div style={{ background: COLOR_HEADER, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${COLOR_DARK_GREEN}`, position: '-webkit-sticky', WebkitPosition: 'sticky', top: 0, zIndex: 100, transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)' }}>
      {showBack && (
        <button onClick={onBack || goBack} style={{ ...BTN_BASE, padding: '8px 12px', background: 'transparent', color: COLOR_MUTED, fontSize: 20, minWidth: 44 }}>←</button>
      )}
      <div style={{ flex: 1, fontSize: 17, fontWeight: 700, color: COLOR_WHITE }}>{title}</div>
      {screenHistory.length > 2 && (
        <button onClick={() => { setScreenHistory(['home']); setScreen('home'); setTimeout(() => scrollToTop(), 50); }} style={{ ...BTN_BASE, padding: '6px 12px', background: COLOR_DARK_GREEN, color: COLOR_WHITE, fontSize: 14, fontWeight: 700, borderRadius: 8 }}>Home</button>
      )}
    </div>
  );

// ── COURSE DIRECTORY (merged CourseDetail + 19th Hole per S98) ────────────
  const CourseDirectory = () => {
    const course = selectedCourse;
    const [fullscreen, setFullscreen] = React.useState(null);
    if (!course) return (
      <div style={{ padding: '40px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_MUTED, marginBottom: 12 }}>No course selected.</div>
        <button onClick={() => setShowCourseSwitcher(true)} style={{ ...BTN_LARGE, background: COLOR_GREEN_DARK, justifyContent: 'center', color: COLOR_WHITE }}>Select a Course</button>
      </div>
    );

    const assets = COURSE_ASSETS[course.id] || { scorecard: null, map: null, photos: [] };
    const photos = assets.photos || [];
    const multiMaps = assets.maps || null;
    const multiScorecards = assets.scorecards || null;
    const lore = (cfgLore || COURSE_LORE)[course.id];

    if (fullscreen) return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.97)', zIndex: 9999, display: 'flex', flexDirection: 'column' }}
        onClick={() => setFullscreen(null)}>
        <div style={{ padding: '12px 16px', fontSize: 13, color: COLOR_MUTED, textAlign: 'center' }}>{fullscreen.label} · Tap to close</div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <img src={fullscreen.src} alt={fullscreen.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
        </div>
      </div>
    );

    const SectionHeader = ({ label }) => (
      <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginTop: 20, marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>{label}</div>
    );

    const ACTION_ICONS = {
      MAP:      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
      SCORE:    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/><line x1="9" y1="15" x2="12" y2="15"/></svg>,
      FOOD:     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 11a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 0h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 7.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 14.92z"/></svg>,
      CALL:     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 11a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 0h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 7.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 14.92z"/></svg>,
      WEB:      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
      DIRECTIONS: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>,
    };
    const ActionRow = ({ tag, title, sub, href, onClick, external }) => {
      const icon = ACTION_ICONS[tag] || null;
      const isInteractive = !!(href || onClick);
      const inner = (
        <>
          {icon && <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{icon}</span>}
          <span style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_WHITE }}>{title}</div>
            {sub && <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, marginTop: 2 }}>{sub}</div>}
          </span>
          {isInteractive && <span style={{ fontSize: 18, color: COLOR_MUTED }}>›</span>}
        </>
      );
      const style = { display: 'flex', alignItems: 'center', gap: 12, background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, padding: '14px 16px', marginBottom: 10, textDecoration: 'none', cursor: isInteractive ? 'pointer' : 'default', width: '100%', boxSizing: 'border-box', textAlign: 'left' };
      if (href) return <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} style={style}>{inner}</a>;
      if (onClick) return <button onClick={onClick} style={style}>{inner}</button>;
      return <div style={style}>{inner}</div>; // S175 audit: informational mode (no tap)
    };

    return (
      <div style={{ padding: '12px 16px 60px', maxWidth: 480, margin: '0 auto', boxSizing: 'border-box', overflowX: 'hidden' }}>
        {/* Course header */}
        <div style={{ paddingBottom: 12, marginBottom: 16, borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: COLOR_WHITE }}>{course.name}</div>
          <div style={{ fontSize: 13, color: COLOR_MUTED, marginTop: 2 }}>{course.town} · Par {course.par} · Elev. {course.elevation}ft</div>
        </div>

        {/* 1. Course Map */}
        {(multiMaps || assets.map) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GOLD, textTransform: 'uppercase', letterSpacing: 1 }}>Course Map</div>
          </div>
        )}
        {multiMaps ? multiMaps.map(m => (
          <div key={m.label} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, marginBottom: 4 }}>{m.label} Nine</div>
            <div style={{ borderRadius: 10, overflow: 'hidden', cursor: 'pointer', border: `1px solid ${COLOR_DARK_GREEN}` }}
              onClick={() => setFullscreen({ src: m.src, label: `${m.label} Course Map` })}>
              <img src={m.src} alt={`${m.label} map`} style={{ width: '100%', maxHeight: 200, objectFit: 'contain', background: '#111', display: 'block' }} />
            </div>
          </div>
        )) : assets.map && (
          <div style={{ marginBottom: 12, borderRadius: 10, overflow: 'hidden', cursor: 'pointer', border: `1px solid ${COLOR_DARK_GREEN}` }}
            onClick={() => setFullscreen({ src: assets.map, label: 'Course Map' })}>
            <img src={assets.map} alt="Course map" style={{ width: '100%', maxHeight: 200, objectFit: 'contain', background: '#111', display: 'block' }} />
            <div style={{ padding: '6px 12px', background: COLOR_CARD, fontSize: 11, color: COLOR_MUTED }}>Tap to view fullscreen</div>
          </div>
        )}

        {/* 2. Scorecard */}
        {(multiScorecards || assets.scorecard) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/><line x1="9" y1="15" x2="12" y2="15"/></svg>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GOLD, textTransform: 'uppercase', letterSpacing: 1 }}>Scorecard</div>
          </div>
        )}
        {multiScorecards ? multiScorecards.map(s => (
          <div key={s.label} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, marginBottom: 4 }}>{s.label} Scorecard</div>
            <div style={{ borderRadius: 10, overflow: 'hidden', cursor: 'pointer', border: `1px solid ${COLOR_DARK_GREEN}` }}
              onClick={() => setFullscreen({ src: s.src, label: `${s.label} Scorecard` })}>
              <img src={s.src} alt={`${s.label} scorecard`} style={{ width: '100%', maxHeight: 180, objectFit: 'contain', background: '#111', display: 'block' }} />
            </div>
          </div>
        )) : assets.scorecard && (
          <div style={{ marginBottom: 12, borderRadius: 10, overflow: 'hidden', cursor: 'pointer', border: `1px solid ${COLOR_DARK_GREEN}` }}
            onClick={() => setFullscreen({ src: assets.scorecard, label: 'Scorecard' })}>
            <img src={assets.scorecard} alt="Scorecard" style={{ width: '100%', maxHeight: 200, objectFit: 'contain', background: '#111', display: 'block' }} />
            <div style={{ padding: '6px 12px', background: COLOR_CARD, fontSize: 11, color: COLOR_MUTED }}>Tap to view fullscreen</div>
          </div>
        )}

        {/* 3. Order from Snack Bar — S175 audit: informational mode if value is not a phone number */}
        {course.snackBar && (/^[\d\s()\-+x]+$/.test(course.snackBar)
          ? <ActionRow tag="FOOD" title="Order from Snack Bar" sub={course.snackBar} href={`tel:${course.snackBar.replace(/[^0-9+x]/g, '')}`} />
          : <ActionRow tag="FOOD" title="Snack Bar" sub={course.snackBar} />
        )}

        {/* 4. Call Pro Shop */}
        {course.proShop && <ActionRow tag="CALL" title="Call Pro Shop" sub={course.proShop} href={`tel:${course.proShop.replace(/[^0-9]/g, '')}`} />}

        {/* 5. Course Website */}
        {course.website && <ActionRow tag="WEB" title="Course Website" sub={course.website.replace(/^https?:\/\//, '').replace(/\/$/, '')} href={course.website} external />}
        {course.teeTime && <ActionRow tag="TEE" title="Book Tee Time" sub={course.teeTime.replace(/^https?:\/\//, '').replace(/\/$/, '').split('/')[0]} href={course.teeTime} external />}

        {/* 6. Directions — S175 audit: prefer street address over GPS for clubhouse-accurate destination */}
        <ActionRow tag="MAP" title="Directions" sub="Opens Google Maps" href={`https://www.google.com/maps/dir/?api=1&destination=${course.parkingLat},${course.parkingLng}`} external />

        {/* 7. Course Lore */}
        {lore && (
          <div style={{ background: COLOR_CARD, borderRadius: 12, padding: '14px 16px', marginBottom: 12, border: '1px solid rgba(255,213,79,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 24 }}>{lore.emoji}</span>
              <div>
                <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Course Lore</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_WHITE, marginTop: 2 }}>{lore.headline}</div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: '#c8d8c0', lineHeight: 1.6 }}>{lore.body}</div>
          </div>
        )}

        {/* 8. Course Photos — hidden if none */}
        {photos.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Photos</div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {photos.map((url, i) => (
                <div key={i} style={{ flexShrink: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: `1px solid ${COLOR_DARK_GREEN}` }}
                  onClick={() => setFullscreen({ src: url, label: `Photo ${i+1}` })}>
                  <img src={url} alt={`Photo ${i+1}`} style={{ width: 130, height: 90, objectFit: 'cover', display: 'block' }} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

    // ---- Profile Screen ----
  const ProfileScreen = () => {
    const [localProfile, setLocalProfile] = useState({ ...profile });
    const [settingsTab, setSettingsTab] = useState('settings');
    const [iframeUrl, setIframeUrl] = useState(null);
    const [showQuickRef, setShowQuickRef] = useState(false);
  const [showAdminDocs, setShowAdminDocs] = useState(false);
  const [adminDocsUnlocked, setAdminDocsUnlocked] = useState(false);
  const [adminDocsInput, setAdminDocsInput] = useState('');
    const [bpOpen, setBpOpen] = React.useState(false);
    const [expandedDocPhoto, setExpandedDocPhoto] = React.useState(null);

    return (
      <div style={{ padding: '16px', maxWidth: 480, margin: '0 auto' }}>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[
            { id: 'settings', label: 'My Settings' },
            { id: 'howto',    label: 'Videos & Docs' },
            { id: 'help',     label: 'Help' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setSettingsTab(tab.id)}
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `2px solid ${settingsTab === tab.id ? COLOR_GREEN : COLOR_DARK_GREEN}`,
                background: settingsTab === tab.id ? COLOR_GREEN_DARK : COLOR_CARD,
                color: settingsTab === tab.id ? COLOR_WHITE : COLOR_MUTED,
                fontWeight: settingsTab === tab.id ? 800 : 400, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB 1: MY SETTINGS ── */}
        {settingsTab === 'settings' && (
        <div style={{ ...CARD_STYLE, padding: '20px', marginBottom: 12 }}>
          <div style={{ fontSize: '18px', fontWeight: 800, color: COLOR_WHITE, marginBottom: 20, textTransform: 'uppercase', letterSpacing: '1px' }}>My Conditions</div>
          {/* C4: Two tappable numbers + live 5-zone color bar. Caution OUTSIDE limits only. */}
          {(() => {
            const TEMP_MIN = 30;
            const TEMP_MAX = 115;
            const cold = localProfile.minTemp;
            const hot  = localProfile.maxTemp;
            const range = TEMP_MAX - TEMP_MIN;
            // Zone boundaries — caution is OUTSIDE the limits only
            const noGoLow  = cold - 5;
            const noGoHi   = hot  + 5;
            // Pct helper
            const pct = (v) => Math.max(0, Math.min(100, (v - TEMP_MIN) / range * 100));
            // Gradient: red | yellow(5°) | green | yellow(5°) | red
            const grad = `linear-gradient(to right,
              #F87171 0%, #F87171 ${pct(noGoLow)}%,
              #FACC15 ${pct(noGoLow)}%, #FACC15 ${pct(cold)}%,
              #4ADE80 ${pct(cold)}%, #4ADE80 ${pct(hot)}%,
              #FACC15 ${pct(hot)}%, #FACC15 ${pct(noGoHi)}%,
              #F87171 ${pct(noGoHi)}%, #F87171 100%)`;
            // Plain English — updates as numbers change
            // eslint-disable-next-line no-unused-vars
            const plainText = `Below ${noGoLow}°F = No Go  ·  ${noGoLow}–${cold}°F = Caution  ·  ${cold}–${hot}°F = Go  ·  ${hot}–${noGoHi}°F = Caution  ·  Above ${noGoHi}°F = No Go`;
            return (
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_OFF_WHITE, marginBottom: 14 }}>My Feels-Like Temperature Range</div>
                {/* Two tappable number controls — +/- buttons, no keyboard */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  {[
                    { key: 'minTemp', label: 'Feels Like Too Cold', color: '#5b9bd5', val: cold, min: TEMP_MIN, max: hot - 10 },
                    { key: 'maxTemp', label: 'Feels Like Too Hot',  color: '#e57373', val: hot,  min: cold + 10, max: TEMP_MAX },
                  ].map(({ key, label, color, val, min, max }) => (
                    <div key={key} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${color}`, borderRadius: 10, background: COLOR_DARKEST, overflow: 'hidden' }}>
                        <button
                          onClick={() => { const v = val - 1; if (v >= min) setLocalProfile(p => ({ ...p, [key]: v })); }}
                          style={{ width: 36, height: 56, background: 'transparent', border: 'none', color, fontSize: 22, cursor: 'pointer', flexShrink: 0 }}>−</button>
                        <div style={{ flex: 1, fontSize: 28, fontWeight: 800, color, textAlign: 'center' }}>{val}</div>
                        <button
                          onClick={() => { const v = val + 1; if (v <= max) setLocalProfile(p => ({ ...p, [key]: v })); }}
                          style={{ width: 36, height: 56, background: 'transparent', border: 'none', color, fontSize: 22, cursor: 'pointer', flexShrink: 0 }}>+</button>
                      </div>
                      <div style={{ fontSize: '11px', color: COLOR_MUTED, marginTop: 4 }}>°F</div>
                    </div>
                  ))}
                </div>
                {/* Live 5-zone color bar */}
                <div style={{ height: 20, borderRadius: 10, background: grad, marginBottom: 8, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)' }} />
                {/* Plain English zones — stacked for readability */}
                <div style={{ fontSize: '12px', background: COLOR_DARKEST, borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}><StatusIcon status="GO" size={14}/> <span style={{color:'#4ADE80', fontWeight:700}}>GO</span> <span style={{color:COLOR_MUTED}}>{cold}–{hot}°F</span></div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}><StatusIcon status="CAUTION" size={14}/> <span style={{color:'#FACC15', fontWeight:700}}>CAUTION</span> <span style={{color:COLOR_MUTED}}>{noGoLow}–{cold}°F or {hot}–{noGoHi}°F</span></div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}><StatusIcon status="NO-GO" size={14}/> <span style={{color:'#F87171', fontWeight:700}}>NO-GO</span> <span style={{color:COLOR_MUTED}}>below {noGoLow}°F or above {noGoHi}°F</span></div>
                </div>
              </div>
            );
          })()}
          {/* ── PLAYING WINDOW ── */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_OFF_WHITE, marginBottom: 4 }}>My Playing Window</div>
            <div style={{ fontSize: '11px', color: COLOR_MUTED, marginBottom: 10 }}>Go/No-Go uses worst conditions within this window only. Window is capped 8 AM–8 PM — that's the range the hourly forecast view covers.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#5b9bd5', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tee Time</div>
                <input
                  type="time"
                  value={localProfile.playStart || '08:00'}
                  min="08:00"
                  onChange={e => setLocalProfile(p => ({ ...p, playStart: e.target.value < '08:00' ? '08:00' : e.target.value }))}
                  style={{ width: '100%', padding: '8px', background: COLOR_DARKEST, border: '2px solid #5b9bd5', borderRadius: 10, color: COLOR_WHITE, WebkitTextFillColor: '#ffffff', colorScheme: 'dark', fontSize: '18px', fontWeight: 800, textAlign: 'center' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#e57373', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Done By</div>
                <input
                  type="time"
                  value={localProfile.playEnd || '20:00'}
                  max="20:00"
                  onChange={e => setLocalProfile(p => ({ ...p, playEnd: e.target.value > '20:00' ? '20:00' : e.target.value }))}
                  style={{ width: '100%', padding: '8px', background: COLOR_DARKEST, border: '2px solid #e57373', borderRadius: 10, color: COLOR_WHITE, WebkitTextFillColor: '#ffffff', colorScheme: 'dark', fontSize: '18px', fontWeight: 800, textAlign: 'center' }}
                />
              </div>
            </div>
          </div>
          <button onClick={() => {
            const saved = { ...localProfile };
            setProfile(saved);
            try { localStorage.setItem('zap_profile', JSON.stringify(saved)); } catch {}
            loadCourse(selectedCourse, true, saved);
            setProfileSaved(true);
            setTimeout(() => { setProfileSaved(false); goBack(); }, 1000);
          }}
            style={{ ...BTN_LARGE, background: '#1a1a1a', border: `2px solid ${COLOR_GREEN_DARK}`, color: COLOR_GOLD, justifyContent: 'center', fontSize: '18px', fontWeight: 700 }}>
            {profileSaved ? 'Saved!' : 'Save Settings'}
          </button>

          {/* S226: Storage caution note */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 10, padding: '8px 10px', background: 'rgba(250,204,21,0.08)', borderRadius: 6, border: '1px solid rgba(250,204,21,0.2)' }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
            <span style={{ fontSize: 11, color: COLOR_MUTED, lineHeight: 1.5 }}>
              Your settings are saved on this device. If you ever clear Safari data or browser storage, you'll need to re-enter them here.
            </span>
          </div>

          {/* S227: Super User section */}
          <div style={{ marginTop: 24, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: 1, background: '#222' }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Super User</div>
              <div style={{ flex: 1, height: 1, background: '#222' }} />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: '#111', borderRadius: 10, border: '1px solid #1a1a1a' }}>
            <div style={{ flex: 1, marginRight: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OFF_WHITE, marginBottom: 3 }}>🔊 Storm Audio Alert</div>
              <div style={{ fontSize: 11, color: COLOR_MUTED, lineHeight: 1.5 }}>
                When ON — an alarm fires when a storm is detected. Requires: screen unlocked and permanently on, app open on the Storm screen, and volume up. For weather days when you're monitoring conditions. Tap "Got It" to silence until next update, or "Turn Off Audio" to stop all alerts.
              </div>
            </div>
            <button
              onClick={() => setLocalProfile(p => ({ ...p, audioAlertEnabled: !p.audioAlertEnabled }))}
              style={{
                width: 50, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer', flexShrink: 0,
                background: localProfile.audioAlertEnabled ? '#4ADE80' : '#374151',
                position: 'relative', transition: 'background 0.2s'
              }}>
              <div style={{
                position: 'absolute', top: 3, width: 22, height: 22, borderRadius: 11,
                background: '#fff', transition: 'left 0.2s',
                left: localProfile.audioAlertEnabled ? 25 : 3
              }} />
            </button>
          </div>

          {/* Wind info — universal thresholds, not configurable */}

        </div>
        )} {/* end Tab 1 */}

        {/* ── TAB 2: HOW IT WORKS ── */}
        {settingsTab === 'howto' && (
        <div style={{ marginBottom: 12 }}>

          {/* ── OVERVIEW ── */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>🗺️ Overview</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>What ZAP is, why it exists, and how it beats generic weather apps.</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <a href="https://res.cloudinary.com/dovzgljte/image/upload/ZAP_OVERVIEW_INFOGRAPHIC_1_S239.jpg" target="_blank" rel="noopener noreferrer" style={{ flex: 1, borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}` }}>
                <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_200,h_90,c_fill,g_north/ZAP_OVERVIEW_INFOGRAPHIC_1_S239.jpg" alt="ZAP Overview" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
              </a>
              <a href="https://res.cloudinary.com/dovzgljte/image/upload/ZAP_OVERVIEW_INFOGRAPHIC_2_S239.jpg" target="_blank" rel="noopener noreferrer" style={{ flex: 1, borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}` }}>
                <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_200,h_90,c_fill,g_north/ZAP_OVERVIEW_INFOGRAPHIC_2_S239.jpg" alt="ZAP Overview 2" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
              </a>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setIframeUrl('/ZAP_Overview_Combined_S238.html')}
                style={{ flex: 1, background: COLOR_GREEN_DARK, color: COLOR_WHITE, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_GREEN}`, cursor: 'pointer', fontFamily: 'inherit' }}>Read HTML →</button>
              <a href="https://www.dropbox.com/scl/fi/z1blh5f8xoolh6d10clr6/ZAP_OVERVIEW_SLIDES.pdf?rlkey=zrrydc94tiiw02et25o73nzno&st=mny4plba&dl=0" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1e2a10', color: COLOR_GOLD, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: `1px solid ${COLOR_GOLD}` }}>Slides PDF →</a>
              <a href="https://youtu.be/gsKtMy80TFo" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1a1010', color: '#f87171', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #7f1d1d' }}>▶ Video</a>
            </div>
          </div>

          {/* ── STORY ── */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>📖 The Story</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>How ZAP was born — from a blizzard to the Jersey Circuit.</div>
            <a href="https://res.cloudinary.com/dovzgljte/image/upload/ZAP_STORY_S222.jpg" target="_blank" rel="noopener noreferrer" style={{ display: 'block', borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}` }}>
              <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_400,h_90,c_fill,g_north/ZAP_STORY_S222.jpg" alt="ZAP Story" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
            </a>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setIframeUrl('/ZAP_Complete_Story_S204.html')}
                style={{ flex: 1, background: COLOR_GREEN_DARK, color: COLOR_WHITE, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_GREEN}`, cursor: 'pointer', fontFamily: 'inherit' }}>Read HTML →</button>
              <button onClick={() => setIframeUrl('/ZAP_Complete_Story_ECN_S235.html')}
                style={{ flex: 1, background: '#0a1a2a', color: '#60a5fa', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: '1px solid #1e40af', cursor: 'pointer', fontFamily: 'inherit' }}>ECN →</button>
              <a href="https://www.dropbox.com/scl/fi/2fwwm6iwl9jxmkeo3ck8d/ZAP_Story.pdf?rlkey=hzrttwymqhqbifn0kxnzf1i64&st=o6jzc6tu&dl=0" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1e2a10', color: COLOR_GOLD, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: `1px solid ${COLOR_GOLD}` }}>Slides PDF →</a>
              <a href="https://youtu.be/DgcfnK8hqXk" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1a1010', color: '#f87171', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #7f1d1d' }}>▶ Video</a>
            </div>
          </div>

          {/* ── BLUEPRINT ── */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>📐 Blueprint</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>The philosophy-first architecture — 26 chapters, 7 videos.</div>
            <a href="https://res.cloudinary.com/dovzgljte/image/upload/ZAP_BLUEPRINT_S222.jpg" target="_blank" rel="noopener noreferrer" style={{ display: 'block', borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}`, marginBottom: 10 }}>
              <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_400,h_90,c_fill,g_north/ZAP_BLUEPRINT_S222.jpg" alt="ZAP Blueprint" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
            </a>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <button onClick={() => setIframeUrl('/ZAP_Blueprint_S205.html')}
                style={{ flex: 1, background: COLOR_GREEN_DARK, color: COLOR_WHITE, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_GREEN}`, cursor: 'pointer', fontFamily: 'inherit' }}>Read HTML →</button>
              <a href="https://www.dropbox.com/scl/fi/yxr62ybnistqgh63vyikv/ZAP_Blueprint-Slides.pdf?rlkey=546k1xg5lcmpdgahi0oa4hh24&st=nsw3qjk1&dl=0" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1e2a10', color: COLOR_GOLD, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: `1px solid ${COLOR_GOLD}` }}>Slides PDF →</a>
              <a href="https://youtu.be/PU68MjRigfM" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1a1010', color: '#f87171', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #7f1d1d' }}>▶ Video</a>
            </div>
            {(() => {
              const BP_VIDEOS = [
                { label: 'Ch 1–4 · Architecture', url: 'https://youtu.be/J6ICrLh5oIg' },
                { label: 'Ch 5–8 · Data Pipeline', url: 'https://youtu.be/MtC5Ru1UcvA' },
                { label: 'Ch 9–12 · Go/No-Go Engine', url: 'https://youtu.be/_kbe5ICjaOg' },
                { label: 'Ch 13–16 · Lightning & Backend', url: 'https://youtu.be/ip3da75qLt4' },
                { label: 'Ch 17–21 · Reference', url: 'https://youtu.be/ZH3tIP6HpoQ' },
                { label: 'Ch 22–26 · Use Cases', url: 'https://youtu.be/qCnLJkG4hFw' },
              ];
              return (
                <>
                  {bpOpen && BP_VIDEOS.map((v, i) => (
                    <a key={i} href={v.url} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#0d1a0f', borderRadius: 8, padding: '9px 10px', marginBottom: 5, border: `1px solid ${COLOR_DARK_GREEN}`, textDecoration: 'none' }}>
                      <div style={{ width: 22, height: 22, background: '#1a1010', border: '1px solid #7f1d1d', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <div style={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: '8px solid #f87171', marginLeft: 2 }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#e0f0e3', flex: 1 }}>{v.label}</span>
                      <span style={{ fontSize: 13, color: COLOR_GREEN }}>›</span>
                    </a>
                  ))}
                  <button onClick={() => setBpOpen(o => !o)}
                    style={{ width: '100%', padding: '7px', borderRadius: 6, fontSize: 11, color: COLOR_GREEN, background: 'transparent', border: `1px dashed ${COLOR_DARK_GREEN}`, cursor: 'pointer', marginTop: 2 }}>
                    {bpOpen ? '▲ Hide chapter videos' : '▼ Show 6 chapter videos'}
                  </button>
                </>
              );
            })()}
          </div>

          {/* ── BLUEPRINT ADDENDUM ── */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>📐 Blueprint Addendum</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>Engineering change notices — corrections and additions since the Blueprint was published.</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setIframeUrl('/ZAP_Blueprint_ECN_S235.html')}
                style={{ flex: 1, background: COLOR_GREEN_DARK, color: COLOR_WHITE, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_GREEN}`, cursor: 'pointer', fontFamily: 'inherit' }}>Read HTML →</button>
            </div>
          </div>

          {/* ── PHASE 2 CANDIDATES ── */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>🎯 Phase 2 Candidates</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>Future work, studies, and deferred features for Phase 2.</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setIframeUrl('/ZAP_Phase2_Candidates_ECN_S265.html')}
                style={{ flex: 1, background: '#0a1a2a', color: '#60a5fa', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: '1px solid #1e40af', cursor: 'pointer', fontFamily: 'inherit' }}>ECN →</button>
            </div>
          </div>
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>⚙️ Operations</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>Runbook, diagnostics, and troubleshooting guide.</div>
            <a href="https://res.cloudinary.com/dovzgljte/image/upload/ZAP_OPERATIONS_S222.jpg" target="_blank" rel="noopener noreferrer" style={{ display: 'block', borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}` }}>
              <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_400,h_90,c_fill,g_north/ZAP_OPERATIONS_S222.jpg" alt="ZAP Operations" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
            </a>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setIframeUrl('/ZAP_Operations_S221.html')}
                style={{ flex: 1, background: COLOR_GREEN_DARK, color: COLOR_WHITE, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_GREEN}`, cursor: 'pointer', fontFamily: 'inherit' }}>Read HTML →</button>
              <button onClick={() => setIframeUrl('/ZAP_Operations_Troubleshooting_ECN_S265.html')}
                style={{ flex: 1, background: '#0a1a2a', color: '#60a5fa', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: '1px solid #1e40af', cursor: 'pointer', fontFamily: 'inherit' }}>ECN →</button>
              <a href="https://www.dropbox.com/scl/fi/9l5k7ynswucytrlsc9lvt/ZAP_Operations-Slides.pdf?rlkey=h6k2q0whb26p13juumt67mdlo&st=f9h1t8we&dl=0" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1e2a10', color: COLOR_GOLD, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: `1px solid ${COLOR_GOLD}` }}>Slides PDF →</a>
              <a href="https://youtu.be/czAdN9aULsw" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1a1010', color: '#f87171', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #7f1d1d' }}>▶ Video</a>
            </div>
          </div>

          {/* ── GOLF LESSONS ── */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>⛳ Golf Lessons</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>Dr. Z's tips and techniques to improve your game.</div>
            <a href="https://johnzur.com/videos#golf" target="_blank" rel="noopener noreferrer"
              style={{ display: 'block', background: '#1a1010', color: '#f87171', borderRadius: 8, padding: '11px 16px', fontSize: '13px', fontWeight: 700, textDecoration: 'none', textAlign: 'center', border: '1px solid #7f1d1d', boxSizing: 'border-box' }}>
              ▶ Watch Golf Lesson Videos
            </a>
          </div>


          {/* ── TECHNICAL DOCS (LOCKED) ── */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10, border: '1px solid #374151' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#9CA3AF', marginBottom: 6 }}>🔒 Technical Documents</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>API Keys &amp; Credentials. Engineer access only.</div>
            {!adminDocsUnlocked ? (
              <div>
                <input
                  type="password"
                  placeholder="Password"
                  value={adminDocsInput}
                  onChange={e => setAdminDocsInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      if (adminDocsInput.toLowerCase() === 'lion') {
                        setAdminDocsUnlocked(true);
                        setAdminDocsInput('');
                      } else {
                        setAdminDocsInput('');
                      }
                    }
                  }}
                  style={{ width: '100%', background: '#1a2e1c', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', fontSize: '14px', color: COLOR_WHITE, outline: 'none', marginBottom: 8, boxSizing: 'border-box' }}
                />
                <button
                  onClick={() => {
                    if (adminDocsInput.toLowerCase() === 'lion') {
                      setAdminDocsUnlocked(true);
                      setAdminDocsInput('');
                    } else {
                      setAdminDocsInput('');
                    }
                  }}
                  style={{ ...BTN_LARGE, background: '#1f2937', border: '1px solid #374151', color: '#9CA3AF', justifyContent: 'center', fontSize: 14 }}>
                  Unlock →
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button onClick={() => setIframeUrl('/ZAP_Canonical_Docs_Registry.html')}
                  style={{ display: 'block', width: '100%', background: '#1a2e1c', color: COLOR_WHITE, borderRadius: 8, padding: '10px 14px', fontSize: '13px', fontWeight: 600, textDecoration: 'none', border: '1px solid #374151', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                  📚 Canonical Documents Registry →
                </button>
                <button onClick={() => setIframeUrl('/ZAP_API_Key_Master_S218.html')}
                  style={{ display: 'block', width: '100%', background: '#1a2e1c', color: COLOR_WHITE, borderRadius: 8, padding: '10px 14px', fontSize: '13px', fontWeight: 600, textDecoration: 'none', border: '1px solid #374151', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                  🔑 API Keys & Credentials →
                </button>
                <button onClick={() => { setAdminDocsUnlocked(false); }}
                  style={{ background: 'transparent', border: 'none', color: COLOR_MUTED, fontSize: '12px', cursor: 'pointer', textAlign: 'left', padding: '4px 0' }}>
                  🔒 Lock
                </button>
              </div>
            )}
          </div>

        </div>
        )} {/* end Tab 3 */}

        {settingsTab === 'help' && (
        <div style={{ marginBottom: 12 }}>

          {/* How to Use ZAP — combined modal trigger */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 6 }}>📖 How to Use ZAP</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>Game day guide, thresholds, workflow — everything in one place.</div>
            <button onClick={() => setShowQuickRef(true)} style={{ display: 'block', width: '100%', background: COLOR_GREEN_DARK, color: COLOR_WHITE, borderRadius: 8, padding: '12px 16px', fontSize: '14px', fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_GREEN}`, cursor: 'pointer' }}>
              Open Guide →
            </button>
          </div>

          {/* Onboarding */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>🚀 Onboarding</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>Step-by-step setup guide for new Golf Buddies.</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {[
                { id: 'dls158mozgdecfckgchk', alt: 'ZAP Step-by-Step Guide' },
                { id: 'u07bormzfq9jhe4crv2z', alt: 'ZAP Quick-Start Guide' },
                { id: 'ZAP_ONBOARDING_INFOGRAPHIC_3_S241', alt: 'ZAP Precision Weather Guide' },
              ].map(img => (
                <a key={img.id} href={`https://res.cloudinary.com/dovzgljte/image/upload/${img.id}.jpg`} target="_blank" rel="noopener noreferrer"
                  style={{ flex: 1, borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}` }}>
                  <img src={`https://res.cloudinary.com/dovzgljte/image/upload/w_200,h_90,c_fill,g_north/${img.id}.jpg`} alt={img.alt} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                </a>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setIframeUrl('/ZAP_Onboarding_S241.html')}
                style={{ flex: 1, background: COLOR_GREEN_DARK, color: COLOR_WHITE, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_GREEN}`, cursor: 'pointer', fontFamily: 'inherit' }}>Read HTML →</button>
              <a href="https://www.dropbox.com/scl/fi/5a3o3dc05vd49qzmgmzmk/ZAP_ONBOARDING-SLIDE-DECK.pdf?rlkey=aa1ow1u8f1n7bcsrz5tem4o13&st=7ztmwcbt&dl=0" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1e2a10', color: COLOR_GOLD, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: `1px solid ${COLOR_GOLD}` }}>Slides PDF →</a>
              <a href="https://youtu.be/8Ke84EzVxEs" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1a1010', color: '#f87171', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #7f1d1d' }}>▶ Video 1</a>
              <a href="https://youtu.be/RebGgsNFiqU" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1a1010', color: '#f87171', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #7f1d1d' }}>▶ Video 2</a>
              <a href="https://res.cloudinary.com/dovzgljte/image/upload/ZAP_Jersey_Circuit_Map_S265.png" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#0a1a2a', color: '#60a5fa', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #1e40af' }}>Jersey Circuit Map →</a>
            </div>
          </div>

          {/* Golf Rules */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>⛳ ZAP Golf Rules</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>The official NJ Circuit custom rule set — gimmes, placement, and scoring.</div>
            <button onClick={() => setIframeUrl('/ZAP_Golf_Rules_S269.html')}
              style={{ display: 'block', width: '100%', background: COLOR_GREEN_DARK, color: COLOR_WHITE, borderRadius: 8, padding: '12px 16px', fontSize: '14px', fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_GREEN}`, cursor: 'pointer' }}>
              Open Rules →
            </button>
          </div>

          {/* Training Guide */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLOR_GOLD, marginBottom: 4 }}>🎓 Training Guide</div>
            <div style={{ fontSize: '12px', color: COLOR_MUTED, marginBottom: 10 }}>Full ZAP feature walkthrough — every card, every decision.</div>
            <a href="https://res.cloudinary.com/dovzgljte/image/upload/ZAP_TRAINING_INFOGRAPHIC_S217.jpg" target="_blank" rel="noopener noreferrer" style={{ display: 'block', borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}` }}>
              <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_400,h_90,c_fill,g_north/ZAP_TRAINING_INFOGRAPHIC_S217.jpg" alt="ZAP Training" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
            </a>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setIframeUrl('/ZAP_Training_Guide_S215.html')}
                style={{ flex: 1, background: COLOR_GREEN_DARK, color: COLOR_WHITE, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_GREEN}`, cursor: 'pointer', fontFamily: 'inherit' }}>Read HTML →</button>
              <a href="https://www.dropbox.com/scl/fi/hk8ltujnp586x1gm9hq5l/ZAP_Training-Slides.pdf?rlkey=a3bdv4u1bahs8abbdock8gn4i&st=kq6kljzl&dl=0" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1e2a10', color: COLOR_GOLD, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: `1px solid ${COLOR_GOLD}` }}>Slides PDF →</a>
              <a href="https://youtu.be/AGwefKo5sII" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1a1010', color: '#f87171', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #7f1d1d' }}>▶ Video</a>
            </div>
          </div>

          {/* Quick Reference Modal */}
          {showQuickRef && (
            <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#0a1a0a', borderBottom: '2px solid #22C55E', flexShrink: 0 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: COLOR_WHITE }}>📋 Quick Reference Guide</span>
                <button onClick={() => setShowQuickRef(false)} style={{ background: 'transparent', border: 'none', color: COLOR_MUTED, fontSize: 28, cursor: 'pointer', padding: 4 }}>×</button>
              </div>
              <iframe src="/ZAP_Quick_Reference_S211.html" style={{ flex: 1, border: 'none', width: '100%' }} title="ZAP Quick Reference" />
            </div>
          )}


        </div>
        )} {/* end Help tab */}


      {/* ── IFRAME DOC MODAL ── */}
      {iframeUrl && (
        <div style={{ position: 'fixed', inset: 0, background: '#0a1a0a', zIndex: 99999, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#0a1a0a', borderBottom: '2px solid #22C55E', flexShrink: 0 }}>
            <span style={{ color: '#FDE047', fontSize: 13, fontWeight: 700 }}>ZAP Docs</span>
            <button onClick={() => setIframeUrl(null)} style={{ background: '#22C55E', color: '#000', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>✕ Close</button>
          </div>
          <iframe src={iframeUrl} style={{ flex: 1, border: 'none', width: '100%' }} title="ZAP Doc" />
        </div>
      )}
      </div>
    );
  };

  // ---- UV label helper ----
  const uvLabel = (uv) => {
    if (uv <= 0) return 'None';
    if (uv <= 2) return 'Low';
    if (uv <= 5) return 'Moderate';
    if (uv <= 7) return 'High';
    if (uv <= 10) return 'Very High';
    return 'Extreme';
  };

  // Truth Chain now lives inline on ScoreEntry screen (pre-set buttons + Other)

  // ---- Hourly Drilldown ----
  const Drilldown = () => {
    const [selectedDay, setSelectedDay] = useState(null); // null = today
    // eslint-disable-next-line no-unused-vars
    const windIcon = (mph) => {
      if (mph === 0) return 'Calm';
      if (mph <= 8) return 'Light';
      if (mph <= 18) return 'Breezy';
      return 'Strong';
    };

    if (!selectedCourse || !weatherData) return (
      <div style={{ padding: '40px 16px', textAlign: 'center', color: COLOR_MUTED }}>
        No weather data — go back and load a course first.
      </div>
    );

    const now = new Date();
    const hourly = weatherData.hourly || [];

    // Build list of available days from hourly data
    const dayMap = {};
    hourly.forEach(h => {
      const d = new Date(h.time);
      d.setHours(0,0,0,0);
      const key = d.toLocaleDateString('en-CA');
      if (!dayMap[key]) dayMap[key] = { date: d, hours: [] };
      dayMap[key].hours.push(h);
    });
    // Extend with vcDailyAll days beyond hourly range (days 3-6)
    // Use date string as key (YYYY-MM-DD) to avoid UTC/local timezone mismatch
    const vcDailyAll = weatherData?.vcDailyAll || [];
    // Rebuild dayMap with date string keys to avoid ISO UTC mismatch
    const dayMapByDate = {};
    Object.values(dayMap).forEach(entry => {
      const d = entry.date;
      const dateKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      dayMapByDate[dateKey] = entry;
    });
    vcDailyAll.forEach(vcDay => {
      if (!dayMapByDate[vcDay.date]) {
        const base = new Date(vcDay.date + 'T00:00:00');
        // Synthesize 4 representative hours (9AM, 12PM, 2PM, 5PM) from daily data
        const makeHour = (hr, temp) => ({
          time: new Date(new Date(base).setHours(hr, 0, 0, 0)),
          temp, windSpeedNum: vcDay.windSpeed,
          windGust: Math.round(vcDay.windSpeed * 1.3), conditions: vcDay.conditions,
          precipChance: vcDay.precipChance, precip: vcDay.precip / 4, feelsLike: null,
        });
        dayMapByDate[vcDay.date] = {
          date: base,
          hours: [
            makeHour(9,  vcDay.tempMin),
            makeHour(12, Math.round((vcDay.tempMin + vcDay.tempMax) / 2)),
            makeHour(14, vcDay.tempMax),
            makeHour(17, Math.round((vcDay.tempMin + vcDay.tempMax) / 2)),
          ],
        };
      }
    });
    // Replace dayMap with date-keyed version for selector
    Object.keys(dayMap).forEach(k => delete dayMap[k]);
    Object.values(dayMapByDate).forEach(entry => {
      const d = new Date(entry.date);
      d.setHours(0,0,0,0);
      dayMap[d.toLocaleDateString('en-CA')] = { ...entry, date: d };
    });
    const days = Object.values(dayMap).sort((a,b) => a.date - b.date).slice(0, 7);

    // Selected day data
    const activeDay = selectedDay || (days[0] ? days[0].date.toLocaleDateString('en-CA') : null);
    const activeDayData = dayMap[activeDay] || (days[0] ? days[0] : null);
    const activeDayDate = activeDayData ? activeDayData.date : now;

    // Is this day beyond 48 hours?
    const hoursAhead = activeDayDate ? (activeDayDate - now) / 3600000 : 0;
    const isExtended = hoursAhead > 48;

    // Day label helper
    const dayLabel = (d) => {
      const today = new Date(); today.setHours(0,0,0,0);
      const tomorrow = new Date(today); tomorrow.setDate(today.getDate()+1);
      if (d.getTime() === today.getTime()) return 'Today';
      if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    // Go/No-Go reason for a single hour slot — uses canonical getHourStatus
    // eslint-disable-next-line no-unused-vars
    const hourGoNoGo = (h) => {
      const result = getHourStatus(h, profile);
      return { status: result.status, color: result.color, reason: result.reason };
    };

    // eslint-disable-next-line no-unused-vars

    const isPast = (h) => new Date(h.time) < now;
    // Single closest slot only — prevents duplicate ▶ Now rows
    const nowMs = now.getTime();
    // Prefer the most recent past hour as "Now". Only advance to next future hour if < 30 min ahead.
    const closestHour = activeDayData
      ? (() => {
          const hours = activeDayData.hours;
          // Find last past hour
          const pastHours = hours.filter(h => new Date(h.time).getTime() <= nowMs);
          if (pastHours.length) return pastHours[pastHours.length - 1];
          // All future — pick nearest
          return hours.reduce((best, h) => {
            const diff = Math.abs(new Date(h.time).getTime() - nowMs);
            return diff < Math.abs(new Date(best.time).getTime() - nowMs) ? h : best;
          }, hours[0]);
        })()
      : null;
    // eslint-disable-next-line no-unused-vars
    const isNow = (h) => !!(closestHour && h.time === closestHour.time && Math.abs(new Date(h.time).getTime() - nowMs) < 7200000);

    return (
      <div style={{ maxWidth: 480, margin: '0 auto', width: '100%', overflowX: 'hidden', boxSizing: 'border-box', paddingBottom: 16 }}>

        {/* Extended forecast notice */}
        {isExtended && (
          <div style={{ padding: '10px 16px', background: '#2a2a00', borderBottom: `1px solid ${COLOR_GOLD}`, fontSize: '13px', color: COLOR_GOLD, textAlign: 'center' }}>
            Extended forecast — use for planning, conditions may change
          </div>
        )}

        {/* Day selector */}
        <div style={{ display: 'flex', overflowX: 'auto', padding: '12px 16px', gap: 8, borderBottom: `1px solid ${COLOR_DARK_GREEN}`, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {days.map(({ date }) => {
            const key = date.toLocaleDateString('en-CA');
            const isActive = key === activeDay;
            return (
              <button key={key} onClick={() => setSelectedDay(key)}
                style={{ flexShrink: 0, padding: '10px 16px', borderRadius: 10, border: `2px solid ${isActive ? COLOR_GREEN : COLOR_DARK_GREEN}`,
                  background: isActive ? COLOR_GREEN_DARK : COLOR_CARD, color: isActive ? COLOR_GOLD : COLOR_MUTED,
                  fontWeight: isActive ? 800 : 400, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', minHeight: 44 }}>
                {dayLabel(date)}
              </button>
            );
          })}
        </div>

        {/* Hourly rows — S98 HourlyRow card design */}
        <div style={{ padding: '8px 12px' }}>
          {activeDayData ? activeDayData.hours.map((h, idx) => {
            // Pollen banner on first hour only
            const pollenBanner = (() => {
              const pd = pollenData;
              if (!pd || !pd.success || pd.offSeason || idx !== 0) return null;
              if (pd.level === 0) return null;
              const colors = [COLOR_GREEN, COLOR_GOLD, '#FB923C', COLOR_RED, COLOR_RED];
              return { label: `Pollen today: ${pd.label}`, color: colors[pd.level] };
            })();
            return (
              <div key={idx}>
                {pollenBanner && (
                  <div style={{ padding: '6px 12px', background: '#1a2a1a', fontSize: '12px', color: pollenBanner.color, fontWeight: 700, borderRadius: 8, marginBottom: 6 }}>
                    {pollenBanner.label}
                  </div>
                )}
                <HourlyRow h={h} profile={profile} />
              </div>
            );
          }) : (
            <div style={{ padding: '30px', textAlign: 'center', color: COLOR_MUTED }}>No data for this day</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 16px', background: COLOR_HEADER, borderTop: `1px solid ${COLOR_DARK_GREEN}`,
          fontSize: '12px', color: COLOR_MUTED, display: 'flex', justifyContent: 'space-between' }}>
          <span>▶ = Current hour</span>
          <span style={{display:'flex',alignItems:'center',gap:4}}><StatusIcon status="CAUTION" size={12}/> Caution · <StatusIcon status="NO-GO" size={12}/> No-Go</span>
        </div>
      </div>
    );
  };

  // RadarScreen removed — Check Radar opens Windy directly from Game Day card


  // ---- Admin Panel ----
  const AdminPanel = () => {
    const [log, setLog] = useState(() => getWULog());
    const [showRaw, setShowRaw] = useState(false);

    const handleClear = () => {
      clearWULog();
      setLog([]);
    };

    const exportCSV = () => {
      if (!log.length) return;
      const headers = 'Timestamp,Course,VC Temp,VC Wind,VC Gust,VC Humidity,VC PrecipChance,VC Conditions,VC Source,WU Temp,WU Wind,WU PrecipChance';
      const rows = log.map(e => [
        e.ts, e.course,
        e.vc?.temp ?? '', e.vc?.windSpeed ?? '', e.vc?.windGust ?? '',
        e.vc?.humidity ?? '', e.vc?.precipChance ?? '',
        `"${(e.vc?.conditions || '').replace(/"/g, "'")}"`,
        e.vc?.source ?? '',
        e.wu?.temp ?? 'pending', e.wu?.windSpeed ?? 'pending', e.wu?.precipChance ?? 'pending'
      ].join(','));
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `zap_wu_compare_${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    };

    return (
      <div style={{ padding: '16px 16px 40px' }}>
        <div style={{ ...CARD_STYLE, padding: '14px 16px', marginBottom: 12, background: COLOR_RED_BG, border: `1px solid ${COLOR_RED}` }}>
          <div style={{ fontSize: 13, color: COLOR_RED, fontWeight: 700 }}>ADMIN MODE — Dr. Z Only</div>
          <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 4 }}>WU comparison logging active. {log.length} entries stored.</div>
        </div>

        {/* WU vs VC Comparison Log */}
        <div style={{ fontSize: 14, fontWeight: 800, color: COLOR_GOLD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>VC vs WU Comparison Log</div>
        <div style={{ fontSize: 12, color: COLOR_MUTED, marginBottom: 12 }}>
          VC data logged on every course load. WU column shows "pending" until June trial is wired.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button onClick={exportCSV} style={{ ...BTN_BASE, padding: '10px 16px', background: COLOR_GREEN_DARK, color: COLOR_WHITE, fontSize: 13, fontWeight: 700 }}>
            Export CSV
          </button>
          <button onClick={() => setShowRaw(!showRaw)} style={{ ...BTN_BASE, padding: '10px 16px', background: COLOR_CARD, color: COLOR_GOLD, fontSize: 13, border: `1px solid ${COLOR_GOLD}` }}>
            {showRaw ? 'Hide Raw' : 'Show Raw JSON'}
          </button>
          <button onClick={handleClear} style={{ ...BTN_BASE, padding: '10px 16px', background: COLOR_RED_BG, color: COLOR_RED, fontSize: 13, border: `1px solid ${COLOR_RED}` }}>
            Clear Log
          </button>
        </div>

        {log.length === 0 ? (
          <div style={{ color: COLOR_MUTED, fontSize: 14 }}>No entries yet. Load a course to start logging.</div>
        ) : (
          <>
            {/* Last 10 entries table */}
            <div style={{ overflowX: 'auto', marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: COLOR_OFF_WHITE }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>
                    {['Time','Course','VC°F','VC Wind','VC Gust','VC Precip%','WU°F','WU Wind','WU Precip%','Δ Temp'].map(h => (
                      <th key={h} style={{ padding: '6px 8px', color: COLOR_GOLD, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...log].reverse().slice(0, 20).map((e, i) => {
                    const deltaTemp = (e.vc?.temp != null && e.wu?.temp != null) ? (e.vc.temp - e.wu.temp).toFixed(1) : '—';
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${COLOR_DARKEST}`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.03)' }}>
                        <td style={{ padding: '5px 8px', color: COLOR_MUTED, whiteSpace: 'nowrap' }}>{new Date(e.ts).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{e.course}</td>
                        <td style={{ padding: '5px 8px' }}>{e.vc?.temp ?? '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{e.vc?.windSpeed ?? '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{e.vc?.windGust ?? '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{e.vc?.precipChance ?? '—'}%</td>
                        <td style={{ padding: '5px 8px', color: COLOR_MUTED }}>{e.wu?.temp ?? 'pending'}</td>
                        <td style={{ padding: '5px 8px', color: COLOR_MUTED }}>{e.wu?.windSpeed ?? 'pending'}</td>
                        <td style={{ padding: '5px 8px', color: COLOR_MUTED }}>{e.wu?.precipChance != null ? `${e.wu.precipChance}%` : 'pending'}</td>
                        <td style={{ padding: '5px 8px', color: deltaTemp !== '—' ? (Math.abs(parseFloat(deltaTemp)) > 2 ? COLOR_RED : COLOR_GREEN) : COLOR_MUTED }}>{deltaTemp !== '—' ? `${deltaTemp > 0 ? '+' : ''}${deltaTemp}°` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {log.length > 20 && <div style={{ fontSize: 12, color: COLOR_MUTED, marginBottom: 12 }}>Showing 20 of {log.length} entries. Export CSV for full dataset.</div>}
          </>
        )}

        {showRaw && log.length > 0 && (
          <div style={{ ...CARD_STYLE, padding: 12, marginTop: 8 }}>
            <pre style={{ fontSize: 10, color: COLOR_MUTED, overflowX: 'auto', margin: 0 }}>
              {JSON.stringify(log.slice(-5), null, 2)}
            </pre>
          </div>
        )}

        {/* Sign out */}
        <button onClick={() => {
          try { localStorage.removeItem('zap_authed'); localStorage.removeItem('zap_admin'); } catch {}
          setAuthed(false); setIsAdmin(false);
        }} style={{ ...BTN_LARGE, background: COLOR_CARD, color: COLOR_MUTED, marginTop: 24, justifyContent: 'center', fontSize: 14 }}>
          Sign Out
        </button>
      </div>
    );
  };

  // ---- Root render ----
  // Password gate
  if (!authed) {
    return (
      <div style={{ background: COLOR_BG, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Georgia', serif", padding: 24 }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: COLOR_GREEN, marginBottom: 12 }}>ZAP</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: COLOR_GOLD, marginBottom: 4 }}>ZAP</div>
        <div style={{ fontSize: 13, color: COLOR_MUTED, marginBottom: 32 }}>The Field-Level Truth</div>
        <div style={{ width: '100%', maxWidth: 320 }}>
          <input
            type="password"
            placeholder="Enter access code"
            value={authInput}
            onChange={e => setAuthInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAuth()}
            style={{ width: '100%', padding: '14px 16px', fontSize: 16, borderRadius: 10, border: `2px solid ${authError ? COLOR_RED : COLOR_DARK_GREEN}`, background: COLOR_CARD, color: COLOR_WHITE, fontFamily: "'Georgia', serif", boxSizing: 'border-box', marginBottom: 10 }}
            autoFocus
          />
          {authError && <div style={{ color: COLOR_RED, fontSize: 13, marginBottom: 10 }}>{authError}</div>}
          <button onClick={handleAuth} style={{ ...BTN_LARGE, background: COLOR_GREEN_DARK, color: COLOR_WHITE, justifyContent: 'center', fontSize: 16, fontWeight: 700 }}>
            Enter
          </button>
        </div>
        <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 32 }}>Jersey Circuit Alpha — Summer 2026</div>
      </div>
    );
  }


  // ============================================================
  // SHARED STATS HELPERS
  // ============================================================
  // Canonical date formatter — handles ISO strings from Apps Script
  // Shared expanded round card — Tier 1 + Tier 2
  // worstHoleIndex: if set, highlights that hole cell in red

  // Collapsed round summary row — shared across All/By Course/By Player tabs
  // S161 Item 7: RoundSummaryRow — shared component per S160 ROUND-SUMMARY-ROW spec.
  // Layout (per JZ S161 confirmed design):
  //   {course name, white bold}              {score, gold big}
  //   {date · tee → finish, gold}            {Par 71, muted}
  //   {4h 0m, white}                          {-15 / E / +3, color-coded}
  //   {players, muted grey}                   {▼ chevron}
  //   {weather line · NJWxNet}  (full-width row below)
  //
  // Differential color rules (golf standard):
  //   negative (under par) → RED
  //   zero (even par)      → GREEN
  //   positive (over par)  → BLUE
  //
  // NOTE: This component handles DISPLAY only. Best/Worst/avg/sort logic still uses raw
  // round.total — that overhaul is deferred to next session per JZ direction.
  // ============================================================
  // ROUND HISTORY SCREEN (3-tab: All Rounds | By Course | By Player)
  // ============================================================
  const RoundHistoryScreen = ({ initialTab, filterPlayer }) => {
    const allRounds = getRounds(sharedRounds).filter(r => r.total != null);
    const sorted = sortRoundsNewestFirst(allRounds);

    const [activeTab, setActiveTab] = React.useState(initialTab || 'all');
    const [allRoundsSort, setAllRoundsSort] = React.useState('score'); // 'score' | 'recent'
    const [expandedId, setExpandedId] = React.useState(null);
    const [expandedCourse, setExpandedCourse] = React.useState(null);
    const [expandedCourseRoundId, setExpandedCourseRoundId] = React.useState(null);
    const [expandedPlayer, setExpandedPlayer] = React.useState(filterPlayer || null);
    const [expandedPlayerRoundId, setExpandedPlayerRoundId] = React.useState(null);

    const TAB_STYLE = (active) => ({
      flex: 1, padding: '9px 4px', fontSize: 12, fontWeight: active ? 700 : 500,
      color: active ? COLOR_WHITE : COLOR_MUTED,
      background: active ? COLOR_GREEN_DARK : 'transparent',
      border: 'none', borderBottom: `2px solid ${active ? COLOR_GREEN : COLOR_DARK_GREEN}`,
      cursor: 'pointer'
    });

    if (allRounds.length === 0) return (
      <div style={{ padding: '40px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: COLOR_MUTED, marginBottom: 12 }}>ROUNDS</div>
        <div style={{ fontSize: 18, color: COLOR_OFF_WHITE, fontWeight: 700, marginBottom: 8 }}>No Rounds Yet</div>
        <div style={{ fontSize: 14, color: COLOR_MUTED }}>Play a round and enter your scorecard to see history here.</div>
      </div>
    );

    // ── Tab 1: All Rounds ──────────────────────────────────────
    const AllRoundsTab = () => {
      const sortedByScore = [...allRounds].sort((a, b) => getRoundDiff(a) - getRoundDiff(b));
      const displayRounds = allRoundsSort === 'score' ? sortedByScore : sorted;
      return (
        <div style={{ padding: '12px 16px 60px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: COLOR_MUTED }}>{displayRounds.length} round{displayRounds.length !== 1 ? 's' : ''} recorded</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['score', 'Best → Worst'], ['recent', 'Most Recent']].map(([val, label]) => (
                <button key={val} onClick={() => setAllRoundsSort(val)} style={{
                  fontSize: 11, fontWeight: allRoundsSort === val ? 700 : 500,
                  padding: '4px 10px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: allRoundsSort === val ? COLOR_GREEN : COLOR_DARK_GREEN,
                  color: allRoundsSort === val ? COLOR_WHITE : COLOR_MUTED,
                }}>{label}</button>
              ))}
            </div>
          </div>
          {displayRounds.map(round => {
            const isExp = expandedId === round.id;
            return (
              <div key={round.id} style={{ marginBottom: 10 }}>
                <RoundSummaryRow round={round} isExpanded={isExp} onToggle={() => setExpandedId(isExp ? null : round.id)} />
                {isExp && <ExpandedRoundCard round={round} />}
              </div>
            );
          })}
        </div>
      );
    };

    // ── Tab 2: By Course ──────────────────────────────────────
    const ByCourseTab = () => {
      const courseMap = {};
      allRounds.forEach(r => {
        const key = r.courseId || r.courseName;
        if (!courseMap[key]) courseMap[key] = { name: r.courseName, rounds: [] };
        courseMap[key].rounds.push(r);
      });
      const courses = Object.values(courseMap).sort((a, b) => { const avgA = a.rounds.reduce((s,r) => s+getRoundDiff(r),0)/a.rounds.length; const avgB = b.rounds.reduce((s,r) => s+getRoundDiff(r),0)/b.rounds.length; return avgA - avgB; });

      return (
        <div style={{ padding: '12px 16px 60px' }}>
          {courses.map(course => {
            const isExpCourse = expandedCourse === course.name;
            const best = course.rounds.reduce((b, r) => getRoundDiff(r) < getRoundDiff(b) ? r : b);
            const avgDiff = course.rounds.reduce((s, r) => s + getRoundDiff(r), 0) / course.rounds.length;
            const avgDiffDisplay = (avgDiff >= 0 ? '+' : '') + avgDiff.toFixed(1);
            const bestDiff = getRoundDiff(best);
            const bestDiffDisplay = (bestDiff >= 0 ? '+' : '') + bestDiff;
            const lastPlayed = fmtDateStats(course.rounds[course.rounds.length - 1].date);
            const sortedRounds = [...course.rounds].sort((a, b) => getRoundDiff(a) - getRoundDiff(b));

            return (
              <div key={course.name} style={{ marginBottom: 10 }}>
                <button
                  onClick={() => setExpandedCourse(isExpCourse ? null : course.name)}
                  style={{ ...CARD_STYLE, width: '100%', textAlign: 'left', cursor: 'pointer', padding: '14px 16px',
                    border: isExpCourse ? `2px solid ${COLOR_GOLD}` : `1px solid ${COLOR_DARK_GREEN}`,
                    borderBottomLeftRadius: isExpCourse ? 0 : 12, borderBottomRightRadius: isExpCourse ? 0 : 12,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, color: COLOR_OFF_WHITE, fontWeight: 700 }}>{course.name}</div>
                    <div style={{ fontSize: 12, color: COLOR_GOLD, marginTop: 2 }}>Last played: {lastPlayed}</div>
                    <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                      <span style={{ fontSize: 12, color: COLOR_MUTED }}>Best: <span style={{ color: getDiffColor(bestDiff), fontWeight: 700 }}>{bestDiffDisplay}</span></span>
                      <span style={{ fontSize: 12, color: COLOR_MUTED }}>Avg: <span style={{ color: getDiffColor(avgDiff), fontWeight: 700 }}>{avgDiffDisplay}</span></span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', marginLeft: 12, flexShrink: 0 }}>
                    <div style={{ fontSize: 13, color: COLOR_MUTED }}>{course.rounds.length} round{course.rounds.length !== 1 ? 's' : ''}</div>
                    <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 4 }}>{isExpCourse ? '▲' : '▼'}</div>
                  </div>
                </button>
                {isExpCourse && (
                  <div style={{ ...CARD_STYLE, borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0, padding: '10px 12px 14px' }}>
                    <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Best to Worst</div>
                    {sortedRounds.map(round => {
                      const isExpRound = expandedCourseRoundId === round.id;
                      return (
                        <div key={round.id} style={{ marginBottom: 8 }}>
                          <RoundSummaryRow round={round} isExpanded={isExpRound} onToggle={() => setExpandedCourseRoundId(isExpRound ? null : round.id)} />
                          {isExpRound && <ExpandedRoundCard round={round} />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    // ── Tab 3: By Player ──────────────────────────────────────
    const ByPlayerTab = () => {
      const playerMap = {};
      allRounds.forEach(round => {
        const diff = getRoundDiff(round);
        (round.players || []).forEach(name => {
          if (!name) return;
          if (!playerMap[name]) playerMap[name] = { rounds: [], totalDiff: 0, bestDiff: Infinity };
          playerMap[name].rounds.push(round);
          playerMap[name].totalDiff += diff;
          if (diff < playerMap[name].bestDiff) playerMap[name].bestDiff = diff;
        });
      });

      const players = Object.entries(playerMap).sort((a, b) => {
        const avgA = a[1].totalDiff / a[1].rounds.length;
        const avgB = b[1].totalDiff / b[1].rounds.length;
        return avgA - avgB;
      });

      return (
        <div style={{ padding: '12px 16px 60px' }}>
          {players.map(([name, stats]) => {
            const isExpPlayer = expandedPlayer === name;
            const avgDiff = stats.totalDiff / stats.rounds.length;
            const avgDiffDisplay = (avgDiff >= 0 ? '+' : '') + avgDiff.toFixed(1);
            const playerObj = cfgPlayers.find(p => p.name === name);
            const emoji = playerObj ? playerObj.emoji : '';
            const bestCourse = (() => {
              const best = stats.rounds.reduce((b, r) => getRoundDiff(r) < getRoundDiff(b) ? r : b);
              return best.courseName;
            })();
            const bestDiffDisplay = stats.bestDiff === Infinity ? '—' : (stats.bestDiff >= 0 ? '+' : '') + stats.bestDiff;
            const sortedPlayerRounds = [...stats.rounds].sort((a, b) => getRoundDiff(a) - getRoundDiff(b));

            return (
              <div key={name} style={{ marginBottom: 10 }}>
                <button
                  onClick={() => setExpandedPlayer(isExpPlayer ? null : name)}
                  style={{ ...CARD_STYLE, width: '100%', textAlign: 'left', cursor: 'pointer', padding: '14px 16px',
                    border: isExpPlayer ? `2px solid ${COLOR_GOLD}` : `1px solid ${COLOR_DARK_GREEN}`,
                    borderBottomLeftRadius: isExpPlayer ? 0 : 12, borderBottomRightRadius: isExpPlayer ? 0 : 12,
                    display: 'flex', alignItems: 'center', gap: 12 }}
                >
                  <div style={{ fontSize: 24 }}>{emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, color: COLOR_OFF_WHITE, fontWeight: 700 }}>{name}</div>
                    <div style={{ fontSize: 12, color: COLOR_MUTED }}>{stats.rounds.length} round{stats.rounds.length !== 1 ? 's' : ''} · avg {avgDiffDisplay}</div>
                  </div>
                  <div style={{ fontSize: 11, color: COLOR_MUTED }}>{isExpPlayer ? '▲' : '▼'}</div>
                </button>
                {isExpPlayer && (
                  <div style={{ ...CARD_STYLE, borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0, padding: '10px 12px 14px' }}>
                    {/* Personal summary */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 12, padding: '8px 4px', borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_GOLD }}>{stats.rounds.length}</div>
                        <div style={{ fontSize: 10, color: COLOR_MUTED }}>Rounds</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: getDiffColor(avgDiff) }}>{avgDiffDisplay}</div>
                        <div style={{ fontSize: 10, color: COLOR_MUTED }}>Avg</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: getDiffColor(stats.bestDiff === Infinity ? null : stats.bestDiff) }}>{bestDiffDisplay}</div>
                        <div style={{ fontSize: 10, color: COLOR_MUTED }}>Best</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, color: COLOR_GOLD, fontWeight: 600 }}>{bestCourse}</div>
                        <div style={{ fontSize: 10, color: COLOR_MUTED }}>Best Course</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Best to Worst</div>
                    {sortedPlayerRounds.map(round => {
                      const isExpRound = expandedPlayerRoundId === round.id;
                      return (
                        <div key={round.id} style={{ marginBottom: 8 }}>
                          <RoundSummaryRow round={round} isExpanded={isExpRound} onToggle={() => setExpandedPlayerRoundId(isExpRound ? null : round.id)} />
                          {isExpRound && <ExpandedRoundCard round={round} />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: `2px solid ${COLOR_DARK_GREEN}`, background: COLOR_DARKEST, position: 'sticky', top: 56, zIndex: 10 }}>
          <button style={TAB_STYLE(activeTab === 'all')} onClick={() => setActiveTab('all')}>All Rounds</button>
          <button style={TAB_STYLE(activeTab === 'course')} onClick={() => setActiveTab('course')}>By Course</button>
          <button style={TAB_STYLE(activeTab === 'player')} onClick={() => setActiveTab('player')}>By Player</button>
        </div>
        {activeTab === 'all'    && <AllRoundsTab />}
        {activeTab === 'course' && <ByCourseTab />}
        {activeTab === 'player' && <ByPlayerTab />}
      </div>
    );
  };

  // ============================================================
  // STATISTICS HUB (ScoreStatsScreen)
  // ============================================================
  const ScoreStatsScreen = () => {
    const allRounds = getRounds(sharedRounds).filter(r => r.total != null);

    if (allRounds.length === 0) return (
      <div style={{ padding: '40px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: COLOR_MUTED, marginBottom: 12 }}>STATISTICS</div>
        <div style={{ fontSize: 18, color: COLOR_OFF_WHITE, fontWeight: 700, marginBottom: 8 }}>No Stats Yet</div>
        <div style={{ fontSize: 14, color: COLOR_MUTED }}>Play some rounds and your stats will appear here.</div>
      </div>
    );

    const HubCard = ({ title, sub, onClick }) => (
      <button onClick={onClick} style={{ width: '100%', background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 12, padding: '18px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left', marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: COLOR_WHITE }}>{title}</div>
          <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 2 }}>{sub}</div>
        </div>
        <div style={{ fontSize: 18, color: COLOR_MUTED }}>›</div>
      </button>
    );

    return (
      <div style={{ paddingTop: 'env(safe-area-inset-top, 12px)', paddingLeft: 16, paddingRight: 16, paddingBottom: 60, maxWidth: 480, margin: '0 auto' }}>
        <div style={{ fontSize: 11, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>{allRounds.length} rounds this season</div>
        <HubCard title="Round History" sub="All rounds · By course · By player" onClick={() => navigateTo('roundhistory')} />
        <HubCard title="Overview" sub="Season summary, scoring breakdown, notable rounds" onClick={() => navigateTo('statsoverview')} />
        <HubCard title="Players Luck Charm" sub="How the team scores when each player shows up" onClick={() => navigateTo('statsplayers')} />
      </div>
    );
  };

  // ============================================================
  // STATS OVERVIEW
  // ============================================================
  const StatsOverviewScreen = () => {
    const [expandedRoundId, setExpandedRoundId] = React.useState(null);
    const [expandedWorstHole, setExpandedWorstHole] = React.useState(false);
    const [notableFilter, setNotableFilter] = React.useState('birdies');
    const [expandedNotableId, setExpandedNotableId] = React.useState(null);
    const [expandedFoursome, setExpandedFoursome] = React.useState(null);
    const [expandedFoursomeRoundId, setExpandedFoursomeRoundId] = React.useState(null);

    const allRounds = getRounds(sharedRounds).filter(r => r.total != null);
    if (allRounds.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: COLOR_MUTED }}>No rounds yet.</div>;

    const totalRounds = allRounds.length;
    const allTimeRecord = getAllTimeRecord(sharedRounds);
    const worstRecord   = getWorstRecord(sharedRounds);
    const totalHoles    = allRounds.reduce((sum, r) => sum + (r.holesEntered || (r.scores||[]).filter(s=>s!=null).length), 0);
    const totalDiff     = allRounds.reduce((sum, r) => sum + getRoundDiff(r), 0);
    const avgDiff       = totalDiff / totalRounds;
    const avgDiffDisplay = (avgDiff >= 0 ? '+' : '') + avgDiff.toFixed(1);
    const bestDiff      = allTimeRecord ? getRoundDiff(allTimeRecord) : null;
    const bestDiffDisplay = bestDiff != null ? (bestDiff >= 0 ? '+' : '') + bestDiff : '—';

    let eagles=0, birdies=0, pars=0, bogeys=0, doubles=0, triples=0, quads=0, snowmen=0;
    let worstHole = { score: 0, par: 4, course: '', hole: 0, diff: 0, roundId: null, holeIndex: 0 };

    allRounds.forEach(round => {
      const roundPars = round.pars || Array(18).fill(4);
      (round.scores||[]).forEach((score, i) => {
        if (!score) return;
        const par = roundPars[i], diff = score - par;
        if (score >= par * 2)  snowmen++;
        else if (diff <= -2)   eagles++;
        else if (diff === -1)  birdies++;
        else if (diff === 0)   pars++;
        else if (diff === 1)   bogeys++;
        else if (diff === 2)   doubles++;
        else if (diff === 3)   triples++;
        else if (diff >= 4)    quads++;
        if (diff > worstHole.diff) worstHole = { score, par, course: round.courseName, hole: i+1, diff, roundId: round.id, holeIndex: i, round };
      });
    });

    // Foursome combo rankings
    const comboMap = {};
    allRounds.forEach(round => {
      const diff = getRoundDiff(round);
      const players = (round.players||[]).filter(Boolean).sort();
      if (players.length < 2) return;
      const key = players.join('|');
      if (!comboMap[key]) comboMap[key] = { players, rounds: [], totalDiff: 0 };
      comboMap[key].rounds.push(round);
      comboMap[key].totalDiff += diff;
    });
    const foursomeCombos = Object.values(comboMap)
      .map(c => ({ ...c, avgDiff: c.totalDiff / c.rounds.length }))
      .sort((a, b) => a.avgDiff - b.avgDiff);

    // Notable rounds data
    const roundBirdieCounts = allRounds.map(r => {
      const rp = r.pars || Array(18).fill(4);
      const count = (r.scores||[]).filter((s,i) => s && (s - rp[i]) === -1).length;
      return { round: r, count };
    }).sort((a,b) => b.count - a.count);

    const roundBogeyCounts = allRounds.map(r => {
      const rp = r.pars || Array(18).fill(4);
      const count = (r.scores||[]).filter((s,i) => s && (s - rp[i]) === 1).length;
      return { round: r, count };
    }).sort((a,b) => b.count - a.count);

    const roundDoubleCounts = allRounds.map(r => {
      const rp = r.pars || Array(18).fill(4);
      const count = (r.scores||[]).filter((s,i) => s && (s - rp[i]) === 2).length;
      return { round: r, count };
    }).sort((a,b) => b.count - a.count);

    const roundSnowmanCounts = allRounds.map(r => {
      const rp = r.pars || Array(18).fill(4);
      const count = (r.scores||[]).filter((s,i) => s && s >= rp[i]*2).length;
      return { round: r, count };
    }).sort((a,b) => b.count - a.count);

    const bogeyFreeRounds = allRounds.filter(round => {
      const rp = round.pars || Array(18).fill(4);
      return (round.scores||[]).every((s, i) => !s || (s - rp[i]) <= 0);
    });

    const notableData = {
      birdies: { list: roundBirdieCounts.slice(0,3), label: 'birdies', key: 'count' },
      bogeys:  { list: roundBogeyCounts.slice(0,3),  label: 'bogeys',  key: 'count' },
      doubles: { list: roundDoubleCounts.slice(0,3), label: 'doubles', key: 'count' },
      snowmen: { list: roundSnowmanCounts.slice(0,3),label: 'snowmen', key: 'count' },
      bogeyFree: { list: bogeyFreeRounds.map(r => ({ round: r, count: 0 })), label: 'bogey-free', key: null },
    };

    const PILL = (key, label) => (
      <button key={key} onClick={() => { setNotableFilter(key); setExpandedNotableId(null); }}
        style={{ padding: '6px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
          background: notableFilter === key ? COLOR_GOLD : 'transparent',
          color: notableFilter === key ? '#000' : COLOR_MUTED,
          border: `1px solid ${notableFilter === key ? COLOR_GOLD : COLOR_DARK_GREEN}` }}>
        {label}
      </button>
    );

    return (
      <div style={{ paddingTop: 'env(safe-area-inset-top, 12px)', paddingLeft: 16, paddingRight: 16, paddingBottom: 60, maxWidth: 480, margin: '0 auto' }}>

        {/* Section 1 — Season Summary */}
        <div style={{ ...CARD_STYLE, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Season Summary</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: 24, fontWeight: 800, color: COLOR_GOLD }}>{totalRounds}</div><div style={{ fontSize: 11, color: COLOR_MUTED }}>Rounds</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: 24, fontWeight: 800, color: getDiffColor(bestDiff) }}>{bestDiffDisplay}</div><div style={{ fontSize: 11, color: COLOR_MUTED }}>Best Round</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: 24, fontWeight: 800, color: getDiffColor(avgDiff) }}>{avgDiffDisplay}</div><div style={{ fontSize: 11, color: COLOR_MUTED }}>Average</div></div>
          </div>
          <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 10, textAlign: 'center' }}>{totalHoles} holes played</div>
        </div>

        {/* Section 2 — Scoring Breakdown */}
        <div style={{ ...CARD_STYLE, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Scoring Breakdown</div>
          {[
            { label: 'Eagles',  count: eagles,  color: '#F1373D', sym: 'E' },
            { label: 'Birdies', count: birdies, color: '#F1373D', sym: 'B' },
            { label: 'Pars',    count: pars,    color: '#FFFFFF', sym: '—' },
            { label: 'Bogeys',  count: bogeys,  color: '#3B82F6', sym: '+1' },
            { label: 'Doubles', count: doubles, color: '#3B82F6', sym: '+2' },
            { label: 'Triples', count: triples, color: '#FF6B35', sym: '+3' },
            { label: 'Quads',   count: quads,   color: '#DC2626', sym: '+4' },
            { label: 'Snowmen', count: snowmen, color: COLOR_RED, sym: 'S' },
          ].filter(s => s.count > 0).map(stat => {
            const total = eagles+birdies+pars+bogeys+doubles+triples+quads+snowmen;
            const pct = total > 0 ? Math.round(stat.count/total*100) : 0;
            return (
              <div key={stat.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 14, color: stat.color, fontWeight: 600, minWidth: 100 }}>{stat.sym} {stat.label}</div>
                <div style={{ flex: 1, margin: '0 12px', height: 8, background: COLOR_DARKEST, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: stat.color, borderRadius: 4, minWidth: pct > 0 ? 4 : 0 }} />
                </div>
                <div style={{ fontSize: 14, color: stat.color, fontWeight: 700, minWidth: 50, textAlign: 'right' }}>{stat.count} <span style={{ fontSize: 11, color: COLOR_MUTED }}>({pct}%)</span></div>
              </div>
            );
          })}
        </div>

        {/* Section 3 — Foursome Rankings */}
        {foursomeCombos.length > 0 && (
          <div style={{ ...CARD_STYLE, padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Foursome Rankings</div>
            <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 12 }}>Best average score by player combination</div>
            {foursomeCombos.map((combo, idx) => {
              const isExp = expandedFoursome === idx;
              return (
                <div key={idx} style={{ marginBottom: 8 }}>
                  <button onClick={() => setExpandedFoursome(isExp ? null : idx)}
                    style={{ width: '100%', background: 'transparent', border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, padding: '10px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <div>
                      <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, fontWeight: 600 }}>{combo.players.join(' · ')}</div>
                      <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 2 }}>{combo.rounds.length} round{combo.rounds.length !== 1 ? 's' : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: 12 }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: getDiffColor(combo.avgDiff) }}>{(combo.avgDiff >= 0 ? '+' : '') + combo.avgDiff.toFixed(1)}</div>
                      <div style={{ fontSize: 10, color: COLOR_MUTED }}>avg</div>
                    </div>
                  </button>
                  {isExp && (
                    <div style={{ paddingTop: 4 }}>
                      {combo.rounds.sort((a,b)=>getRoundDiff(a)-getRoundDiff(b)).map(r => {
                        const isExpRound = expandedFoursomeRoundId === r.id;
                        return (
                          <div key={r.id}>
                            <RoundSummaryRow round={r} isExpanded={isExpRound} onToggle={() => setExpandedFoursomeRoundId(isExpRound ? null : r.id)} />
                            {isExpRound && <ExpandedRoundCard round={r} />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Section 4 — Best Round */}
        {allTimeRecord && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>BEST ROUND</div>
            <RoundSummaryRow round={allTimeRecord} isExpanded={expandedRoundId === allTimeRecord.id} onToggle={() => setExpandedRoundId(expandedRoundId === allTimeRecord.id ? null : allTimeRecord.id)} />
            {expandedRoundId === allTimeRecord.id && <ExpandedRoundCard round={allTimeRecord} />}
          </div>
        )}

        {/* Section 5 — Worst Round */}
        {worstRecord && worstRecord.id !== allTimeRecord?.id && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>WORST ROUND</div>
            <RoundSummaryRow round={worstRecord} isExpanded={expandedRoundId === worstRecord.id} onToggle={() => setExpandedRoundId(expandedRoundId === worstRecord.id ? null : worstRecord.id)} />
            {expandedRoundId === worstRecord.id && <ExpandedRoundCard round={worstRecord} />}
          </div>
        )}

        {/* Section 6 — Worst Single Hole */}
        {worstHole.diff > 0 && worstHole.round && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>WORST SINGLE HOLE</div>
            <button onClick={() => setExpandedWorstHole(!expandedWorstHole)}
              style={{ ...CARD_STYLE, width: '100%', textAlign: 'left', cursor: 'pointer', padding: '14px 16px', borderLeft: `4px solid ${COLOR_MUTED}`,
                borderBottomLeftRadius: expandedWorstHole ? 0 : 12, borderBottomRightRadius: expandedWorstHole ? 0 : 12,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 14, color: COLOR_OFF_WHITE }}>{worstHole.course} — Hole {worstHole.hole}</div>
                <div style={{ fontSize: 12, color: COLOR_MUTED }}>Par {worstHole.par} · scored {worstHole.score} (+{worstHole.diff})</div>
                {worstHole.round?.date && <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 2 }}>{new Date(worstHole.round.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{worstHole.round.players?.length ? ' · ' + worstHole.round.players.filter(Boolean).join(', ') : ''}</div>}
                {worstHole.round?.weatherSnapshot && <WeatherSnapshotLine ws={worstHole.round.weatherSnapshot} variant="compact" />}
              </div>
              <div style={{ textAlign: 'right', marginLeft: 12 }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: COLOR_RED }}>{worstHole.score}</div>
                <div style={{ fontSize: 11, color: COLOR_MUTED }}>{expandedWorstHole ? '▲' : '▼'}</div>
              </div>
            </button>
            {expandedWorstHole && <ExpandedRoundCard round={worstHole.round} worstHoleIndex={worstHole.holeIndex} />}
          </div>
        )}

        {/* Section 7 — Notable Rounds */}
        <div style={{ ...CARD_STYLE, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Notable Rounds</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {PILL('birdies',  'Most Birdies')}
            {PILL('bogeys',   'Most Bogeys')}
            {PILL('doubles',  'Most Doubles')}
            {PILL('snowmen',  'Most Snowmen')}
            {PILL('bogeyFree','Bogey-Free')}
          </div>
          {notableData[notableFilter].list.length === 0 && (
            <div style={{ fontSize: 13, color: COLOR_MUTED }}>No rounds match this filter yet.</div>
          )}
          {notableData[notableFilter].list.map(({ round, count }, idx) => {
            const isExp = expandedNotableId === `${notableFilter}-${round.id}`;
            const countLabel = notableFilter === 'bogeyFree' ? '' : `${count} ${notableData[notableFilter].label}`;
            return (
              <div key={round.id} style={{ marginBottom: 8 }}>
                <button onClick={() => setExpandedNotableId(isExp ? null : `${notableFilter}-${round.id}`)}
                  style={{ ...CARD_STYLE, width: '100%', textAlign: 'left', cursor: 'pointer', padding: '12px 14px',
                    border: isExp ? `2px solid ${COLOR_GOLD}` : `1px solid ${COLOR_DARK_GREEN}`,
                    borderBottomLeftRadius: isExp ? 0 : 12, borderBottomRightRadius: isExp ? 0 : 12,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, fontWeight: 700 }}>{round.courseName}</div>
                    <div style={{ fontSize: 12, color: COLOR_GOLD }}>{fmtDateStats(round.date)}</div>
                    <div style={{ fontSize: 11, color: COLOR_MUTED }}>{(round.players||[]).filter(Boolean).join(' · ')}</div>
                    {round.weatherSnapshot && <WeatherSnapshotLine ws={round.weatherSnapshot} variant="compact" />}
                  </div>
                  <div style={{ textAlign: 'right', marginLeft: 12 }}>
                    {countLabel && <div style={{ fontSize: 13, color: COLOR_GOLD, fontWeight: 700, marginBottom: 2 }}>{countLabel}</div>}
                    {(() => { const d = getRoundDiff(round); const txt = d < 0 ? String(d) : d > 0 ? '+'+d : 'E'; const col = d < 0 ? '#F1373D' : d > 0 ? '#3B82F6' : '#22C55E'; return <div style={{ fontSize: 20, fontWeight: 800, color: col }}>{txt}</div>; })()}
                    <div style={{ fontSize: 11, color: COLOR_MUTED }}>{isExp ? '▲' : '▼'}</div>
                  </div>
                </button>
                {isExp && <ExpandedRoundCard round={round} />}
              </div>
            );
          })}
        </div>

      </div>
    );
  };

  // ============================================================
  // PLAYERS LUCK CHARM
  // ============================================================
  const StatsPlayersScreen = ({ filterPlayer }) => {
    const [activeTab, setActiveTab] = React.useState('scoring');
    const allRounds = getRounds(sharedRounds).filter(r => r.total != null);
    if (allRounds.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: COLOR_MUTED }}>No rounds yet.</div>;

    // Weather misery score per round — continuous formula, higher = worse conditions
    const calcMisery = (ws) => {
      if (!ws) return null;
      const fl = ws.feelsLike ?? 65;
      const w  = ws.wind ?? 0;
      const p  = ws.precipAccum ?? 0;
      return (p * 40) + (w * 1.5) + Math.max(0, 60 - fl) * 0.5;
    };

    const playerMap = {};
    allRounds.forEach(round => {
      const diff = getRoundDiff(round);
      const misery = calcMisery(round.weatherSnapshot);
      (round.players||[]).forEach(name => {
        if (!name) return;
        if (!playerMap[name]) playerMap[name] = { rounds: 0, totalDiff: 0, bestDiff: Infinity, teamBirdies: 0, teamEagles: 0, teamSnowmen: 0, totalMisery: 0, miseryRounds: 0 };
        playerMap[name].rounds++;
        playerMap[name].totalDiff += diff;
        if (diff < playerMap[name].bestDiff) playerMap[name].bestDiff = diff;
        if (misery != null) { playerMap[name].totalMisery += misery; playerMap[name].miseryRounds++; }
        const rp = round.pars || Array(18).fill(4);
        (round.scores||[]).forEach((score, i) => {
          if (!score) return;
          const par = rp[i], d = score - par;
          if (score >= par*2)  playerMap[name].teamSnowmen++;
          else if (d <= -2)    playerMap[name].teamEagles++;
          else if (d === -1)   playerMap[name].teamBirdies++;
        });
      });
    });

    const MIN_ROUNDS_TO_RANK = 4;
    const sorted = Object.entries(playerMap)
      .filter(([, s]) => s.rounds >= MIN_ROUNDS_TO_RANK)
      .sort((a,b) => {
        const avgA = a[1].totalDiff / a[1].rounds;
        const avgB = b[1].totalDiff / b[1].rounds;
        return avgA - avgB;
      });
    const unqualified = Object.entries(playerMap)
      .filter(([, s]) => s.rounds < MIN_ROUNDS_TO_RANK)
      .sort((a,b) => {
        const avgA = a[1].totalDiff / a[1].rounds;
        const avgB = b[1].totalDiff / b[1].rounds;
        return avgA - avgB;
      });

    // Scoring charms — support ties
    const bestAvg  = sorted.length ? sorted[0][1].totalDiff / sorted[0][1].rounds : null;
    const worstAvg = sorted.length ? sorted[sorted.length-1][1].totalDiff / sorted[sorted.length-1][1].rounds : null;
    const luckSet  = new Set(sorted.filter(([,s]) => s.totalDiff/s.rounds === bestAvg).map(([n]) => n));
    const badSet   = new Set(sorted.filter(([,s]) => s.totalDiff/s.rounds === worstAvg).map(([n]) => n));

    // Weather charms — only players with at least MIN_ROUNDS_TO_RANK rounds of weather data
    const weatherSorted = sorted
      .filter(([, s]) => s.miseryRounds >= MIN_ROUNDS_TO_RANK)
      .sort((a,b) => (a[1].totalMisery/a[1].miseryRounds) - (b[1].totalMisery/b[1].miseryRounds));
    const weatherUnqualified = Object.entries(playerMap)
      .filter(([, s]) => s.miseryRounds > 0 && s.miseryRounds < MIN_ROUNDS_TO_RANK)
      .sort((a,b) => (a[1].totalMisery/a[1].miseryRounds) - (b[1].totalMisery/b[1].miseryRounds));
    const wBestAvg  = weatherSorted.length ? weatherSorted[0][1].totalMisery/weatherSorted[0][1].miseryRounds : null;
    const wWorstAvg = weatherSorted.length ? weatherSorted[weatherSorted.length-1][1].totalMisery/weatherSorted[weatherSorted.length-1][1].miseryRounds : null;
    const weatherLuckSet = new Set(weatherSorted.filter(([,s]) => s.totalMisery/s.miseryRounds === wBestAvg).map(([n]) => n));
    const weatherBadSet  = new Set(weatherSorted.filter(([,s]) => s.totalMisery/s.miseryRounds === wWorstAvg).map(([n]) => n));

    const TAB = (key, label) => (
      <button onClick={() => setActiveTab(key)} style={{
        flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        background: activeTab === key ? COLOR_GREEN_DARK : 'transparent',
        color: activeTab === key ? COLOR_WHITE : COLOR_MUTED,
        border: `1px solid ${activeTab === key ? COLOR_GREEN : COLOR_DARK_GREEN}`,
        borderRadius: 8,
      }}>{label}</button>
    );

    return (
      <div style={{ paddingTop: 'env(safe-area-inset-top, 12px)', paddingLeft: 16, paddingRight: 16, paddingBottom: 60, maxWidth: 480, margin: '0 auto' }}>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {TAB('scoring', '🏆 Scoring')}
          {TAB('weather', '⛈️ Weather')}
        </div>

        {/* SCORING TAB */}
        {activeTab === 'scoring' && sorted.map(([name, stats], idx) => {
          const avgDiff = stats.totalDiff / stats.rounds;
          const avgDiffDisplay = (avgDiff >= 0 ? '+' : '') + avgDiff.toFixed(1);
          const bestDiffDisplay = stats.bestDiff === Infinity ? '—' : (stats.bestDiff >= 0 ? '+' : '') + stats.bestDiff;
          const playerObj = cfgPlayers.find(p => p.name === name);
          const emoji = playerObj ? playerObj.emoji : '';
          const isLuck = luckSet.has(name);
          const isBad  = badSet.has(name);
          return (
            <button key={name}
              onClick={() => navigateTo('roundhistory', { initialTab: 'player', filterPlayer: name })}
              style={{ ...CARD_STYLE, width: '100%', textAlign: 'left', cursor: 'pointer', padding: '14px 16px', marginBottom: 10, display: 'block',
                border: isLuck ? `2px solid #4ADE80` : isBad ? `2px solid #FCA5A5` : `1px solid ${COLOR_DARK_GREEN}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontSize: 22 }}>{emoji}</div>
                  <div>
                    <div style={{ fontSize: 15, color: COLOR_OFF_WHITE, fontWeight: 700 }}>{name}</div>
                    <div style={{ fontSize: 12, color: COLOR_MUTED }}>{stats.rounds} round{stats.rounds !== 1 ? 's' : ''}</div>
                  </div>
                </div>
                {isLuck && <span style={{ fontSize: 11, background: '#166534', color: '#4ADE80', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>🏆 Luck Charm</span>}
                {isBad  && <span style={{ fontSize: 11, background: '#7f1d1d', color: '#FCA5A5', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>💀 Bad Charm</span>}
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: getDiffColor(avgDiff), marginBottom: 8, letterSpacing: '-0.5px' }}>{avgDiffDisplay}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, marginBottom: 8 }}>
                <span style={{ color: '#F1373D', fontWeight: 600 }}>Eagles: {stats.teamEagles}</span>
                <span style={{ color: '#F1373D', fontWeight: 600 }}>Birdies: {stats.teamBirdies}</span>
                <span style={{ color: COLOR_RED, fontWeight: 600 }}>Snowmen: {stats.teamSnowmen}</span>
              </div>
              <div style={{ fontSize: 12, color: COLOR_MUTED, borderTop: `1px solid ${COLOR_DARK_GREEN}`, paddingTop: 8 }}>
                Best: <span style={{ color: getDiffColor(stats.bestDiff === Infinity ? null : stats.bestDiff), fontWeight: 700 }}>{bestDiffDisplay}</span>
              </div>
            </button>
          );
        })}

        {/* SCORING TAB — Not Yet Qualified */}
        {activeTab === 'scoring' && unqualified.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Not Yet Qualified</div>
            <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 10 }}>Needs {MIN_ROUNDS_TO_RANK}+ rounds to rank</div>
            {unqualified.map(([name, stats]) => {
              const avgDiff = stats.totalDiff / stats.rounds;
              const avgDiffDisplay = (avgDiff >= 0 ? '+' : '') + avgDiff.toFixed(1);
              const playerObj = cfgPlayers.find(p => p.name === name);
              const emoji = playerObj ? playerObj.emoji : '';
              return (
                <button key={name}
                  onClick={() => navigateTo('roundhistory', { initialTab: 'player', filterPlayer: name })}
                  style={{ width: '100%', textAlign: 'left', cursor: 'pointer', padding: '10px 14px', marginBottom: 8, display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center', background: 'transparent',
                    border: `1px dashed ${COLOR_DARK_GREEN}`, borderRadius: 10, opacity: 0.65 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 18 }}>{emoji}</div>
                    <div>
                      <div style={{ fontSize: 14, color: COLOR_MUTED, fontWeight: 600 }}>{name}</div>
                      <div style={{ fontSize: 11, color: COLOR_MUTED }}>{stats.rounds} of {MIN_ROUNDS_TO_RANK} round{stats.rounds !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLOR_MUTED }}>{avgDiffDisplay}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* WEATHER TAB */}
        {activeTab === 'weather' && (
          <>
            {/* Legend */}
            <div style={{ ...CARD_STYLE, padding: '12px 14px', marginBottom: 14, fontSize: 11, color: COLOR_MUTED }}>
              <div style={{ fontWeight: 700, color: COLOR_OFF_WHITE, marginBottom: 6 }}>Weather Misery Index — lower is better</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                <span>Rain: inches × 40 pts</span>
                <span>Wind: mph × 1.5 pts</span>
                <span>Cold: degrees below 60°F × 0.5 pts</span>
              </div>
            </div>
            {weatherSorted.length === 0 && (
              <div style={{ textAlign: 'center', color: COLOR_MUTED, fontSize: 14, padding: 24 }}>No weather data yet.</div>
            )}
            {weatherSorted.map(([name, stats]) => {
              const avgMisery = (stats.totalMisery / stats.miseryRounds).toFixed(1);
              const playerObj = cfgPlayers.find(p => p.name === name);
              const emoji = playerObj ? playerObj.emoji : '';
              const isWLuck = weatherLuckSet.has(name);
              const isWBad  = weatherBadSet.has(name);
              return (
                <button key={name}
                  onClick={() => navigateTo('roundhistory', { initialTab: 'player', filterPlayer: name })}
                  style={{ ...CARD_STYLE, width: '100%', textAlign: 'left', cursor: 'pointer', padding: '14px 16px', marginBottom: 10, display: 'block',
                    border: isWLuck ? `2px solid #93C5FD` : isWBad ? `2px solid #F0ABFC` : `1px solid ${COLOR_DARK_GREEN}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontSize: 22 }}>{emoji}</div>
                      <div>
                        <div style={{ fontSize: 15, color: COLOR_OFF_WHITE, fontWeight: 700 }}>{name}</div>
                        <div style={{ fontSize: 12, color: COLOR_MUTED }}>{stats.miseryRounds} round{stats.miseryRounds !== 1 ? 's' : ''} with weather data</div>
                      </div>
                    </div>
                    {isWLuck && <span style={{ fontSize: 11, background: '#1e3a5f', color: '#93C5FD', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>🌤️ Weather Luck</span>}
                    {isWBad  && <span style={{ fontSize: 11, background: '#4a1942', color: '#F0ABFC', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>⛈️ Weather Curse</span>}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: isWLuck ? '#93C5FD' : isWBad ? '#F0ABFC' : COLOR_OFF_WHITE, marginBottom: 4, letterSpacing: '-0.5px' }}>{avgMisery}</div>
                  <div style={{ fontSize: 11, color: COLOR_MUTED }}>avg misery score</div>
                </button>
              );
            })}

            {/* WEATHER TAB — Not Yet Qualified */}
            {weatherUnqualified.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Not Yet Qualified</div>
                <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 10 }}>Needs {MIN_ROUNDS_TO_RANK}+ weather-tracked rounds to rank</div>
                {weatherUnqualified.map(([name, stats]) => {
                  const avgMisery = (stats.totalMisery / stats.miseryRounds).toFixed(1);
                  const playerObj = cfgPlayers.find(p => p.name === name);
                  const emoji = playerObj ? playerObj.emoji : '';
                  return (
                    <button key={name}
                      onClick={() => navigateTo('roundhistory', { initialTab: 'player', filterPlayer: name })}
                      style={{ width: '100%', textAlign: 'left', cursor: 'pointer', padding: '10px 14px', marginBottom: 8, display: 'flex',
                        justifyContent: 'space-between', alignItems: 'center', background: 'transparent',
                        border: `1px dashed ${COLOR_DARK_GREEN}`, borderRadius: 10, opacity: 0.65 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ fontSize: 18 }}>{emoji}</div>
                        <div>
                          <div style={{ fontSize: 14, color: COLOR_MUTED, fontWeight: 600 }}>{name}</div>
                          <div style={{ fontSize: 11, color: COLOR_MUTED }}>{stats.miseryRounds} of {MIN_ROUNDS_TO_RANK} round{stats.miseryRounds !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: COLOR_MUTED }}>{avgMisery}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

      </div>
    );
  };

  // ============================================================
  // SCORECARD SCREEN
  // ============================================================
  const EventsScreen = () => {
    const [eventsData, setEventsData] = useState(null);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [eventsError, setEventsError] = useState(null);

    useEffect(() => {
      const fetchEvents = async () => {
        try {
          setEventsLoading(true);
          const resp = await fetch(SPOND_EVENTS_URL, {
            headers: { 'Authorization': `token ${SPOND_GH_TOKEN}`, 'Accept': 'application/vnd.github.v3+json' }
          });
          if (!resp.ok) throw new Error(`GitHub API: ${resp.status}`);
          const json = await resp.json();
          const decoded = JSON.parse(atob(json.content));
          setEventsData(decoded);
        } catch (err) {
          console.error('Events fetch error:', err);
          setEventsError(err.message);
        } finally {
          setEventsLoading(false);
        }
      };
      fetchEvents();
    }, []);

    const formatDate = (isoStr) => {
      if (!isoStr) return '';
      const d = new Date(isoStr);
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
    };

    const formatTime = (isoStr) => {
      if (!isoStr) return '';
      const d = new Date(isoStr);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
    };

    const getMeetTime = (startStr) => {
      if (!startStr) return '';
      const d = new Date(startStr);
      d.setMinutes(d.getMinutes() - 30);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
    };

    const isFuture = (isoStr) => new Date(isoStr) > new Date();

    const courseEmojis = {
      'Spooky Brook': '🌲', 'Quail Brook': '🦅', 'Warrenbrook': '⛰️',
      'Green Knoll': '🌿', 'Neshanic Valley': '🏞️', 'Fox Hollow': '🦊',
      'High Bridge Hills': '⛰️', 'Architects': '📐', 'Pebble Creek': '🪨',
      'Hominy Hill': '🌾', 'Oak Hill': '🌳', 'Shark River': '🦈'
    };

    if (eventsLoading) return (
      <div style={{ padding: 20, textAlign: 'center', color: COLOR_MUTED }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_MUTED, marginBottom: 12 }}>EVENTS</div>
        Loading events from Spond...
      </div>
    );

    if (eventsError) return (
      <div style={{ padding: 20, textAlign: 'center', color: COLOR_RED }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}><StatusIcon status="CAUTION" size={40}/></div>
        Could not load events: {eventsError}
      </div>
    );

    const events = eventsData?.events || [];
    const futureEvents = events.filter(e => isFuture(e.startTime) && !e.cancelled);
    const pastEvents = events.filter(e => !isFuture(e.startTime));

    return (
      <div style={{ padding: '0 12px 24px' }}>
        {/* Quick call roster — top of screen */}
        <div style={{ marginTop: 12, marginBottom: 16, background: COLOR_CARD, borderRadius: 10, padding: '14px 16px', border: `1px solid ${COLOR_DARK_GREEN}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GOLD, marginBottom: 10 }}>Quick Call</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[
              { name: 'Gary', phone: '+19084182242' }, { name: 'Jeff', phone: '+14845157673' },
              { name: 'John', phone: '+19084513021' }, { name: 'Matt', phone: '+17322211637' },
              { name: 'Patrick', phone: '+19082466354' }, { name: 'Tom', phone: '+19083032387' },
              { name: 'Tommy', phone: '+19085477457' }, { name: 'Tony', phone: '+19082083088' },
            ].map(m => (
              <a key={m.name} href={`tel:${m.phone}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#000', border: `1px solid ${COLOR_GREEN}`, padding: '8px 12px', borderRadius: 8, color: COLOR_WHITE, textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLOR_GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
                {m.name}
              </a>
            ))}
          </div>
        </div>

        {/* Last updated */}
        {eventsData?.fetchedAt && (
          <div style={{ fontSize: 10, color: COLOR_MUTED, textAlign: 'right', marginBottom: 8 }}>
            Updated: {new Date(eventsData.fetchedAt).toLocaleString()}
          </div>
        )}

        {/* Upcoming events */}
        {futureEvents.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_GOLD, marginBottom: 10, letterSpacing: '0.5px' }}>
              UPCOMING ({futureEvents.length})
            </div>
            {futureEvents.map(evt => {
              const spotsText = evt.spotsRemaining > 0 ? `${evt.spotsRemaining} spot${evt.spotsRemaining !== 1 ? 's' : ''} open` : 'FULL';
              const spotsColor = evt.spotsRemaining > 0 ? COLOR_GREEN : COLOR_RED;
              const courseEmoji = courseEmojis[evt.course] || '';

              return (
                <div key={evt.id} style={{ background: COLOR_CARD, borderRadius: 12, marginBottom: 12, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}` }}>
                  {/* Event photo banner */}
                  {evt.coursePhoto && evt.coursePhoto.startsWith('http') && (
                    <div style={{ width: '100%', overflow: 'hidden' }}>
                      <img src={evt.coursePhoto} alt={evt.course || evt.heading}
                        style={{ width: '100%', height: '200px', objectFit: 'cover', objectPosition: 'center', background: '#111', display: 'block' }}
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  {/* Course banner */}
                  <div style={{ background: '#111', padding: '16px 16px 12px', borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 11, color: COLOR_MUTED, fontWeight: 600, marginBottom: 4 }}>{formatDate(evt.startTime)}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_OFF_WHITE }}>{courseEmoji} {evt.course || evt.heading}</div>
                        {evt.locationAddress && <div style={{ fontSize: 11, color: COLOR_OFF_WHITE, marginTop: 3 }}>{evt.locationAddress}</div>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_WHITE, background: spotsColor === COLOR_GREEN ? COLOR_GREEN_BG : COLOR_RED_BG, padding: '3px 8px', borderRadius: 6 }}>
                          {spotsText}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Time details */}
                  <div style={{ padding: '10px 16px', display: 'flex', gap: 20, borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>
                    <div>
                      <div style={{ fontSize: 10, color: COLOR_MUTED, fontWeight: 600 }}>TEE TIME</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_GREEN }}>{formatTime(evt.startTime)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: COLOR_MUTED, fontWeight: 600 }}>ARRIVE BY</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OFF_WHITE }}>{getMeetTime(evt.startTime)}</div>
                    </div>
                    {evt.maxSpots && (
                      <div>
                        <div style={{ fontSize: 10, color: COLOR_MUTED, fontWeight: 600 }}>GROUP SIZE</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OFF_WHITE }}>{evt.attendance.in.length}/{evt.maxSpots}</div>
                      </div>
                    )}
                  </div>

                  {/* Attendance */}
                  <div style={{ padding: '10px 16px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_MUTED, marginBottom: 6 }}>ATTENDANCE</div>
                    {evt.attendance.in.length > 0 && (
                      <div style={{ marginBottom: 6 }}>
                        {evt.attendance.in.map((m, i) => (
                          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLOR_GREEN_BG, padding: '3px 8px', borderRadius: 6, marginRight: 6, marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#000' }}>
                            <StatusIcon status="GO" size={12}/> {m.name}
                            {m.phone && <a href={`tel:${m.phone}`} style={{ color: '#000', textDecoration: 'none', marginLeft: 4, display: 'inline-flex', alignItems: 'center' }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg></a>}
                          </span>
                        ))}
                      </div>
                    )}
                    {evt.attendance.out.length > 0 && (
                      <div style={{ marginBottom: 6 }}>
                        {evt.attendance.out.map((m, i) => (
                          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLOR_RED_BG, padding: '3px 8px', borderRadius: 6, marginRight: 6, marginBottom: 4, fontSize: 12, fontWeight: 600, opacity: 0.7, color: COLOR_WHITE }}>
                            Declined: {m.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {evt.attendance.no_answer.length > 0 && (
                      <div style={{ marginBottom: 6 }}>
                        {evt.attendance.no_answer.map((m, i) => (
                          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLOR_GOLD_BG, padding: '3px 8px', borderRadius: 6, marginRight: 6, marginBottom: 4, fontSize: 12, fontWeight: 600, opacity: 0.7, color: '#000' }}>
                            Maybe: {m.name}
                            {m.phone && <a href={`tel:${m.phone}`} style={{ color: '#000', textDecoration: 'none', marginLeft: 4, display: 'inline-flex', alignItems: 'center' }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg></a>}
                          </span>
                        ))}
                      </div>
                    )}
                    {evt.attendance.waiting.length > 0 && (
                      <div>
                        {evt.attendance.waiting.map((m, i) => (
                          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLOR_GOLD_BG, padding: '3px 8px', borderRadius: 6, marginRight: 6, marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#000' }}>
                            Pending: {m.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Spond link */}
                  <a href={evt.spondUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'block', padding: '10px 16px', borderTop: `1px solid ${COLOR_DARK_GREEN}`, textAlign: 'center', color: COLOR_WHITE, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                    Open in Spond →
                  </a>
                </div>
              );
            })}
          </div>
        )}

        {/* Past events */}
        {pastEvents.length > 0 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_MUTED, marginBottom: 10, letterSpacing: '0.5px' }}>
              PAST ({pastEvents.length})
            </div>
            {pastEvents.map(evt => (
              <div key={evt.id} style={{ background: COLOR_CARD, borderRadius: 10, marginBottom: 8, padding: '12px 16px', opacity: 0.6, border: `1px solid ${COLOR_DARK_GREEN}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OFF_WHITE }}>{courseEmojis[evt.course] || ''} {evt.course || evt.heading}</div>
                    <div style={{ fontSize: 11, color: COLOR_MUTED }}>{formatDate(evt.startTime)}</div>
                  </div>
                  <div style={{ fontSize: 11, color: COLOR_MUTED }}>
                    {evt.attendance.in.length} played
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {events.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: COLOR_MUTED }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: COLOR_MUTED, marginBottom: 12 }}>EVENTS</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>No events scheduled yet</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>Events will appear here once created in Spond</div>
          </div>
        )}

        {/* Invite link banner — bottom of screen */}
        <div style={{ marginTop: 20, background: COLOR_CARD, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${COLOR_DARK_GREEN}` }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GOLD }}>Join Golf-Buddies on Spond</div>
            <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 2 }}>RSVP, chat, and coordinate tee times</div>
          </div>
          <a href={SPOND_INVITE_LINK} target="_blank" rel="noopener noreferrer"
            style={{ background: COLOR_GREEN_DARK, color: COLOR_WHITE, padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Join Group
          </a>
        </div>
      </div>
    );
  };


  // ── PLANNING MODE — CALENDAR TILE ─────────────────────────
  const CalendarTile = ({ day, period, status, summary, reason, past }) => {
    const isPast = past || status === 'PAST';
    const isUnknown = status === 'UNKNOWN';
    const isActive = activeWindow.dayIdx === day.d && activeWindow.period === period;
    const ss = (isPast || isUnknown) ? { bg: '#1e3620', color: '#556655', border: '#2a3d2a', text: '#556655' } : statusStyle(status);
    const periodLabel = period === 'morn' ? 'AM' : 'PM';
    const periodHours = period === 'morn' ? '6a–12p' : '12p–8p';
    const isToday = day.d === 0;

    // Build trigger hierarchy from hourly data (S84 max-case card format)
    const hrs = period === 'morn' ? day.mornHrs : day.aftnHrs;
    const tileTriggers = React.useMemo(() => {
      if (!hrs || !hrs.length || isPast || isUnknown || status === 'GO') return null;
      // Collect all triggers, then deduplicate by type — keep worst value per type per severity
      const rawTriggers = {};
      for (const h of hrs) {
        const hr = getHourStatus(h, profile);
        if (hr.triggers) {
          for (const [lv, trigs] of Object.entries(hr.triggers)) {
            if (!rawTriggers[lv]) rawTriggers[lv] = {};
            for (const t of trigs) {
              // Extract trigger type: "Wind 12 mph" → "Wind", "Gusts 31 mph" → "Gusts", "Precip 68%" → "Precip"
              // "Feels 22° (limit 45°)" → "Feels", "Thunderstorms" → "Thunderstorms"
              const typeMatch = t.match(/^([A-Za-z\s\/]+?)[\s]?\d/);
              const trigType = typeMatch ? typeMatch[1].trim() : t;
              // Extract numeric value for comparison
              const numMatch = t.match(/(\d+)/);
              const numVal = numMatch ? parseInt(numMatch[1]) : 0;
              // Keep the trigger string with the worst (highest) numeric value for this type
              // Exception: Feels-like triggers — lower is worse for cold, higher for hot
              if (!rawTriggers[lv][trigType] || numVal > rawTriggers[lv][trigType].val) {
                rawTriggers[lv][trigType] = { val: numVal, text: t };
              }
            }
          }
        }
      }
      // Convert to arrays, suppressing trigger types already shown in a higher severity tier
      const levels = ['NO-GO', 'STRONG CAUTION', 'CAUTION'];
      const seenTypes = new Set();
      const result = {};
      for (const lv of levels) {
        if (rawTriggers[lv]) {
          const filtered = {};
          for (const [type, data] of Object.entries(rawTriggers[lv])) {
            if (!seenTypes.has(type)) {
              filtered[type] = data;
              seenTypes.add(type);
            }
          }
          if (Object.keys(filtered).length > 0) {
            result[lv] = Object.values(filtered).map(v => v.text);
          }
        }
      }
      return Object.keys(result).length > 0 ? result : null;
    }, [hrs, status, isPast, isUnknown]);

    const handleTap = () => {
      if (isPast || isUnknown) return;
      if (isActive) {
        setActiveWindow(prev => ({ ...prev, dayIdx: -1 }));
      } else {
        setActiveWindow({ dayIdx: day.d, period });
      }
      setTimeout(() => scrollToTop(), 50);
    };

    return (
      <button
        onClick={handleTap}
        style={{
          flex: 1, minWidth: 0, border: `2px solid ${isActive ? ss.color : ss.border}`,
          borderRadius: 12, background: ss.bg,
          padding: '7px 6px', cursor: (isPast || isUnknown) ? 'default' : 'pointer',
          opacity: isPast ? 0.3 : isUnknown ? 0.5 : 1, textAlign: 'left',
          boxShadow: isActive ? `0 0 14px ${ss.color}55` : 'none',
          transition: 'all 0.15s',
        }}
      >
        {/* Period label + hours + StatusIcon */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700, color: isPast ? '#556655' : ss.text }}>{periodLabel}</span>
            <span style={{ fontSize: 11, color: isPast ? '#445544' : ss.text, opacity: 0.7, marginLeft: 6 }}>{periodHours}</span>
          </div>
          {!isPast && !isUnknown && <StatusIcon status={status} size={22} />}
        </div>

        {isPast ? (
          <div style={{ fontSize: 14, color: '#445544', marginTop: 6 }}>— Past —</div>
        ) : isUnknown ? (
          <div style={{ fontSize: 12, color: '#556655', marginTop: 6 }}>No forecast</div>
        ) : (
          <>
            {/* Sky: emoji + condition text — prominent, same pattern as HourlyRow */}
            {summary && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, marginBottom: 6 }}>
                {renderSkyIcon(summary.dominantCondition || 'Clear', summary.isDaytime !== false, 26)}
                <span style={{ fontSize: 13, fontWeight: 600, color: ss.text }}>{skyLabel(summary.dominantCondition || 'Clear')}</span>
              </div>
            )}

            {/* Feels-Like (primary) + actual in parens */}
            <div style={{ fontSize: 12, color: ss.text, opacity: 0.8 }}>Feels Like</div>
            {summary && (
              <div style={{ fontSize: 22, fontWeight: 700, color: ss.text, marginBottom: 6 }}>
                {Math.round(summary.maxFeelsLike)}°
                {summary.high != null && (
                  <span style={{ fontSize: 13, fontWeight: 400, opacity: 0.6 }}> ({summary.high}°)</span>
                )}
              </div>
            )}

            {/* TRIGGER HIERARCHY — S84 max-case card format */}
            {tileTriggers && status !== 'GO' && (() => {
              const levels = ['NO-GO', 'STRONG CAUTION', 'CAUTION'];
              const parts = [];
              for (const lv of levels) {
                if (tileTriggers[lv] && tileTriggers[lv].length > 0) {
                  const isWorst = lv === status;
                  const lvLabel = lv === 'STRONG CAUTION' ? 'STRONG CAUTION' : lv;
                  // eslint-disable-next-line no-unused-vars
                  const lvStyle = statusStyle(lv);
                  if (isWorst) {
                    // Worst level = section header + white bold triggers with indicator icons
                    parts.push(
                      <div key={lv} style={{ marginTop: 4 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#fff', marginBottom: 2 }}>
                          {lvLabel} TRIGGERS:
                        </div>
                        {tileTriggers[lv].map((t, i) => (
                          <div key={i} style={{ fontSize: 11, fontWeight: 700, color: '#fff', paddingLeft: 2, display:'flex', alignItems:'center', gap:4 }}>
                            <StatusIcon status={lv} size={12} /> {t}
                          </div>
                        ))}
                      </div>
                    );
                  } else {
                    // Lower severity = "ALSO {LEVEL}:" + triggers in card dark text with indicator icons
                    parts.push(
                      <div key={lv} style={{ marginTop: 3 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: ss.text, opacity: 0.8 }}>
                          ALSO {lv === 'STRONG CAUTION' ? 'STR CAU' : lv}:
                        </div>
                        {tileTriggers[lv].map((t, i) => (
                          <div key={i} style={{ fontSize: 11, fontWeight: 500, color: ss.text, opacity: 0.7, paddingLeft: 2, display:'flex', alignItems:'center', gap:4 }}>
                            <StatusIcon status={lv} size={12} /> {t}
                          </div>
                        ))}
                      </div>
                    );
                  }
                }
              }
              return parts.length > 0 ? <div style={{ marginTop: 2 }}>{parts}</div> : null;
            })()}

            {/* UV + Bugs footer */}
            {summary && (summary.maxUV > 3 || summary.maxBug > 1) && (
              <div style={{ fontSize: 11, color: ss.text, opacity: 0.6, marginTop: 4 }}>
                {summary.maxUV > 3 ? `UV ${Math.round(summary.maxUV)} ${uvLabel(Math.round(summary.maxUV))}` : ''}{summary.maxUV > 3 && summary.maxBug > 1 ? ' · ' : ''}{summary.maxBug > 1 ? `Bugs ${summary.maxBug}/10` : ''}
              </div>
            )}

            {/* Cart (today only) */}
            {isToday && badResult?.ground && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, paddingTop: 3, borderTop: `1px solid ${ss.text}33` }}>
                <span style={{ fontSize: 11, color: ss.text, opacity: 0.8 }}>Cart</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: badResult.ground.sogginess === 'soggy' ? '#fff' : ss.text }}>
                  {badResult.ground.cartStatus || 'All Clear'}
                </span>
              </div>
            )}
          </>
        )}
      </button>
    );
  };

  // ── PLANNING MODE — INLINE DRILL-DOWN ─────────────────────
  const InlineDrillDown = ({ day, period }) => {
    const hrs = period === 'morn' ? day.mornHrs : day.aftnHrs;
    const status = period === 'morn' ? day.mornStatus : day.aftnStatus;
    const ss = statusStyle(status);
    const isToday = day.d === 0;

    if (!hrs || !hrs.length) {
      return (
        <div style={{ background: COLOR_CARD, borderRadius: 12, padding: 16, margin: '8px 0', border: `1px solid ${COLOR_DARK_GREEN}` }}>
          <div style={{ color: COLOR_MUTED, fontSize: 13 }}>No forecast data for this window.</div>
        </div>
      );
    }

    // Gust color helper (S85 #5: 20/30/40)
    const gustColor = (g) => g > 40 ? STATUS_COLORS['NO-GO'].color : g > 30 ? STATUS_COLORS['STRONG CAUTION'].color : g > 20 ? STATUS_COLORS['CAUTION'].color : '#e0e0e0';
    // Precip color
    // eslint-disable-next-line no-unused-vars
    const pColor = (v) => v > PRECIP_NOGO ? STATUS_COLORS['NO-GO'].color : v > PRECIP_STR_CAU ? STATUS_COLORS['STRONG CAUTION'].color : v > PRECIP_CAUTION ? STATUS_COLORS['CAUTION'].color : '#e0e0e0';
    // Wind color
    // eslint-disable-next-line no-unused-vars
    const wColor = (v) => v > WIND_NOGO ? STATUS_COLORS['NO-GO'].color : v > WIND_STR_CAU ? STATUS_COLORS['STRONG CAUTION'].color : v > WIND_CAUTION ? STATUS_COLORS['CAUTION'].color : '#e0e0e0';
    // Temp color
    // eslint-disable-next-line no-unused-vars
    const tColor = (f) => (f < profile.minTemp - 5 || f > profile.maxTemp + 5) ? STATUS_COLORS['NO-GO'].color : (f < profile.minTemp || f > profile.maxTemp) ? STATUS_COLORS['CAUTION'].color : '#e0e0e0';

    return (
      <div style={{ background: ss.bg, borderRadius: 12, padding: 14, margin: '8px 0', border: `2px solid ${ss.border}` }}>
        {/* Status header with close button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <StatusIcon status={status} size={20} />
            <span style={{ fontSize: 18, fontWeight: 800, color: ss.text }}>{status}</span>
            <span style={{ fontSize: 12, color: ss.text, opacity: 0.7, marginLeft: 4 }}>
              {day.dayLabel} · {period === 'morn' ? '6 AM – 12 PM' : '12 PM – 8 PM'}
            </span>
          </div>
          <button
            onClick={() => setActiveWindow(prev => ({ ...prev, dayIdx: -1 }))}
            style={{ background: 'transparent', border: 'none', color: ss.text, fontSize: 24, cursor: 'pointer', padding: 4 }}
          >×</button>
        </div>

        {/* Cart status (today only) */}
        {isToday && badResult?.ground && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'rgba(0,0,0,0.2)', borderRadius: 6, marginBottom: 8, border: `1px solid ${badResult.ground.sogginess === 'soggy' ? STATUS_COLORS['CAUTION'].border : STATUS_COLORS['GO'].border}` }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: badResult.ground.sogginess === 'soggy' ? STATUS_COLORS['CAUTION'].color : STATUS_COLORS['GO'].color }}>
              Cart: {badResult.ground.cartStatus || 'All Clear'}
            </span>
            {badResult.past24Rain > 0 && (
              <span style={{ fontSize: 12, color: '#e0e0e0' }}>{badResult.past24Rain}" rain (24h)</span>
            )}
          </div>
        )}



        {/* Pollen — 3 types compact */}
        <div style={{ display: 'flex', gap: 12, fontSize: 13, padding: '6px 12px', background: 'rgba(0,0,0,0.25)', borderRadius: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ color: '#e0e0e0', fontWeight: 600 }}>Pollen</span>
          {pollenData?.success && !pollenData.offSeason
            ? <>
                <span style={{ color: pollenData.tree.color }}>🌳 {pollenData.tree.label}</span>
                <span style={{ color: pollenData.grass.color }}>🌾 {pollenData.grass.label}</span>
                <span style={{ color: pollenData.weed.color }}>🌿 {pollenData.weed.label}</span>
              </>
            : <span style={{ color: '#668866' }}>No data</span>
          }
        </div>

        {/* Hourly rows — S164 fix: width:100% on wrapper so cards fill container */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {hrs.map((h, i) => (
            <div key={i} style={{ width: '100%' }}>
              <HourlyRow h={h} profile={profile} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── PLANNING MODE SCREEN ───────────────────────────────────
  const PlanningScreen = () => {
    const calDays = buildCalendarDays();
    const activeDayObj = activeWindow.dayIdx >= 0 ? calDays[activeWindow.dayIdx] : null;

    return (
      <div style={{ padding: '12px 12px 80px' }}>
        {/* Source legend removed — lives behind 📡 icon in header (S91) */}

        {/* Inline drill-down at top when active */}
        {activeDayObj && (
          <InlineDrillDown day={activeDayObj} period={activeWindow.period} />
        )}

        {/* Loading / error */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: 32, color: COLOR_MUTED }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_MUTED, marginBottom: 8 }}>Loading...</div>
            <div>Loading weather for {selectedCourse.name}…</div>
          </div>
        )}
        {weatherError && !isLoading && (
          <div style={{ background: '#EF4444', border: '1px solid #DC2626', borderRadius: 10, padding: 16, marginBottom: 12, color: '#450A0A', fontSize: 13, fontWeight: 600 }}>
            {weatherError}
          </div>
        )}

        {/* 7-day calendar grid */}
        {!isLoading && calDays.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {calDays.map(day => (
              <div key={day.d} style={{ marginBottom: 6 }}>
                {/* Day label — bold name + date */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4, paddingLeft: 2 }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: COLOR_WHITE }}>{day.dayLabel.split(',')[0] || day.dayLabel}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLOR_WHITE }}>{day.dateStr ? new Date(day.dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                  {(() => {
                    const sunsetRaw = weatherData?.daily?.find?.(d2 => d2.dateStr === day.dateStr)?.sunset
                      || weatherData?.daily?.sunset?.[day.d];
                    if (!sunsetRaw) return null;
                    const st = new Date(sunsetRaw);
                    const h = st.getHours(), m = st.getMinutes();
                    const label = `🌇 ${h > 12 ? h-12 : h}:${String(m).padStart(2,'0')} PM`;
                    return <span style={{ fontSize: 11, color: '#FCD34D', opacity: 0.8, marginLeft: 4 }}>{label}</span>;
                  })()}
                </div>
                {/* AM / PM tiles side by side */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <CalendarTile
                    day={day} period="morn"
                    status={day.mornStatus} summary={day.mornSummary}
                    reason={day.mornReason}
                    past={day.d === 0 && new Date().getHours() >= 12}
                  />
                  <CalendarTile
                    day={day} period="aftn"
                    status={day.aftnStatus} summary={day.aftnSummary}
                    reason={day.aftnReason}
                    past={day.d === 0 && new Date().getHours() >= 20}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Spond events */}
        {(() => {
          const tileEvents = spondEvents.filter(e => new Date(e.startTime) > new Date() && !e.cancelled).slice(0, 3);
          if (!tileEvents.length) return null;
          return (
          <div 
            onClick={() => setScreen('events')}
            style={{ background: COLOR_CARD, borderRadius: 12, padding: 14, border: `1px solid ${COLOR_DARK_GREEN}`, cursor: 'pointer' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_MUTED, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Upcoming Events</span>
              <span style={{ fontSize: 12, color: COLOR_GREEN }}>View All →</span>
            </div>
            {tileEvents.map((ev, i) => {
              const teeTime = ev.startTime ? new Date(ev.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : null;
              const arriveTime = ev.startTime ? (() => { const d = new Date(ev.startTime); d.setMinutes(d.getMinutes() - 30); return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }); })() : null;
              const dateStr = ev.startTime ? new Date(ev.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' }) : '';
              return (
                <div key={i} style={{ padding: '8px 0', borderBottom: i < tileEvents.length - 1 ? `1px solid ${COLOR_DARK_GREEN}` : 'none' }}>
                  <div style={{ fontSize: 15, color: COLOR_WHITE, fontWeight: 700 }}>{ev.course || ev.heading || 'TBD'}</div>
                  <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 2 }}>
                    {dateStr}{teeTime ? ` · Tee Time ${teeTime}` : ''}{arriveTime ? ` · Arrive ${arriveTime}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
          );
        })()}
      </div>
    );
  };

  // ── ON COURSE SCREEN ──────────────────────────────────────
  const OnCourseScreen = () => {
    const [iemData, setIemData] = React.useState(null);
    const [iemLoading, setIemLoading] = React.useState(true);
    const [iemRefreshTick, setIemRefreshTick] = React.useState(0);

    // S227: Storm audio alert
    const audioLoopRef = React.useRef(null);
    const audioSilencedRef = React.useRef(false);
    const prevAlertStateRef = React.useRef('CLEAR');
    const [showAudioPopup, setShowAudioPopup] = React.useState(false);

    const stopAudioLoop = React.useCallback(() => {
      if (audioLoopRef.current) { clearTimeout(audioLoopRef.current); audioLoopRef.current = null; }
    }, []);

    const playAudioPattern = React.useCallback((state) => {
      stopAudioLoop();
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume();

      let beeps, cycleDur;
      if (state === 'GET IN NOW') {
        beeps = [
          {freq:1400,start:0,    dur:0.12,type:'square'},
          {freq:1400,start:0.2,  dur:0.12,type:'square'},
          {freq:1400,start:0.4,  dur:0.12,type:'square'},
          {freq:1600,start:0.6,  dur:0.4, type:'square'},
        ];
        cycleDur = 1200;
      } else if (state === 'HEAVY STORM') {
        beeps = [
          {freq:1200,start:0,    dur:0.2,type:'sawtooth'},
          {freq:1000,start:0.3,  dur:0.2,type:'sawtooth'},
          {freq:800, start:0.6,  dur:0.4,type:'sawtooth'},
        ];
        cycleDur = 1800;
      } else {
        beeps = [
          {freq:880,start:0,    dur:0.4,type:'sine'},
          {freq:660,start:0.6,  dur:0.6,type:'sine'},
        ];
        cycleDur = 2500;
      }

      const playOneCycle = () => {
        if (audioSilencedRef.current || !audioEnabledRef.current) return;
        for (const b of beeps) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = b.type || 'square';
          osc.frequency.setValueAtTime(b.freq, ctx.currentTime + b.start);
          gain.gain.setValueAtTime(1.0, ctx.currentTime + b.start);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + b.start + b.dur);
          osc.start(ctx.currentTime + b.start);
          osc.stop(ctx.currentTime + b.start + b.dur + 0.05);
        }
        audioLoopRef.current = setTimeout(playOneCycle, cycleDur);
      };
      playOneCycle();
    }, [stopAudioLoop]);

    // Watch alertState — fire audio on storm, stop on CLEAR
    React.useEffect(() => {
      const state = iemData?.alertState;
      if (!state) return;

      if (state === 'CLEAR' || state === 'UNKNOWN' || state === 'OFF_HOURS') {
        stopAudioLoop();
        setShowAudioPopup(false);
        audioSilencedRef.current = false;
        prevAlertStateRef.current = 'CLEAR';
        return;
      }

      if (!audioEnabledRef.current) {
        stopAudioLoop();
        setShowAudioPopup(false);
        return;
      }

      // If audio was just toggled ON — reset so current storm fires as new
      if (audioEnabledRef._justEnabled) {
        audioEnabledRef._justEnabled = false;
        prevAlertStateRef.current = 'CLEAR';
        audioSilencedRef.current = false;
      }

      const prev = prevAlertStateRef.current;
      const escalated = (
        (state === 'GET IN NOW' && prev !== 'GET IN NOW') ||
        (state === 'HEAVY STORM' && prev === 'RAIN APPROACHING')
      );
      const isNew = prev === 'CLEAR' || prev === 'UNKNOWN';

      if (isNew || escalated) {
        audioSilencedRef.current = false;
        playAudioPattern(state);
        setShowAudioPopup(true);
      } else if (!audioSilencedRef.current) {
        // Same state, not silenced — keep playing (loop manages itself)
        if (!audioLoopRef.current) {
          playAudioPattern(state);
          setShowAudioPopup(true);
        }
      }
      prevAlertStateRef.current = state;
    }, [iemData, profile.audioAlertEnabled, playAudioPattern, stopAudioLoop]);

    // Cleanup on unmount
    React.useEffect(() => {
      return () => { stopAudioLoop(); };
    }, [stopAudioLoop]);

    // Fetch IEM NEXRAD cells — locked spec S129 — runs 24/7, no off-hours gate
    React.useEffect(() => {
      const load = async () => {
        try {
          const url = 'https://mesonet.agron.iastate.edu/geojson/nexrad_attr.py';
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          const r = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (r.status === 503) throw new Error('IEM_503');
          if (!r.ok) throw new Error('IEM fetch failed');
          const gj = await r.json();
          const features = gj.features || [];
          const lat = selectedCourse?.lat;
          const lng = selectedCourse?.lng;

          const R = 3958.8;
          const toRad = d => d * Math.PI / 180;
          const distMi = (la1, lo1, la2, lo2) => {
            const dLat = toRad(la2-la1), dLon = toRad(lo2-lo1);
            const a = Math.sin(dLat/2)**2 + Math.cos(toRad(la1))*Math.cos(toRad(la2))*Math.sin(dLon/2)**2;
            return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          };
          const bearing = (la1, lo1, la2, lo2) => {
            const y = Math.sin(toRad(lo2-lo1))*Math.cos(toRad(la2));
            const x = Math.cos(toRad(la1))*Math.sin(toRad(la2)) - Math.sin(toRad(la1))*Math.cos(toRad(la2))*Math.cos(toRad(lo2-lo1));
            return (Math.atan2(y, x)*180/Math.PI + 360) % 360;
          };
          const bearingLabel = b => {
            const dirs = ['N','NE','E','SE','S','SW','W','NW'];
            return dirs[Math.round(b/45)%8];
          };

          // Process all cells DBZ>=35 within 50mi
          const allCells = features
            .filter(f => (f.properties?.max_dbz || 0) >= 35)
            .map(f => {
              const p = f.properties;
              const coords = f.geometry?.coordinates;
              const cLat = coords?.[1] ?? 0;
              const cLon = coords?.[0] ?? 0;
              const dist = distMi(lat, lng, cLat, cLon);
              const bear = bearing(lat, lng, cLat, cLon);
              // S177 BUGFIX: IEM `drct` is meteorological "direction storm is COMING FROM"
              // (per IEM docs: a "west" drct = storm moving from west to east).
              // Flip by 180° to get actual motion direction. Same pattern as NWS warning
              // text parser at line 2482. Without this flip, movingToward is fully inverted:
              // east cells moving away triggered false alerts; west cells approaching were ignored.
              const motionBear = ((p.drct || 0) + 180) % 360;
              const speedKt = p.sknt || 0;
              const speedMph = speedKt * 1.15078;
              // Valid speed required for ETA (except 10mi hard floor)
              const hasSpeed = speedMph > 2;
              const etaMin = hasSpeed ? Math.round(dist / speedMph * 60) : null;
              // ±45° bearing cone
              const bearFromCellToCourse = bearing(cLat, cLon, lat, lng);
              const angleDelta = Math.abs(((motionBear - bearFromCellToCourse) + 360) % 360);
              const movingToward = angleDelta < 45 || angleDelta > 315;
              return {
                dist: Math.round(dist*10)/10,
                bear, bearLabel: bearingLabel(bear),
                dbz: p.max_dbz,
                speedMph: Math.round(speedMph),
                etaMin, movingToward, hasSpeed,
                cLat, cLon
              };
            })
            .filter(c => c.dist <= 50)
            .sort((a, b) => (a.etaMin ?? 999) - (b.etaMin ?? 999));

          // Determine alert tier per locked spec:
          // GET IN NOW:      DBZ>=45, ETA<=20min OR dist<=10mi (moving toward)
          // HEAVY STORM:     DBZ>=45, ETA<=60min OR dist<=10mi (no-speed fallback)
          // RAIN APPROACHING:DBZ>=35, ETA<=90min, moving toward, has speed
          // CLEAR:           nothing qualifies

          let alertState = 'CLEAR';
          let alertColor = '#4ADE80';
          let alertMsg = 'No storms detected';
          let primaryCell = null;
          let additionalCount = 0;

          // Check GET IN NOW first (worst state)
          const getInNow = allCells.filter(c =>
            c.dbz >= 45 && c.movingToward &&
            ((c.hasSpeed && c.etaMin != null && c.etaMin <= 20) || c.dist <= 10)
          );
          // Check HEAVY STORM
          const heavyStorm = allCells.filter(c =>
            c.dbz >= 45 && c.movingToward &&
            ((c.hasSpeed && c.etaMin != null && c.etaMin <= 60) || c.dist <= 10)
          );
          // Check RAIN APPROACHING
          const rainApproach = allCells.filter(c =>
            c.dbz >= 35 && c.movingToward && c.hasSpeed &&
            c.etaMin != null && c.etaMin <= 90
          );

          if (getInNow.length > 0) {
            alertState = 'GET IN NOW';
            alertColor = '#EF4444';
            primaryCell = getInNow[0];
            additionalCount = getInNow.length - 1;
            const eta = primaryCell.etaMin;
            alertMsg = primaryCell.dist <= 10 && !primaryCell.hasSpeed
              ? `Storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — speed unknown — monitor closely`
              : `Storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${eta} min`;
          } else if (heavyStorm.length > 0) {
            alertState = 'HEAVY STORM';
            alertColor = '#FB923C';
            primaryCell = heavyStorm[0];
            additionalCount = heavyStorm.length - 1;
            const etaLow = primaryCell.etaMin;
            const etaHigh = primaryCell.etaMin ? primaryCell.etaMin + Math.round(primaryCell.etaMin * 0.2) : null;
            alertMsg = primaryCell.dist <= 10 && !primaryCell.hasSpeed
              ? `Heavy storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — speed unknown — monitor closely`
              : `Heavy storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${etaLow}–${etaHigh} min · potential lightning`;
          } else if (rainApproach.length > 0) {
            alertState = 'RAIN APPROACHING';
            alertColor = '#FACC15';
            primaryCell = rainApproach[0];
            additionalCount = rainApproach.length - 1;
            const etaLow = primaryCell.etaMin;
            const etaHigh = primaryCell.etaMin + Math.round(primaryCell.etaMin * 0.2);
            alertMsg = `Rain from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${etaLow}–${etaHigh} min`;
          }

          setIemData({
            cells: allCells, primaryCell, additionalCount,
            alertState, alertColor, alertMsg,
            fetchedAt: new Date()
          });
        } catch (err) {
          // On fetch failure during active alert: hold last known state
          setIemData(prev => {
            if (prev && prev.alertState && prev.alertState !== 'CLEAR' && prev.alertState !== 'UNKNOWN') {
              return { ...prev, alertMsg: `${prev.alertMsg} · tracking lost ${new Date().toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})} — check sky`, fetchedAt: new Date() };
            }
            return { error: err.message, alertState: 'UNKNOWN', alertColor: COLOR_MUTED, alertMsg: err.message === 'IEM_503' ? 'Storm tracking unavailable — check sky.' : 'Storm data unavailable — check radar', fetchedAt: new Date() };
          });
        }
        setIemLoading(false);
      };
      load();
      const interval = setInterval(() => {
        // S181: Visibility guard — skip polling when app backgrounded.
        // Storm tracking matters when user is actively checking sky → app foreground.
        // Resume immediately on return-to-foreground.
        if (typeof document !== 'undefined' && document.hidden) return;
        load();
      }, 5 * 60 * 1000);
      const onVisibility = () => {
        if (typeof document !== 'undefined' && !document.hidden) load();
      };
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
      return () => {
        clearInterval(interval);
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      };
    }, [selectedCourse, iemRefreshTick]);

    const cur = weatherData?.current;
    const now = new Date();
    const midnight = new Date(now); midnight.setHours(23, 59, 59, 999);
    const nextHours = (weatherData?.hourly || []).filter(h => {
      const t = new Date(h.time || h.dt*1000);
      // Include current hour even if its timestamp is minutes past (e.g. 10:05 still shows 10 PM card)
      const tHourStart = new Date(t); tHourStart.setMinutes(0, 0, 0);
      const nowHourStart = new Date(now); nowHourStart.setMinutes(0, 0, 0);
      return tHourStart >= nowHourStart && t <= midnight;
    });

    return (
      <div style={{ padding: '12px 12px 80px', maxWidth: 480, margin: '0 auto' }}>

        {/* Realtime source legend removed — lives behind 📡 icon in header (S91) */}

        {/* ── S227: STORM AUDIO ALERT POP-UP ── */}
        {showAudioPopup && profile.audioAlertEnabled && iemData?.alertState && iemData.alertState !== 'CLEAR' && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: '#0d0d0d', border: `2px solid ${iemData.alertColor || '#EF4444'}`, borderRadius: 14, padding: '24px 20px', width: '100%', maxWidth: 360 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: iemData.alertColor || '#EF4444', marginBottom: 8 }}>
                {iemData.alertState}
              </div>
              <div style={{ fontSize: 14, color: '#e5e7eb', marginBottom: 20, lineHeight: 1.5 }}>
                {iemData.alertMsg}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => {
                    audioSilencedRef.current = true;
                    stopAudioLoop();
                    if (audioCtxRef.current) audioCtxRef.current.suspend();
                    setShowAudioPopup(false);
                  }}
                  style={{ flex: 1, padding: '14px 0', borderRadius: 10, border: `2px solid ${iemData.alertColor || '#4ADE80'}`, background: '#0d0d0d', color: iemData.alertColor || '#4ADE80', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  Got It
                </button>
                <button
                  onClick={() => {
                    stopAudioLoop();
                    setShowAudioPopup(false);
                    audioSilencedRef.current = false;
                    setProfile(p => {
                      const updated = { ...p, audioAlertEnabled: false };
                      try { localStorage.setItem('zap_profile', JSON.stringify(updated)); } catch {}
                      return updated;
                    });
                  }}
                  style={{ flex: 1, padding: '14px 0', borderRadius: 10, border: '2px solid #EF4444', background: '#0d0d0d', color: '#EF4444', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  Turn Off Audio
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STORM ALERT ZONE ── */}
        <div style={{ background: '#050505', border: `2px solid ${iemData?.alertColor || '#4ADE80'}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
          {/* Header row: label + time + refresh */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: iemData?.alertColor || '#4ADE80' }}>
              STORM ALERT ZONE
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {iemData?.fetchedAt && (
                <div style={{ fontSize: 11, color: '#999' }}>
                  {iemData.fetchedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </div>
              )}
              <button
                onClick={() => { clearAllCaches(); setIemLoading(true); setIemData(null); setIemRefreshTick(t => t + 1); loadCourse(selectedCourse); }}
                style={{ background: 'transparent', border: `1px solid #333`, borderRadius: 6, color: COLOR_MUTED, fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>
                ↻
              </button>
            </div>
          </div>

          {iemLoading ? (
            <div style={{ color: COLOR_MUTED, fontSize: 13 }}>Scanning for storm cells…</div>
          ) : (
            <>
              <div style={{ fontSize: 16, fontWeight: 800, color: iemData?.alertColor || '#4ADE80', marginBottom: 4 }}>
                {iemData?.alertState === 'OFF_HOURS' ? 'MONITORING OFF' : (iemData?.alertState || 'CLEAR')}
              </div>
              <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, marginBottom: iemData?.additionalCount > 0 ? 8 : 0 }}>
                {iemData?.alertMsg}
              </div>
              {iemData?.additionalCount > 0 && (
                <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 4 }}>
                  +{iemData.additionalCount} additional cell{iemData.additionalCount > 1 ? 's' : ''} behind it
                </div>
              )}

            </>
          )}
        </div>

        {/* ── DECISION FLOW STRIP ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 12, background: '#111', borderRadius: 10, padding: '10px 8px' }}>
          {[
            { icon: '🌩️', label: 'Storm Alert', sub: "What's coming" },
            { icon: '📡', label: 'Radar',       sub: 'Where it is' },
            { icon: '⚡', label: 'Right Now',   sub: "What's happening" },
            { icon: '📅', label: 'Forecast',    sub: "What's next" },
          ].map((step, i) => (
            <div key={step.label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <span style={{ fontSize: 18 }}>{step.icon}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: COLOR_OFF_WHITE, marginTop: 2, textAlign: 'center' }}>{step.label}</span>
                <span style={{ fontSize: 9, color: COLOR_MUTED, textAlign: 'center', marginTop: 1 }}>{step.sub}</span>
              </div>
              {i < 3 && <span style={{ fontSize: 14, color: COLOR_MUTED, flexShrink: 0, marginBottom: 14 }}>→</span>}
            </div>
          ))}
        </div>

        {/* ── CHECK RADAR — opens Windy directly; ℹ shows tips modal ── */}
        {(() => {
          const windyUrl = `https://www.windy.com/?radar,${selectedCourse.lat},${selectedCourse.lng},8`;
          return (
            <>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <button
                  onClick={() => {
                    window.open(windyUrl, '_blank', 'noopener,noreferrer');
                    try { navigator.clipboard.writeText(`${selectedCourse.lat},${selectedCourse.lng}`); } catch {}
                  }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px 0', background: COLOR_CARD, border: `2px solid ${iemData?.alertColor || COLOR_GREEN}`, borderRadius: 10, color: COLOR_WHITE, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxSizing: 'border-box' }}>
                  <span style={{ color: '#7aafff' }}>📡</span> Check Radar
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowRadarInfo(true); }}
                  aria-label="Radar tips"
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}
                ><svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="none" stroke="#E5E7EB" strokeWidth="1"/><text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#E5E7EB" fontFamily="Arial,sans-serif">i</text></svg></button>
              </div>
              {showRadarInfo && (
                <div onClick={() => setShowRadarInfo(false)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                  <div onClick={(e) => e.stopPropagation()}
                    style={{ background: '#0d1f0d', border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 14, padding: '20px 18px', maxWidth: 360, width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OFF_WHITE }}>What to look for on Windy</div>
                      <button onClick={() => setShowRadarInfo(false)}
                        style={{ background: 'transparent', border: 'none', color: COLOR_MUTED, fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 0 }}>✕</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: 13, color: COLOR_OFF_WHITE }}><span style={{ color: COLOR_GOLD, fontWeight: 700 }}>Radar</span><span style={{ color: COLOR_MUTED }}> — </span>Default view. Shows where rain is right now and which direction it's moving. Watch the animation.</div>
                      <div style={{ fontSize: 13, color: COLOR_OFF_WHITE }}><span style={{ color: COLOR_GOLD, fontWeight: 700 }}>Rain, Thunder</span><span style={{ color: COLOR_MUTED }}> — </span>Shows if the storm has lightning activity.</div>
                      <div style={{ fontSize: 13, color: COLOR_OFF_WHITE }}><span style={{ color: COLOR_GOLD, fontWeight: 700 }}>Wind</span><span style={{ color: COLOR_MUTED }}> — </span>See if strong winds are pushing ahead of the storm.</div>
                      <div style={{ fontSize: 13, color: COLOR_OFF_WHITE }}><span style={{ color: COLOR_GOLD, fontWeight: 700 }}>Clouds</span><span style={{ color: COLOR_MUTED }}> — </span>Shows how big the system is and how long it may last.</div>
                    </div>
                    <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 12, borderTop: `1px solid #1a3a1a`, paddingTop: 10 }}>Pinch to zoom in for a closer look at the course.</div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* ── COLD-START RACE BANNER — settings loaded after forecast ── */}
        {profileLoadedLate && (
          <div style={{ background: '#1a1a00', border: '1px solid #7c6f00', borderRadius: 8, padding: '8px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 12, color: '#fde047' }}>⚠ Settings loaded after forecast — tap to refresh for accurate thresholds</span>
            <button
              onClick={() => { setProfileLoadedLate(false); if (selectedCourse) loadCourse(selectedCourse, false); }}
              style={{ background: '#7c6f00', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              Refresh
            </button>
          </div>
        )}

        {/* ── RIGHT NOW — S98 card design ───── */}
        {cur && (() => {
          const rnStatus = (() => {
            // RIGHT NOW card status — real-time data only, no forecast. S212 locked.
            const SEVERITY = { 'GO': 0, 'CAUTION': 1, 'STRONG CAUTION': 2, 'NO-GO': 3 };
            const noGoTriggers = [], strCauTriggers = [], cauTriggers = [];

            // Precip — NJWxNet inches P1, WeatherAPI inches P2. No forecast % ever.
            const p1hr = njwxData?.success ? njwxData.current.precip1hr
                       : (weatherData?.current?.precip1hr ?? null);
            if (p1hr != null) {
              if      (p1hr >= 0.30) noGoTriggers.push('Heavy Rain');
              else if (p1hr >= 0.16) strCauTriggers.push('Raining');
              else if (p1hr >= 0.06) cauTriggers.push('Light Rain');
            }

            // Wind — same locked thresholds as rest of app
            const wind = cur.windSpeedNum || 0;
            const gust = cur.windGust || 0;
            if      (wind >= WIND_NOGO)    noGoTriggers.push(`Wind ${wind} mph`);
            else if (wind >= WIND_STR_CAU) strCauTriggers.push(`Wind ${wind} mph`);
            else if (wind >= WIND_CAUTION) cauTriggers.push(`Wind ${wind} mph`);
            // Gust ≥40 → NO-GO (matches forecast path getHourStatus)
            if (gust >= 40 && !noGoTriggers.some(t => t.startsWith('Wind'))) noGoTriggers.push(`Gust ${gust} mph`);

            // Feels Like — same locked thresholds as rest of app
            const fl = cur.feelsLike != null ? cur.feelsLike : (cur.temp || 0);
            if      (fl < profile.minTemp - 5) noGoTriggers.push(`Feels ${Math.round(fl)}° (limit ${profile.minTemp}°)`);
            else if (fl > profile.maxTemp + 5) noGoTriggers.push(`Feels ${Math.round(fl)}° (limit ${profile.maxTemp}°)`);
            else if (fl < profile.minTemp)     cauTriggers.push(`Feels ${Math.round(fl)}° (limit ${profile.minTemp}°)`);
            else if (fl > profile.maxTemp)     cauTriggers.push(`Feels ${Math.round(fl)}° (limit ${profile.maxTemp}°)`);

            // Determine status
            const triggers = {};
            if (noGoTriggers.length)   triggers['NO-GO']          = noGoTriggers;
            if (strCauTriggers.length) triggers['STRONG CAUTION'] = strCauTriggers;
            if (cauTriggers.length)    triggers['CAUTION']         = cauTriggers;
            let status = noGoTriggers.length ? 'NO-GO' : strCauTriggers.length ? 'STRONG CAUTION' : cauTriggers.length ? 'CAUTION' : 'GO';
            let color  = STATUS_COLORS[status].color;

            // IEM storm override — always wins or doesn't downgrade. Unchanged from prior logic.
            const STORM_TIER = {
              'GET IN NOW':       { status: 'NO-GO',          color: STATUS_COLORS['NO-GO'].color },
              'HEAVY STORM':      { status: 'STRONG CAUTION', color: STATUS_COLORS['STRONG CAUTION'].color },
              'RAIN APPROACHING': { status: 'CAUTION',        color: STATUS_COLORS['CAUTION'].color },
            };
            const tier = STORM_TIER[iemData?.alertState];
            if (tier) {
              const stormSev = SEVERITY[tier.status];
              if (stormSev > (SEVERITY[status] ?? 0)) {
                status = tier.status;
                color  = tier.color;
              }
              const stormTrigger = `${iemData.alertState} — ${iemData.alertMsg || 'Storm detected'}`;
              triggers[tier.status] = [...(triggers[tier.status] || []), stormTrigger];
            }

            return { status, color, triggers };
          })();
          const sc = STATUS_COLORS[rnStatus.status] || STATUS_COLORS['GO'];
          const showGust = (cur.windGust || 0) > (cur.windSpeedNum || 0) + 10;
          // R1 sky icon + label — amCharts icons, bucket drives when raining (slot 28)
          const p1hrR1 = njwxData?.success ? njwxData.current.precip1hr : (weatherData?.current?.precip1hr ?? null);
          // S216 F1: if wapiCurrentData hasn't arrived after 3s, show refresh prompt
          const wapiTimedOut = !wapiCurrentData && wapiLoadStartRef.current && (Date.now() - wapiLoadStartRef.current) > 3000;
          const r1Icon = p1hrR1 != null && p1hrR1 >= 0.30 ? 'rainy-7'
                       : p1hrR1 != null && p1hrR1 >= 0.16 ? 'rainy-5'
                       : p1hrR1 != null && p1hrR1 >= 0.06 ? 'rainy-3'
                       : p1hrR1 != null && p1hrR1 >= 0.01 ? 'rainy-1'
                       : (() => {
                           const c = (wapiCurrentData?.current?.conditions || '').toLowerCase();
                           if (/thunder|lightning/.test(c)) return 'thunder';
                           if (/snow|blizzard/.test(c)) return 'snowy-3';
                           if (/heavy rain|heavy drizzle/.test(c)) return 'rainy-7';
                           if (/rain|drizzle|shower/.test(c)) return 'rainy-3';
                           if (/overcast/.test(c)) return 'cloudy';
                           if (/mostly cloudy|cloudy/.test(c)) return 'cloudy-day-3';
                           if (/partly cloudy/.test(c)) return 'cloudy-day-1';
                           return wapiTimedOut ? null : 'day';
                         })();
          const r1Label = p1hrR1 != null && p1hrR1 >= 0.30 ? 'Heavy Rain'
                        : p1hrR1 != null && p1hrR1 >= 0.16 ? 'Raining'
                        : p1hrR1 != null && p1hrR1 >= 0.06 ? 'Light Rain'
                        : p1hrR1 != null && p1hrR1 >= 0.01 ? 'Trace'
                        : wapiTimedOut ? 'Hit refresh button for sky info'
                        : wapiCurrentData ? skyLabel(wapiCurrentData.current.conditions || 'Clear')
                        : '—';
          return (
            <div style={{ background: '#000', border: `8px solid ${sc.color}`, borderRadius: 12, padding: 14, marginBottom: 12 }}>
              {/* R1: label + status (left) + amCharts sky icon + condition (right) — S213 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: COLOR_WHITE }}>RIGHT NOW</span>
                  <StatusIcon status={rnStatus.status} size={18} />
                  <span style={{ fontSize: 13, fontWeight: 800, color: sc.color }}>{rnStatus.status}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {r1Icon && <img src={`/icons/amcharts/${r1Icon}.svg`} alt="" style={{ width: 44, height: 44 }} />}
                  <span style={{ fontSize: r1Icon ? 18 : 13, fontWeight: 600, color: r1Icon ? '#fff' : COLOR_MUTED }}>{r1Label}</span>
                </div>
              </div>
              {/* Source attribution — own line */}
              {njwxData?.sourceDetail && (
                <div style={{ fontSize: 10, color: COLOR_MUTED, marginBottom: 8 }}><span style={{ color: '#7aafff' }}>📡</span> {njwxData.sourceDetail}</div>
              )}
              {/* R3: FEELS + actual */}
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 1, marginRight: 6 }}>FEELS</span>
                <span style={{ fontSize: 26, fontWeight: 800, color: '#fff' }}>{cur.feelsLike != null ? `${Math.round(cur.feelsLike)}°` : '—'}</span>
                {cur.temp != null && <span style={{ fontSize: 14, color: COLOR_MUTED, marginLeft: 6 }}>({Math.round(cur.temp)}° actual)</span>}
              </div>
              {/* R4: stats row — Precip · Wind · Gust · Hum · UV · Sunset */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: rnStatus.status !== 'GO' ? 8 : 0 }}>
                {(() => {
                  const p = njwxData?.success ? njwxData.current.precip1hr : (weatherData?.current?.precip1hr ?? null);
                  const rainLabel = p == null ? 'Unavailable'
                                  : p >= 0.30 ? 'Heavy Rain'
                                  : p >= 0.16 ? 'Raining'
                                  : p >= 0.06 ? 'Light Rain'
                                  : p >= 0.01 ? 'Trace'
                                  : wapiCurrentData ? (() => {
                                      const c = (wapiCurrentData.current.conditions || '').toLowerCase();
                                      if (/thunder|lightning/.test(c)) return 'Raining';
                                      if (/heavy rain|heavy drizzle/.test(c)) return 'Heavy Rain';
                                      if (/rain|drizzle|shower/.test(c)) return 'Light Rain';
                                      return 'Dry';
                                    })()
                                  : 'Dry';
                  const rainColor = p == null ? COLOR_MUTED
                                  : p >= 0.30 ? STATUS_COLORS['NO-GO'].color
                                  : p >= 0.16 ? STATUS_COLORS['STRONG CAUTION'].color
                                  : p >= 0.06 ? STATUS_COLORS['CAUTION'].color
                                  : COLOR_WHITE;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 11, color: COLOR_MUTED }}>Rain </span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: rainColor }}>{rainLabel}</span>
                    </div>
                  );
                })()}
                <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>Wind </span><span style={{ fontSize: 14, fontWeight: 700, color: cur.windSpeedNum >= WIND_NOGO ? STATUS_COLORS['NO-GO'].color : cur.windSpeedNum >= WIND_STR_CAU ? STATUS_COLORS['STRONG CAUTION'].color : cur.windSpeedNum >= WIND_CAUTION ? STATUS_COLORS['CAUTION'].color : COLOR_WHITE }}>{cur.windSpeedNum != null ? (cur.windSpeedNum === 0 ? `< 2 mph` : `${cur.windSpeedNum} mph`) : '—'}</span></div>
                {showGust && <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>Gust </span><span style={{ fontSize: 14, fontWeight: 700, color: cur.windGust >= 40 ? STATUS_COLORS['NO-GO'].color : cur.windGust >= 30 ? STATUS_COLORS['STRONG CAUTION'].color : cur.windGust >= 20 ? STATUS_COLORS['CAUTION'].color : COLOR_WHITE }}>{cur.windGust} mph</span></div>}
                <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>Hum </span><span style={{ fontSize: 14, fontWeight: 700, color: COLOR_WHITE }}>{cur.humidity != null ? `${Math.round(cur.humidity)}%` : '—'}</span></div>
                {cur.uvIndex >= 3 && cur.isDaytime !== false && <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>UV </span><span style={{ fontSize: 14, fontWeight: 700, color: Math.round(cur.uvIndex) >= 11 ? '#EF4444' : Math.round(cur.uvIndex) >= 8 ? '#F97316' : Math.round(cur.uvIndex) >= 6 ? '#FDE047' : COLOR_WHITE }}>{Math.round(cur.uvIndex)} {uvLabel(Math.round(cur.uvIndex))}</span></div>}
                {(cur.sunset || weatherData?.sunsetToday) && <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>Sunset </span><span style={{ fontSize: 14, fontWeight: 700, color: COLOR_WHITE }}>{cur.sunset ? formatTime12h(cur.sunset) : weatherData.sunsetToday}</span></div>}
              </div>
              {/* R5: trigger lines */}
              {rnStatus.status !== 'GO' && rnStatus.triggers && (
                <div style={{ borderTop: `1px solid ${sc.color}33`, paddingTop: 8 }}>
                  {Object.entries(rnStatus.triggers).map(([level, trigs]) => (
                    trigs.map((t, i) => {
                      const popup = buildTriggerPopup(t, cur, profile);
                      return (
                        <div key={`${level}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <StatusIcon status={level} size={12} style={{ marginRight: 4 }} />
                          <span style={{ fontSize: 13, color: STATUS_COLORS[level]?.color || sc.color, fontWeight: 600, flex: 1 }}>{t}</span>
                          {popup && (
                            <button
                              onClick={() => setTriggerPopup(popup)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '1px 4px', flexShrink: 0, lineHeight: 1 }}
                            ><svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="none" stroke="#E5E7EB" strokeWidth="1"/><text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#E5E7EB" fontFamily="Arial,sans-serif">i</text></svg></button>
                          )}
                        </div>
                      );
                    })
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── CART + BUG + POLLEN — combined compact card ── */}
        {(badResult?.ground || badResult?.bug || pollenData?.success) && (
          <div style={{ background: '#000', border: `2px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {/* Cart */}
              {badResult?.ground && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 100 }}>
                  <img src="/icons/cart.png" alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                  <div>
                    <div style={{ fontSize: 10, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1 }}>Cart</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: badResult.ground.sogginess === 'soggy' ? STATUS_COLORS['CAUTION'].color : STATUS_COLORS['GO'].color }}>
                      {badResult.ground.cartStatus || 'All Clear'}
                    </div>

                  </div>
                </div>
              )}
              {/* Fairways */}
              {badResult?.ground && (() => {
                const { cartStatus } = badResult.ground;
                const fairways =
                  cartStatus === 'Cart Path Only' ? 'Wet — ball stops on landing' :
                  cartStatus === '90-Degree Rule Likely' ? 'Soft — reduced roll' :
                  null;
                if (!fairways) return null;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 100%', minWidth: 100, marginTop: 8 }}>
                    <span style={{ fontSize: 28, lineHeight: 1 }}>⛳</span>
                    <div>
                      <div style={{ fontSize: 10, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1 }}>Fairways</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: cartStatus === 'Cart Path Only' ? STATUS_COLORS['CAUTION'].color : STATUS_COLORS['GO'].color }}>{fairways}</div>
                    </div>
                  </div>
                );
              })()}
              {/* Bug */}
              {badResult?.bug && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 100 }}>
                  <img src="/icons/mosquito.png" alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                  <div>
                    <div style={{ fontSize: 10, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1 }}>Bugs</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: badResult.bug.index >= 8 ? STATUS_COLORS['NO-GO'].color : badResult.bug.index >= 5 ? STATUS_COLORS['CAUTION'].color : STATUS_COLORS['GO'].color }}>
                      {badResult.bug.index}/10
                    </div>
                    <div style={{ fontSize: 11, color: COLOR_MUTED }}>{badResult.bug.label || ''}</div>
                  </div>
                </div>
              )}
              {/* Pollen — compact single line, worst type first. S175 Fix #7: full-width row to prevent wrap */}
              {!(pollenData?.success && pollenData?.offSeason) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 100%', minWidth: 100 }}>
                  <span style={{ fontSize: 28, lineHeight: 1 }}>🌿</span>
                  <div>
                    <div style={{ fontSize: 10, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Pollen</div>
                    {pollenData === null ? (
                      <div style={{ fontSize: 13, color: COLOR_MUTED }}>…</div>
                    ) : pollenData?.success ? (
                      <div style={{ fontSize: 12, color: COLOR_MUTED }}>
                        <span style={{ color: pollenData.tree.color }}>🌳 {pollenData.tree.label}</span>
                        <span style={{ color: COLOR_MUTED }}> · </span>
                        <span style={{ color: pollenData.grass.color }}>🌾 {pollenData.grass.label}</span>
                        <span style={{ color: COLOR_MUTED }}> · </span>
                        <span style={{ color: pollenData.weed.color }}>🌿 {pollenData.weed.label}</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: COLOR_MUTED }}>Unavailable</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}



        {/* ── Forecast Till Midnight ───────────────────── */}
        {(() => {
          // Parse sunset hour from weatherData.sunsetToday string e.g. "7:48 PM"
          let sunsetHour = 20; // fallback if no data
          const sunsetStr = weatherData?.sunsetToday;
          if (sunsetStr && sunsetStr !== 'N/A' && sunsetStr !== '') {
            try {
              const m = sunsetStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
              if (m) {
                let h = parseInt(m[1]);
                const pm = m[3].toUpperCase() === 'PM';
                if (pm && h !== 12) h += 12;
                if (!pm && h === 12) h = 0;
                sunsetHour = h + parseInt(m[2]) / 60;
              }
            } catch {}
          }
          return (
            <div style={{ background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OFF_WHITE, marginBottom: 10 }}>
                Forecast Till Midnight
              </div>
              {nextHours.length === 0 ? (
                <div style={{ color: COLOR_MUTED, fontSize: 13 }}>No forecast data available.</div>
              ) : (
                // S161 Item 6: Column-major fill — morning/early hours down col 1, later hours down col 2.
                // Variable hour count (now → midnight) → row count computed from hours array.
                // Odd hour counts produce one empty cell at bottom-right (acceptable trade-off for consistency
                // with forecast 7-day and drilldown which also use column-major).
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: `repeat(${Math.ceil(nextHours.length / 2)}, auto)`, gridAutoFlow: 'column', gap: 6, alignItems: 'stretch' }}>
                  {nextHours.map((h, i) => {
                    const hHour = new Date(h.time || h.dt*1000).getHours();
                    const isDimmed = hHour < 8 || hHour >= 21;
                    return (
                      <DrilldownHourCard key={i} h={h} profile={profile} isPast={isDimmed} isInWindow={true} />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

      </div>
    );
  };

  // ── 19TH HOLE SCREEN ─────────────────────────────────────
  const NineteenthHoleScreen = () => {
    const [expandedPhoto, setExpandedPhoto] = React.useState(null);
    const [expandedDocPhoto, setExpandedDocPhoto] = React.useState(null);
    const [expandedRoundId, setExpandedRoundId] = React.useState(null);
    const toggleRound = (id, suffix) => setExpandedRoundId(prev => prev === id + suffix ? null : id + suffix);
    // Cache invalidation fix: re-fetch rounds on every mount so stale data doesn't show
    React.useEffect(() => { refreshRounds(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const course = selectedCourse;
    const allRounds = getRounds(sharedRounds).filter(r => r.total > 0 && r.holesEntered >= 18);

    // Best round: lowest total
    const bestRound = allRounds.length > 0 ? allRounds.reduce((b, r) => getRoundDiff(r) < getRoundDiff(b) ? r : b) : null;

    // Most recent round: actual newest by date+teeTime (sharedRounds arrives
    // in arbitrary Sheet row order, so [length-1] is unreliable).
    const recentRound = mostRecentRound(allRounds);

    const fmtDate = (d) => {
      try { return new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
      catch { return String(d).slice(0, 10); }
    };
    const fmtTime = (t) => {
      if (!t) return '';
      try {
        const s = String(t).trim();
        if (/[AP]M$/i.test(s)) return s;
        const match = s.match(/T(\d{2}):(\d{2})/);
        if (match) {
          let h = parseInt(match[1]), m = parseInt(match[2]);
          if (isNaN(h) || isNaN(m)) return '';
          const ampm = h >= 12 ? 'PM' : 'AM';
          h = h % 12 || 12;
          return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
        }
        const parts = s.split(':').map(Number);
        if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          let h = parts[0], m = parts[1];
          const ampm = h >= 12 ? 'PM' : 'AM';
          h = h % 12 || 12;
          return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
        }
      } catch {}
      return '';
    };

    const RoundCard = ({ label, round }) => {
      if (!round) return null;
      const isExpanded = expandedRoundId === round.id;
      return (
        <div style={{ marginBottom: 12 }}>
        <div onClick={() => setExpandedRoundId(isExpanded ? null : round.id)}
          style={{ ...CARD_STYLE, padding: '14px 16px', cursor: 'pointer' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div style={{ flex: 1, paddingRight: 12 }}>
              <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_WHITE }}>{round.courseName}</div>
              <div style={{ fontSize: 13, color: COLOR_GOLD, fontWeight: 600, marginTop: 2 }}>{fmtDate(round.date)}{round.teeTime ? ` · ${fmtTime(round.teeTime)}` : ''}{round.finishTime ? ` — ${fmtTime(round.finishTime)}` : ''}</div>
              {round.players && round.players.length > 0 && (
                <div style={{ fontSize: 12, color: COLOR_OFF_WHITE, marginTop: 4 }}>{round.players.join(' · ')}</div>
              )}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: COLOR_GOLD, flexShrink: 0 }}>{round.total}</div>
          </div>
        </div>
        {isExpanded && <ExpandedRoundCard round={round} />}
        </div>
      );
    };

    // Upcoming events — future only, first 3
    const futureEvents = spondEvents.filter(e => new Date(e.startTime) > new Date() && !e.cancelled).slice(0, 3);

    const courseEmojis = {
      'Spooky Brook': '🌲', 'Quail Brook': '🦅', 'Warrenbrook': '⛰️',
      'Green Knoll': '🌿', 'Neshanic Valley': '🏞️', 'Fox Hollow': '🦊',
      'High Bridge Hills': '⛰️', 'Architects': '📐', 'Pebble Creek': '🪨',
      'Hominy Hill': '🌾', 'Oak Hill': '🌳', 'Shark River': '🦈'
    };
    const fmtEvtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' }) : '';
    const fmtEvtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '';
    const fmtArriveBy = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      d.setMinutes(d.getMinutes() - 30);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
    };

    return (
      <div style={{ padding: '12px 16px 60px', maxWidth: 480, margin: '0 auto' }}>

        {/* 0. Golf Quote Card — funny self-deprecating, cycles by day of year */}
        {(() => {
          const GOLF_QUOTES = [
            // GEORGE CARLIN
            "Did you ever watch golf on TV? It's like watching flies fuck. — George Carlin",
            "Think of the intellect it must take to draw pleasure from this: hitting a ball with a crooked stick and then walking after it. And then hitting it again! I say pick it up, asshole — you're lucky you found the fucking thing! — George Carlin",
            "Put it in your pocket and go the fuck home. You're a winner! — George Carlin",
            "O.J. Simpson has already received the ultimate punishment: for the rest of his life he has to associate with golfers. — George Carlin",
            // ROBIN WILLIAMS
            "Golf — the manly sport where you can dress like a pimp and no one will care. — Robin Williams",
            "Whack the ball, get in the cart. Whack the ball, get in the cart. And the commentary is electrifying — just this side of curling for getting me going. — Robin Williams",
            "Right near the end I'll put a little flat piece with a little flag to give you fucking hope. But then I'll put a pool and a sandbox to fuck with your ball again. — Robin Williams",
            "I want the guy who does Mexican soccer to do golf one time. Just to see those waspy motherfuckers go: 'My God, they're not gardening, they're playing now!' — Robin Williams",
            // NORM MACDONALD
            "It is finally official: murder is legal in the state of California. — Norm Macdonald",
            "O.J. Simpson said he won't rest until he finds Nicole's real killer. The manhunt continues. — Norm Macdonald",
            "This weekend, O.J. Simpson — playing golf in Scotland — was criticized for missing the first Mother's Day since Nicole Brown Simpson's murder. An angry Simpson responded: 'Idiots, I didn't spend Mother's Day with my kids because I killed their mother!' — Norm Macdonald",
            "When I was a kid, everybody that played golf was an old man. Until Tiger showed up, they weren't in very good shape. — Norm Macdonald",
            // DAVID FEHERTY
            "Watching Phil Mickelson play golf is like watching a drunk chasing a balloon near the edge of a cliff. — David Feherty",
            "That ball is so far left, Lassie couldn't find it if it was wrapped in bacon. — David Feherty",
            "I was swinging like a toilet door on a prawn trawler. — David Feherty",
            "Playing Augusta is like playing a Salvador Dali landscape. I expected a clock to fall out of the trees and hit me in the face. — David Feherty",
            "Jim Furyk's swing looks like an octopus falling out of a tree. — David Feherty",
            "It would be easier to pick a broken nose than a winner in that group. — David Feherty",
            "The world's No. 1 tennis player spends 90% of his time winning. The world's No. 1 golfer spends 90% of his time losing. Golfers are great losers. — David Feherty",
            "I am sorry Nick Faldo couldn't be here this week. He is attending the birth of his next wife. — David Feherty",
            // JOHN DALY
            "I hit the ball as hard as I can. If I find it, I hit it again. — John Daly",
            "I don't think I've ever stepped into a gym — they won't let me smoke there. I just thank God Miller Lite isn't as fattening as most beers. If I cut back on beer, I'd look anorexic. — John Daly",
            "There are probably some things I could do to keep my flexibility up, but I'd rather smoke, drink Diet Cokes and eat. — John Daly",
            "The only way I'll ever make the Ryder Cup team is when I become captain; then I can name myself to the team. — John Daly",
            "I learned you can't drink whiskey and play golf. — John Daly",
            "My slogan is: who needs fitness when you've got great equipment. — John Daly",
            // LARRY DAVID
            "If I wasn't a golfer, I would still be miserable — but not as miserable. — Larry David",
            // CADDYSHACK
            "Cinderella story. Outta nowhere. A former greenskeeper, now, about to become the Masters champion. — Bill Murray, Caddyshack",
            "So I've got that going for me, which is nice. — Bill Murray, Caddyshack",
            "Don't sell yourself short Judge, you're a tremendous slouch. — Rodney Dangerfield, Caddyshack",
            "A flute without holes is not a flute. A donut without a hole is a Danish. — Ty Webb, Caddyshack",
            // HAPPY GILMORE
            "Just tap it in. Just tap it in. Give it a little tappy. Tap tap taparoo. — Happy Gilmore",
            "The price is wrong, Bob. — Happy Gilmore",
            "You're gonna die, clown. — Happy Gilmore",
            // CLASSIC VERIFIED
            "My swing is so bad I look like a caveman killing his lunch. — Lee Trevino",
            "They call it golf because all the other four-letter words were taken. — Raymond Floyd",
            "I know I'm getting better at golf because I'm hitting fewer spectators. — Gerald Ford",
            "I have a tip that can take five strokes off anyone's golf game: it's called an eraser. — Arnold Palmer",
            "If you think it's hard to meet new people, try picking up the wrong golf ball. — Jack Lemmon",
            "The reason the pro tells you to keep your head down is so you can't see him laughing. — Phyllis Diller",
            "Golf and sex are the only things you can enjoy without being good at them. — Jimmy Demaret",
            "I asked my caddie for a sand wedge and he came back ten minutes later with a ham on rye. — Chi Chi Rodriguez",
            "Give me golf clubs, fresh air, and a beautiful partner, and you can keep the clubs and the fresh air. — Jack Benny",
            "I play in the low 80s. If it's any hotter than that, I won't play. — Joe E. Lewis",
            "Putts get real difficult the day they hand out the money. — Lee Trevino",
            "Golf is a game in which you yell 'fore,' shoot six, and write down five. — Paul Harvey",
            // ANONYMOUS
            "Golf is a good walk spoiled. — Anonymous",
            "The best wood in most amateurs' bags is the pencil. — Anonymous",
            "Every time a golfer makes a birdie, he must subsequently make two triple bogeys to restore the fundamental equilibrium of the universe. — Anonymous",
            "You can hit a 2-acre fairway 10% of the time and a 2-inch branch 90% of the time. — Anonymous",
            "My drinking team has a golfing problem. — Anonymous",
            "Golf: the only sport where the worse you are, the more you get to play. — Anonymous",
            "I don't always one-putt, but when I do, it's usually for double bogey. — Anonymous",
            "The rake is always in the other trap. — Anonymous",
            "Golf is like marriage: if you take yourself too seriously it won't work, and both are expensive. — Anonymous",
            "A bad day of golf still beats a good day at work. — Anonymous",
            "Real golfers don't cry when they line up their fourth putt. — Anonymous",
            "Golf is a hard game to figure. One day you'll slice it, shank it, miss every green. The next day, for no reason at all, you really stink. — Anonymous",
          ];
          // Seeded Fisher-Yates shuffle — deterministic per cycle, random across cycles
          // cycle = Math.floor(dayOfYear / N), position = dayOfYear % N
          // Same quote never repeats until full batch exhausted
          const now = new Date();
          const start = new Date(now.getFullYear(), 0, 0);
          const dayOfYear = Math.floor((now - start) / 86400000);
          const N = GOLF_QUOTES.length;
          const cycle = Math.floor(dayOfYear / N);
          const pos = dayOfYear % N;
          // Seeded PRNG (mulberry32)
          const seed = cycle * 2654435761 + 1;
          let s = seed >>> 0;
          const rand = () => { s += 0x6D2B79F5; let t = Math.imul(s ^ s >>> 15, 1 | s); t ^= t + Math.imul(t ^ t >>> 7, 61 | t); return ((t ^ t >>> 14) >>> 0) / 4294967296; };
          // Build shuffled index array for this cycle
          const indices = Array.from({length: N}, (_, i) => i);
          for (let i = N - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [indices[i], indices[j]] = [indices[j], indices[i]]; }
          const quote = GOLF_QUOTES[indices[pos]];
          const parts = quote.split(' — ');
          const quoteText = parts[0];
          const attribution = parts.length > 1 ? `— ${parts[1]}` : null;
          return (
            <div style={{ ...CARD_STYLE, padding: '14px 16px', marginBottom: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 18, marginBottom: 8 }}>⛳</div>
              <div style={{ fontSize: 15, color: COLOR_OFF_WHITE, fontStyle: 'italic', lineHeight: 1.6, marginBottom: attribution ? 8 : 0 }}>"{quoteText}"</div>
              {attribution && <div style={{ fontSize: 12, color: COLOR_GOLD, fontWeight: 600 }}>{attribution}</div>}
            </div>
          );
        })()}

        {/* 1. Bars & Restaurants Nearby — beer mug icon left */}
        {course && (
          <a href={`https://www.google.com/maps/search/bars+and+restaurants/@${course.lat},${course.lng},14z`}
            target="_blank" rel="noopener noreferrer"
            style={{ ...BTN_LARGE, background: COLOR_CARD, marginBottom: 12, textDecoration: 'none', border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, boxSizing: 'border-box', overflow: 'hidden' }}>
            <div style={{ fontSize: 26, lineHeight: 1, flexShrink: 0 }}>🍺🍔</div>
            <span style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_WHITE }}>Bars & Restaurants Nearby</div>
              <div style={{ fontSize: 13, color: COLOR_MUTED, marginTop: 2 }}>Within 3 miles · Opens Google Maps</div>
            </span>
            <span style={{ fontSize: 18, color: COLOR_MUTED, flexShrink: 0 }}>›</span>
          </a>
        )}

        {/* 2. Best round card */}
        {bestRound && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>BEST ROUND</div>
            <RoundSummaryRow round={bestRound} isExpanded={expandedRoundId === bestRound.id + '_best'} onToggle={() => toggleRound(bestRound.id, '_best')} />
            {expandedRoundId === bestRound.id + '_best' && <ExpandedRoundCard round={bestRound} />}
          </div>
        )}

        {/* 3. Most Recent Round — always show if exists */}
        {recentRound && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLOR_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>MOST RECENT ROUND</div>
            <RoundSummaryRow round={recentRound} isExpanded={expandedRoundId === recentRound.id + '_recent'} onToggle={() => toggleRound(recentRound.id, '_recent')} />
            {expandedRoundId === recentRound.id + '_recent' && <ExpandedRoundCard round={recentRound} />}
          </div>
        )}

        {/* 4. Upcoming Events — full detail cards, first 3, See All → */}
        <div style={{ ...CARD_STYLE, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_GOLD, textTransform: 'uppercase', letterSpacing: 1 }}>Upcoming Events</div>
            <button onClick={() => navigateTo('events')} style={{ background: 'transparent', border: 'none', color: COLOR_GREEN, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>See All →</button>
          </div>
          {futureEvents.length === 0 ? (
            <div style={{ fontSize: 13, color: COLOR_MUTED, fontStyle: 'italic' }}>No upcoming events scheduled.</div>
          ) : futureEvents.map((evt, i) => {
            const spotsText = evt.spotsRemaining > 0 ? `${evt.spotsRemaining} spot${evt.spotsRemaining !== 1 ? 's' : ''} open` : 'FULL';
            const spotsColor = evt.spotsRemaining > 0 ? COLOR_GREEN : COLOR_RED;
            const courseEmoji = courseEmojis[evt.course] || '';
            return (
              <div key={evt.id || i} style={{ borderTop: i > 0 ? `1px solid ${COLOR_DARK_GREEN}` : 'none', paddingTop: i > 0 ? 12 : 0, marginTop: i > 0 ? 12 : 0 }}>
                {/* Course photo banner */}
                {evt.coursePhoto && evt.coursePhoto.startsWith('http') && (
                  <div style={{ marginBottom: 8, borderRadius: 8, overflow: 'hidden', height: 200 }}>
                    <img src={evt.coursePhoto} alt={evt.course} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                      onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                  </div>
                )}
                {/* Date + course + spots */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 2 }}>{fmtEvtDate(evt.startTime)}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OFF_WHITE }}>{courseEmoji} {evt.course || evt.heading}</div>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_WHITE, background: spotsColor === COLOR_GREEN ? COLOR_GREEN_BG : COLOR_RED_BG, padding: '3px 8px', borderRadius: 6, flexShrink: 0 }}>
                    {spotsText}
                  </div>
                </div>
                {/* Tee time / Arrive by / Group size */}
                <div style={{ display: 'flex', gap: 20, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: COLOR_MUTED, fontWeight: 600 }}>TEE TIME</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_GREEN }}>{fmtEvtTime(evt.startTime)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: COLOR_MUTED, fontWeight: 600 }}>ARRIVE BY</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OFF_WHITE }}>{fmtArriveBy(evt.startTime)}</div>
                  </div>
                  {evt.maxSpots && (
                    <div>
                      <div style={{ fontSize: 10, color: COLOR_MUTED, fontWeight: 600 }}>GROUP SIZE</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OFF_WHITE }}>{evt.attendance?.in?.length ?? 0}/{evt.maxSpots}</div>
                    </div>
                  )}
                </div>
                {/* Attendance */}
                {evt.attendance && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: COLOR_MUTED, marginBottom: 4 }}>ATTENDANCE</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(evt.attendance.in || []).map((m, j) => (
                        <span key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLOR_GREEN_BG, padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#000' }}>
                          <StatusIcon status="GO" size={12}/> {m.name}
                        </span>
                      ))}
                      {(evt.attendance.out || []).map((m, j) => (
                        <span key={j} style={{ background: COLOR_RED_BG, padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: COLOR_WHITE, opacity: 0.7 }}>
                          Declined: {m.name}
                        </span>
                      ))}
                      {(evt.attendance.no_answer || []).map((m, j) => (
                        <span key={j} style={{ background: COLOR_GOLD_BG, padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#000', opacity: 0.7 }}>
                          Maybe: {m.name}
                        </span>
                      ))}
                      {(evt.attendance.waiting || []).map((m, j) => (
                        <span key={j} style={{ background: COLOR_GOLD_BG, padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#000' }}>
                          Pending: {m.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 5. Single Statistics button — replaces Round History + Statistics pair */}
        <button onClick={() => navigateTo('scorestats')}
          style={{ width: '100%', background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 14, padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left', marginBottom: 10, boxSizing: 'border-box' }}>
          <div style={{ fontSize: 26, lineHeight: 1 }}>📊</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: COLOR_WHITE }}>Statistics</div>
            <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 2 }}>Rounds, history, stats</div>
          </div>
          <div style={{ fontSize: 18, color: COLOR_MUTED }}>›</div>
        </button>

        {/* Full-screen photo overlay */}
        {expandedPhoto && (
          <div onClick={() => setExpandedPhoto(null)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.95)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <img src={expandedPhoto} alt="Round" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 10, objectFit: 'contain' }} />
          </div>
        )}

        {expandedDocPhoto && (
          <div onClick={() => setExpandedDocPhoto(null)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.97)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <img src={expandedDocPhoto} alt="ZAP Doc" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 10, objectFit: 'contain' }} />
            <div style={{ position: 'absolute', top: 16, right: 20, color: '#fff', fontSize: 32, cursor: 'pointer' }} onClick={() => setExpandedDocPhoto(null)}>x</div>
          </div>
        )}
      </div>
    );
  };
  // Keep alias for any legacy references
  const NineteenthHoleStub = NineteenthHoleScreen;

    // ── SCORECARD TAB SCREEN ─────────────────────────────────
  const ScorecardTabScreen = () => {
    const allRounds = getLocalRounds();
    const lastRound = allRounds.length > 0 ? allRounds[allRounds.length - 1] : null;

    return (
      <div style={{ padding: '12px 12px 80px' }}>

        {/* 1. Record Round */}
        <button
          onClick={() => navigateTo('scorecard')}
          style={{ width: '100%', padding: '18px', background: COLOR_GREEN_DARK, border: `2px solid ${COLOR_GREEN}`, borderRadius: 14, color: COLOR_WHITE, fontSize: 18, fontWeight: 800, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          Record Round
        </button>

        {/* 2. Last Round summary */}
        {lastRound ? (
          <div style={{ background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_MUTED, marginBottom: 10 }}>LAST ROUND</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_WHITE, marginBottom: 4 }}>
              {lastRound.courseName || lastRound.course || 'Unknown course'}
            </div>
            <div style={{ fontSize: 12, color: COLOR_MUTED, marginBottom: 10 }}>
              {lastRound.date ? new Date(lastRound.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
            </div>
            {lastRound.players && lastRound.players.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {lastRound.players
                  .filter(p => p.scores && p.scores.some(s => s !== null && s !== ''))
                  .sort((a, b) => {
                    const aTotal = (a.scores || []).reduce((s, v) => s + (parseInt(v) || 0), 0);
                    const bTotal = (b.scores || []).reduce((s, v) => s + (parseInt(v) || 0), 0);
                    return aTotal - bTotal;
                  })
                  .map((p, i) => {
                    const total = (p.scores || []).reduce((s, v) => s + (parseInt(v) || 0), 0);
                    const par = lastRound.par || 72;
                    const diff = total - par;
                    const diffStr = diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`;
                    const medal = i === 0 ? '1st' : i === 1 ? '2nd' : i === 2 ? '3rd' : '';
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: i === 0 ? '#2a4a1a' : 'rgba(0,0,0,0.15)', borderRadius: 6 }}>
                        <div style={{ fontSize: 14, color: COLOR_WHITE }}>{medal} {p.name}</div>
                        <div style={{ display: 'flex', gap: 12 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_WHITE }}>{total}</div>
                          <div style={{ fontSize: 13, color: diff < 0 ? '#4ADE80' : diff > 0 ? '#FB923C' : COLOR_MUTED, fontWeight: 600 }}>{diffStr}</div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 14, padding: 16, marginBottom: 12, textAlign: 'center', color: COLOR_MUTED, fontSize: 13 }}>
            No rounds recorded yet
          </div>
        )}

        {/* 3. Round History */}
        <button onClick={() => navigateTo('roundhistory')}
          style={{ width: '100%', padding: '14px 16px', background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, color: COLOR_OFF_WHITE, fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          Round History
        </button>

        {/* 4. Season Stats */}
        <button onClick={() => navigateTo('scorestats')}
          style={{ width: '100%', padding: '14px 16px', background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, color: COLOR_OFF_WHITE, fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          Season Stats
        </button>

      </div>
    );
  };

  // ── HOME SCREEN ────────────────────────────────────────────

  // ── STORM WATCH SCREEN ─────────────────────────────────────────
  const StormWatchScreen = () => {
    const [gpsState, setGpsState] = React.useState('idle');
    const [gpsCoords, setGpsCoords] = React.useState(null);
    const [gpsFallback, setGpsFallback] = React.useState(false);
    const [gpsAddress, setGpsAddress] = React.useState(null);
    const [gpsErrorMsg, setGpsErrorMsg] = React.useState(null);
    const [iemData, setIemData] = React.useState(null);
    const [iemLoading, setIemLoading] = React.useState(true);
    const [iemRefreshTick, setIemRefreshTick] = React.useState(0);

    // 5-minute auto-refresh once GPS is locked — same visibility guard as Game Day
    React.useEffect(() => {
      if (!gpsCoords) return;
      const interval = setInterval(() => {
        if (typeof document !== 'undefined' && document.hidden) return;
        loadIem(gpsCoords.lat, gpsCoords.lng);
      }, 5 * 60 * 1000);
      const onVisibility = () => {
        if (typeof document !== 'undefined' && !document.hidden) loadIem(gpsCoords.lat, gpsCoords.lng);
      };
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
      return () => {
        clearInterval(interval);
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      };
    }, [gpsCoords]);

    const loadIem = React.useCallback((lat, lng) => {
      setIemLoading(true);
      const run = async () => {
        try {
          const url = 'https://mesonet.agron.iastate.edu/geojson/nexrad_attr.py';
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          const r = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (r.status === 503) throw new Error('IEM_503');
          if (!r.ok) throw new Error('IEM fetch failed');
          const gj = await r.json();
          const features = gj.features || [];

          const R = 3958.8;
          const toRad = d => d * Math.PI / 180;
          const distMi = (la1, lo1, la2, lo2) => {
            const dLat = toRad(la2-la1), dLon = toRad(lo2-lo1);
            const a = Math.sin(dLat/2)**2 + Math.cos(toRad(la1))*Math.cos(toRad(la2))*Math.sin(dLon/2)**2;
            return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          };
          const bearing = (la1, lo1, la2, lo2) => {
            const y = Math.sin(toRad(lo2-lo1))*Math.cos(toRad(la2));
            const x = Math.cos(toRad(la1))*Math.sin(toRad(la2)) - Math.sin(toRad(la1))*Math.cos(toRad(la2))*Math.cos(toRad(lo2-lo1));
            return (Math.atan2(y, x)*180/Math.PI + 360) % 360;
          };
          const bearingLabel = b => {
            const dirs = ['N','NE','E','SE','S','SW','W','NW'];
            return dirs[Math.round(b/45)%8];
          };

          const allCells = features
            .filter(f => (f.properties?.max_dbz || 0) >= 35)
            .map(f => {
              const p = f.properties;
              const coords = f.geometry?.coordinates;
              const cLat = coords?.[1] ?? 0;
              const cLon = coords?.[0] ?? 0;
              const dist = distMi(lat, lng, cLat, cLon);
              const bear = bearing(lat, lng, cLat, cLon);
              const motionBear = ((p.drct || 0) + 180) % 360;
              const speedKt = p.sknt || 0;
              const speedMph = speedKt * 1.15078;
              const hasSpeed = speedMph > 2;
              const etaMin = hasSpeed ? Math.round(dist / speedMph * 60) : null;
              const bearFromCellToCourse = bearing(cLat, cLon, lat, lng);
              const angleDelta = Math.abs(((motionBear - bearFromCellToCourse) + 360) % 360);
              const movingToward = angleDelta < 45 || angleDelta > 315;
              return {
                dist: Math.round(dist*10)/10,
                bear, bearLabel: bearingLabel(bear),
                dbz: p.max_dbz,
                speedMph: Math.round(speedMph),
                etaMin, movingToward, hasSpeed,
                cLat, cLon
              };
            })
            .filter(c => c.dist <= 50)
            .sort((a, b) => (a.etaMin ?? 999) - (b.etaMin ?? 999));

          let alertState = 'CLEAR';
          let alertColor = '#4ADE80';
          let alertMsg = 'No storms detected';
          let primaryCell = null;
          let additionalCount = 0;

          const getInNow = allCells.filter(c =>
            c.dbz >= 45 && c.movingToward &&
            ((c.hasSpeed && c.etaMin != null && c.etaMin <= 20) || c.dist <= 10)
          );
          const heavyStorm = allCells.filter(c =>
            c.dbz >= 45 && c.movingToward &&
            ((c.hasSpeed && c.etaMin != null && c.etaMin <= 60) || c.dist <= 10)
          );
          const rainApproach = allCells.filter(c =>
            c.dbz >= 35 && c.movingToward && c.hasSpeed &&
            c.etaMin != null && c.etaMin <= 90
          );

          if (getInNow.length > 0) {
            alertState = 'GET IN NOW';
            alertColor = '#EF4444';
            primaryCell = getInNow[0];
            additionalCount = getInNow.length - 1;
            const eta = primaryCell.etaMin;
            alertMsg = primaryCell.dist <= 10 && !primaryCell.hasSpeed
              ? `Storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — speed unknown — monitor closely`
              : `Storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${eta} min`;
          } else if (heavyStorm.length > 0) {
            alertState = 'HEAVY STORM';
            alertColor = '#FB923C';
            primaryCell = heavyStorm[0];
            additionalCount = heavyStorm.length - 1;
            const etaLow = primaryCell.etaMin;
            const etaHigh = primaryCell.etaMin ? primaryCell.etaMin + Math.round(primaryCell.etaMin * 0.2) : null;
            alertMsg = primaryCell.dist <= 10 && !primaryCell.hasSpeed
              ? `Heavy storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — speed unknown — monitor closely`
              : `Heavy storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${etaLow}–${etaHigh} min · potential lightning`;
          } else if (rainApproach.length > 0) {
            alertState = 'RAIN APPROACHING';
            alertColor = '#FACC15';
            primaryCell = rainApproach[0];
            additionalCount = rainApproach.length - 1;
            const etaLow = primaryCell.etaMin;
            const etaHigh = primaryCell.etaMin + Math.round(primaryCell.etaMin * 0.2);
            alertMsg = `Rain from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${etaLow}–${etaHigh} min`;
          }

          setIemData({ cells: allCells, primaryCell, additionalCount, alertState, alertColor, alertMsg, fetchedAt: new Date() });
        } catch (err) {
          setIemData(prev => {
            if (prev && prev.alertState && prev.alertState !== 'CLEAR' && prev.alertState !== 'UNKNOWN') {
              return { ...prev, alertMsg: `${prev.alertMsg} · tracking lost ${new Date().toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})} — check sky`, fetchedAt: new Date() };
            }
            return { error: err.message, alertState: 'UNKNOWN', alertColor: COLOR_MUTED, alertMsg: err.message === 'IEM_503' ? 'Storm tracking unavailable — check sky.' : 'Storm data unavailable — check radar', fetchedAt: new Date() };
          });
        }
        setIemLoading(false);
      };
      run();
    }, []);

    const doGps = React.useCallback(() => {
      setGpsState('loading');
      setIemData(null);
      setIemLoading(true);
      setGpsErrorMsg(null);
      if (!navigator.geolocation) { setGpsErrorMsg('Device GPS timed out — tap Refresh to retry'); setGpsState('error'); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setGpsFallback(false);
          setGpsErrorMsg(null);
          setGpsCoords({ lat, lng });
          setGpsState('ready');
          loadIem(lat, lng);
          // Reverse geocode via Nominatim
          fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`, {
            headers: { 'Accept-Language': 'en-US,en', 'User-Agent': 'ZAP-Golf-Weather/1.0' }
          }).then(r => r.json()).then(data => {
            const a = data.address || {};
            const parts = [
              a.house_number && a.road ? `${a.house_number} ${a.road}` : a.road,
              a.city || a.town || a.village || a.suburb,
              a.state
            ].filter(Boolean);
            setGpsAddress(parts.join(', '));
          }).catch(() => {});
        },
        (err) => {
          if (err.code === 1) {
            setGpsErrorMsg('Location access blocked. To enable: Settings → Privacy & Security → Location Services → Safari → set to "Ask Next Time", then tap Refresh.');
          } else {
            setGpsErrorMsg('Device GPS timed out — tap Refresh to retry.');
          }
          setGpsState('error');
        },
        { timeout: 8000, maximumAge: 0, enableHighAccuracy: false }
      );
    }, [loadIem]);

    const windyUrl = gpsCoords
      ? `https://www.windy.com/?radar,${gpsCoords.lat.toFixed(4)},${gpsCoords.lng.toFixed(4)},8`
      : 'https://www.windy.com/?radar';

    return (
      <div style={{ padding: '0 12px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0 8px' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: COLOR_WHITE }}>⚡ Storm Watch</div>
          <button onClick={() => navigateTo('home')} style={{ background: 'none', border: 'none', color: COLOR_GREEN, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Home</button>
        </div>

        {gpsState === 'idle' && (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <button
              onClick={doGps}
              style={{ background: COLOR_GREEN_DARK, border: `2px solid ${COLOR_GREEN}`, borderRadius: 14, padding: '18px 32px', fontSize: 18, fontWeight: 800, color: COLOR_WHITE, cursor: 'pointer', width: '100%' }}>
              📍 Get My Location
            </button>
            <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 12 }}>Tap to check storm conditions at your location</div>
          </div>
        )}

        {gpsState === 'loading' && (
          <div style={{ textAlign: 'center', padding: 32, color: COLOR_MUTED, fontSize: 14 }}>Locating you…</div>
        )}

        {gpsState === 'error' && (
          <div style={{ background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 12, padding: 20, marginBottom: 12 }}>
            <div style={{ fontSize: 14, color: '#FFA500', fontWeight: 700, marginBottom: 8 }}>📍 GPS Unavailable</div>
            <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, lineHeight: 1.5 }}>{gpsErrorMsg}</div>
          </div>
        )}

        {gpsState === 'ready' && (
          <>
            {/* Storm Alert Zone — verbatim match to Game Day */}
            <div style={{ background: '#050505', border: `2px solid ${iemData?.alertColor || '#4ADE80'}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: iemData?.alertColor || '#4ADE80' }}>
                  STORM ALERT ZONE
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 11, color: COLOR_MUTED }}>
                    {gpsAddress || 'Your GPS location'}
                  </div>
                  {iemData?.fetchedAt && (
                    <div style={{ fontSize: 11, color: COLOR_MUTED }}>
                      {iemData.fetchedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              </div>

              {iemLoading ? (
                <div style={{ color: COLOR_MUTED, fontSize: 13 }}>Scanning for storm cells…</div>
              ) : (
                <>
                  <div style={{ fontSize: 16, fontWeight: 800, color: iemData?.alertColor || '#4ADE80', marginBottom: 4 }}>
                    {iemData?.alertState || 'CLEAR'}
                  </div>
                  <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, marginBottom: iemData?.additionalCount > 0 ? 8 : 0 }}>
                    {iemData?.alertMsg}
                  </div>
                  {iemData?.additionalCount > 0 && (
                    <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 4 }}>
                      +{iemData.additionalCount} additional cell{iemData.additionalCount > 1 ? 's' : ''} behind it
                    </div>
                  )}
                </>
              )}
            </div>

            <a href={windyUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px 0', background: COLOR_CARD, border: `2px solid ${iemData?.alertColor || COLOR_GREEN}`, borderRadius: 10, color: COLOR_WHITE, fontSize: 15, fontWeight: 700, textDecoration: 'none', boxSizing: 'border-box', marginBottom: 12 }}>
              <span style={{ color: '#7aafff' }}>📡</span> Check Radar on Windy
            </a>

            <button onClick={doGps} style={{ width: '100%', background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 12, padding: '12px 16px', fontSize: 14, fontWeight: 700, color: COLOR_WHITE, cursor: 'pointer' }}>
              ↻ Refresh
            </button>
          </>
        )}
      </div>
    );
  };

  const HomeScreen = () => {
    const navCards = [
      {
        label: 'Forecast',
        sub: '7-day outlook',
        icon: '☀️',
        action: () => navigateTo('forecast'),
      },
      {
        label: 'Game Day',
        sub: 'Live course conditions',
        icon: '⛳',
        action: () => navigateTo('dayofplay'),
      },
      {
        label: 'Course Directory',
        sub: 'Info, map, scorecard',
        icon: '📍',
        action: () => navigateTo('coursedetail'),
      },
      {
        label: 'Enter Score',
        sub: 'Record your round',
        icon: '⛳',
        action: () => navigateTo('scorecard'),
      },
      {
        label: '19th Hole',
        sub: 'Dining, bar & events',
        icon: '🍺',
        action: () => navigateTo('nineteenth'),
      },
      {
        label: 'Statistics',
        sub: 'History & stats',
        icon: '📊',
        action: () => navigateTo('scorestats'),
      },
      {
        label: 'Upcoming Events',
        sub: 'Group schedule',
        icon: '📅',
        action: () => navigateTo('events'),
      },
      {
        label: 'Settings',
        sub: 'Settings · Help · Videos & Docs',
        icon: '⚙️',
        action: () => navigateTo('profile'),
      },
    ];

    return (
      <div style={{ padding: '4px 12px 0' }}>
        {/* ZAP logo — inline SVG, bolt clear of P, no external dependency */}
        <div style={{ textAlign: 'center', padding: '4px 0 4px', background: COLOR_BG, cursor: 'pointer' }} onClick={() => navigateTo('stormWatch')}>
          <svg width="220" height="48" viewBox="0 0 260 52" xmlns="http://www.w3.org/2000/svg" aria-label="ZAP">
            <text x="105" y="47" textAnchor="middle" fontFamily="'Arial Black', 'Arial Bold', sans-serif" fontSize="47" fontWeight="900" fill="#22C55E">ZAP</text>
            <polygon fill="#FDE047" points="192,5 182,26 191,26 179,51 202,23 192,23 202,5"/>
          </svg>
        </div>


        {/* Nav cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {navCards.map((card) => (
            <button
              key={card.label}
              onClick={card.action}
              style={{
                width: '100%',
                background: COLOR_CARD,
                border: `1px solid ${COLOR_DARK_GREEN}`,
                borderRadius: 14,
                padding: '10px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                textAlign: 'left',
              }}
            >
              <div style={{ fontSize: 26, lineHeight: 1 }}>{card.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_WHITE }}>{card.label}</div>
                <div style={{ fontSize: 13, color: COLOR_MUTED, marginTop: 2 }}>{card.sub}</div>
              </div>
              <div style={{ fontSize: 18, color: COLOR_MUTED }}>›</div>
            </button>
          ))}
        </div>

        {/* Copyright — home screen only */}
        <div style={{ textAlign: 'center', fontSize: 13, color: COLOR_MUTED, padding: '2px 0 12px' }}>
          © John Zur · 2026
        </div>
      </div>
    );
  };

    // ── COURSE NAME TAPPABLE HEADER ────────────────────────────

  // ── FORECAST SCREEN — S98 spec: 7-day card list ───────────
  const ForecastScreen = () => {
    if (!weatherData) return (
      <div style={{ padding: '40px 16px', textAlign: 'center', color: COLOR_MUTED }}>
        Loading weather data…
      </div>
    );

    const vcDailyAll = weatherData.vcDailyAll || [];
    const hourly = weatherData.hourly || [];

    // Build 7 days. For each day get worst-status from hourly (if available) else from daily.
    const now = new Date();
    const days = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(now);
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() + d);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;

      // Get hourly data for user's playing window (profile.playStart–playEnd)
      const [psH] = (profile?.playStart || '08:00').split(':').map(Number);
      const [peH] = (profile?.playEnd   || '20:00').split(':').map(Number);
      const dayHours = hourly.filter(h => {
        const hd = new Date(h.time);
        hd.setHours(0,0,0,0);
        const hr = new Date(h.time).getHours();
        // S268: for today, drop hours that have already fully elapsed — card should reflect only remaining window
        // S268 fix: once the window itself has fully closed for today, stop dropping hours — revert to the
        // full window (unchanged end-of-window behavior) instead of falling through to the synthetic daily fallback
        const windowEndMs = date.getTime() + peH * 3600000;
        const windowClosed = d === 0 && now.getTime() >= windowEndMs;
        const hourEndMs = new Date(h.time).getTime() + 3600000;
        const notElapsed = d !== 0 || windowClosed || hourEndMs > now.getTime();
        return hd.getTime() === date.getTime() && hr >= psH && hr < peH && notElapsed;
      });

      // Daily summary from vcDailyAll
      const daily = vcDailyAll.find(v => v.date === dateStr) || null;

      // Feels hi/lo — computed first so status can use feelsHi.
      let feelsHi, feelsLo, tempHi, tempLo;
      if (dayHours.length > 0) {
        const feels = dayHours.map(h => h.feelsLike != null ? h.feelsLike : h.temp).filter(v => v != null);
        const temps = dayHours.map(h => h.temp).filter(v => v != null);
        feelsHi = feels.length ? Math.round(Math.max(...feels)) : null;
        feelsLo = feels.length ? Math.round(Math.min(...feels)) : null;
        tempHi  = temps.length ? Math.round(Math.max(...temps)) : null;
        tempLo  = temps.length ? Math.round(Math.min(...temps)) : null;
        if ((feelsHi == null || feelsLo == null) && daily) {
          feelsHi = feelsHi ?? daily.tempMax;
          feelsLo = feelsLo ?? daily.tempMin;
          tempHi  = tempHi  ?? daily.tempMax;
          tempLo  = tempLo  ?? daily.tempMin;
        }
      } else if (daily) {
        feelsHi = daily.tempMax; feelsLo = daily.tempMin;
        tempHi  = daily.tempMax; tempLo  = daily.tempMin;
      }

      // Status: worst hour across the playing window. Same logic as drilldown.
      let status = 'GO', worstResult = null, synth = null;
      if (dayHours.length > 0) {
        worstResult = getWindowStatus(dayHours, profile);
        status = worstResult.status;
      } else if (daily) {
        synth = {
          temp:         daily.tempMax,
          feelsLike:    calcFeelsLike(daily.tempMax, daily.humidity || 50, daily.windSpeed || 0),
          windSpeedNum: daily.windSpeed || 0,
          windGust:     Math.round((daily.windSpeed || 0) * 1.3),
          precipChance: daily.precipChance || 0,
          conditions:   daily.conditions || '',
        };
        worstResult = getHourStatus(synth, profile);
        status = worstResult.status;
      }

      // Dominant conditions + sky — use worst hour (highest precip) not midday
      const midday = dayHours.find(h => new Date(h.time).getHours() === 12) || dayHours[Math.floor(dayHours.length/2)] || null;
      const worstHour = dayHours.length > 0
        ? dayHours.reduce((worst, h) => (h.precipChance || 0) > (worst.precipChance || 0) ? h : worst, dayHours[0])
        : null;
      const conditions = (worstHour && (worstHour.precipChance || 0) >= 30 ? worstHour?.conditions : null) || midday?.conditions || daily?.conditions || 'Clear'; // S267: apply 30% threshold — match hourly drilldown rule

      // Max precip % for the window
      const maxPrecip = dayHours.length > 0
        ? Math.max(...dayHours.map(h => h.precipChance || 0))
        : (daily?.precipChance || 0);

      // Max wind
      const maxWind = dayHours.length > 0
        ? Math.max(...dayHours.map(h => h.windSpeedNum || 0))
        : (daily?.windSpeed || 0);

      // Avg humidity
      const avgHumidity = dayHours.length > 0
        ? Math.round(dayHours.reduce((s, h) => s + (h.humidity || 0), 0) / dayHours.length)
        : null;

      // Sunset — today/tomorrow from weatherData, days 2-6 from vcDailyAll daily data
      let sunset = null;
      if (d === 0) sunset = weatherData.sunsetToday;
      else if (d === 1) sunset = weatherData.sunsetTomorrow;
      else sunset = daily?.sunset || null;

      // Day label
      const dayLabel = d === 0 ? 'Today' : d === 1 ? 'Tomorrow'
        : date.toLocaleDateString('en-US', { weekday: 'long' });
      // eslint-disable-next-line no-unused-vars
      const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      // Primary trigger (worst reason)
      const primaryTrigger = worstResult?.reason || null;

      const maxUV = dayHours.length > 0 ? Math.max(...dayHours.map(h => h.uvIndex || 0)) : 0;

      days.push({ dateStr, date, dayLabel, dateLabel, status, feelsHi, feelsLo, tempHi, tempLo, conditions, maxPrecip, maxWind, avgHumidity, sunset, primaryTrigger, hasHourly: dayHours.length > 0, midday, synth, maxUV });
    }

    const statusColor = (s) => STATUS_COLORS[s]?.color || '#9CA3AF';
    const DAY_ABBREVS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

    // ⓘ triggers — present for threshold-based triggers only
    const POPUP_TRIGGERS = ['wind', 'precip', 'feels', 'gust'];
    const hasTriggerPopup = (triggerText) => {
      if (!triggerText) return false;
      const t = triggerText.toLowerCase();
      return POPUP_TRIGGERS.some(k => t.includes(k));
    };

    return (
      <div style={{ padding: '0 12px 16px', boxSizing: 'border-box', overflowX: 'hidden', width: '100%' }}>
        {/* Back nav — no bar, no border, minimal height */}
        <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 3 }}>
          <button onClick={goBack} style={{ ...BTN_BASE, padding: '4px 8px', background: 'transparent', color: COLOR_MUTED, fontSize: 20, minWidth: 44 }}>←</button>
          <div style={{ flex: 1, fontSize: 17, fontWeight: 700, color: COLOR_WHITE }}>Forecast</div>
          {screenHistory.length > 2 && (
            <button onClick={() => { setScreenHistory(['home']); setScreen('home'); setTimeout(() => scrollToTop(), 50); }} style={{ ...BTN_BASE, padding: '6px 12px', background: COLOR_DARK_GREEN, color: COLOR_WHITE, fontSize: 14, fontWeight: 700, borderRadius: 8 }}>Home</button>
          )}
        </div>
        {/* Spec: 2-col grid, uniform card height via align-items:stretch */}
        {/* S161 Item 4: Column-major fill — Tue/Wed/Thu/Fri down col 1, Sat/Sun/Mon/Quote down col 2.
            Calendar-style scan vs row-major (Tue-Wed, Thu-Fri, Sat-Sun, Mon-Quote). */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'repeat(4, 1fr)', gridAutoFlow: 'column', gap: 6, alignItems: 'stretch', width: '100%', boxSizing: 'border-box' }}>
        {days.map((day) => {
          const sc = statusColor(day.status);
          // Spec: GO / CAUTION / STR CAU / NO-GO
          const labelShort = day.status === 'STRONG CAUTION' ? 'STR CAU' : day.status;
          const isSelected = selectedForecastDay === day.dateStr;
          // Spec: Mon/Tue/Wed/Thu/Fri/Sat/Sun
          const dayAbbrev = DAY_ABBREVS[day.date.getDay()];
          // Dim past days + today after the user's playing window ends (S268: was hardcoded to 8PM)
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
          const [peHour] = (profile?.playEnd || '20:00').split(':').map(Number);
          const isPast = day.dateStr < todayStr || (day.dateStr === todayStr && now.getHours() >= peHour);
          // Spec UI-J: use midday from days[] — NOT repHour lookup (fails days 2-7)
          const popupHour = day.midday;
          // Spec R5: ⓘ only for Wind/Precip/Feels/Gust — condition triggers (Fog, Thunder, Snow) get no ⓘ
          const isThresholdTrigger = day.primaryTrigger && /^(Precip|Wind|Gust|Feels)/.test(day.primaryTrigger);
          const popup = (day.status !== 'GO' && isThresholdTrigger && day.synth)
            ? buildTriggerPopup(day.primaryTrigger, day.synth, profile)
            : null;

          return (
            <button
              key={day.dateStr}
              onClick={() => { setSelectedForecastDay(day.dateStr); navigateTo('forecastdrilldown'); }}
              style={{
                /* Card shell — bg #000, border 5px status color, radius 12px, padding 8px, Georgia serif */
                background: isSelected ? '#111' : '#000',
                opacity: isPast ? 0.4 : 1,
                border: `5px solid ${sc}`,
                borderRadius: 12,
                padding: 6,
                outline: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'Georgia, serif',
                boxSizing: 'border-box',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                position: 'relative',
              }}
            >
              {/* R1: date · DayAbbrev · sunset (left, flex:1) | status icon + label (right, flex-shrink:0) */}
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 2 }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 3, whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, color: '#fff', minWidth: 0 }}>
                  <span>{day.dateLabel} · {dayAbbrev}</span>
                  {day.sunset && (<>
                    <svg width="13" height="13" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
                      <path fill="#fff" d="M7.646 4.854a.5.5 0 0 0 .708 0l1.5-1.5a.5.5 0 0 0-.708-.708l-.646.647V1.5a.5.5 0 0 0-1 0v1.793l-.646-.647a.5.5 0 1 0-.708.708zm-5.303-.51a.5.5 0 0 1 .707 0l1.414 1.413a.5.5 0 0 1-.707.707L2.343 5.05a.5.5 0 0 1 0-.707zm11.314 0a.5.5 0 0 1 0 .706l-1.414 1.414a.5.5 0 1 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0zM8 7a3 3 0 0 1 2.599 4.5H5.4A3 3 0 0 1 8 7m3.71 4.5a4 4 0 1 0-7.418 0H.499a.5.5 0 0 0 0 1h15a.5.5 0 0 0 0-1h-3.79zM0 10a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2A.5.5 0 0 1 0 10m13 0a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5" />
                    </svg>
                    <span style={{ fontSize: 10, color: '#E5E7EB', fontWeight: 700 }}>{day.sunset}</span>
                  </>)}
                </div>
                <div style={{ flexShrink: 0, marginLeft: 6, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <StatusIcon status={day.status} size={16} />
                  <span style={{ fontSize: 9, fontWeight: 700, color: sc, whiteSpace: 'nowrap', lineHeight: 1, marginTop: 2 }}>{labelShort}</span>
                </div>
              </div>

              {/* R2: sky SVG 36px + condition 20px bold white — DOMINANT element (locked UI-DAY-1 S123) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                {renderSkyIcon(day.conditions, true, 36)}
                <span style={{ fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{skyLabel(day.conditions)}</span>
              </div>

              {/* R3: feels hi/lo (left) | precip 14px bold / wind 11px (right, border-left) */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {day.feelsHi != null && day.feelsLo != null ? (<>
                    <div style={{ display: 'flex', alignItems: 'baseline', whiteSpace: 'nowrap', marginBottom: 0 }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color: COLOR_WHITE, lineHeight: 1.2, minWidth: 48, display: 'inline-block' }}>{day.feelsHi}°</span>
                      <span style={{ fontSize: 10, fontWeight: 400, color: '#E5E7EB' }}>({day.tempHi}°)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 400, color: COLOR_WHITE, lineHeight: 1.1, minWidth: 48, display: 'inline-block' }}>{day.feelsLo}°</span>
                      <span style={{ fontSize: 10, fontWeight: 400, color: '#E5E7EB' }}>({day.tempLo}°)</span>
                    </div>
                  </>) : <span style={{ fontSize: 12, color: COLOR_MUTED }}>—</span>}
                </div>
                <div style={{ borderLeft: '1px solid #333', paddingLeft: 6, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', display:'flex', alignItems:'center' }}><svg width="11" height="13" viewBox="0 0 14 18" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><path d="M7 1 C7 1 1 8 1 12 A6 6 0 0 0 13 12 C13 8 7 1 7 1Z" fill="#4a90d9"/></svg>{day.maxPrecip}%</span>
                  <span style={{ fontSize: 11, color: '#fff', whiteSpace: 'nowrap', display:'flex', alignItems:'center' }}><svg width="14" height="12" viewBox="0 0 16 12" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><line x1="1" y1="3" x2="15" y2="3" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="6.5" x2="12" y2="6.5" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="10" x2="9" y2="10" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/></svg>{Math.round(day.maxWind)} mph</span>
                </div>
              </div>

              {/* R4: ELIMINATED */}

              {/* R5: always rendered for uniform height. Visible only on non-GO cards with a trigger. */}
              <div style={{ borderTop: day.status !== 'GO' && day.primaryTrigger ? '1px solid #333' : '1px solid transparent', paddingTop: day.status !== 'GO' && day.primaryTrigger ? 3 : 0, marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 4, minHeight: 14 }}>
                {day.status !== 'GO' && day.primaryTrigger ? (<>
                  <StatusIcon status={day.status} size={12} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: sc, lineHeight: 1.3, flex: 1 }}>{day.primaryTrigger}</span>
                  {popup && (
                    <button
                      onClick={e => { e.stopPropagation(); setTriggerPopup(popup); }}
                      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16">
                        <circle cx="8" cy="8" r="7" fill="none" stroke="#E5E7EB" strokeWidth="1" />
                        <text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#E5E7EB" fontFamily="Arial,sans-serif">i</text>
                      </svg>
                    </button>
                  )}
                </>) : null}
              </div>
            </button>
          );
        })}
        {/* 8th slot — Daily Quote Card. Curated 125 quotes ≤100 chars. Grid 1fr rows = same height as day cards. */}
        {(() => {
          const QUOTES = [
            // SUN TZU
            { q: "Opportunities multiply as they are seized.", a: "Sun Tzu" },
            { q: "Know yourself and you will win all battles.", a: "Sun Tzu" },
            { q: "In the midst of chaos, there is also opportunity.", a: "Sun Tzu" },
            { q: "Speed is the essence of war.", a: "Sun Tzu" },
            { q: "The greatest victory is that which requires no battle.", a: "Sun Tzu" },
            // CONFUCIUS
            { q: "It does not matter how slowly you go as long as you do not stop.", a: "Confucius" },
            { q: "Everything has beauty, but not everyone sees it.", a: "Confucius" },
            { q: "Life is really simple, but we insist on making it complicated.", a: "Confucius" },
            { q: "He who learns but does not think is lost.", a: "Confucius" },
            { q: "Our greatest glory is not in never falling, but in rising every time we fall.", a: "Confucius" },
            { q: "Choose a job you love and you will never have to work a day in your life.", a: "Confucius" },
            // ARISTOTLE
            { q: "We are what we repeatedly do. Excellence, then, is not an act, but a habit.", a: "Aristotle" },
            { q: "Knowing yourself is the beginning of all wisdom.", a: "Aristotle" },
            { q: "Hope is a waking dream.", a: "Aristotle" },
            { q: "Happiness depends upon ourselves.", a: "Aristotle" },
            { q: "In all things of nature there is something of the marvelous.", a: "Aristotle" },
            // PLATO
            { q: "Be kind, for everyone you meet is fighting a harder battle.", a: "Plato" },
            { q: "Courage is knowing what not to fear.", a: "Plato" },
            { q: "The measure of a man is what he does with power.", a: "Plato" },
            // SOCRATES
            { q: "The unexamined life is not worth living.", a: "Socrates" },
            { q: "I know that I know nothing.", a: "Socrates" },
            { q: "To find yourself, think for yourself.", a: "Socrates" },
            { q: "Be as you wish to seem.", a: "Socrates" },
            // BERTRAND RUSSELL
            { q: "The good life is one inspired by love and guided by knowledge.", a: "Bertrand Russell" },
            { q: "The time you enjoy wasting is not wasted time.", a: "Bertrand Russell" },
            { q: "To conquer fear is the beginning of wisdom.", a: "Bertrand Russell" },
            { q: "Do not fear to be eccentric in opinion, for every opinion now accepted was once eccentric.", a: "Bertrand Russell" },
            // ALBERT CAMUS
            { q: "In the midst of winter, I found there was, within me, an invincible summer.", a: "Albert Camus" },
            { q: "Life is the sum of all your choices.", a: "Albert Camus" },
            { q: "Real generosity toward the future lies in giving all to the present.", a: "Albert Camus" },
            { q: "Blessed are the hearts that can bend; they shall never be broken.", a: "Albert Camus" },
            // CARL SAGAN
            { q: "Somewhere, something incredible is waiting to be known.", a: "Carl Sagan" },
            { q: "We are made of star stuff.", a: "Carl Sagan" },
            { q: "The cosmos is within us.", a: "Carl Sagan" },
            { q: "Extraordinary claims require extraordinary evidence.", a: "Carl Sagan" },
            { q: "We are a way for the cosmos to know itself.", a: "Carl Sagan" },
            { q: "For small creatures such as we the vastness is bearable only through love.", a: "Carl Sagan" },
            // NEIL DEGRASSE TYSON
            { q: "The universe is under no obligation to make sense to you.", a: "Neil deGrasse Tyson" },
            { q: "The good thing about science is that it's true whether or not you believe in it.", a: "Neil deGrasse Tyson" },
            { q: "The sky is not the limit. Your mind is.", a: "Neil deGrasse Tyson" },
            { q: "Kids are never the problem. They are born scientists.", a: "Neil deGrasse Tyson" },
            // STEPHEN HAWKING
            { q: "Intelligence is the ability to adapt to change.", a: "Stephen Hawking" },
            { q: "Remember to look up at the stars and not down at your feet.", a: "Stephen Hawking" },
            { q: "However difficult life may seem, there is always something you can do and succeed at.", a: "Stephen Hawking" },
            { q: "Quiet people have the loudest minds.", a: "Stephen Hawking" },
            { q: "Life would be tragic if it weren't funny.", a: "Stephen Hawking" },
            // ALBERT EINSTEIN
            { q: "Imagination is more important than knowledge.", a: "Albert Einstein" },
            { q: "A person who never made a mistake never tried anything new.", a: "Albert Einstein" },
            { q: "Life is like riding a bicycle. To keep your balance you must keep moving.", a: "Albert Einstein" },
            { q: "Logic will get you from A to B. Imagination will take you everywhere.", a: "Albert Einstein" },
            { q: "If you can't explain it simply, you don't understand it well enough.", a: "Albert Einstein" },
            { q: "Try not to become a man of success, but rather try to become a man of value.", a: "Albert Einstein" },
            // NIKOLA TESLA
            { q: "The present is theirs; the future, for which I really worked, is mine.", a: "Nikola Tesla" },
            { q: "Be alone, that is the secret of invention.", a: "Nikola Tesla" },
            { q: "I don't care that they stole my idea. I care that they don't have any of their own.", a: "Nikola Tesla" },
            { q: "Of all things, I liked books best.", a: "Nikola Tesla" },
            // WINSTON CHURCHILL
            { q: "Success is not final, failure is not fatal: it is the courage to continue that counts.", a: "Winston Churchill" },
            { q: "If you're going through hell, keep going.", a: "Winston Churchill" },
            { q: "We make a living by what we get, but we make a life by what we give.", a: "Winston Churchill" },
            { q: "Attitude is a little thing that makes a big difference.", a: "Winston Churchill" },
            { q: "You have enemies? Good. That means you've stood up for something.", a: "Winston Churchill" },
            // THEODORE ROOSEVELT
            { q: "Do what you can, with what you have, where you are.", a: "Theodore Roosevelt" },
            { q: "Believe you can and you're halfway there.", a: "Theodore Roosevelt" },
            { q: "Keep your eyes on the stars and your feet on the ground.", a: "Theodore Roosevelt" },
            { q: "The man who makes no mistakes does not usually make anything.", a: "Theodore Roosevelt" },
            { q: "In any moment of decision, the best thing you can do is the right thing.", a: "Theodore Roosevelt" },
            // LECH WALESA
            { q: "Deep faith eliminates fear.", a: "Lech Wałęsa" },
            { q: "Words are plentiful; deeds are precious.", a: "Lech Wałęsa" },
            { q: "We hold our heads high, despite the price we have paid, because freedom is priceless.", a: "Lech Wałęsa" },
            { q: "The thing that matters is not what you bear, but how you bear it.", a: "Lech Wałęsa" },
            { q: "You can't have freedom without responsibility.", a: "Lech Wałęsa" },
            // VACLAV HAVEL
            { q: "Work for something because it is good, not just because it stands a chance to succeed.", a: "Václav Havel" },
            { q: "Keep the company of those who seek the truth — run from those who have found it.", a: "Václav Havel" },
            { q: "Vision is not enough; it must be combined with venture.", a: "Václav Havel" },
            { q: "The salvation of this human world lies nowhere else than in the human heart.", a: "Václav Havel" },
            { q: "Anyone who takes himself too seriously always risks looking ridiculous.", a: "Václav Havel" },
            // FRANZ KAFKA
            { q: "There is infinite hope, but not for us.", a: "Franz Kafka" },
            { q: "In the fight between you and the world, back the world.", a: "Franz Kafka" },
            { q: "A book must be the axe for the frozen sea within us.", a: "Franz Kafka" },
            { q: "Start with what is right rather than what is acceptable.", a: "Franz Kafka" },
            { q: "Anyone who keeps the ability to see beauty never grows old.", a: "Franz Kafka" },
            // MIKE TYSON
            { q: "Everyone has a plan until they get punched in the face.", a: "Mike Tyson" },
            { q: "Discipline is doing what you hate to do, but do it like you love it.", a: "Mike Tyson" },
            { q: "Fear is your best friend or your worst enemy.", a: "Mike Tyson" },
            { q: "As long as we persevere and endure, we can get anything we want.", a: "Mike Tyson" },
            { q: "I'm a dreamer. I have to dream and reach for the stars.", a: "Mike Tyson" },
            // STEVE JOBS
            { q: "Stay hungry, stay foolish.", a: "Steve Jobs" },
            { q: "Innovation distinguishes between a leader and a follower.", a: "Steve Jobs" },
            { q: "Simple can be harder than complex.", a: "Steve Jobs" },
            { q: "Don't let the noise of others' opinions drown out your own inner voice.", a: "Steve Jobs" },
            { q: "Creativity is just connecting things.", a: "Steve Jobs" },
            { q: "The only way to do great work is to love what you do.", a: "Steve Jobs" },
            // WARREN BUFFETT
            { q: "Someone is sitting in the shade today because someone planted a tree a long time ago.", a: "Warren Buffett" },
            { q: "The best investment you can make is in yourself.", a: "Warren Buffett" },
            { q: "Price is what you pay. Value is what you get.", a: "Warren Buffett" },
            { q: "Risk comes from not knowing what you're doing.", a: "Warren Buffett" },
            { q: "The most important thing to do if you find yourself in a hole is to stop digging.", a: "Warren Buffett" },
            // NAVAL RAVIKANT
            { q: "Play long-term games with long-term people.", a: "Naval Ravikant" },
            { q: "Read what you love until you love to read.", a: "Naval Ravikant" },
            { q: "Happiness is a choice and a skill and you can dedicate yourself to learning that skill.", a: "Naval Ravikant" },
            { q: "A fit body, a calm mind, a house full of love. These things cannot be bought — they must be earned.", a: "Naval Ravikant" },
            // JAMES CLEAR
            { q: "You do not rise to the level of your goals. You fall to the level of your systems.", a: "James Clear" },
            { q: "Every action you take is a vote for the type of person you wish to become.", a: "James Clear" },
            { q: "Success is the product of daily habits — not once-in-a-lifetime transformations.", a: "James Clear" },
            { q: "Goals are good for setting a direction, but systems are best for making progress.", a: "James Clear" },
            { q: "Be the designer of your world and not merely the consumer of it.", a: "James Clear" },
            // RYAN HOLIDAY
            { q: "The obstacle is the way.", a: "Ryan Holiday" },
            { q: "Ego is the enemy.", a: "Ryan Holiday" },
            { q: "Stillness is the key.", a: "Ryan Holiday" },
            { q: "The best way to get what you want is to deserve what you want.", a: "Ryan Holiday" },
            // BRENE BROWN
            { q: "Vulnerability is not weakness; it's our greatest measure of courage.", a: "Brené Brown" },
            { q: "Talk to yourself like you would to someone you love.", a: "Brené Brown" },
            { q: "Owning our story can be hard but not nearly as difficult as spending our lives running from it.", a: "Brené Brown" },
            { q: "What makes you vulnerable makes you beautiful.", a: "Brené Brown" },
            { q: "Connection is why we're here. It is what gives purpose and meaning to our lives.", a: "Brené Brown" },
            // BARACK OBAMA
            { q: "Change will not come if we wait for some other person or some other time.", a: "Barack Obama" },
            { q: "The best way to not feel hopeless is to get up and do something.", a: "Barack Obama" },
            { q: "Our destiny is not written for us, but by us.", a: "Barack Obama" },
            { q: "We are the ones we've been waiting for.", a: "Barack Obama" },
            // DALAI LAMA
            { q: "My religion is very simple. My religion is kindness.", a: "Dalai Lama" },
            { q: "Remember that sometimes not getting what you want is a wonderful stroke of luck.", a: "Dalai Lama" },
            { q: "The purpose of our lives is to be happy.", a: "Dalai Lama" },
            { q: "Be kind whenever possible. It is always possible.", a: "Dalai Lama" },
            { q: "Sleep is the best meditation.", a: "Dalai Lama" },
            { q: "Judge your success by what you had to give up in order to get it.", a: "Dalai Lama" },
          ];
          const now = new Date();
          const start = new Date(now.getFullYear(), 0, 0);
          const dayOfYear = Math.floor((now - start) / 86400000);
          const quote = QUOTES[(dayOfYear * 17) % QUOTES.length];
          return (
            <div style={{
              background: '#0a0a0a',
              border: '2px solid #B8860B',
              borderRadius: 12,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              boxSizing: 'border-box',
              minWidth: 0,
              overflow: 'hidden',
              height: '100%',
            }}>
              <div style={{ fontSize: 13, color: '#FFFFFF', fontStyle: 'italic', lineHeight: 1.45, fontWeight: 500, marginBottom: 6 }}>{quote.q}</div>
              <div style={{ fontSize: 11, color: '#B8860B', fontWeight: 700, letterSpacing: '0.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>— {quote.a}</div>
            </div>
          );
        })()}
        </div>
      </div>
    );
  };

  // ── FORECAST DRILLDOWN — S98 spec: separate page, hourly 8AM-8PM ──
  const ForecastDrilldown = () => {
    if (!weatherData || !selectedForecastDay) return (
      <div style={{ padding: '40px 16px', textAlign: 'center', color: COLOR_MUTED }}>No data</div>
    );

    const dateStr = selectedForecastDay;
    const date = new Date(dateStr + 'T00:00:00');
    const dayLabel = (() => {
      const now = new Date(); now.setHours(0,0,0,0);
      const tomorrow = new Date(now); tomorrow.setDate(now.getDate()+1);
      if (date.getTime() === now.getTime()) return 'Today';
      if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    })();
    const isToday = dayLabel === 'Today';

    // Show all daylight hours 8AM-7PM; tint hours within user's playing window
    const hourly = weatherData.hourly || [];
    const [ddPsH] = (profile?.playStart || '08:00').split(':').map(Number);
    const [ddPeH] = (profile?.playEnd   || '20:00').split(':').map(Number);
    const dayHours = hourly.filter(h => {
      const hd = new Date(h.time); hd.setHours(0,0,0,0);
      const hr = new Date(h.time).getHours();
      return hd.getTime() === date.getTime() && hr >= 8 && hr <= 19;
    });

    // If no hourly data, synthesize from vcDailyAll
    const vcDailyAll = weatherData.vcDailyAll || [];
    const daily = vcDailyAll.find(v => v.date === dateStr);
    const hours = dayHours.length > 0 ? dayHours : (daily ? [8,9,10,11,12,13,14,15,16,17,18,19].map(hr => ({
      time: new Date(new Date(date).setHours(hr, 0, 0, 0)),
      temp: Math.round(daily.tempMin + (daily.tempMax - daily.tempMin) * (hr - 8) / 12),
      feelsLike: null,
      windSpeedNum: daily.windSpeed || 0,
      windGust: Math.round((daily.windSpeed || 0) * 1.3),
      precipChance: daily.precipChance || 0,
      conditions: daily.conditions || 'Clear',
      humidity: daily.humidity || 60,
      uvIndex: hr >= 10 && hr <= 16 ? 4 : 1,
      isDaytime: true,
    })) : []);

    return (
      <div>
        {/* Today-only: Cart + Pollen advisory strip — single horizontal line */}
        {isToday && (badResult?.ground || (pollenData?.success && !pollenData.offSeason)) && (
          <div style={{ padding: '6px 12px', background: '#111', borderBottom: `1px solid ${COLOR_DARK_GREEN}`, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {badResult?.ground && (
              <span style={{ fontSize: 12, fontWeight: 700, color: badResult.ground.sogginess === 'soggy' ? STATUS_COLORS.CAUTION.color : STATUS_COLORS.GO.color }}>
                🛺 Cart: {badResult.ground.cartStatus || 'All Clear'}
              </span>
            )}
            {pollenData?.success && !pollenData.offSeason && (
              <span style={{ fontSize: 12, color: COLOR_MUTED }}>
                🌿 Pollen: <span style={{ color: pollenData.tree.color }}>🌳 {pollenData.tree.label}</span>
                {' · '}<span style={{ color: pollenData.grass.color }}>🌾 {pollenData.grass.label}</span>
                {' · '}<span style={{ color: pollenData.weed.color }}>🌿 {pollenData.weed.label}</span>
              </span>
            )}
            {badResult?.ground && (() => {
              const { cartStatus } = badResult.ground;
              const fairways =
                cartStatus === 'Cart Path Only' ? 'Wet — ball stops on landing' :
                cartStatus === '90-Degree Rule Likely' ? 'Soft — reduced roll' :
                null;
              if (!fairways) return null;
              return (
                <span style={{ fontSize: 12, fontWeight: 700, color: cartStatus === 'Cart Path Only' ? STATUS_COLORS.CAUTION.color : STATUS_COLORS.GO.color }}>
                  ⛳ Fairways: {fairways}
                </span>
              );
            })()}
          </div>
        )}
        {/* Hour cards — 2-per-row grid per S115 spec */}
        <div style={{ padding: '8px 12px 24px', boxSizing: 'border-box', overflowX: 'hidden' }}>
          {hours.length > 0 ? (
            // S161 Item 5: Column-major fill — morning hours down col 1, afternoon down col 2.
            // 6 rows × 2 cols accommodates the 12 hours of playing window (8AM-7PM).
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'repeat(6, auto)', gridAutoFlow: 'column', gap: 6, alignItems: 'stretch' }}>
              {hours.map((h, i) => {
                const hHour = new Date(h.time).getHours();
                const nowHour = new Date().getHours();
                const isPastHour = isToday && hHour < nowHour;
                const isInWindow = hHour >= ddPsH && hHour < ddPeH;
                return <DrilldownHourCard key={i} h={h} profile={profile} isPast={isPastHour} isInWindow={isInWindow} />;
              })}
            </div>
          ) : (
            <div style={{ padding: '40px 16px', textAlign: 'center', color: COLOR_MUTED }}>
              No hourly data available for this day
            </div>
          )}
        </div>
      </div>
    );
  };
  const CourseHeader = () => (
    <div style={{ background: COLOR_HEADER, borderBottom: `1px solid ${COLOR_DARK_GREEN}`, position: 'sticky', top: 0, zIndex: 100 }}>
      <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <button
            onClick={() => navigateTo('coursedetail')}
            style={{ background: 'transparent', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_WHITE }}>{selectedCourse.name} <span style={{ fontSize: 10, color: '#7aafff' }}>ⓘ</span></div>
          </button>
          <div style={{ fontSize: 11, color: COLOR_MUTED }}>{selectedCourse.town} · Par {selectedCourse.par} · Elev. {selectedCourse.elevation}ft</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => { setShowSourceLegend(true); }} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', fontSize: 16 }} title="Data Sources">  {/* S237: open shows current state only — refresh is inside modal ↻ */}
            <span style={{ color: '#7aafff' }}>📡</span>
          </button>
          {isLoading && <div style={{ fontSize: 12, color: COLOR_MUTED }}>Loading…</div>}
          {lastRefreshTime && !isLoading && (
            <button
              onClick={() => {
                // S222: clearAllCaches() — complete cache clear on dashboard refresh
                clearAllCaches();
                loadCourse(selectedCourse);
              }}
              style={{ ...BTN_BASE, padding: '6px 12px', background: '#0a1a0a', border: `1px solid #4ADE80`, borderRadius: 8, fontSize: 12, color: '#4ADE80', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <span style={{ fontSize: 14 }}>↻</span> {lastRefreshTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </button>
          )}
          <button
            onClick={() => setShowCourseSwitcher(true)}
            style={{ ...BTN_BASE, padding: '6px 12px', background: COLOR_DARK_GREEN, border: `1px solid ${COLOR_GREEN}`, borderRadius: 8, fontSize: 12, color: COLOR_GREEN, fontWeight: 600 }}
          >
            Change
          </button>
        </div>
      </div>
    </div>
  );

  // ── SOURCE LEGEND OVERLAY ──────────────────────────────────
  // ── SOURCE LEGEND OVERLAY ──────────────────────────────────
  const SourceLegendOverlay = () => {
    if (!showSourceLegend) return null;
    const stationId = selectedCourse ? (NJWXNET_COURSE_MAP[selectedCourse.id] || null) : null;

    // Forecast cascade state — S174 per-variable cascade (GribStream/Meteoblue removed)
    const fcStatus = weatherData?._sourceStatus || {};
    const fcSources = weatherData?._sources || {};
    const fcAllFailed = !weatherData?.success;

    // Per-row dot logic: cascade table for one variable looks like
    //   [P1, P2, P3]  →  [green if won, yellow if fetched-and-standby, red if failed, muted if never tried]
    const cascadeMap = {
      // sourceCode: success flag for that source
      NWS:           fcStatus.nws,
      VC:            fcStatus.visualCrossing,
      WeatherAPI:    fcStatus.weatherapi,
      PirateWeather: fcStatus.pirateweather,
      OpenMeteo:     fcStatus.openMeteo,
      Meteoblue:     fcStatus.meteoblue,
    };
    const sourceLabel = {
      NWS: 'NWS Hourly', VC: 'Visual Crossing', WeatherAPI: 'WeatherAPI',
      PirateWeather: 'PirateWeather', OpenMeteo: 'Open-Meteo', Meteoblue: 'Meteoblue',
    };
    // For each row, the winner is the first source in cascade that succeeded.
    // Anything BEFORE the winner = failed (red). Anything AFTER = muted (never tried).
    const rowState = (cascade, winnerName) => {
      const winnerIdx = cascade.indexOf(winnerName);
      return cascade.map((src, i) => {
        if (winnerName == null) {
          // entire cascade failed — show all red
          return { name: sourceLabel[src], ok: false, standby: false };
        }
        if (i < winnerIdx) return { name: sourceLabel[src], ok: false, standby: false }; // failed
        if (i === winnerIdx) return { name: sourceLabel[src], ok: true, standby: false }; // active
        return { name: sourceLabel[src], ok: false, standby: true, muted: true };  // never tried
      });
    };

    const forecastRows = [
      { label: 'Temperature',    cascade: ['VC','PirateWeather','NWS'],         winner: fcSources.temp },
      { label: 'Wind',           cascade: ['VC','WeatherAPI','NWS'],            winner: fcSources.wind },
      { label: 'Gust',           cascade: ['VC','PirateWeather','WeatherAPI'],  winner: fcSources.gust },
      { label: 'Humidity',       cascade: ['VC','PirateWeather'],               winner: fcSources.humidity },
      { label: 'Precip 0–12h',   cascade: ['Meteoblue','VC','NWS'],             winner: fcSources.precip0_12 },  // S206: MB P1
      { label: 'Precip 12–48h',  cascade: ['Meteoblue','VC','OpenMeteo'],        winner: fcSources.precip12_48 },  // S206: MB P1
      { label: 'Precip 48h+',    cascade: ['OpenMeteo','WeatherAPI'],           winner: fcSources.precip48plus },
      { label: 'Conditions',     cascade: ['VC','OpenMeteo','WeatherAPI'],      winner: fcSources.conditions },
      { label: 'UV Index',       cascade: ['OpenMeteo','WeatherAPI'],           winner: fcSources.uv },
      { label: 'Feels Like',     cascade: [],                                   winner: 'calculated' },
    ];

    // Realtime P1/P2 — NJWxNet success drives everything (unchanged from S173)
    const njOk  = njwxData?.success;
    const p2DotOk      = !njOk;
    const p2DotStandby = njOk;

    const omOk  = fcStatus?.openMeteo;
    const iem   = lightningData !== null;
    const polOk = pollenData?.success;

    const Dot = ({ ok, standby }) => (
      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, flexShrink: 0,
        background: standby ? '#FACC15' : ok ? '#22C55E' : '#EF4444', verticalAlign: 'middle' }} />
    );

    const sourceColor = (active, standby, failed) => {
      if (active) return '#22C55E';
      if (standby) return '#FACC15';
      return failed ? '#EF4444' : COLOR_MUTED;
    };

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, overflowY: 'auto' }}
        onClick={() => setShowSourceLegend(false)}>
        <div style={{ maxWidth: 440, margin: '40px auto', padding: 20, background: '#111', borderRadius: 14 }} onClick={e => e.stopPropagation()}>

          {/* Header + refresh */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_WHITE }}>📡 Data Sources</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', marginTop: 2 }}>{selectedCourse?.name}</div>
            </div>
            <button onClick={() => setShowSourceLegend(false)} style={{ ...BTN_BASE, background: 'transparent', color: COLOR_MUTED, fontSize: 24, padding: 4 }}>×</button>
          </div>

          {/* Timestamp + refresh button — identical to header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            {lastRefreshTime && (
              <button
                onClick={() => { clearAllCaches(); loadCourse(selectedCourse); setModalRefreshTime(new Date()); }}
                style={{ ...BTN_BASE, padding: '6px 12px', background: '#0a1a0a', border: `1px solid #4ADE80`, borderRadius: 8, fontSize: 12, color: '#4ADE80', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
              >
                <span style={{ fontSize: 14 }}>↻</span> {(modalRefreshTime || lastRefreshTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </button>
            )}
            {isLoading && <span style={{ fontSize: 12, color: COLOR_MUTED }}>Loading…</span>}
          </div>

          {/* Forecast Sources */}
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GOLD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Forecast Sources</div>
          <div style={{ background: COLOR_CARD, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: COLOR_WHITE, lineHeight: 1.8 }}>
            {forecastRows.map(({ label, cascade, winner }) => {
              if (cascade.length === 0) {
                // Feels Like — calculated locally
                return (
                  <div key={label} style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 2 }}>
                    <span style={{ color: COLOR_MUTED, marginRight: 4 }}>{label}:</span>
                    <Dot ok={true} standby={false} />
                    <span style={{ color: '#22C55E' }}>Calculated (NWS formula)</span>
                  </div>
                );
              }
              const cells = rowState(cascade, winner);
              return (
                <div key={label} style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 2 }}>
                  <span style={{ color: COLOR_MUTED, marginRight: 4 }}>{label}:</span>
                  {cells.map((s, i) => (
                    <span key={s.name}>
                      <Dot ok={s.ok} standby={s.standby} />
                      <span style={{ color: sourceColor(s.ok, s.standby, false) }}>{s.name}</span>
                      {i < cells.length - 1 && <span style={{ color: COLOR_MUTED }}> → </span>}
                    </span>
                  ))}
                </div>
              );
            })}
            {fcAllFailed && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 4 }}>⚠ All forecast sources failed</div>}
          </div>

          {/* Realtime Sources — S216: reads from _sources/_rtSources, not hardcoded */}
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GOLD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Realtime Sources</div>
          <div style={{ background: COLOR_CARD, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: COLOR_WHITE, lineHeight: 1.8 }}>
            {(() => {
              const rtSrc = weatherData?._rtSources || weatherData?._sources || {};
              const rtRow = (varName, p1Label, p2Name) => {
                const winner = rtSrc[varName] || '';
                const p1Won = !!winner && !winner.startsWith(p2Name);
                const p2Won = !!winner && winner.startsWith(p2Name);
                return [
                  { name: p1Label, ok: p1Won || (!winner && njOk), standby: !p1Won && njOk && !p2Won },
                  { name: p2Won ? winner : p2Name, ok: p2Won, standby: p1Won },
                ];
              };
              return [
                { label: 'Temperature', sources: rtRow('temp',   `NJWxNet ${stationId || '—'}`, 'WeatherAPI') },
                { label: 'Wind',        sources: rtRow('wind',   `NJWxNet ${stationId || '—'}`, 'PirateWeather') },
                { label: 'Gust',        sources: rtRow('gust',   `NJWxNet ${stationId || '—'}`, 'PirateWeather') },
                { label: 'Precip',      sources: rtRow('precip', `NJWxNet ${stationId || '—'} 2h sum`, 'WeatherAPI') },
                { label: 'Feels Like',  sources: [{ name: 'Calculated (NWS formula)', ok: true, standby: false }] },
                { label: 'Sky',         sources: [{ name: 'Open-Meteo WMO', ok: omOk, standby: false }] },
              ];
            })().map(({ label, sources }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 2 }}>
                <span style={{ color: COLOR_MUTED, marginRight: 4 }}>{label}:</span>
                {sources.map((s, i) => (
                  <span key={s.name}>
                    <Dot ok={s.ok} standby={s.standby} />
                    <span style={{ color: sourceColor(s.ok, s.standby, false) }}>{s.name}</span>
                    {i < sources.length - 1 && <span style={{ color: COLOR_MUTED }}> → </span>}
                  </span>
                ))}
              </div>
            ))}
          </div>

          {/* Other Sources — Meteoblue UV row REMOVED (UV now in forecast cascade) */}
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GOLD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Other Sources</div>
          <div style={{ background: COLOR_CARD, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: COLOR_WHITE, lineHeight: 1.8 }}>
            <div><Dot ok={iem} standby={false}/><span style={{ color: COLOR_MUTED }}>Lightning:</span> <span style={{ color: iem ? '#22C55E' : '#EF4444' }}>IEM NEXRAD</span> (free, permanent)</div>
            <div><Dot ok={polOk} standby={false}/><span style={{ color: COLOR_MUTED }}>Pollen:</span> <span style={{ color: polOk ? '#22C55E' : '#EF4444' }}>Google Pollen API</span></div>
            <div><Dot ok={true} standby={false}/><span style={{ color: COLOR_MUTED }}>Events:</span> <span style={{ color: '#22C55E' }}>Spond</span> (cron 3 AM ET)</div>
          </div>

          {/* Legend */}
          <div style={{ fontSize: 11, color: COLOR_MUTED, textAlign: 'center' }}>
            <svg width="8" height="8" style={{display:'inline-block',verticalAlign:'middle',marginRight:4}}><circle cx="4" cy="4" r="4" fill="#22C55E"/></svg>active{'  '}
            <svg width="8" height="8" style={{display:'inline-block',verticalAlign:'middle',marginRight:4}}><circle cx="4" cy="4" r="4" fill="#FACC15"/></svg>standby{'  '}
            <svg width="8" height="8" style={{display:'inline-block',verticalAlign:'middle',marginRight:4}}><circle cx="4" cy="4" r="4" fill="#EF4444"/></svg>error
          </div>
          <div style={{ fontSize: 11, color: COLOR_MUTED, textAlign: 'center', marginTop: 8 }}>Tap outside to close</div>
        </div>
      </div>
    );
  };

  // ── MAIN RENDER ────────────────────────────────────────────
  // S166: outer ErrorBoundary catches render-time errors anywhere in the App tree.
  // Previously only the review screen inside ScorecardScreen was wrapped — a render
  // error elsewhere produced a white screen. This converts any such crash into a
  // visible red panel with the error message + Dismiss button.
  return (
    <ErrorBoundary>
    <div style={{ minHeight: '100svh', background: COLOR_BG, overflowX: 'hidden' }}>
      {/* ── OVERLAYS — rendered at root level, never clipped ── */}
      <TriggerPopupOverlay />
      <SourceLegendOverlay />
    <div style={{ background: COLOR_BG, color: COLOR_WHITE, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', maxWidth: 480, margin: '0 auto', overflowX: 'hidden' }}>
      {/* ── LAST ROUND OVERLAY ─────────────────────────────── */}
      {showRoundOverlay && (() => {
        const allRounds = getLocalRounds();
        const lastRound = allRounds.length > 0 ? allRounds[allRounds.length - 1] : null;
        // Season best
        let bestRound = null;
        let bestRoundDiff = Infinity;
        allRounds.forEach(r => {
          const t = (r.players || []).reduce((s, p) => {
            const tot = (p.scores || []).reduce((a, v) => a + (parseInt(v) || 0), 0);
            return s + tot;
          }, 0);
          const par = r.par || 72;
          const d = t - par;
          if (d < bestRoundDiff) { bestRoundDiff = d; bestRound = { ...r, total: t }; }
        });
        if (!lastRound) return null;
        const lastTotal = (lastRound.players || []).reduce((s, p) => {
          return s + (p.scores || []).reduce((a, v) => a + (parseInt(v) || 0), 0);
        }, 0);
        const lastPar = lastRound.par || 72;
        if (lastTotal === 0) return null; // No valid scores entered
        const lastDiff = lastTotal - lastPar;
        // eslint-disable-next-line no-unused-vars
        const lastDiffStr = lastDiff === 0 ? 'E' : lastDiff > 0 ? `+${lastDiff}` : `${lastDiff}`;
        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.75)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }} onClick={dismissOverlay}>
            <div style={{
              background: '#1e3620', border: `2px solid ${COLOR_GREEN}`, borderRadius: 16,
              padding: 20, maxWidth: 380, width: '100%', position: 'relative',
            }} onClick={e => e.stopPropagation()}>
              {/* X button */}
              <button onClick={dismissOverlay} style={{
                position: 'absolute', top: 12, right: 12,
                background: 'transparent', border: 'none', color: COLOR_MUTED,
                fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4,
              }}>×</button>

              {/* Last round */}
              <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Last Round
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_WHITE, marginBottom: 2 }}>
                {lastRound.course || lastRound.courseName || 'Unknown course'}
              </div>
              <div style={{ fontSize: 12, color: COLOR_MUTED, marginBottom: 10 }}>
                {lastRound.date ? new Date(lastRound.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
              </div>
              {(lastRound.players || [])
                .filter(p => p.scores && p.scores.some(s => s !== null && s !== ''))
                .sort((a, b) => {
                  const aT = (a.scores||[]).reduce((s,v)=>s+(parseInt(v)||0),0);
                  const bT = (b.scores||[]).reduce((s,v)=>s+(parseInt(v)||0),0);
                  return aT - bT;
                })
                .map((p, i) => {
                  const tot = (p.scores||[]).reduce((s,v)=>s+(parseInt(v)||0),0);
                  const diff = tot - lastPar;
                  const diffStr = diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`;
                  const medal = i===0?'1st':i===1?'2nd':i===2?'3rd':'';
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', background: i===0?'rgba(102,187,106,0.15)':'rgba(0,0,0,0.2)', borderRadius: 6, marginBottom: 4 }}>
                      <div style={{ fontSize: 14, color: COLOR_WHITE }}>{medal} {p.name}</div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_WHITE }}>{tot}</div>
                        <div style={{ fontSize: 13, color: diff<0?'#4ADE80':diff>0?'#FB923C':COLOR_MUTED, fontWeight: 600, minWidth: 28, textAlign: 'right' }}>{diffStr}</div>
                      </div>
                    </div>
                  );
                })}

              {/* Season best — only show if different round */}
              {bestRound && bestRound.date !== lastRound.date && (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${COLOR_DARK_GREEN}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_GOLD, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                    Season Best
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 14, color: COLOR_WHITE, fontWeight: 600 }}>{bestRound.course || bestRound.courseName || 'Unknown'}</div>
                      <div style={{ fontSize: 12, color: COLOR_MUTED }}>
                        {bestRound.date ? new Date(bestRound.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: COLOR_GOLD }}>{bestRound.total}</div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${COLOR_DARK_GREEN}`, fontSize: 11, color: COLOR_MUTED, textAlign: 'center', lineHeight: 1.5 }}>
                To see this anytime, go to <span style={{ color: COLOR_GREEN, fontWeight: 700 }}>Statistics → Round History</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Sub-screen routing (profile, radar, scorecard, etc.) */}
      {screen === 'stormWatch' && <StormWatchScreen />}
      {screen === 'home' && <><CourseHeader /><HomeScreen /></>}
      {screen === 'forecast' && <ForecastScreen />}
      {screen === 'forecastdrilldown' && <><Header title={(() => { if (!selectedForecastDay) return 'Forecast'; const d = new Date(selectedForecastDay + 'T00:00:00'); const now = new Date(); now.setHours(0,0,0,0); const tom = new Date(now); tom.setDate(now.getDate()+1); const day = d.getTime()===now.getTime()?'Today':d.getTime()===tom.getTime()?'Tomorrow':d.toLocaleDateString('en-US',{weekday:'long'}); const dt = d.toLocaleDateString('en-US',{month:'short',day:'numeric'}); return `${day} · ${dt}`; })()} showBack /><ForecastDrilldown /></>}
      {screen === 'dayofplay' && <><Header title="Game Day" showBack /><OnCourseScreen /></>}
      {screen === 'profile' && <><Header title="Settings" showBack /><ProfileScreen /></>}
      {screen === 'drilldown' && <><Header title="Hourly Forecast" showBack /><Drilldown /></>}
      {screen === 'coursedetail' && <><Header title="Course Directory" showBack /><CourseDirectory /></>}
      {screen === 'nineteenth' && <><Header title="19th Hole" showBack /><NineteenthHoleScreen /></>}
      {screen === 'scorecard' && (
        <><Header title={scorecardInReview ? "Confirmation" : "Scorecard"} showBack onBack={() => { if (scorecardBackGuard.current) { scorecardBackGuard.current(); } else { goBack(); } }} />
        <ScorecardScreen selectedCourse={selectedCourse} sharedRounds={sharedRounds} setSharedRounds={setSharedRounds} navigateTo={navigateTo} goBack={goBack} cfgPlayers={cfgPlayers} scorecardBackGuard={scorecardBackGuard} cfgHolePars={cfgHolePars} weatherData={weatherData} badResult={badResult} spondEvents={spondEvents} onEnterReview={() => setScorecardInReview(true)} onExitReview={() => setScorecardInReview(false)} /></>
      )}
      {screen === 'events' && <><Header title="Upcoming Events" showBack /><EventsScreen /></>}
      {screen === 'roundhistory' && <><Header title="Round History" showBack /><RoundHistoryScreen initialTab={(screenParams['roundhistory']||{}).initialTab} filterPlayer={(screenParams['roundhistory']||{}).filterPlayer} /></>}
      {screen === 'scorestats' && <><Header title="Statistics" showBack /><ScoreStatsScreen /></>}
      {screen === 'statsoverview' && <><Header title="Overview" showBack /><StatsOverviewScreen /></>}
      {screen === 'statsplayers' && <><Header title="Players Luck Charm" showBack /><StatsPlayersScreen /></>}
      {screen === 'admin' && isAdmin && <><Header title="Admin" showBack /><AdminPanel /></>}
      {screen === 'recover' && <><Header title="Data Recovery" showBack /><RecoveryScreen webhookUrl={SCORECARD_WEBHOOK} /></>}

      {/* Mode screens — only when no sub-screen active (legacy modes reachable from Home) */}
      {!screen && !selectedCourse && !showCourseSwitcher && (
        // R-1: First-time user — no course selected, show course selector immediately
        <div style={{ padding: '40px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_MUTED, marginBottom: 12 }}>Welcome to ZAP — select a course to get started.</div>
          <button onClick={() => setShowCourseSwitcher(true)} style={{ ...BTN_LARGE, background: COLOR_GREEN_DARK, justifyContent: 'center', color: COLOR_WHITE }}>Select a Course</button>
        </div>
      )}
      {!screen && selectedCourse && (
        <>
          <CourseHeader />
          {mode === 'planning' && <PlanningScreen />}
          {mode === 'oncourse' && <OnCourseScreen />}
          {mode === '19thhole' && <NineteenthHoleStub />}
          {mode === 'scorecard' && <ScorecardTabScreen />}
        </>
      )}
    </div>
    </div>
    </ErrorBoundary>
  );
}



































































